//MIT License
//
//Part of this code is copyright (c) 2020 Devias
//
//Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
//
//The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
//
//THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Autocomplete from '@material-ui/lab/Autocomplete';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Collapse,
  Grid,
  Badge,
  TextField,
  Typography,
  makeStyles,
  Avatar,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Menu,
  MenuItem,
  Tooltip
} from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import MarkunreadMailboxIcon from '@material-ui/icons/MarkunreadMailbox';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import PersonIcon from '@material-ui/icons/Person';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ReceiptIcon from '@material-ui/icons/Receipt';
import CancelIcon from '@material-ui/icons/Cancel';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import VideoCallIcon from '@material-ui/icons/VideoCall';
import VisibilityIcon from '@material-ui/icons/Visibility';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import EditIcon from '@material-ui/icons/Edit';
import GetAppIcon from '@material-ui/icons/GetApp';
import html2pdf from 'html2pdf.js';
import axios from 'axios';
import InvoicePrintableContent from '../../components/InvoicePrintableContent';
import { calculatePayslipPayroll } from './payslipEngine';
import { useSelector, useDispatch } from "react-redux"
import ClinicSubscription_Settings from "./clinicSubscription_Settings";
import CRMProviderDashboard from './CRMProviderDashboard';
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { apiBaseUrl } from '../../utilities/configureApiFetch';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import VideoCallModal from '../../components/VideoCall/VideoCallModal';

const ACTIVE_VIDEO_CALL_SESSION_KEY = 'virtus.videoCall.active';

const isVideoCallAlreadyActive = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage.getItem(ACTIVE_VIDEO_CALL_SESSION_KEY) === '1';
  } catch (error) {
    return false;
  }
};

const warnIfVideoCallAlreadyActive = () => {
  if (!isVideoCallAlreadyActive()) {
    return false;
  }

  window.alert('A videocall is already open. Close the current call before starting another one.');
  return true;
};

// Country list for UK, EU, and Norway
const countryOptions = [
  'Denmark',
  'United Kingdom',
  'Norway',
  'Sweden',
  'Finland',
  'Germany',
  'France',
  'Netherlands',
  'Belgium',
  'Spain',
  'Italy',
  'Poland',
  'Austria',
  'Ireland',
  'Portugal',
  'Greece',
  'Czech Republic',
  'Hungary',
  'Romania',
  'Bulgaria',
  'Slovakia',
  'Slovenia',
  'Estonia',
  'Latvia',
  'Lithuania',
  'Luxembourg',
  'Croatia',
  'Cyprus',
  'Malta',
];

const SELECT_MENU_PROPS = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: { style: { borderRadius: 14, marginTop: 8 } },
};

const CLASS_WEEKDAY_OPTIONS = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const normalizeClassWeekdays = (weekdays) => Array.from(new Set((Array.isArray(weekdays) ? weekdays : [weekdays])
  .map((weekday) => Number(weekday))
  .filter((weekday) => Number.isFinite(weekday) && weekday >= 0 && weekday <= 6)))
  .sort((left, right) => left - right);

const formatClassWeekdayLabel = (weekday) => {
  const normalizedWeekdays = normalizeClassWeekdays(weekday);
  if (normalizedWeekdays.length === 0) {
    return 'Unset day';
  }

  return normalizedWeekdays
    .map((value) => CLASS_WEEKDAY_OPTIONS.find((option) => option.value === value)?.label || 'Unset day')
    .join(', ');
};

const normalizeClassWeekdayPrimaryStaffs = (weekdayPrimaryStaffs, weekdays) => {
  const selectedWeekdays = normalizeClassWeekdays(weekdays);
  const sourceMap = weekdayPrimaryStaffs && typeof weekdayPrimaryStaffs === 'object' ? weekdayPrimaryStaffs : {};

  return selectedWeekdays.reduce((accumulator, weekday) => {
    const staffId = String(sourceMap[String(weekday)] || sourceMap[weekday] || '').trim();
    if (staffId) {
      accumulator[String(weekday)] = staffId;
    }
    return accumulator;
  }, {});
};

const getClassWeekdayPrimaryStaffValue = (weekdayPrimaryStaffs, weekday, fallbackValue = '') => {
  if (!weekdayPrimaryStaffs || typeof weekdayPrimaryStaffs !== 'object') {
    return String(fallbackValue || '').trim();
  }

  return String(weekdayPrimaryStaffs[String(weekday)] || weekdayPrimaryStaffs[weekday] || fallbackValue || '').trim();
};

const CLASS_SCHEDULE_START_MINUTES = 6 * 60;
const CLASS_SCHEDULE_END_MINUTES = 22 * 60;
const CLASS_SCHEDULE_SLOT_MINUTES = 30;
const CLASS_SCHEDULE_ROW_HEIGHT = 24;
const CLASS_SCHEDULE_HOUR_MARKERS = Array.from(
  { length: Math.floor((CLASS_SCHEDULE_END_MINUTES - CLASS_SCHEDULE_START_MINUTES) / 60) + 1 },
  (_, index) => CLASS_SCHEDULE_START_MINUTES + (index * 60),
);

const formatClassScheduleMinutesLabel = (minutes) => {
  const safeMinutes = Math.max(0, Number(minutes) || 0);
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const getClassStartMinutes = (startTime) => {
  const [hours, minutes] = String(startTime || '').split(':').map((value) => Number(value));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }
  return (hours * 60) + minutes;
};

const getClassScheduleSlotIndexFromClientY = (clientY, rect) => {
  if (!rect || typeof clientY !== 'number') {
    return 0;
  }

  const relativeY = Math.max(0, clientY - rect.top);
  const slotIndex = Math.round(relativeY / CLASS_SCHEDULE_ROW_HEIGHT);
  return Math.max(0, Math.min(slotIndex, Math.floor((CLASS_SCHEDULE_END_MINUTES - CLASS_SCHEDULE_START_MINUTES) / CLASS_SCHEDULE_SLOT_MINUTES) - 1));
};

const buildClassScheduleRangeFromIndices = (anchorIndex, currentIndex) => {
  const safeAnchorIndex = Number.isFinite(anchorIndex) ? anchorIndex : 0;
  const safeCurrentIndex = Number.isFinite(currentIndex) ? currentIndex : safeAnchorIndex;
  const startIndex = Math.min(safeAnchorIndex, safeCurrentIndex);
  const endIndex = Math.max(safeAnchorIndex, safeCurrentIndex) + 1;
  return {
    startMinutes: CLASS_SCHEDULE_START_MINUTES + (startIndex * CLASS_SCHEDULE_SLOT_MINUTES),
    endMinutes: CLASS_SCHEDULE_START_MINUTES + (endIndex * CLASS_SCHEDULE_SLOT_MINUTES),
  };
};

const getClassScheduleBlockLayout = (service) => {
  const startMinutes = getClassStartMinutes(service.class_start_time);
  const durationMinutes = Number(service.duration_minutes || 0);
  if (startMinutes == null || !durationMinutes || Number.isNaN(durationMinutes)) {
    return null;
  }

  const boundedStartMinutes = Math.max(CLASS_SCHEDULE_START_MINUTES, startMinutes);
  const boundedEndMinutes = Math.min(CLASS_SCHEDULE_END_MINUTES, boundedStartMinutes + durationMinutes);
  const top = ((boundedStartMinutes - CLASS_SCHEDULE_START_MINUTES) / CLASS_SCHEDULE_SLOT_MINUTES) * CLASS_SCHEDULE_ROW_HEIGHT;
  const height = Math.max(
    ((boundedEndMinutes - boundedStartMinutes) / CLASS_SCHEDULE_SLOT_MINUTES) * CLASS_SCHEDULE_ROW_HEIGHT,
    CLASS_SCHEDULE_ROW_HEIGHT,
  );

  return { top, height };
};

const MARK_PAID_PAYMENT_METHOD_OPTIONS = [
  { value: 'MobilePay', label: 'MobilePay' },
  { value: 'Card', label: 'Card (terminal)' },
  { value: 'CardAutopay', label: 'Card (stripe)' },
  { value: 'Cash', label: 'Cash' },
  { value: 'Invoice', label: 'Invoice' },
];

const normalizeMarkPaidPaymentMethodValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  const normalizedMap = {
    mobilepay: 'MobilePay',
    card: 'Card',
    'card (terminal)': 'Card',
    cardautopay: 'CardAutopay',
    'card (stripe)': 'CardAutopay',
    cash: 'Cash',
    invoice: 'Invoice',
    other: 'Invoice',
  };

  return normalizedMap[normalized] || MARK_PAID_PAYMENT_METHOD_OPTIONS[0]?.value || '';
};

const getCurrentMonthInputValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const toYearMonthToken = (value = '') => String(value || '').replace('-', '').slice(0, 6);

const getMonthRangeFromInput = (value = '') => {
  const monthToken = toYearMonthToken(value);
  if (monthToken.length !== 6) {
    return null;
  }

  const year = Number(monthToken.slice(0, 4));
  const monthIndex = Number(monthToken.slice(4, 6)) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) {
    return null;
  }

  return {
    start: moment({ year, month: monthIndex, date: 1 }).startOf('day'),
    end: moment({ year, month: monthIndex, date: 1 }).endOf('month').startOf('day'),
  };
};

const getOverlapDaysForMonth = (startDate, endDate, monthRange) => {
  if (!startDate || !endDate || !monthRange) {
    return 0;
  }

  const absenceStart = moment(startDate).startOf('day');
  const absenceEnd = moment(endDate).startOf('day');
  const overlapStart = moment.max(absenceStart, monthRange.start);
  const overlapEnd = moment.min(absenceEnd, monthRange.end);

  if (overlapEnd.isBefore(overlapStart, 'day')) {
    return 0;
  }

  return overlapEnd.diff(overlapStart, 'days') + 1;
};

const buildPayslipWorkHoursBreakdown = ({ monthValue, weeklyWorkHours, absenceSummary }) => {
  const monthRange = getMonthRangeFromInput(monthValue);
  if (!monthRange) {
    return [];
  }

  const registeredWeeklyHours = Number(weeklyWorkHours || 0) || 0;
  const dailyWorkHours = registeredWeeklyHours > 0 ? registeredWeeklyHours / 5 : 0;
  const ranges = Array.isArray(absenceSummary?.ranges) ? absenceSummary.ranges : [];

  const weekMap = new Map();
  const cursor = monthRange.start.clone().startOf('day');
  const monthEnd = monthRange.end.clone().startOf('day');

  while (cursor.isSameOrBefore(monthEnd, 'day')) {
    const isWorkday = cursor.isoWeekday() >= 1 && cursor.isoWeekday() <= 5;
    const weekKey = `${cursor.isoWeekYear()}-${String(cursor.isoWeek()).padStart(2, '0')}`;
    if (!weekMap.has(weekKey)) {
      weekMap.set(weekKey, {
        weekKey,
        weekNumber: cursor.isoWeek(),
        weekYear: cursor.isoWeekYear(),
        startDate: cursor.clone().startOf('isoWeek'),
        endDate: cursor.clone().endOf('isoWeek'),
        days: [],
      });
    }
    if (isWorkday) {
      weekMap.get(weekKey).days.push(cursor.clone());
    }
    cursor.add(1, 'day');
  }

  return Array.from(weekMap.values())
    .filter((week) => Array.isArray(week.days) && week.days.length > 0)
    .sort((left, right) => left.days[0].valueOf() - right.days[0].valueOf())
    .map((week) => {
      const monthWeekdays = week.days;
      const weekAbsenceDays = monthWeekdays.filter((day) => ranges.some((range) => {
        if (!range?.start_date || !range?.end_date) return false;
        const dayToken = day.format('YYYY-MM-DD');
        return dayToken >= String(range.start_date) && dayToken <= String(range.end_date);
      }));
      const absenceDayTokens = Array.from(new Set(weekAbsenceDays.map((day) => day.format('YYYY-MM-DD'))));
      const absenceDateLabels = weekAbsenceDays.length > 0 ? weekAbsenceDays.map((day) => day.format('D MMM')).join(', ') : '';
      const absenceDayCount = absenceDayTokens.length;
      const workdayCount = monthWeekdays.length;
      const weekRegisteredHours = workdayCount * dailyWorkHours;
      const subtotalHours = Math.max(weekRegisteredHours - (absenceDayCount * dailyWorkHours), 0);

      return {
        weekKey: week.weekKey,
        weekNumber: week.weekNumber,
        weekYear: week.weekYear,
        dateRangeLabel: `${week.startDate.format('D MMM')} - ${week.endDate.format('D MMM')}`,
        registeredHours: weekRegisteredHours,
        workdayCount,
        absenceDayCount,
        absenceDateLabels,
        dailyWorkHours,
        subtotalHours,
      };
    });
};

const buildPayslipSaldoOverviewRows = ({
  monthValue,
  weeklyWorkHours,
  staffId,
  currentPayroll,
  archivedPayrolls = [],
  vacationRecords = [],
  isDanishClinic = false,
}) => {
  const periodToken = toYearMonthToken(monthValue);
  if (periodToken.length !== 6) {
    return [];
  }

  const targetYear = periodToken.slice(0, 4);
  const targetMonth = Number(periodToken.slice(4, 6));
  const staffToken = String(staffId || '');
  const asNumber = (value) => Number(value || 0) || 0;
  const extractPayroll = (source = {}) => source?.payload?.payroll || source?.payload || source || {};
  const current = extractPayroll(currentPayroll);
  const currentVacation = current?.vacation || {};
  const relevantVacations = Array.isArray(vacationRecords)
    ? vacationRecords.filter((vacation) => String(vacation.staff_member || vacation.staff_member_id || '') === staffToken)
    : [];

  const monthAbsenceSummary = (monthToken) => {
    const monthRange = getMonthRangeFromInput(monthToken);
    if (!monthRange) {
      return { ranges: [] };
    }

    const ranges = [];
    relevantVacations.forEach((vacation) => {
      const days = getOverlapDaysForMonth(vacation.start_date, vacation.end_date, monthRange);
      if (!days) {
        return;
      }
      ranges.push({
        type: vacation.absence_type || 'vacation',
        start_date: vacation.start_date,
        end_date: vacation.end_date,
        days,
      });
    });

    return { ranges };
  };

  const monthlyWorkHours = (monthToken) => {
    const breakdown = buildPayslipWorkHoursBreakdown({
      monthValue: monthToken,
      weeklyWorkHours,
      absenceSummary: monthAbsenceSummary(monthToken),
    });
    return breakdown.reduce((sum, week) => sum + asNumber(week.subtotalHours), 0);
  };

  const archiveMap = new Map();
  (Array.isArray(archivedPayrolls) ? archivedPayrolls : []).forEach((archive) => {
    const reportToken = toYearMonthToken(archive?.report_month || '');
    if (!reportToken || reportToken.slice(0, 4) !== targetYear || reportToken >= periodToken) {
      return;
    }
    archiveMap.set(reportToken, extractPayroll(archive));
  });

  const sumRow = (selector, periodValue, options = {}) => {
    const annualValue = (() => {
      let total = 0;
      for (let monthIndex = 1; monthIndex <= targetMonth; monthIndex += 1) {
        const monthToken = `${targetYear}${String(monthIndex).padStart(2, '0')}`;
        const payroll = monthToken === periodToken ? current : archiveMap.get(monthToken);
        if (!payroll) {
          continue;
        }
        total += asNumber(selector(payroll, monthToken));
      }
      return total;
    })();

    return {
      label: options.label,
      periodValue,
      annualValue,
      unit: options.unit || 'money',
    };
  };

  const currentVacationValue = currentVacation.scheme === 'holiday_allowance'
    ? asNumber(currentVacation.grossAllowance || currentVacation.gross_allowance || currentVacation.brutto_feriepenge || current.brutto_feriepenge)
    : asNumber(currentVacation.daysAccrued || currentVacation.days_accrued || current.vacation_accrual_per_month);

  const rowConfigs = [
    {
      label: isDanishClinic ? 'AM-indkomst' : 'AM income',
      period: asNumber(current.AM_indkomst || current.am_income),
      unit: 'money',
      selector: (payroll) => asNumber(payroll.AM_indkomst || payroll.am_income),
    },
    {
      label: isDanishClinic ? 'A-indkomst' : 'A income',
      period: asNumber(current.A_indkomst || current.a_income),
      unit: 'money',
      selector: (payroll) => asNumber(payroll.A_indkomst || payroll.a_income),
    },
    {
      label: isDanishClinic ? 'A-skat' : 'Tax',
      period: asNumber(current.A_skat || current.a_skat),
      unit: 'money',
      selector: (payroll) => asNumber(payroll.A_skat || payroll.a_skat),
    },
    {
      label: isDanishClinic ? 'AM-bidrag' : 'AM contribution',
      period: asNumber(current.AM_bidrag || current.am_bidrag),
      unit: 'money',
      selector: (payroll) => asNumber(payroll.AM_bidrag || payroll.am_bidrag),
    },
    {
      label: 'ATP',
      period: asNumber(current.atp_employee || current.ATP_medarbejder) + asNumber(current.atp_employer || current.ATP_arbejdsgiver),
      unit: 'money',
      selector: (payroll) => asNumber(payroll.atp_employee || payroll.ATP_medarbejder) + asNumber(payroll.atp_employer || payroll.ATP_arbejdsgiver),
    },
    {
      label: isDanishClinic ? 'Pension' : 'Pension',
      period: asNumber(current.pension_employee || current.medarbejder_pension) + asNumber(current.pension_employer || current.pension_arbejdsgiver),
      unit: 'money',
      selector: (payroll) => asNumber(payroll.pension_employee || payroll.medarbejder_pension) + asNumber(payroll.pension_employer || payroll.pension_arbejdsgiver),
    },
    {
      label: isDanishClinic ? 'Ferieberettiget indkomst' : 'Vacationable income',
      period: asNumber(current.ferieberettiget_indkomst || current.gross_pay || current.grossPay),
      unit: 'money',
      selector: (payroll) => asNumber(payroll.ferieberettiget_indkomst || payroll.gross_pay || payroll.grossPay),
    },
    {
      label: isDanishClinic ? 'Arbejdstimer' : 'Work hours',
      period: monthlyWorkHours(periodToken),
      unit: 'hours',
      selector: (_payroll, monthToken) => monthlyWorkHours(monthToken),
    },
    {
      label: isDanishClinic ? 'Optjent ferie' : 'Accrued vacation',
      period: currentVacationValue,
      unit: currentVacation.scheme === 'holiday_allowance' ? 'money' : 'days',
      selector: (payroll) => {
        const payrollVacation = payroll?.vacation || {};
        return payrollVacation.scheme === 'holiday_allowance'
          ? asNumber(payrollVacation.grossAllowance || payrollVacation.gross_allowance || payrollVacation.brutto_feriepenge || payroll.brutto_feriepenge)
          : asNumber(payrollVacation.daysAccrued || payrollVacation.days_accrued || payroll.vacation_accrual_per_month);
      },
    },
  ];

  return rowConfigs.map((config) => ({
    label: config.label,
    periodValue: config.period,
    annualValue: sumRow(config.selector, config.period, { label: config.label, unit: config.unit }).annualValue,
    unit: config.unit,
  }));
};

const SERVICE_COLOR_PRESETS = ['#1976d2', '#002D55', '#FF7F27', '#4caf50', '#f44336', '#9c27b0', '#00bcd4', '#ff9800'];
const DEFAULT_SERVICE_COLOR = SERVICE_COLOR_PRESETS[0];

const useStyles = makeStyles((theme) => ({
  '@global': {
    '.MuiMenuItem-root': {
      fontSize: 'clamp(0.72rem, 0.9vw, 0.875rem) !important',
    },
    '.MuiMenu-paper': {
      borderRadius: '14px !important',
    },
  },
  root: { 
    backgroundColor: "#f7faff",
    borderRadius: 14,
  },
  Button: {
    color: "#FFFFFF",
    backgroundColor: "#002D55",
    borderRadius: 18,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(4px, 0.39vw, 6px) clamp(10px, 1.04vw, 16px)',
    marginRight: 'clamp(6px, 0.52vw, 8px)',
    fontSize: 'clamp(0.72rem, calc(2px + 0.722vw), 1.0rem)',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: "#fb741a",
    }
  },
  detailsSaveButton: {
    borderRadius: 50,
    border: '2px solid transparent',
    boxShadow: 'none',
    minHeight: 'clamp(38px, 3vw, 44px)',
    minWidth: 'clamp(170px, 15vw, 220px)',
    margin: 0,
    '&:hover': {
      boxShadow: 'none',
    },
  },
  detailsCancelButton: {
    borderRadius: 50,
    border: '2px solid transparent',
    boxShadow: 'none',
    minHeight: 'clamp(38px, 3vw, 44px)',
    margin: 0,
    '&:hover': {
      boxShadow: 'none',
    },
  },
  Button2: {
    transition: 'background 0.2s',
    backgroundColor: '#002D55',
    color: '#FFFF',
    borderRadius: 18,
    zIndex: 2,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(4px, 0.39vw, 6px) clamp(10px, 1.04vw, 16px)',
    fontSize: 'clamp(0.68rem, calc(2px + 0.722vw), 0.95rem)',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#F79500',
      fontWeight: 'bold',
      zIndex: 2,
    },
  },
  Button3: {
    transition: 'background 0.2s',
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 18,
    zIndex: 2,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(4px, 0.39vw, 6px) clamp(10px, 1.04vw, 16px)',
    fontSize: 'clamp(0.68rem, calc(2px + 0.722vw), 0.95rem)',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#F79500',
      fontWeight: 'bold',
      zIndex: 2,
    },
  },
  actionButton: {
    height: 36,
    minWidth: 120,
    padding: '6px 12px',
  },
  avatar: {
    backgroundColor: '#6a8caf',
    height: 40,
    width: 40,
    marginRight: 0,
    marginLeft: 10,
    marginTop: 0,
  },
      textfieldSpacing: {
        marginBottom: 8,
      },
  card: {
    background: 'transparent',
    borderRadius: 14,
    border: 'none',
    padding: 'clamp(8px, 1.05vw, 24px)',
    boxShadow: 'none',
  },
  cardContent: {
    background: 'transparent',
    borderRadius: 14,
    padding: 'clamp(12px, 1.25vw, 24px)',
  },
  tabsIndicator: {
    backgroundColor: '#FF7F27',
    height: 3,
    borderRadius: 18,
    bottom: 0,
  },
  tabRoot: {
    fontWeight: 600,
    fontSize: 'clamp(0.75rem, 1.04vw, 0.95rem)',
    textTransform: 'uppercase',
    color: '#002D55',
    minHeight: 'clamp(24px, calc(4px + 4.5vw), 34px)',
    height: 'clamp(24px, calc(4px + 4.5vw), 34px)',
    minWidth: 'clamp(60px, 6.9vw, 100px)',
    padding: '0 clamp(4px, 0.7vw, 12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    '&.Mui-selected': {
      color: '#002D55',
    },
    '&:hover': {
      color: '#FF7F27',
    },
  },
  tabsWrapper: {
    backgroundColor: '#f7faff',
    borderRadius: 12,
    marginBottom: '0.6%',
    height: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0,45,85,0.12)',
    [theme.breakpoints.only('md')]: {
      height: 'clamp(24px, calc(4px + 4.5vw), 38px)',
    },
  },
  tabsControlRoot: {
    minHeight: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    height: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    '& .MuiTabs-flexContainer': {
      height: '100%',
      alignItems: 'center',
    },
    [theme.breakpoints.only('md')]: {
      minHeight: 'clamp(24px, calc(4px + 4.5vw), 38px)',
      height: 'clamp(24px, calc(4px + 4.5vw), 38px)',
    },
  },
  staffForm: {
    marginBottom: 8,
    background: '#f7faff',
    borderRadius: 12,
    padding: 16,
    paddingTop: 18,
    paddingBottom: 18,
  },
  staffList: {
    margin: 16,
    marginTop: 16,
    marginBottom: 32 
  },
  staffItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid #e0e0e0',
    fontSize: 'clamp(0.75rem, 1.1vw, 0.875rem)',
  },
  staffRole: {
    fontWeight: 500,
    color: '#F79500',
    marginLeft: 8,
  },
  staffExclusionText: {
    fontSize: 'clamp(0.75rem, 1.1vw, 0.875rem)',
    lineHeight: 1.2,
    color: '#002D55',
    fontWeight: 600,
  },
  staffExclusionChip: {
    fontSize: 'clamp(0.75rem, 1.1vw, 0.875rem)',
    lineHeight: 1.2,
    fontWeight: 600,
    padding: '2px 10px',
  },
  removeBtn: {
    color: '#f44336',
    marginLeft: 16,
    fontWeight: 600,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    fontSize: 'clamp(0.72rem, 0.78vw, 0.875rem)',
    lineHeight: 1.1,
  },
  staffActionBtn: {
    fontSize: 'clamp(0.72rem, 0.78vw, 0.875rem)',
    lineHeight: 1.1,
    minHeight: 'clamp(28px, 2.4vw, 32px)',
    padding: 'clamp(4px, 0.45vw, 6px) clamp(8px, 0.8vw, 10px)',
  },
  orangeFocused: {
    backgroundColor: "#FFFFFF",
    borderRadius: 14,
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink:not(.Mui-focused)': {
      color: '#002D55',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#002D55',
    },
    '& .MuiSelect-selectMenu': {
      minHeight: 'unset !important',
      paddingTop: '10.5px',
      paddingBottom: '10.5px',
      lineHeight: 'normal',
      backgroundColor: "transparent",
    },
    [theme.breakpoints.down('md')]: {
      '& .MuiOutlinedInput-input': {
        fontSize: 'clamp(0.68rem, 1vw, 0.82rem)',
      },
      '& .MuiInputLabel-root': {
        fontSize: 'clamp(0.68rem, 1vw, 0.82rem)',
      },
      '& .MuiInputLabel-shrink': {
        fontSize: 'clamp(0.62rem, 0.9vw, 0.72rem)',
      },
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 12,
      },
      '& .MuiOutlinedInput-input': {
        paddingTop: 10,
        paddingBottom: 10,
        fontSize: 'clamp(0.72rem, 1.26vw, 0.85rem)',
      },
      '& .MuiInputLabel-root': {
        fontSize: 'clamp(0.72rem, 1.26vw, 0.85rem)',
      },
      '& .MuiInputLabel-shrink': {
        fontSize: 'clamp(0.68rem, 1.06vw, 0.75rem)',
      },
    },
  },
  textfieldColor: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
    },
    '& .MuiInputBase-input': {
      color: '#002D55',
      fontSize: 'clamp(0.7875rem, 1.26vw, 0.85rem)'
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#002D55',
      opacity: 1,
      fontSize: 'clamp(0.7875rem, 1.26vw, 0.85rem)'
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: '#002D55',
      opacity: 1,
      fontSize: 'clamp(0.7875rem, 1.26vw, 0.85rem)'
    },
    // Make InputLabel (the TextField label) responsive in various states
    '& .MuiInputLabel-root': {
      fontSize: 'clamp(0.7875rem, 1.26vw, 0.85rem)'
    },
    '& .MuiInputLabel-outlined': {
      fontSize: 'clamp(0.7875rem, 1.26vw, 0.85rem)'
    },
    '& .MuiInputLabel-shrink': {
      fontSize: 'clamp(0.6875rem, 1.06vw, 0.75rem)'
    },
    [theme.breakpoints.down('md')]: {
      '& .MuiInputBase-input': {
        fontSize: 'clamp(0.68rem, 1vw, 0.82rem)',
      },
      '& .MuiInputLabel-root': {
        fontSize: 'clamp(0.68rem, 1vw, 0.82rem)',
      },
      '& .MuiInputLabel-shrink': {
        fontSize: 'clamp(0.62rem, 0.9vw, 0.72rem)',
      },
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiInputBase-input': {
        fontSize: 'clamp(0.72rem, 1.26vw, 0.85rem)',
      },
      '& .MuiInputLabel-root': {
        fontSize: 'clamp(0.72rem, 1.26vw, 0.85rem)',
      },
      '& .MuiInputLabel-shrink': {
        fontSize: 'clamp(0.68rem, 1.06vw, 0.75rem)',
      },
    },
  },
  textfieldSpacing: {
    marginBottom: -20,
  },
  sectionDivider: {
    margin: '24px 0',
  },
  footerDivider: {
    marginTop: 16,
    marginLeft: 8
  },
  footerActions: {
    padding: 16,
    paddingBottom: 0
  },
  footerActionButton: {
    marginTop: 10,
    marginBottom: 10,
    marginLeft: 0,
    marginRight: 0,
  },
  responsiveText: {
    fontSize: 13,
  },
  bodyText: {
    color: '#5f6c7b',
    fontSize: 13,
  },
  menuItem: {
    fontSize: 'clamp(0.7875rem, 1.26vw, 0.85rem)',
  },
  dialogPaper: {
    borderRadius: 18,
    overflow: 'hidden',
    boxShadow: '0 18px 40px rgba(0,45,85,0.18)',
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
      boxShadow: 'none',
      width: '100%',
      maxWidth: '100%',
      margin: 0,
    },
  },
  dialogTitle: {
    borderBottom: '1px solid #eeeeee',
    padding: '16px 24px',
    backgroundColor: '#ffffff',
    [theme.breakpoints.down('sm')]: {
      padding: '12px 16px',
    },
  },
  dialogTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      alignItems: 'flex-start',
    },
  },
  dialogTitleText: {
    flex: 1,
    color: '#002D55',
    fontWeight: 600,
    fontSize: 'clamp(0.95rem, calc(4px + 0.72vw), 1.15rem)',
    textTransform: 'uppercase',
    [theme.breakpoints.down('md')]: {
      fontSize: 'clamp(0.82rem, 1.3vw, 1rem)',
      lineHeight: 1.15,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 'clamp(0.75rem, 1.04vw, 0.95rem)',
      lineHeight: 1.2,
    },
  },
  closeButton: {
    color: '#64748B',
    padding: 6,
    '&:hover': {
      backgroundColor: '#edf5fc',
    },
  },
  dialogContent: {
    padding: '24px !important',
    backgroundColor: '#e6eaf0',
    '&.MuiDialogContent-dividers': {
      borderTop: 'none',
      borderBottom: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '16px !important',
    },
  },
  dialogContentFlush: {
    padding: '0 !important',
  },
  dialogPanelStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    [theme.breakpoints.down('sm')]: {
      gap: 12,
    },
  },
  dialogPanel: {
    backgroundColor: '#f7faff',
    borderRadius: 18,
    padding: 16,
    border: '1px solid #dce8f5',
    boxShadow: '0 10px 24px rgba(0, 45, 85, 0.08)',
    [theme.breakpoints.down('sm')]: {
      borderRadius: 14,
      padding: 12,
    },
  },
  dialogSectionLabel: {
    fontSize: 12,
    color: '#666666',
    marginBottom: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    [theme.breakpoints.down('sm')]: {
      fontSize: 'clamp(0.68rem, 1.04vw, 0.72rem)',
      marginBottom: 8,
    },
  },
  dialogActions: {
    padding: 16,
    gap: 8,
    justifyContent: 'flex-end',
    borderTop: '1px solid #eeeeee',
    backgroundColor: '#ffffff',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      padding: 12,
    },
  },
  dialogActionButton: {
    borderRadius: 50,
  },
}));


const localizer = momentLocalizer(moment);
const ADMIN_NAV_SHORTCUT_KEY = 'vp_adminNavigationShortcut';
const ADMIN_NAV_SHORTCUT_EVENT = 'vp:adminNavigationShortcut';

const ClinicDetails = ({ className, ...rest }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false)
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const backendBaseUrl = apiBaseUrl;

  useEffect(() => {
    try {
      const pendingTab = localStorage.getItem('vp_clinicDetailsTabIndex');
      if (pendingTab !== null) {
        const nextTabIndex = Number(pendingTab);
        if (!Number.isNaN(nextTabIndex)) {
          setTabIndex(nextTabIndex);
        }
        localStorage.removeItem('vp_clinicDetailsTabIndex');
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Dialog state for invoice PDF export
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Services state
  const [services, setServices] = useState([]);
  const [clinicLocations, setClinicLocations] = useState([]);
  const [serviceCode, setServiceCode] = useState('');
  const [danmarkCode, setDanmarkCode] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [serviceDuration, setServiceDuration] = useState('');
  const [servicePrice, setServicePrice] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [serviceRequiresVat, setServiceRequiresVat] = useState(false);
  const [serviceVatAmountPercent, setServiceVatAmountPercent] = useState('');
  const [serviceCommissionRate, setServiceCommissionRate] = useState('');
  const [serviceCommissionOverrides, setServiceCommissionOverrides] = useState({});
  const [serviceCommissionOverridesOpen, setServiceCommissionOverridesOpen] = useState(false);
  const [serviceColor, setServiceColor] = useState(DEFAULT_SERVICE_COLOR);
  const [serviceStaffMembers, setServiceStaffMembers] = useState([]);
  const [serviceIsClass, setServiceIsClass] = useState(false);
  const [serviceClassParticipantLimit, setServiceClassParticipantLimit] = useState('');
  const [serviceClassWeekdays, setServiceClassWeekdays] = useState([]);
  const [serviceClassStartTime, setServiceClassStartTime] = useState('');
  const [serviceClassDefaultRoomId, setServiceClassDefaultRoomId] = useState('');
  const [serviceClassPrimaryStaff, setServiceClassPrimaryStaff] = useState('');
  const [serviceClassWeekdayPrimaryStaffs, setServiceClassWeekdayPrimaryStaffs] = useState({});
  const [servicesLoading, setServicesLoading] = useState(false);
  const [clinicPatients, setClinicPatients] = useState([]);
  const [clinicPatientsLoading, setClinicPatientsLoading] = useState(false);
  const [clinicPatientSearchInput, setClinicPatientSearchInput] = useState('');
  const [clinicPatientDirectory, setClinicPatientDirectory] = useState({});
  const [clinicPatientTreatmentInfo, setClinicPatientTreatmentInfo] = useState({});
  const [clinicPatientTreatmentOptions, setClinicPatientTreatmentOptions] = useState({});
  const [classScheduleSelectionPreview, setClassScheduleSelectionPreview] = useState(null);
  const [currency, setCurrency] = useState('DKK');
  const currencyOptions = ['DKK', 'EUR', 'GBP', 'NOK', 'SEK'];
  // Edit service dialog state
  const [editingService, setEditingService] = useState(null); // null = closed
  const [editServiceForm, setEditServiceForm] = useState({ service_code: '', danmark_code: '', name: '', duration_minutes: '', price: '', currency: 'DKK', description: '', commission_rate: '', commission_overrides: {}, requires_vat: false, vat_amount_percent: '', color: DEFAULT_SERVICE_COLOR, staff_members: [], is_class: false, class_participant_limit: '', class_weekdays: [], class_weekday: '', class_start_time: '', class_default_room: '', class_primary_staff: '', class_weekday_primary_staffs: {}, class_patients: [], class_patient_treatment_links: {} });
  const [editServiceCommissionOverridesOpen, setEditServiceCommissionOverridesOpen] = useState(false);
  const [editServiceSaving, setEditServiceSaving] = useState(false);
  const classScheduleSelectionRef = useRef(null);
  const loadedClinicPatientIdsRef = useRef(new Set());
  // Edit staff dialog state
  const [editingStaff, setEditingStaff] = useState(null); // null = closed
  const [editStaffForm, setEditStaffForm] = useState({ firstName: '', lastName: '', initials: '', email: '', role: 'Physiotherapist', cpr: '', address: '', city: '', zip: '', employment_started_at: '', employment_active: true, employment_terminated_at: '', staff_engagement_type: 'employee', compensation_basis: 'fixed_salary', monthly_salary_amount: '', pension_percent: '', employer_pension_percent: '', vacation_scheme: 'paid_vacation_days', paid_vacation_days: '', tax_fradrag: '', tax_trakprocent: '', holiday_allowance_percent: '12.5', vacation_accrual_per_month: '2.08', authorization_ID: '', seat_tier: '' });
  const [editStaffSaving, setEditStaffSaving] = useState(false);
  const [editStaffError, setEditStaffError] = useState('');
  // Clinic details state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [PIItype, setPIItype] = useState("CPR");
  const [PIInumber, setPIInumber] = useState('');
  const [country, setContry] = useState('');
  const clinicOperatesInDenmark = String(country || '').trim() === 'Denmark';
  // Clinic / CareProvider fields
  const [clinicName, setClinicName] = useState("");
  const [clinicRegNumber, setClinicRegNumber] = useState("");
  const [clinicAddress, setClinicAddress] = useState("");
  const [clinicCity, setClinicCity] = useState("");
  const [clinicPostalCode, setClinicPostalCode] = useState("");
  const [clinicEmail, setClinicEmail] = useState("");
  const [clinicPhone, setClinicPhone] = useState("");
  const [mainContactPhone, setMainContactPhone] = useState("");
  const [mainContactPerson, setMainContactPerson] = useState("");
  const [insuranceNetworkOptions, setInsuranceNetworkOptions] = useState([]);
  const [supportedInsuranceNetworks, setSupportedInsuranceNetworks] = useState([]);
  const [insuranceStaffOverrides, setInsuranceStaffOverrides] = useState([]);


  // Staff state
  const [staffList, setStaffList] = useState([]);
  const [staffFirstName, setStaffFirstName] = useState("");
  const [staffLastName, setStaffLastName] = useState("");
  const [staffInitials, setStaffInitials] = useState("");
  const [staffInitialsCustomized, setStaffInitialsCustomized] = useState(false);
  const [staffEmail, setStaffEmail] = useState("");
  const [staffEmailError, setStaffEmailError] = useState('');
  const [staffAuthIDError, setStaffAuthIDError] = useState('');
  const [staffCpr, setStaffCpr] = useState('');
  const [staffAddress, setStaffAddress] = useState('');
  const [staffCity, setStaffCity] = useState('');
  const [staffZip, setStaffZip] = useState('');
  const [staffRole, setStaffRole] = useState("Physiotherapist");
  const [staffEngagementType, setStaffEngagementType] = useState('employee');
  const [staffCompensationBasis, setStaffCompensationBasis] = useState('fixed_salary');
  const [staffMonthlySalaryAmount, setStaffMonthlySalaryAmount] = useState('');
  const [staffPensionPercent, setStaffPensionPercent] = useState('');
  const [staffEmployerPensionPercent, setStaffEmployerPensionPercent] = useState('');
  const [staffVacationScheme, setStaffVacationScheme] = useState('paid_vacation_days');
  const [staffPaidVacationDays, setStaffPaidVacationDays] = useState('');
  const [staffTaxFradrag, setStaffTaxFradrag] = useState('');
  const [staffTaxTrakprocent, setStaffTaxTrakprocent] = useState('');
  const [staffHolidayAllowancePercent, setStaffHolidayAllowancePercent] = useState('12.5');
  const [staffVacationAccrualPerMonth, setStaffVacationAccrualPerMonth] = useState('2.08');
  const [staffWeeklyWorkHours, setStaffWeeklyWorkHours] = useState('37.5');
  const [staffEmploymentStartedAt, setStaffEmploymentStartedAt] = useState('');
  const [staffEmploymentActive, setStaffEmploymentActive] = useState(true);
  const [staffEmploymentTerminatedAt, setStaffEmploymentTerminatedAt] = useState('');
  const [staffAuthorization_ID, setStaffAuthorization_ID] = useState("");
  const [staffSeatTier, setStaffSeatTier] = useState("");
  const [staffAuthorizationValid, setStaffAuthorizationValid] = useState(null); // null = unknown, true/false = result
  const [staffAuthorizationLoading, setStaffAuthorizationLoading] = useState(false);
  const [staffAuthorizationError, setStaffAuthorizationError] = useState("");
  const [staffFormLanguage, setStaffFormLanguage] = useState('en');
  // Owner confirmation password (care provider must re-enter their own password)
  const [ownerPassword, setOwnerPassword] = useState("");

  const handleStartStaffStripeOnboarding = async (staff) => {
    if (!token || !staff?.userId) return;

    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
      const response = await fetch(`${baseUrl}/api/calendar/stripe/connect/onboarding/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ account_type: 'staff', staff_id: staff.userId }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Unable to start staff Stripe onboarding');
      }

      if (data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }

      await fetchStaffList();
    } catch (error) {
      alert(error.message || 'Unable to start staff Stripe onboarding');
    }
  };

  const staffLabel = (english, danish) => (staffFormLanguage === 'da' ? danish : english);
  
    // Validate an authorization ID via the Django backend proxy, which
    // calls the autregwebservice.stps.dk REST API server-side to avoid browser
    // CORS restrictions.
    const validateAuthorizationID = async (authID) => {
      if (!authID) {
        setStaffAuthorizationValid(null);
        setStaffAuthorizationError('');
        return false;
      }

      setStaffAuthorizationLoading(true);
      setStaffAuthorizationError('');
      setStaffAuthorizationValid(null);

      try {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
        const resp = await fetch(
          `${baseUrl}/api/services/validate-authorization/?auth_id=${encodeURIComponent(authID)}`,
          { headers: { Authorization: `Token ${token}` } }
        );

        if (!resp.ok) {
          setStaffAuthorizationError(`Server error: HTTP ${resp.status}`);
          setStaffAuthorizationValid(false);
          return false;
        }

        const data = await resp.json();

        if (!data.valid) {
          setStaffAuthorizationValid(false);
          setStaffAuthorizationError(data.detail || 'Authorization ID not found in registry');
          return false;
        }

        const parts = [
          data.name,
          data.profession && `(${data.profession})`,
          data.authorized_since && `authorized since ${data.authorized_since}`,
        ].filter(Boolean);

        setStaffAuthorizationValid(true);
        setStaffAuthorizationError(parts.join(' | '));
        return true;
      } catch (err) {
        setStaffAuthorizationError(err?.message || 'Network error');
        setStaffAuthorizationValid(false);
        return false;
      } finally {
        setStaffAuthorizationLoading(false);
      }
    };
  // Dialog state for confirming owner password before creating staff
  const [ownerPasswordDialogOpen, setOwnerPasswordDialogOpen] = useState(false);
  const staffRoles = ["Physiotherapist", "Admin", "Receptionist", "Assistant"];
  const staffEngagementTypes = [
    { value: 'employee', label: 'Employee' },
    { value: 'subcontractor', label: 'Independent subcontractor' },
  ];
  const staffCompensationBasisOptions = [
    { value: 'fixed_salary', label: 'Fixed salary' },
    { value: 'commission_salary', label: 'Commission salary' },
    { value: 'subcontractor_commission', label: 'Subcontractor commission' },
  ];

  // Clinical roles (e.g. physiotherapists) require an authorization ID; non-clinical
  // staff such as receptionists, assistants and admins do not.
  const isClinicalStaffRole = (role) => String(role || '').trim().toLowerCase() === 'physiotherapist';

  const normalizeStaffRoleValue = (role) => {
    const normalized = String(role || '').trim().toLowerCase();
    const roleMap = {
      physiotherapist: 'Physiotherapist',
      admin: 'Admin',
      receptionist: 'Receptionist',
      assistant: 'Assistant',
    };
    return roleMap[normalized] || String(role || '').trim();
  };

  const normalizeEditableStaffRoleValue = (role) => {
    const normalized = String(role || '').trim().toLowerCase();
    const roleMap = {
      physiotherapist: 'physiotherapist',
      admin: 'admin',
      receptionist: 'care_staff',
      assistant: 'care_staff',
      care_staff: 'care_staff',
      careprovider_admin: 'careprovider_admin',
      clinic_admin: 'careprovider_admin',
      clinic: 'careprovider_admin',
    };
    return roleMap[normalized] || normalized || 'physiotherapist';
  };

  const formatStaffEngagementTypeLabel = (value) => (
    String(value || 'employee').toLowerCase() === 'subcontractor'
      ? 'Independent subcontractor'
      : 'Employee'
  );

  const normalizeStaffCompensationBasis = (engagementType, compensationBasis) => {
    const normalizedEngagement = String(engagementType || 'employee').toLowerCase();
    const normalizedBasis = String(compensationBasis || '').toLowerCase();

    if (normalizedEngagement === 'subcontractor') {
      return 'subcontractor_commission';
    }

    return normalizedBasis === 'commission_salary' ? 'commission_salary' : 'fixed_salary';
  };

  const getStaffCompensationBasisOptions = (engagementType) => {
    const normalizedEngagement = String(engagementType || 'employee').toLowerCase();
    return staffCompensationBasisOptions.filter((option) => (
      normalizedEngagement === 'subcontractor'
        ? option.value === 'subcontractor_commission'
        : option.value !== 'subcontractor_commission'
    ));
  };

  const formatStaffCompensationBasisLabel = (value) => {
    const match = staffCompensationBasisOptions.find((option) => option.value === value);
    return match ? match.label : 'Fixed salary';
  };

  const buildCommissionOverridesPayload = (overrides = {}, selectedStaffMembers = []) => {
    const selectedStaffIds = new Set((selectedStaffMembers || []).map(String));
    return Object.entries(overrides || {}).reduce((result, [staffId, value]) => {
      const trimmedValue = String(value ?? '').trim();
      if (!trimmedValue) {
        return result;
      }
      if (selectedStaffIds.size > 0 && !selectedStaffIds.has(String(staffId))) {
        return result;
      }
      result[String(staffId)] = Number(trimmedValue);
      return result;
    }, {});
  };

  // Revenue state for backend
  const [revenueList, setRevenueList] = useState([]);
  const [filteredRevenue, setFilteredRevenue] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [revenueError, setRevenueError] = useState("");
  const [revenueStartDate, setRevenueStartDate] = useState("");
  const [revenueEndDate, setRevenueEndDate] = useState("");

  // Billing Statistics Tab State
  const [billingStats, setBillingStats] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [billingStatusScope, setBillingStatusScope] = useState("all");
  const [billingDateRangeStart, setBillingDateRangeStart] = useState("");
  const [billingDateRangeEnd, setBillingDateRangeEnd] = useState("");
  const [billingStaffId, setBillingStaffId] = useState("");
  const [billingPayerType, setBillingPayerType] = useState("");
  const [staffList2, setStaffList2] = useState([]);
  const [selectedBillingRows, setSelectedBillingRows] = useState([]);
  const [selectedBillingInvoices, setSelectedBillingInvoices] = useState([]);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedBillingRow, setSelectedBillingRow] = useState(null);
  const [bulkActionMenuAnchor, setBulkActionMenuAnchor] = useState(null);
  
  // Action Dialogs
  const [billDialogOpen, setBillDialogOpen] = useState(false);
  const [billingGroupMode, setBillingGroupMode] = useState("group_by_payer");
  const [billDialogError, setBillDialogError] = useState("");
  const [billDialogLoading, setBillDialogLoading] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditError, setCreditError] = useState("");
  const [creditPatientHasPaidBalance, setCreditPatientHasPaidBalance] = useState(false);
  const [creditTransferToPatientBalance, setCreditTransferToPatientBalance] = useState(false);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [markPaidError, setMarkPaidError] = useState("");
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [markPaidInvoiceIds, setMarkPaidInvoiceIds] = useState([]);
  const [markPaidPaymentMethod, setMarkPaidPaymentMethod] = useState(MARK_PAID_PAYMENT_METHOD_OPTIONS[0]?.value || '');
  const [cancelBillingDialogOpen, setCancelBillingDialogOpen] = useState(false);
  const [cancelBillingLoading, setCancelBillingLoading] = useState(false);
  const [cancelBillingError, setCancelBillingError] = useState("");
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderType, setReminderType] = useState("1st");
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderError, setReminderError] = useState("");
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [selectedInvoiceForPreview, setSelectedInvoiceForPreview] = useState(null);
  const [invoiceIssuerNameMode, setInvoiceIssuerNameMode] = useState('staff');
  const [invoiceCustomIssuerName, setInvoiceCustomIssuerName] = useState('');
  const [billPreviewOpen, setBillPreviewOpen] = useState(false);
  const [billPreviewInvoices, setBillPreviewInvoices] = useState([]);
  const [billPreviewLoading, setBillPreviewLoading] = useState(false);
  const [billPreviewReturnTarget, setBillPreviewReturnTarget] = useState('dialog');

  // Edit Appointment Dialog State
  const [editAppointmentDialogOpen, setEditAppointmentDialogOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [editingVideoSession, setEditingVideoSession] = useState(null);
  const [videoCallOpen, setVideoCallOpen] = useState(false);
  const [videoCallAppointment, setVideoCallAppointment] = useState(null);
  const [editingPayer, setEditingPayer] = useState('patient');
  const [editingPayerInsuranceId, setEditingPayerInsuranceId] = useState('');
  const [editingPayerOtherId, setEditingPayerOtherId] = useState('');
  const [editingPayerInsuranceOptions, setEditingPayerInsuranceOptions] = useState([]);
  const [editingPayerOtherOptions, setEditingPayerOtherOptions] = useState([]);
  const [editingPayerOptionsLoading, setEditingPayerOptionsLoading] = useState(false);
  const [editingPayerOptionsError, setEditingPayerOptionsError] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Change Payer Dialog State (unbilled row action)
  const [changePayerDialogOpen, setChangePayerDialogOpen] = useState(false);
  const [changePayerTarget, setChangePayerTarget] = useState(null); // row
  const [changePayerValue, setChangePayerValue] = useState('patient');
  const [changePayerLoading, setChangePayerLoading] = useState(false);

  // Staff sort
  const [billingSortByStaff, setBillingSortByStaff] = useState('');
  const [billingVatPeriod, setBillingVatPeriod] = useState('month');
  const [cockpitOpen, setCockpitOpen] = useState(false);
  const [cockpitTab, setCockpitTab] = useState(0);
  const [billingCommissionSummaryOpen, setBillingCommissionSummaryOpen] = useState(true);
  const [billingSelectMenuAnchor, setBillingSelectMenuAnchor] = useState(null);
  const [billingSelectMenuRows, setBillingSelectMenuRows] = useState([]);
  const [monthEndMonth, setMonthEndMonth] = useState(getCurrentMonthInputValue());
  const [monthEndStaffIds, setMonthEndStaffIds] = useState([]);
  const [monthEndFinalizeMode, setMonthEndFinalizeMode] = useState('clinic');
  const [monthEndFinalizeStaffId, setMonthEndFinalizeStaffId] = useState('');
  const [monthEndReport, setMonthEndReport] = useState(null);
  const [monthEndReportLoading, setMonthEndReportLoading] = useState(false);
  const [monthEndReportError, setMonthEndReportError] = useState('');
  const [monthEndAbsenceReport, setMonthEndAbsenceReport] = useState(null);
  const [monthEndAbsenceLoading, setMonthEndAbsenceLoading] = useState(false);
  const [monthEndSnapshots, setMonthEndSnapshots] = useState([]);
  const [monthEndSnapshotsLoading, setMonthEndSnapshotsLoading] = useState(false);
  const [monthEndSnapshotSaving, setMonthEndSnapshotSaving] = useState(false);
  const bankStatementUploadRef = useRef(null);
  const [bankStatementUploadLoading, setBankStatementUploadLoading] = useState(false);
  const [bankStatementUploadError, setBankStatementUploadError] = useState('');
  const [bankStatementUploadResult, setBankStatementUploadResult] = useState(null);
  // Payroll & Payslips
  const [payslipSelectedStaffId, setPayslipSelectedStaffId] = useState('');
  const [payslipLocale, setPayslipLocale] = useState('da');
  const [payslipSending, setPayslipSending] = useState(false);
  const [payslipSendResult, setPayslipSendResult] = useState(null);
  const [payslipArchives, setPayslipArchives] = useState([]);
  const [payslipArchivesLoading, setPayslipArchivesLoading] = useState(false);
  const [payslipArchivesError, setPayslipArchivesError] = useState('');
  const [careProviderAvgNps, setCareProviderAvgNps] = useState(null);
  // Subcontractor Settlement
  const [settlementStaffId, setSettlementStaffId] = useState('');
  const [settlementData, setSettlementData] = useState(null);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementError, setSettlementError] = useState('');
  // Annual Summary
  const [annualYear, setAnnualYear] = useState(new Date().getFullYear());
  const [annualStats, setAnnualStats] = useState(null);
  const [annualStatsLoading, setAnnualStatsLoading] = useState(false);
  const [annualStatsError, setAnnualStatsError] = useState('');
  // Aging / Receivables
  const [agingReport, setAgingReport] = useState(null);
  const [agingLoading, setAgingLoading] = useState(false);
  const [agingError, setAgingError] = useState('');
  const [editingPaidInvoiceId, setEditingPaidInvoiceId] = useState(null);
  const [editingPaidAmount, setEditingPaidAmount] = useState('');
  const [billPreviewBillingType, setBillPreviewBillingType] = useState('clinic');
  const [billPreviewStaffMemberId, setBillPreviewStaffMemberId] = useState('');

  const [token, _] = useState(useSelector(state => state.auth.token))
  const user = useSelector(state => state.auth.user);
  const [monthPickerDate, setMonthPickerDate] = useState(new Date());
  // Keep originals fetched from server so we can cancel frontend edits
  const [originalProfile, setOriginalProfile] = useState(null);
  const [originalCp, setOriginalCp] = useState(null);
  const [originalServerStaff, setOriginalServerStaff] = useState([]);

  useEffect(() => {
    if (!token) {
      return;
    }
    setIsLoading(true)
    fetchUserData()
    // fetchUserData is intentionally triggered when the authenticated session hydrates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, backendBaseUrl])

  const findInsuranceStaffOverride = (staffId) => (
    (insuranceStaffOverrides || []).find((item) => item.staffId === String(staffId)) || null
  );

  const toggleClinicInsuranceNetwork = (networkValue) => {
    setSupportedInsuranceNetworks((prev) => {
      const exists = prev.includes(networkValue);
      const nextValues = exists ? prev.filter((item) => item !== networkValue) : [...prev, networkValue];
      const deduped = Array.from(new Set(nextValues));
      setInsuranceStaffOverrides((currentOverrides) => currentOverrides.map((item) => ({
        ...item,
        excludedNetworks: (item.excludedNetworks || []).filter((value) => deduped.includes(value)),
      })));
      return deduped;
    });
  };

  const toggleInsuranceStaffOverride = (staffId, enabled) => {
    const normalizedStaffId = String(staffId || '').trim();
    if (!normalizedStaffId) {
      return;
    }

    setInsuranceStaffOverrides((prev) => {
      if (!enabled) {
        return prev.filter((item) => item.staffId !== normalizedStaffId);
      }
      if (prev.some((item) => item.staffId === normalizedStaffId)) {
        return prev;
      }
      return [...prev, { staffId: normalizedStaffId, excludedNetworks: [] }];
    });
  };

  const toggleInsuranceStaffNetwork = (staffId, networkValue) => {
    const normalizedStaffId = String(staffId || '').trim();
    if (!normalizedStaffId) {
      return;
    }

    setInsuranceStaffOverrides((prev) => prev.map((item) => {
      if (item.staffId !== normalizedStaffId) {
        return item;
      }

      const exists = (item.excludedNetworks || []).includes(networkValue);
      return {
        ...item,
        excludedNetworks: exists
          ? item.excludedNetworks.filter((value) => value !== networkValue)
          : [...(item.excludedNetworks || []), networkValue],
      };
    }));
  };

  // Pre-select all staff when staffList loads (default for new services)
  useEffect(() => {
    if (staffList.length > 0 && serviceStaffMembers.length === 0) {
      setServiceStaffMembers(staffList.map(s => String(s.userId || s.user_id || s.id)));
    }
    // eslint-disable-next-line
  }, [staffList]);

  const fetchUserData = async () => {
    // profileData will hold the parsed profile response so it can be referenced
    // later when deciding whether to prefer CareProvider contact names.
    let profileData = null;
    const response = await fetch(`${backendBaseUrl}/profile/profile/`, {
      headers: {
        "Authorization": `Token ${token}`
      },
      method: "GET",
    });

    if (response.ok) {
      profileData = await response.json();
      setEmail(profileData.profile.email)
      // Use profile names as a default; if CareProvider exists we'll prefer its contact name below
      setFirstName(profileData.profile.first_name)
      setLastName(profileData.profile.last_name)
      // save original profile snapshot for cancel
      setOriginalProfile(profileData.profile || null);
    } else {
      alert("ERROR")
    }
    // Attempt to fetch CareProvider data for this user and populate clinic fields
    try {
      const cpRes = await fetch(`${backendBaseUrl}/profile/careprovider/`, {
        headers: {
          "Authorization": `Token ${token}`,
          Accept: 'application/json'
        },
        method: "GET",
      });
      if (cpRes.ok) {
        const cpData = await cpRes.json();
        if (cpData.has_care_provider) {
          // If a CareProvider exists prefer the clinic's main contact as owner name
          if (cpData.firstName_mainContactPerson || cpData.lastName_mainContactPerson) {
            setFirstName(cpData.firstName_mainContactPerson || (profileData && profileData.profile ? profileData.profile.first_name : ''));
            setLastName(cpData.lastName_mainContactPerson || (profileData && profileData.profile ? profileData.profile.last_name : ''));
          }
          setClinicName(cpData.name_business || "");
          setClinicRegNumber(cpData.business_registration_number || "");
          setClinicAddress(cpData.address || "");
          setClinicCity(cpData.city || "");
          setClinicPostalCode(cpData.postal_code || "");
          setClinicEmail(cpData.email_CareProvider || cpData.email_contactPerson || "");
          setClinicPhone(cpData.phoneNumber_CareProvider || "");
          setMainContactPhone(cpData.phoneNumber_ContactPerson || "");
          setMainContactPerson((cpData.firstName_mainContactPerson || "") + (cpData.lastName_mainContactPerson ? (' ' + cpData.lastName_mainContactPerson) : ''));
          setContry(cpData.country || "");
          setCareProviderAvgNps(cpData.avg_nps != null ? cpData.avg_nps : null);
          // save original care provider snapshot for cancel
          setOriginalCp(cpData || null);
          // Fetch the current staff list for this CareProvider and store original
          try {
            const fetched = await fetchStaffList();
            setOriginalServerStaff(fetched || []);
          } catch (err) {
            console.warn('Failed to fetch staff after loading CareProvider', err);
            setStaffList([]);
            setOriginalServerStaff([]);
          }

          try {
            const settingsRes = await fetch(`${backendBaseUrl}/api/profile/clinic/settings/`, {
              headers: {
                Authorization: `Token ${token}`,
                Accept: 'application/json'
              },
              method: 'GET',
            });
            if (settingsRes.ok) {
              const settingsData = await settingsRes.json();
              setInsuranceNetworkOptions(Array.isArray(settingsData.insurance_support_catalog?.network_options) ? settingsData.insurance_support_catalog.network_options : []);
              setSupportedInsuranceNetworks(Array.isArray(settingsData.insurance_support_settings?.supported_networks) ? settingsData.insurance_support_settings.supported_networks : []);
              setInsuranceStaffOverrides(
                Array.isArray(settingsData.insurance_support_settings?.staff_overrides)
                  ? settingsData.insurance_support_settings.staff_overrides.map((item) => ({
                      staffId: String(item.staff_id || ''),
                      excludedNetworks: Array.isArray(item.excluded_networks) ? item.excluded_networks : [],
                    }))
                  : []
              );
            }
          } catch (err) {
            console.warn('Failed to fetch clinic insurance settings', err);
          }
        }
      }
    } catch (err) {
      console.warn('Failed to fetch care provider data', err);
    }
    setIsLoading(false)
  }

  const persistInsuranceSupportSettings = async () => {
    const payload = {
      insurance_support_settings: {
        supported_networks: Array.from(new Set(supportedInsuranceNetworks || [])),
        staff_overrides: (insuranceStaffOverrides || []).map((item) => ({
          staff_id: String(item.staffId || ''),
          excluded_networks: (item.excludedNetworks || []).filter((value) => (supportedInsuranceNetworks || []).includes(value)),
        })).filter((item) => item.staff_id),
      },
    };

    const settingsRes = await fetch(`${backendBaseUrl}/api/profile/clinic/settings/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
      body: JSON.stringify(payload),
    });

    if (!settingsRes.ok) {
      const txt = await settingsRes.text();
      throw new Error(txt || 'Failed to update clinic insurance settings');
    }

    const settingsData = await settingsRes.json().catch(() => ({}));
    setInsuranceNetworkOptions(Array.isArray(settingsData.insurance_support_catalog?.network_options) ? settingsData.insurance_support_catalog.network_options : insuranceNetworkOptions);
    setSupportedInsuranceNetworks(Array.isArray(settingsData.insurance_support_settings?.supported_networks) ? settingsData.insurance_support_settings.supported_networks : payload.insurance_support_settings.supported_networks);
    setInsuranceStaffOverrides(
      Array.isArray(settingsData.insurance_support_settings?.staff_overrides)
        ? settingsData.insurance_support_settings.staff_overrides.map((item) => ({
            staffId: String(item.staff_id || ''),
            excludedNetworks: Array.isArray(item.excluded_networks) ? item.excluded_networks : [],
          }))
        : payload.insurance_support_settings.staff_overrides.map((item) => ({
            staffId: item.staff_id,
            excludedNetworks: item.excluded_networks,
          }))
    );
  };

  const handleSaveInsuranceSupport = async () => {
    setIsLoading(true);
    try {
      await persistInsuranceSupportSettings();
      alert('Insurance support saved');
    } catch (err) {
      console.error('Insurance support save failed', err);
      alert(`Save failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch staff list for the current authenticated CareProvider and normalize
  // backend response into the frontend shape used by this component.
  const fetchStaffList = async () => {
    try {
      const res = await fetch(`${backendBaseUrl}/profile/my_careprovider_staff/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          Accept: 'application/json'
        }
      });
      if (!res.ok) {
        console.warn('list_staff returned', res.status);
        setStaffList([]);
        return [];
      }
      const data = await res.json();
      // Backend returns { staff: [ { user_id, email, first_name, last_name, initials, authorization_ID, role_type, employment_started_at, employment_active, employment_terminated_at, staff_engagement_type } ] }
      const mapped = (data.staff || []).map(s => ({
        userId: s.user_id,
        email: s.email,
        firstName: s.first_name || '',
        lastName: s.last_name || '',
        initials: s.initials || '',
        authorization_ID: s.authorization_ID || s.authorizationId || '',
        seat_tier: s.seat_tier || '',
        role: normalizeStaffRoleValue(s.role_type || s.role || s.profile?.role_type || s.profile?.role || ''),
        cpr: s.cpr || s.pii_number || '',
        address: s.address || '',
        city: s.city || '',
        zip: s.zip || '',
        staff_engagement_type: s.staff_engagement_type || s.staffEngagementType || 'employee',
        weekly_work_hours: s.weekly_work_hours || s.weeklyWorkHours || '37.5',
        employment_started_at: s.employment_started_at || s.employmentStartedAt || '',
        employment_active: s.employment_active === undefined || s.employment_active === null ? true : Boolean(s.employment_active),
        employment_terminated_at: s.employment_terminated_at || s.employmentTerminatedAt || '',
        compensation_basis: normalizeStaffCompensationBasis(
          s.staff_engagement_type || s.staffEngagementType || 'employee',
          s.compensation_basis || s.compensationBasis || ''
        ),
        monthly_salary_amount: s.monthly_salary_amount || s.monthlySalaryAmount || '',
        pension_percent: s.pension_percent || s.pensionPercent || s.profile?.pension_percent || s.profile?.pensionPercent || '',
        employer_pension_percent: s.employer_pension_percent || s.employerPensionPercent || s.profile?.employer_pension_percent || s.profile?.employerPensionPercent || '',
        vacation_scheme: s.vacation_scheme || s.vacationScheme || s.profile?.vacation_scheme || s.profile?.vacationScheme || 'paid_vacation_days',
        paid_vacation_days: s.paid_vacation_days || s.paidVacationDays || s.profile?.paid_vacation_days || s.profile?.paidVacationDays || '',
        tax_fradrag: s.tax_fradrag || s.taxFradrag || s.profile?.tax_fradrag || s.profile?.taxFradrag || '',
        tax_trakprocent: s.tax_trakprocent || s.taxTrakprocent || s.profile?.tax_trakprocent || s.profile?.taxTrakprocent || '',
        holiday_allowance_percent: s.holiday_allowance_percent || s.holidayAllowancePercent || s.profile?.holiday_allowance_percent || s.profile?.holidayAllowancePercent || '12.5',
        vacation_accrual_per_month: s.vacation_accrual_per_month || s.vacationAccrualPerMonth || s.profile?.vacation_accrual_per_month || s.profile?.vacationAccrualPerMonth || '2.08',
        stripe_account_id: s.stripe_account_id || s.stripeAccountId || '',
        stripe_onboarding_complete: Boolean(s.stripe_onboarding_complete),
      }));
      setStaffList(mapped);
      return mapped;
    } catch (err) {
      console.warn('Failed to fetch staff list', err);
      setStaffList([]);
      return [];
    }
  }

  // must be updated to save clinic details instead of user profile details
  const saveHandler = async () => {
    setIsLoading(true)
    try {
      // Update profile (first/last name)
      const profileRes = await fetch(`${backendBaseUrl}/profile/update/`, {
        method: "PUT",
        headers: {
          "content-type": "application/json",
          "Authorization": `Token ${token}`
        },
        body: JSON.stringify({ first_name: firstName, last_name: lastName })
      });
      if (!profileRes.ok) {
        const txt = await profileRes.text();
        throw new Error(txt || 'Failed to update profile');
      }

      // Update or create CareProvider fields (PATCH current user's careprovider)
      const cpPayload = {
        name_business: clinicName,
        business_registration_number: clinicRegNumber,
        address: clinicAddress,
        email_CareProvider: clinicEmail,
        phoneNumber_CareProvider: clinicPhone,
        phoneNumber_ContactPerson: mainContactPhone,
        firstName_mainContactPerson: mainContactPerson ? mainContactPerson.split(' ')[0] : '',
        lastName_mainContactPerson: mainContactPerson ? mainContactPerson.split(' ').slice(1).join(' ') : '',
        postal_code: clinicPostalCode,
        country: country,
        // notes omitted
      };
      const cpRes = await fetch(`${backendBaseUrl}/profile/careprovider/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${token}` },
        body: JSON.stringify(cpPayload)
      });
      if (!cpRes.ok) {
        const txt = await cpRes.text();
        throw new Error(txt || 'Failed to update care provider');
      }

      await persistInsuranceSupportSettings();
      // Optionally show success UI
      alert('Clinic details saved');
    } catch (err) {
      console.error('Save failed', err);
      alert('Save failed: ' + (err && err.message ? err.message : 'Unknown error'));
    } finally {
      setIsLoading(false)
    }
  }

  // Save staff to backend: create users and attach roles. The backend will
  // determine the CareProvider from the request token and associate created staff.
  const saveStaffChanges = async () => {
    const pendingStaff = (staffList || []).filter((staff) => !(
      staff.userId || staff.user_id || staff.id
    ));
    if (pendingStaff.length === 0) {
      alert('No new staff to save');
      return;
    }
    if (!ownerPassword) {
      alert('Please enter your password to confirm creating staff');
      return;
    }
    setIsLoading(true);
    try {
      const results = await Promise.all(pendingStaff.map(async (s) => {
        // Generate a temporary password using staff initials + '1234' when available.
        // Fallback to previous strategy if initials are missing.
        const base = (s.initials && String(s.initials).trim().length > 0) ? s.initials.trim() : (s.firstName || s.email || 'pass');
        const pw = base + '1234';
        const res = await fetch(`${backendBaseUrl}/user/create_staff/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}` // ensure backend knows creator
          },
          body: JSON.stringify({ email: s.email, password: pw, role_type: s.role, cpr: s.cpr || '', address: s.address || '', city: s.city || '', zip: s.zip || '', employment_started_at: s.employment_started_at || '', employment_active: s.employment_active, employment_terminated_at: s.employment_terminated_at || '', staff_engagement_type: s.staff_engagement_type || 'employee', compensation_basis: normalizeStaffCompensationBasis(s.staff_engagement_type || 'employee', s.compensation_basis), monthly_salary_amount: String(s.staff_engagement_type || 'employee').toLowerCase() === 'subcontractor' ? '' : String(s.monthly_salary_amount || '').trim(), pension_percent: String(s.pension_percent || '').trim(), employer_pension_percent: String(s.employer_pension_percent || '').trim(), vacation_scheme: String(s.vacation_scheme || 'paid_vacation_days').trim(), paid_vacation_days: String(s.paid_vacation_days || '').trim(), tax_fradrag: String(s.tax_fradrag || '').trim(), tax_trakprocent: String(s.tax_trakprocent || '').trim(), holiday_allowance_percent: String(s.holiday_allowance_percent || '12.5').trim(), vacation_accrual_per_month: String(s.vacation_accrual_per_month || '2.08').trim(), first_name: s.firstName, last_name: s.lastName, initials: s.initials, authorization_ID: s.authorization_ID, seat_tier: s.seat_tier || '', age: 0, creator_password: ownerPassword })
        });
        const txt = await res.text();
        return { ok: res.ok, status: res.status, text: txt };
      }));

      const failed = results.filter(r => !r.ok);
      if (failed.length === 0) {
        alert('Staff saved successfully');
        try {
          await fetchStaffList();
        } catch (err) {
          console.warn('Failed to refresh staff list after create', err);
        }
      } else {
        console.error('Some staff failed to save', failed);
        const errorMessages = failed.map(r => {
          try {
            const parsed = JSON.parse(r.text);
            const detail = parsed.error || parsed.detail || parsed.message || '';
            if (detail) return detail;
            const fieldErrors = Object.entries(parsed)
              .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
              .join('\n');
            return fieldErrors || `HTTP ${r.status}`;
          } catch {
            return r.text || `HTTP ${r.status}`;
          }
        });
        alert(`Some staff could not be saved:\n\n${errorMessages.join('\n\n')}`);
        try {
          await fetchStaffList();
        } catch (err) {
          console.warn('Failed to refresh staff list after partial create', err);
        }
      }
    } catch (err) {
      console.error('Failed saving staff', err);
      alert('Failed to save staff: ' + (err && err.message ? err.message : 'Unknown'));
    } finally {
      setIsLoading(false);
    }
  }

  const hasPendingStaff = (staffList || []).some((staff) => !(
    staff.userId || staff.user_id || staff.id
  ));

  // Send password-reset email to a staff member (caller must be authenticated)
  const sendStaffResetEmail = async (staff) => {
    if (!staff || !staff.userId) {
      alert('Cannot send reset: missing staff user id');
      return;
    }
    try {
      const res = await fetch(`${backendBaseUrl}/user/send_staff_reset/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        },
        body: JSON.stringify({ user_id: staff.userId })
      });
      if (res.ok) {
        alert('Password reset email sent');
      } else {
        const txt = await res.text();
        console.error('Failed to send reset email', res.status, txt);
        alert('Failed to send reset email');
      }
    } catch (err) {
      console.error('Error sending reset email', err);
      alert('Error sending reset email');
    }
  }

  // Open the edit dialog for an existing (already saved) staff member.
  const openEditStaff = (staff) => {
    if (!staff || !staff.userId) {
      // Locally-added staff that hasn't been saved yet has no userId; editing
      // happens through the add form, so guard against opening the dialog.
      alert('Save this staff member first, then you can edit their details.');
      return;
    }
    const resolvedProfile = staff.profile || staff.profile_info || {};
    const resolvedRole = normalizeEditableStaffRoleValue(staff.role || staff.role_type || resolvedProfile.role_type || resolvedProfile.role || '');
    setEditStaffError('');
    setEditStaffForm({
      firstName: staff.firstName || '',
      lastName: staff.lastName || '',
      initials: staff.initials || '',
      email: staff.email || '',
      role: resolvedRole || 'Physiotherapist',
      cpr: staff.cpr || '',
      address: staff.address || '',
      city: staff.city || '',
      zip: staff.zip || '',
      employment_started_at: staff.employment_started_at || '',
      employment_active: staff.employment_active === undefined || staff.employment_active === null ? true : Boolean(staff.employment_active),
      employment_terminated_at: staff.employment_terminated_at || '',
      weekly_work_hours: staff.weekly_work_hours || '37.5',
      staff_engagement_type: staff.staff_engagement_type || 'employee',
      compensation_basis: normalizeStaffCompensationBasis(staff.staff_engagement_type || 'employee', staff.compensation_basis || ''),
      monthly_salary_amount: staff.monthly_salary_amount || resolvedProfile.monthly_salary_amount || '',
      pension_percent: staff.pension_percent || resolvedProfile.pension_percent || '',
      employer_pension_percent: staff.employer_pension_percent || resolvedProfile.employer_pension_percent || '',
      vacation_scheme: staff.vacation_scheme || resolvedProfile.vacation_scheme || 'paid_vacation_days',
      paid_vacation_days: staff.paid_vacation_days || resolvedProfile.paid_vacation_days || '',
      tax_fradrag: staff.tax_fradrag || resolvedProfile.tax_fradrag || '',
      tax_trakprocent: staff.tax_trakprocent || resolvedProfile.tax_trakprocent || '',
      holiday_allowance_percent: staff.holiday_allowance_percent || resolvedProfile.holiday_allowance_percent || '12.5',
      vacation_accrual_per_month: staff.vacation_accrual_per_month || resolvedProfile.vacation_accrual_per_month || '2.08',
      authorization_ID: staff.authorization_ID || '',
      seat_tier: staff.seat_tier || '',
    });
    setEditingStaff(staff);
  };

  const handleSaveEditStaff = async () => {
    if (!editingStaff || !editingStaff.userId) {
      return;
    }
    const form = editStaffForm;
    if (!String(form.firstName || '').trim()) {
      setEditStaffError('First name is required.');
      return;
    }
    if (!String(form.email || '').trim()) {
      setEditStaffError('Email is required.');
      return;
    }
    if (isClinicalStaffRole(form.role) && !String(form.authorization_ID || '').trim()) {
      setEditStaffError('Authorization ID is required for clinical staff.');
      return;
    }
    setEditStaffSaving(true);
    setEditStaffError('');
    try {
      const resolvedRole = form.role || editingStaff.role || editingStaff.role_type || editingStaff.profile?.role_type || editingStaff.profile?.role || 'Physiotherapist';
      const res = await fetch(`${backendBaseUrl}/user/update_staff/${editingStaff.userId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({
          first_name: form.firstName,
          last_name: form.lastName,
          initials: form.initials,
          email: form.email,
          role_type: resolvedRole,
          cpr: form.cpr,
          address: form.address,
          city: form.city,
          zip: form.zip,
          employment_started_at: form.employment_started_at,
          employment_active: form.employment_active,
          employment_terminated_at: form.employment_terminated_at,
          weekly_work_hours: form.weekly_work_hours,
          staff_engagement_type: form.staff_engagement_type,
          compensation_basis: normalizeStaffCompensationBasis(form.staff_engagement_type, form.compensation_basis),
          monthly_salary_amount: String(form.staff_engagement_type || '').toLowerCase() === 'subcontractor'
            ? ''
            : String(form.monthly_salary_amount || '').trim(),
          pension_percent: String(form.staff_engagement_type || '').toLowerCase() === 'subcontractor' ? '' : String(form.pension_percent || '').trim(),
          employer_pension_percent: String(form.staff_engagement_type || '').toLowerCase() === 'subcontractor' ? '' : String(form.employer_pension_percent || '').trim(),
          vacation_scheme: String(form.staff_engagement_type || '').toLowerCase() === 'subcontractor' ? 'holiday_allowance' : String(form.vacation_scheme || 'paid_vacation_days').trim(),
          paid_vacation_days: String(form.staff_engagement_type || '').toLowerCase() === 'subcontractor' ? '' : String(form.paid_vacation_days || '').trim(),
          tax_fradrag: String(form.staff_engagement_type || '').toLowerCase() === 'subcontractor' ? '' : String(form.tax_fradrag || '').trim(),
          tax_trakprocent: String(form.staff_engagement_type || '').toLowerCase() === 'subcontractor' ? '' : String(form.tax_trakprocent || '').trim(),
          holiday_allowance_percent: String(form.staff_engagement_type || '').toLowerCase() === 'subcontractor' ? '' : String(form.holiday_allowance_percent || '12.5').trim(),
          vacation_accrual_per_month: String(form.staff_engagement_type || '').toLowerCase() === 'subcontractor' ? '' : String(form.vacation_accrual_per_month || '2.08').trim(),
          authorization_ID: isClinicalStaffRole(resolvedRole) ? form.authorization_ID : '',
          seat_tier: form.seat_tier || '',
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        let message = `HTTP ${res.status}`;
        try {
          const parsed = JSON.parse(txt);
          message = parsed.error || parsed.detail || message;
        } catch {
          message = txt || message;
        }
        throw new Error(message);
      }
      setEditingStaff(null);
      try {
        await fetchStaffList();
      } catch (err) {
        console.warn('Failed to refresh staff list after edit', err);
      }
    } catch (err) {
      setEditStaffError(err && err.message ? err.message : 'Failed to update staff.');
    } finally {
      setEditStaffSaving(false);
    }
  };

  // Cancel edits made in the frontend since last fetch and restore server state
  const handleCancel = async () => {
    // restore profile fields
    if (originalProfile) {
      setFirstName(originalProfile.first_name || '');
      setLastName(originalProfile.last_name || '');
      setEmail(originalProfile.email || '');
    }
    // restore care provider fields
    if (originalCp) {
      setClinicName(originalCp.name_business || '');
      setClinicRegNumber(originalCp.business_registration_number || '');
      setClinicAddress(originalCp.address || '');
      setClinicCity(originalCp.city || '');
      setClinicPostalCode(originalCp.postal_code || '');
      setClinicEmail(originalCp.email_CareProvider || originalCp.email_contactPerson || '');
      setClinicPhone(originalCp.phoneNumber_CareProvider || '');
      setMainContactPhone(originalCp.phoneNumber_ContactPerson || '');
      setMainContactPerson(((originalCp.firstName_mainContactPerson || '') + (originalCp.lastName_mainContactPerson ? (' ' + originalCp.lastName_mainContactPerson) : '')).trim());
      setContry(originalCp.country || '');
    }
    // restore staff list to server snapshot
    setStaffList(originalServerStaff || []);
    // clear owner password and close dialog if open
    setOwnerPassword('');
    setOwnerPasswordDialogOpen(false);
  }

  const handleDetailsCancel = () => {
    if (originalProfile) {
      setFirstName(originalProfile.first_name || '');
      setLastName(originalProfile.last_name || '');
      setEmail(originalProfile.email || '');
    }
    if (originalCp) {
      setClinicName(originalCp.name_business || '');
      setClinicRegNumber(originalCp.business_registration_number || '');
      setClinicAddress(originalCp.address || '');
      setClinicCity(originalCp.city || '');
      setClinicPostalCode(originalCp.postal_code || '');
      setClinicEmail(originalCp.email_CareProvider || originalCp.email_contactPerson || '');
      setClinicPhone(originalCp.phoneNumber_CareProvider || '');
      setMainContactPhone(originalCp.phoneNumber_ContactPerson || '');
      setMainContactPerson(((originalCp.firstName_mainContactPerson || '') + (originalCp.lastName_mainContactPerson ? (' ' + originalCp.lastName_mainContactPerson) : '')).trim());
      setContry(originalCp.country || '');
    }
  }

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const getAppointmentPatientId = (appointment) => (
    appointment?.patient_id
    || appointment?.patient?.id
    || appointment?.patient?.user_id
    || appointment?.user_id
    || appointment?.patient
    || ''
  );

  const navigateToSelectedPatientPaymentOverview = (appointment) => {
    const selectedPatientId = getAppointmentPatientId(appointment);
    if (!selectedPatientId) return;

    const normalizedPatientId = String(selectedPatientId);
    const storageKey = user?.id ? `vp_selectedPatientId_${user.id}` : 'vp_selectedPatientId';
    const payload = {
      target: 'patientPayment',
      patientId: normalizedPatientId,
      timestamp: Date.now(),
    };
    const serialized = JSON.stringify(payload);

    try {
      localStorage.setItem(storageKey, normalizedPatientId);
      localStorage.setItem('vp_selectedPatientId', normalizedPatientId);
      localStorage.setItem('vp_activeTab', 'patientDetails');
      localStorage.setItem('vp_patientDetailsTab', 'payment');
      localStorage.setItem(ADMIN_NAV_SHORTCUT_KEY, serialized);
    } catch (e) {
      // ignore
    }

    try {
      window.dispatchEvent(new StorageEvent('storage', {
        key: storageKey,
        newValue: normalizedPatientId,
        storageArea: localStorage,
      }));
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'vp_selectedPatientId',
        newValue: normalizedPatientId,
        storageArea: localStorage,
      }));
      window.dispatchEvent(new StorageEvent('storage', {
        key: ADMIN_NAV_SHORTCUT_KEY,
        newValue: serialized,
        storageArea: localStorage,
      }));
    } catch (e) {
      // ignore
    }

    window.dispatchEvent(new CustomEvent(ADMIN_NAV_SHORTCUT_EVENT, { detail: payload }));

    history.push('/my_appointments');
  };

  const dispatchAdminNavigationShortcut = (target) => {
    if (typeof window === 'undefined') return;

    const payload = {
      target,
      timestamp: Date.now(),
    };
    const serialized = JSON.stringify(payload);

    try {
      localStorage.setItem(ADMIN_NAV_SHORTCUT_KEY, serialized);
    } catch (e) {
      // ignore
    }

    try {
      window.dispatchEvent(new StorageEvent('storage', {
        key: ADMIN_NAV_SHORTCUT_KEY,
        newValue: serialized,
        storageArea: localStorage,
      }));
    } catch (e) {
      // ignore
    }

    window.dispatchEvent(new CustomEvent(ADMIN_NAV_SHORTCUT_EVENT, { detail: payload }));
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleShortcut = (shortcutTarget) => {
      if (shortcutTarget !== 'clinicStatistics') return;
      setTabIndex(3);
      try {
        localStorage.removeItem(ADMIN_NAV_SHORTCUT_KEY);
      } catch (e) {
        // ignore
      }
    };

    const handleStorage = (event) => {
      if (event?.key !== ADMIN_NAV_SHORTCUT_KEY || !event.newValue) return;
      try {
        const payload = JSON.parse(event.newValue);
        handleShortcut(payload?.target);
      } catch (e) {
        // ignore malformed payloads
      }
    };

    const handleCustomEvent = (event) => {
      handleShortcut(event?.detail?.target);
    };

    const handleFocus = () => {
      try {
        const raw = localStorage.getItem(ADMIN_NAV_SHORTCUT_KEY);
        if (!raw) return;
        const payload = JSON.parse(raw);
        handleShortcut(payload?.target);
      } catch (e) {
        // ignore
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(ADMIN_NAV_SHORTCUT_EVENT, handleCustomEvent);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(ADMIN_NAV_SHORTCUT_EVENT, handleCustomEvent);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Fetch revenue from backend (to be implemented)
  const handleRevenueSearch = async () => {
    setRevenueLoading(true);
    setRevenueError("");
    setFilteredRevenue([]);
    try {
      // Example endpoint and params, adjust as needed
      let url = `${backendBaseUrl}/api/revenue/?`;
      if (revenueStartDate) url += `start_date=${revenueStartDate}&`;
      if (revenueEndDate) url += `end_date=${revenueEndDate}&`;
      const response = await fetch(url, {
        headers: { "Authorization": `Token ${token}` },
        method: "GET",
      });
      if (!response.ok) throw new Error("Failed to fetch revenue");
      const data = await response.json();
      setRevenueList(data.results || data); // adjust if backend returns differently
      setFilteredRevenue(data.results || data);
    } catch (err) {
      setRevenueError("Could not load revenue data.");
    }
    setRevenueLoading(false);
  };

  // ============ SERVICES FUNCTIONS ============

  const fetchServices = async () => {
    setServicesLoading(true);
    try {
      const response = await fetch(`${backendBaseUrl}/api/services/services/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setServices(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('[Services] Failed to fetch services:', err);
    }
    setServicesLoading(false);
  };

  const fetchClinicLocations = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/profile/clinic/locations/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setClinicLocations(Array.isArray(data) ? data : []);
      } else {
        setClinicLocations([]);
      }
    } catch (err) {
      console.error('[Services] Failed to fetch clinic locations:', err);
      setClinicLocations([]);
    }
  };

  const getClinicPatientId = (patient) => String(patient?.user_id || patient?.userId || patient?.user_id_str || patient?.id || '').trim();

  const fetchClinicPatients = async ({ search = '', ids = [], replaceOptions = true } = {}) => {
    setClinicPatientsLoading(true);
    try {
      const normalizedIds = Array.isArray(ids)
        ? ids.map((patientId) => String(patientId).trim()).filter(Boolean)
        : [];
      const normalizedSearch = String(search || '').trim();
      const params = new URLSearchParams();
      if (normalizedSearch) {
        params.set('search', normalizedSearch);
      }
      if (normalizedIds.length > 0) {
        params.set('ids', normalizedIds.join(','));
      }

      const url = params.toString()
        ? `${backendBaseUrl}/profile/profiles/?${params.toString()}`
        : `${backendBaseUrl}/profile/profiles/`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const patients = Array.isArray(data)
          ? data.filter((profile) => !String(profile.role_type || '').trim())
          : [];
        if (replaceOptions) {
          setClinicPatients(patients);
        }
        if (patients.length > 0) {
          setClinicPatientDirectory((current) => {
            const next = { ...current };
            patients.forEach((patient) => {
              const patientId = getClinicPatientId(patient);
              if (patientId) {
                next[patientId] = patient;
              }
            });
            return next;
          });
        }
        if (replaceOptions) {
          setClinicPatientTreatmentInfo({});
        }
        return patients;
      } else {
        if (replaceOptions) {
          setClinicPatients([]);
          setClinicPatientTreatmentInfo({});
        }
        return [];
      }
    } catch (err) {
      console.error('[Services] Failed to fetch clinic patients:', err);
      if (replaceOptions) {
        setClinicPatients([]);
        setClinicPatientTreatmentInfo({});
      }
      return [];
    } finally {
      setClinicPatientsLoading(false);
    }
  };

  const fetchClinicPatientTreatmentOptions = async (patientId, preferredTreatmentId = '') => {
    const normalizedUserId = String(patientId || '').trim();
    if (!normalizedUserId || !token) {
      return { userId: normalizedUserId, label: '', treatmentId: '', treatments: [] };
    }

    try {
      const response = await fetch(`${backendBaseUrl}/treatment/user/treatments/?patient=${encodeURIComponent(normalizedUserId)}`, {
        headers: { Authorization: `Token ${token}` },
      });

      if (!response.ok) {
        return { userId: normalizedUserId, label: '', treatmentId: '', treatments: [] };
      }

      const data = await response.json();
      const treatments = (Array.isArray(data) ? data : (data.treatments || data.results || []))
        .slice()
        .sort((left, right) => {
          const leftTime = left?.created_at ? new Date(left.created_at).getTime() : 0;
          const rightTime = right?.created_at ? new Date(right.created_at).getTime() : 0;
          return leftTime - rightTime;
        });

      if (treatments.length === 0) {
        return { userId: normalizedUserId, label: '', treatmentId: '', treatments: [] };
      }

      const preferredId = String(preferredTreatmentId || '').trim();
      const selectedTreatment = treatments.find((treatment) => String(treatment.id) === preferredId) || treatments[treatments.length - 1];

      return {
        userId: normalizedUserId,
        label: getClinicTreatmentDisplayName(selectedTreatment),
        treatmentId: String(selectedTreatment?.id || ''),
        treatments,
      };
    } catch (error) {
      console.error('[ClinicDetails] Failed to fetch patient treatment options:', error);
      return { userId: normalizedUserId, label: '', treatmentId: '', treatments: [] };
    }
  };

  const addService = async () => {
    if (!serviceCode || !serviceName || !serviceDuration || !servicePrice || !serviceCommissionRate) return;
    const normalizedServiceWeekdays = normalizeClassWeekdays(serviceClassWeekdays);
    if (serviceIsClass && (!serviceClassParticipantLimit || normalizedServiceWeekdays.length === 0 || !serviceClassStartTime || !serviceClassDefaultRoomId)) {
      alert('Select participant limit, at least one weekday, start time, and a default room before saving a class service.');
      return;
    }
    try {
      const primaryWeekday = normalizedServiceWeekdays[0] ?? null;
      const response = await fetch(`${backendBaseUrl}/api/services/services/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          service_code: serviceCode,
          danmark_code: clinicOperatesInDenmark ? danmarkCode : null,
          name: serviceName,
          duration_minutes: Number(serviceDuration),
          price: Number(servicePrice),
          commission_rate: Number(serviceCommissionRate),
          commission_overrides: buildCommissionOverridesPayload(serviceCommissionOverrides, serviceStaffMembers),
          currency,
          description: serviceDescription,
          requires_vat: serviceRequiresVat,
          vat_amount_percent: serviceRequiresVat ? Number(serviceVatAmountPercent) : null,
          staff_members: serviceStaffMembers,
          is_active: true,
          is_bookable_online: true,
          color: serviceColor || DEFAULT_SERVICE_COLOR,
          is_class: serviceIsClass,
          class_participant_limit: serviceIsClass ? Number(serviceClassParticipantLimit) : null,
          class_weekday: serviceIsClass ? primaryWeekday : null,
          class_weekdays: serviceIsClass ? normalizedServiceWeekdays : [],
          class_start_time: serviceIsClass ? serviceClassStartTime : null,
          class_default_room: serviceIsClass ? serviceClassDefaultRoomId : null,
          class_primary_staff: serviceIsClass ? (serviceClassPrimaryStaff || serviceStaffMembers[0] || null) : null,
          class_weekday_primary_staffs: serviceIsClass ? normalizeClassWeekdayPrimaryStaffs(serviceClassWeekdayPrimaryStaffs, normalizedServiceWeekdays) : {},
          class_patients: [],
          class_patient_treatment_links: {},
        }),
      });
      if (response.ok) {
        setServiceCode('');
        setDanmarkCode('');
        setServiceName('');
        setServiceDuration('');
        setServicePrice('');
        setServiceDescription('');
        setServiceRequiresVat(false);
        setServiceVatAmountPercent('');
        setServiceCommissionRate('');
        setServiceCommissionOverrides({});
        setServiceColor(DEFAULT_SERVICE_COLOR);
        setServiceStaffMembers(staffList.map(s => String(s.userId || s.user_id || s.id)));
        setServiceIsClass(false);
        setServiceClassParticipantLimit('');
        setServiceClassWeekdays([]);
        setServiceClassStartTime('');
        setServiceClassDefaultRoomId('');
        setServiceClassPrimaryStaff('');
        setServiceClassWeekdayPrimaryStaffs({});
        fetchServices();
      } else {
        const err = await response.json();
        console.error('[Services] Add failed:', err);
        alert('Failed to add service: ' + JSON.stringify(err));
      }
    } catch (err) {
      console.error('[Services] Add error:', err);
    }
  };

  const deleteService = async (serviceId) => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/services/services/${serviceId}/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Token ${token}` },
      });
      if (response.ok) {
        fetchServices();
      } else {
        console.error('[Services] Delete failed');
        alert('Failed to delete service.');
      }
    } catch (err) {
      console.error('[Services] Delete error:', err);
    }
  };

  const openEditService = (service) => {
    setEditingService(service);
    const existingStaff = (service.staff_members || []).map(String);
    setEditServiceForm({
      service_code: service.service_code || '',
      danmark_code: service.danmark_code || '',
      name: service.name || '',
      duration_minutes: service.duration_minutes != null ? String(service.duration_minutes) : '',
      price: service.price != null ? String(service.price) : '',
      commission_rate: service.commission_rate != null ? String(service.commission_rate) : '',
      commission_overrides: service.commission_overrides || {},
      currency: service.currency || 'DKK',
      description: service.description || '',
      requires_vat: Boolean(service.requires_vat),
      vat_amount_percent: service.vat_amount_percent != null ? String(service.vat_amount_percent) : '',
      color: service.color || DEFAULT_SERVICE_COLOR,
      staff_members: existingStaff.length > 0 ? existingStaff : staffList.map(s => String(s.userId || s.user_id || s.id)),
      is_class: Boolean(service.is_class),
      class_participant_limit: service.class_participant_limit != null ? String(service.class_participant_limit) : '',
      class_weekdays: normalizeClassWeekdays(service.class_weekdays?.length ? service.class_weekdays : service.class_weekday),
      class_weekday: service.class_weekday != null ? String(service.class_weekday) : '',
      class_start_time: service.class_start_time || '',
      class_default_room: service.class_default_room || '',
      class_primary_staff: service.class_primary_staff || '',
      class_weekday_primary_staffs: service.class_weekday_primary_staffs || {},
      class_patients: Array.isArray(service.class_patients) ? service.class_patients.map(String) : [],
      class_patient_treatment_links: service.class_patient_treatment_links || {},
    });
    setEditServiceCommissionOverridesOpen(false);
  };

  useEffect(() => {
    if (!editingService?.is_class) {
      setClinicPatientTreatmentInfo({});
      setClinicPatientTreatmentOptions({});
      setClinicPatients([]);
      setClinicPatientSearchInput('');
      loadedClinicPatientIdsRef.current = new Set();
      return;
    }

    const selectedPatientIds = Array.isArray(editServiceForm.class_patients)
      ? editServiceForm.class_patients.map((patientId) => String(patientId)).filter(Boolean)
      : [];
    if (selectedPatientIds.length === 0) {
      setClinicPatientTreatmentInfo({});
      setClinicPatientTreatmentOptions({});
      loadedClinicPatientIdsRef.current = new Set();
      return;
    }

    const missingPatientIds = selectedPatientIds.filter((patientId) => !loadedClinicPatientIdsRef.current.has(patientId));

    if (missingPatientIds.length === 0) {
      return;
    }

    let cancelled = false;
    const loadTreatmentLabels = async () => {
      if (missingPatientIds.length > 0) {
        await fetchClinicPatients({ ids: missingPatientIds, replaceOptions: false });
      }

      const entries = await Promise.all(selectedPatientIds.map((patientId) => {
        const preferredTreatmentId = String(editServiceForm.class_patient_treatment_links?.[patientId] || '').trim();
        return fetchClinicPatientTreatmentOptions(patientId, preferredTreatmentId);
      }));
      if (cancelled) {
        return;
      }

      setClinicPatientTreatmentInfo((current) => {
        const next = { ...current };
        entries.forEach((entry) => {
          next[entry.userId] = {
            treatmentId: entry.treatmentId || '',
            label: entry.label || '',
          };
          loadedClinicPatientIdsRef.current.add(entry.userId);
        });
        return next;
      });

      setClinicPatientTreatmentOptions((current) => {
        const next = { ...current };
        entries.forEach((entry) => {
          next[entry.userId] = entry.treatments || [];
        });
        return next;
      });

      setEditServiceForm((currentForm) => {
        const nextLinks = { ...(currentForm.class_patient_treatment_links || {}) };
        let changed = false;
        entries.forEach((entry) => {
          if (!entry.userId || !entry.treatmentId) {
            return;
          }
          if (String(nextLinks[entry.userId] || '') !== String(entry.treatmentId)) {
            nextLinks[entry.userId] = entry.treatmentId;
            changed = true;
          }
        });
        if (!changed) {
          return currentForm;
        }
        return {
          ...currentForm,
          class_patient_treatment_links: nextLinks,
        };
      });
    };

    void loadTreatmentLabels();

    return () => {
      cancelled = true;
    };
  }, [editServiceForm.class_patients, editingService?.id, editingService?.is_class, token]);

  useEffect(() => {
    if (!editingService?.is_class) {
      return;
    }

    const searchValue = String(clinicPatientSearchInput || '').trim();
    if (searchValue.length < 2) {
      setClinicPatients([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      fetchClinicPatients({ search: searchValue, replaceOptions: true })
        .then((patients) => {
          if (cancelled) {
            return;
          }
          setClinicPatients(patients);
        })
        .catch(() => {});
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [clinicPatientSearchInput, editingService?.id, editingService?.is_class, token]);

  const openNewClassService = (weekdayValue, startTime, durationMinutes = 60) => {
    setEditingService({ id: null, isDraft: true });
    setServiceClassWeekdayPrimaryStaffs({});
    setEditServiceForm({
      service_code: '',
      danmark_code: '',
      name: '',
      duration_minutes: String(Math.max(30, Number(durationMinutes) || 60)),
      price: '',
      commission_rate: '',
      commission_overrides: {},
      currency: 'DKK',
      description: '',
      requires_vat: false,
      vat_amount_percent: '',
      color: DEFAULT_SERVICE_COLOR,
      staff_members: staffList.length > 0 ? staffList.map(s => String(s.userId || s.user_id || s.id)) : [],
      is_class: true,
      class_participant_limit: '',
      class_weekdays: normalizeClassWeekdays([weekdayValue]),
      class_weekday: String(weekdayValue),
      class_start_time: startTime,
      class_default_room: getFirstClassRoomId(),
      class_primary_staff: staffList.length > 0 ? String(staffList[0].userId || staffList[0].user_id || staffList[0].id) : '',
      class_weekday_primary_staffs: {},
      class_patients: [],
      class_patient_treatment_links: {},
    });
    setEditServiceCommissionOverridesOpen(false);
  };

  const updateService = async () => {
    if (!editingService) return;
    setEditServiceSaving(true);
    try {
      const isDraftService = Boolean(editingService.isDraft);
      const response = await fetch(
        isDraftService
          ? `${backendBaseUrl}/api/services/services/`
          : `${backendBaseUrl}/api/services/services/${editingService.id}/`,
        {
          method: isDraftService ? 'POST' : 'PATCH',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
          service_code: editServiceForm.service_code,
          danmark_code: clinicOperatesInDenmark ? editServiceForm.danmark_code : null,
          name: editServiceForm.name,
          duration_minutes: Number(editServiceForm.duration_minutes),
          price: Number(editServiceForm.price),
          commission_rate: Number(editServiceForm.commission_rate),
          commission_overrides: buildCommissionOverridesPayload(editServiceForm.commission_overrides, editServiceForm.staff_members),
          currency: editServiceForm.currency,
          description: editServiceForm.description,
          requires_vat: editServiceForm.requires_vat,
          vat_amount_percent: editServiceForm.requires_vat ? Number(editServiceForm.vat_amount_percent) : null,
          color: editServiceForm.color || DEFAULT_SERVICE_COLOR,
          staff_members: editServiceForm.staff_members,
          is_class: editServiceForm.is_class,
          class_participant_limit: editServiceForm.is_class ? Number(editServiceForm.class_participant_limit) : null,
          class_weekday: editServiceForm.is_class ? (normalizeClassWeekdays(editServiceForm.class_weekdays)[0] ?? null) : null,
          class_weekdays: editServiceForm.is_class ? normalizeClassWeekdays(editServiceForm.class_weekdays) : [],
          class_start_time: editServiceForm.is_class ? editServiceForm.class_start_time : null,
          class_default_room: editServiceForm.is_class ? editServiceForm.class_default_room : null,
          class_primary_staff: editServiceForm.is_class ? editServiceForm.class_primary_staff : null,
          class_weekday_primary_staffs: editServiceForm.is_class ? normalizeClassWeekdayPrimaryStaffs(editServiceForm.class_weekday_primary_staffs, editServiceForm.class_weekdays) : {},
          class_patients: editServiceForm.is_class ? editServiceForm.class_patients : [],
          class_patient_treatment_links: editServiceForm.is_class ? editServiceForm.class_patient_treatment_links : {},
          }),
        }
      );
      if (response.ok) {
        setEditingService(null);
        fetchServices();
      } else {
        const err = await response.json();
        alert('Failed to save: ' + JSON.stringify(err));
      }
    } catch (err) {
      console.error('[Services] Update error:', err);
    }
    setEditServiceSaving(false);
  };

  const startClassScheduleSelection = (weekdayValue, event) => {
    if (event.button !== 0) {
      return;
    }

    if (event.target?.closest?.('[data-class-block="true"]')) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const anchorIndex = getClassScheduleSlotIndexFromClientY(event.clientY, rect);
    const range = buildClassScheduleRangeFromIndices(anchorIndex, anchorIndex);
    classScheduleSelectionRef.current = {
      weekdayValue,
      anchorIndex,
      currentIndex: anchorIndex,
      columnElement: event.currentTarget,
    };
    setClassScheduleSelectionPreview({
      weekdayValue,
      ...range,
    });
  };

  const clearClassScheduleSelection = () => {
    classScheduleSelectionRef.current = null;
    setClassScheduleSelectionPreview(null);
  };

  const openClassDialogFromSelection = (weekdayValue, startMinutes, endMinutes) => {
    openNewClassService(
      weekdayValue,
      formatClassScheduleMinutesLabel(startMinutes),
      Math.max(CLASS_SCHEDULE_SLOT_MINUTES, endMinutes - startMinutes),
    );
  };

  useEffect(() => {
    const handleWindowMouseMove = (event) => {
      const selection = classScheduleSelectionRef.current;
      if (!selection?.columnElement) {
        return;
      }

      const nextIndex = getClassScheduleSlotIndexFromClientY(event.clientY, selection.columnElement.getBoundingClientRect());
      if (nextIndex !== selection.currentIndex) {
        selection.currentIndex = nextIndex;
        const range = buildClassScheduleRangeFromIndices(selection.anchorIndex, nextIndex);
        setClassScheduleSelectionPreview({
          weekdayValue: selection.weekdayValue,
          ...range,
        });
      }
    };

    const handleWindowMouseUp = () => {
      const selection = classScheduleSelectionRef.current;
      if (!selection) {
        return;
      }

      const range = buildClassScheduleRangeFromIndices(selection.anchorIndex, selection.currentIndex);
      clearClassScheduleSelection();
      openClassDialogFromSelection(selection.weekdayValue, range.startMinutes, range.endMinutes);
    };

    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, []);

  // ============ BILLING STATISTICS FUNCTIONS ============

  // Fetch billing stats with filters
  const fetchBillingStats = async () => {
    setBillingLoading(true);
    setBillingError("");
    try {
      const baseUrl = apiBaseUrl;
      let url = `${baseUrl}/api/calendar/billing-stats/?detail=1`;
      
      if (billingStatusScope && billingStatusScope !== "all") {
        url += `&status_scope=${billingStatusScope}`;
      }
      if (billingDateRangeStart) {
        url += `&start_date=${billingDateRangeStart}`;
      }
      if (billingDateRangeEnd) {
        url += `&end_date=${billingDateRangeEnd}`;
      }
      if (billingStaffId) {
        url += `&staff_id=${billingStaffId}`;
      }
      if (billingPayerType && billingPayerType !== "") {
        url += `&payer_type=${billingPayerType}`;
      }

      const response = await fetch(url, {
        headers: { "Authorization": `Token ${token}` },
        method: "GET",
      });

      if (!response.ok) throw new Error("Failed to fetch billing statistics");
      const data = await response.json();
      setBillingStats(data);
      setSelectedBillingRows([]);
      setSelectedBillingInvoices([]);
    } catch (err) {
      setBillingError(err.message || "Could not load billing data");
    }
    setBillingLoading(false);
  };

  const fetchMonthEndReport = async (options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      setMonthEndReportLoading(true);
      setMonthEndReportError('');
    }

    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
      const query = new URLSearchParams();
      const normalizedMonth = toYearMonthToken(monthEndMonth);
      if (normalizedMonth) {
        query.set('month', normalizedMonth);
      }
      if (billingPayerType) {
        query.set('payer_type', billingPayerType);
      }
      if (monthEndStaffIds.length > 0) {
        query.set('staff_ids', monthEndStaffIds.join(','));
      }

      const response = await fetch(`${baseUrl}/api/calendar/month-end-report/?${query.toString()}`, {
        headers: { Authorization: `Token ${token}` },
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch month-end report');
      }

      const data = await response.json();
      setMonthEndReport(data);

      if (normalizedMonth) {
        await fetchMonthEndAbsenceReport(normalizedMonth, { silent: true });
      } else {
        setMonthEndAbsenceReport(null);
      }
    } catch (err) {
      setMonthEndReportError(err.message || 'Could not load month-end report');
      if (!silent) {
        setMonthEndReport(null);
        setMonthEndAbsenceReport(null);
      }
    }

    if (!silent) {
      setMonthEndReportLoading(false);
    }
  };

  const fetchMonthEndAbsenceReport = async (normalizedMonth, options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      setMonthEndAbsenceLoading(true);
    }

    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
      const response = await fetch(`${baseUrl}/api/calendar/vacations/`, {
        headers: { Authorization: `Token ${token}` },
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch absence data');
      }

      const data = await response.json();
      const monthRange = getMonthRangeFromInput(normalizedMonth || monthEndMonth);
      const vacations = Array.isArray(data?.vacations) ? data.vacations : [];
      const staffSummaryMap = new Map();
      const byTypeTotals = {
        vacation: 0,
        sick_day: 0,
        paid_leave: 0,
        agreed_absence: 0,
      };
      let totalRecords = 0;
      let totalDays = 0;

      const ensureStaffSummary = (vacation) => {
        const staffId = String(vacation.staff_member || vacation.staff_member_id || vacation.staff_member_name || '');
        const existing = staffSummaryMap.get(staffId);
        if (existing) {
          return existing;
        }

        const created = {
          staff_id: staffId,
          staff_name: vacation.staff_member_name || 'Staff member',
          absence_records: 0,
          absence_days: 0,
          vacation_days: 0,
          sick_day_days: 0,
          paid_leave_days: 0,
          agreed_absence_days: 0,
          ranges: [],
        };
        staffSummaryMap.set(staffId, created);
        return created;
      };

      vacations.forEach((vacation) => {
        const days = getOverlapDaysForMonth(vacation.start_date, vacation.end_date, monthRange);
        if (!days) {
          return;
        }

        const absenceType = String(vacation.absence_type || 'vacation');
        const staffSummary = ensureStaffSummary(vacation);
        totalRecords += 1;
        totalDays += days;
        staffSummary.absence_records += 1;
        staffSummary.absence_days += days;
        staffSummary.ranges.push({
          type: absenceType,
          start_date: vacation.start_date,
          end_date: vacation.end_date,
          days,
        });

        if (absenceType === 'sick_day') {
          staffSummary.sick_day_days += days;
          byTypeTotals.sick_day += days;
        } else if (absenceType === 'paid_leave') {
          staffSummary.paid_leave_days += days;
          byTypeTotals.paid_leave += days;
        } else if (absenceType === 'agreed_absence') {
          staffSummary.agreed_absence_days += days;
          byTypeTotals.agreed_absence += days;
        } else {
          staffSummary.vacation_days += days;
          byTypeTotals.vacation += days;
        }
      });

      setMonthEndAbsenceReport({
        month: normalizedMonth || toYearMonthToken(monthEndMonth),
        total_records: totalRecords,
        total_days: totalDays,
        by_type: byTypeTotals,
        staff_summary: Array.from(staffSummaryMap.values()).sort((a, b) => b.absence_days - a.absence_days || a.staff_name.localeCompare(b.staff_name)),
      });
    } catch (err) {
      if (!silent) {
        setMonthEndReportError(err.message || 'Could not load absence data');
      }
      setMonthEndAbsenceReport(null);
    } finally {
      if (!silent) {
        setMonthEndAbsenceLoading(false);
      }
    }
  };

  const fetchMonthEndSnapshots = async (options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      setMonthEndSnapshotsLoading(true);
    }

    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
      const query = new URLSearchParams();
      const normalizedMonth = toYearMonthToken(monthEndMonth);
      if (normalizedMonth) {
        query.set('month', normalizedMonth);
      }
      query.set('limit', '8');

      const response = await fetch(`${baseUrl}/api/calendar/month-end-report/snapshots/?${query.toString()}`, {
        headers: { Authorization: `Token ${token}` },
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch month-end snapshots');
      }

      const data = await response.json();
      setMonthEndSnapshots(Array.isArray(data?.results) ? data.results : []);
    } catch (err) {
      if (!silent) {
        setMonthEndReportError(err.message || 'Could not load month-end snapshots');
      }
    }

    if (!silent) {
      setMonthEndSnapshotsLoading(false);
    }
  };

  const saveMonthEndSnapshot = async () => {
    if (!monthEndReport) {
      setMonthEndReportError('Load a month-end report before saving a snapshot.');
      return;
    }
    if (monthEndFinalizeMode === 'staff_subcontractor' && !monthEndFinalizeStaffId) {
      setMonthEndReportError('Select a subcontractor before saving a subcontractor batch snapshot.');
      return;
    }

    setMonthEndSnapshotSaving(true);
    setMonthEndReportError('');
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
      const normalizedMonth = toYearMonthToken(monthEndMonth);
      const snapshotLabel = monthEndFinalizeMode === 'staff_subcontractor'
        ? `Month-end subcontractor batch ${normalizedMonth}`
        : `Month-end clinic batch ${normalizedMonth}`;
      const response = await fetch(`${baseUrl}/api/calendar/month-end-report/`, {
        method: 'POST',
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          month: normalizedMonth,
          payer_type: billingPayerType || '',
          staff_ids: monthEndStaffIds,
          snapshot_name: snapshotLabel,
          billing_type: monthEndFinalizeMode,
          staff_member_id: monthEndFinalizeMode === 'staff_subcontractor' ? monthEndFinalizeStaffId : null,
        }),
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.error || errorPayload.detail || 'Failed to save month-end snapshot');
      }

      await fetchMonthEndSnapshots({ silent: true });
    } catch (err) {
      setMonthEndReportError(err.message || 'Could not save month-end snapshot');
    }
    setMonthEndSnapshotSaving(false);
  };

  const handleBankStatementUploadClick = () => {
    if (bankStatementUploadRef.current) {
      bankStatementUploadRef.current.click();
    }
  };

  const handleBankStatementUploadChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setBankStatementUploadLoading(true);
    setBankStatementUploadError('');
    setBankStatementUploadResult(null);

    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
      const formData = new FormData();
      formData.append('csv_file', file);
      formData.append('payment_method', 'bank_transfer');

      const response = await fetch(`${baseUrl}/api/calendar/billing-actions/bank-statement-reconciliation/`, {
        method: 'POST',
        headers: {
          Authorization: `Token ${token}`,
        },
        body: formData,
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || payload.detail || 'Failed to reconcile the uploaded CSV');
      }

      setBankStatementUploadResult({
        fileName: file.name,
        ...payload,
      });

      await fetchBillingStats();
      if (monthEndMonth) {
        await fetchMonthEndReport({ silent: true });
      }
    } catch (err) {
      setBankStatementUploadError(err.message || 'Could not process the uploaded CSV');
    } finally {
      setBankStatementUploadLoading(false);
    }
  };

  const downloadMonthEndSnapshotJson = (snapshot) => {
    if (!snapshot) return;
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${snapshot.snapshot_name || 'month_end_snapshot'}_${snapshot.report_month || 'report'}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleSendPayslips = async (month, staffIds = []) => {
    setPayslipSending(true);
    setPayslipSendResult(null);
    try {
      const res = await fetch(`${backendBaseUrl}/api/calendar/billing-actions/send-payslips/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
        body: JSON.stringify({ month, staff_ids: staffIds }),
      });
      const data = await res.json();
      setPayslipSendResult(data);
      if (res.ok) {
        fetchPayslipArchives();
      }
    } catch (err) {
      setPayslipSendResult({ error: err.message });
    } finally {
      setPayslipSending(false);
    }
  };

  const downloadPayslipArchiveHtml = (archive) => {
    if (!archive) return;
    const blob = new Blob([archive.rendered_html || ''], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `payslip_${archive.report_month || 'archive'}_${archive.staff_member_email || archive.staff_member_id || 'staff'}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const openPayslipArchivePdf = async (archive) => {
    if (!archive || !archive.rendered_html) return;

    const parsedDocument = new DOMParser().parseFromString(archive.rendered_html, 'text/html');
    const hiddenHost = document.createElement('div');
    hiddenHost.style.position = 'fixed';
    hiddenHost.style.left = '-10000px';
    hiddenHost.style.top = '0';
    hiddenHost.style.width = '210mm';
    hiddenHost.style.background = '#fff';
    hiddenHost.style.padding = '0 16px';

    const styleMarkup = Array.from(parsedDocument.head?.querySelectorAll('style') || [])
      .map((node) => node.outerHTML)
      .join('');
    hiddenHost.innerHTML = `${styleMarkup}${parsedDocument.body?.innerHTML || archive.rendered_html}`;
    document.body.appendChild(hiddenHost);

    try {
      const pdfBlob = await html2pdf()
        .set({
          margin: 0,
          filename: `payslip_${archive.report_month || 'archive'}_${archive.staff_member_email || archive.staff_member_id || 'staff'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(hiddenHost)
        .outputPdf('blob');

      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (err) {
      setPayslipArchivesError(err.message || 'Failed to open archived payslip as PDF');
    } finally {
      document.body.removeChild(hiddenHost);
    }
  };

  const fetchPayslipArchives = async () => {
    if (!monthEndMonth) return;
    setPayslipArchivesLoading(true);
    setPayslipArchivesError('');
    try {
      const params = new URLSearchParams({ report_month: monthEndMonth });
      if (payslipSelectedStaffId) {
        params.set('staff_member_id', payslipSelectedStaffId);
      }
      const res = await fetch(
        `${backendBaseUrl}/api/calendar/payslip-archives/?${params.toString()}`,
        { headers: { Authorization: `Token ${token}` } },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load archived payslips');
      setPayslipArchives(Array.isArray(data.results) ? data.results : []);
    } catch (err) {
      setPayslipArchivesError(err.message || 'Failed to load archived payslips');
      setPayslipArchives([]);
    } finally {
      setPayslipArchivesLoading(false);
    }
  };

  const fetchSettlementData = async () => {
    if (!settlementStaffId || !monthEndMonth) return;
    setSettlementLoading(true);
    setSettlementError('');
    setSettlementData(null);
    try {
      const res = await fetch(
        `${backendBaseUrl}/api/calendar/billing-actions/subcontractor-settlement/?month=${monthEndMonth}&staff_id=${settlementStaffId}`,
        { headers: { Authorization: `Token ${token}` } },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load settlement');
      setSettlementData(data);
    } catch (err) {
      setSettlementError(err.message);
    } finally {
      setSettlementLoading(false);
    }
  };

  const fetchAnnualStats = async (year) => {
    setAnnualStatsLoading(true);
    setAnnualStatsError('');
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
      const params = new URLSearchParams({
        detail: '1',
        start_date: `${year}-01-01`,
        end_date: `${year}-12-31`,
      });
      if (billingStatusScope && billingStatusScope !== 'all') {
        params.set('status_scope', billingStatusScope);
      }
      if (billingStaffId) {
        params.set('staff_id', billingStaffId);
      }
      if (billingPayerType && billingPayerType !== '') {
        params.set('payer_type', billingPayerType);
      }

      const res = await fetch(
        `${baseUrl}/api/calendar/billing-stats/?${params.toString()}`,
        { headers: { Authorization: `Token ${token}` } },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load annual summary');

      const monthGroups = Array.isArray(data?.month_groups) ? data.month_groups : [];
      const monthMap = new Map(monthGroups.map((monthGroup) => [String(monthGroup.month || ''), monthGroup]));

      const monthRows = [];
      const yearTotals = {
        appointment_count: 0,
        total_revenue: 0,
        billed_revenue: 0,
        collected_revenue: 0,
        unbilled_revenue: 0,
        total_commission: 0,
        total_vat: 0,
      };

      for (let monthIndex = 1; monthIndex <= 12; monthIndex += 1) {
        const monthKey = `${year}${String(monthIndex).padStart(2, '0')}`;
        const monthDisplay = `${year}-${String(monthIndex).padStart(2, '0')}`;
        const monthGroup = monthMap.get(monthKey) || { rows: [] };
        const rows = Array.isArray(monthGroup.rows) ? monthGroup.rows : [];
        const billedRows = rows.filter((row) => Boolean(row.invoice_id));
        const totalRevenue = rows.reduce((acc, row) => acc + Number(row.amount || 0), 0);
        const billedRevenue = billedRows.reduce((acc, row) => acc + Number(row.invoice_total_amount || row.amount || 0), 0);
        const collectedRevenue = billedRows.reduce((acc, row) => acc + Number(row.amount_paid || 0), 0);
        const unbilledRevenue = rows.filter((row) => !row.invoice_id).reduce((acc, row) => acc + Number(row.amount || 0), 0);
        const totalCommission = getMonthCommissionTotal(rows);
        const totalVat = rows.reduce((acc, row) => {
          const service = services.find((svc) => String(svc.id) === String(row.service_id));
          const vatRate = service && service.requires_vat ? Number(service.vat_amount_percent || 0) : 0;
          const vatAmount = service && service.requires_vat ? Number(row.amount || 0) * (vatRate / 100) : 0;
          return acc + vatAmount;
        }, 0);

        const appointmentCount = rows.length;
        monthRows.push({
          month: monthDisplay,
          month_label: monthDisplay,
          appointment_count: appointmentCount,
          total_revenue: totalRevenue.toFixed(2),
          billed_revenue: billedRevenue.toFixed(2),
          collected_revenue: collectedRevenue.toFixed(2),
          unbilled_revenue: unbilledRevenue.toFixed(2),
          total_commission: totalCommission.toFixed(2),
          total_vat: totalVat.toFixed(2),
        });

        yearTotals.appointment_count += appointmentCount;
        yearTotals.total_revenue += totalRevenue;
        yearTotals.billed_revenue += billedRevenue;
        yearTotals.collected_revenue += collectedRevenue;
        yearTotals.unbilled_revenue += unbilledRevenue;
        yearTotals.total_commission += totalCommission;
        yearTotals.total_vat += totalVat;
      }

      setAnnualStats({
        year,
        months: monthRows,
        year_totals: {
          appointment_count: yearTotals.appointment_count,
          total_revenue: yearTotals.total_revenue.toFixed(2),
          billed_revenue: yearTotals.billed_revenue.toFixed(2),
          collected_revenue: yearTotals.collected_revenue.toFixed(2),
          unbilled_revenue: yearTotals.unbilled_revenue.toFixed(2),
          total_commission: yearTotals.total_commission.toFixed(2),
          total_vat: yearTotals.total_vat.toFixed(2),
        },
      });
    } catch (err) {
      setAnnualStatsError(err.message);
    } finally {
      setAnnualStatsLoading(false);
    }
  };

  const fetchAgingReport = async () => {
    setAgingLoading(true);
    setAgingError('');
    try {
      const res = await fetch(
        `${backendBaseUrl}/api/calendar/invoices/ageing-detail/`,
        { headers: { Authorization: `Token ${token}` } },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load aging report');
      setAgingReport(data);
    } catch (err) {
      setAgingError(err.message);
    } finally {
      setAgingLoading(false);
    }
  };

  const handleSetPaidAmount = async (invoiceId, amount) => {
    try {
      const res = await fetch(
        `${backendBaseUrl}/api/calendar/invoices/${invoiceId}/set-paid-amount/`,
        {
          method: 'PATCH',
          headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount_paid: amount }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update');
      setEditingPaidInvoiceId(null);
      setEditingPaidAmount('');
      fetchAgingReport();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleForgiveBalance = async (invoiceId) => {
    if (!window.confirm('Write off (forgive) the remaining balance on this invoice?')) return;
    try {
      const res = await fetch(
        `${backendBaseUrl}/api/calendar/invoices/${invoiceId}/forgive-balance/`,
        {
          method: 'POST',
          headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to forgive');
      fetchAgingReport();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDownloadPayslipPDF = async (staffEntry, month, locale = payslipLocale) => {
    if (!staffEntry) return;
    const isDanishClinic = String(locale || '').toLowerCase() === 'da' || clinicOperatesInDenmark;
    const previewBaseUrl = (process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com').replace(/\/$/, '');
    const basisLabel = isDanishClinic
      ? ({ fixed_salary: 'Fast løn', commission_salary: 'Provisionsløn', subcontractor_commission: 'Selvstændig provision' }[staffEntry.compensation_basis] || staffEntry.compensation_basis || '')
      : ({ fixed_salary: 'Fixed salary', commission_salary: 'Commission salary', subcontractor_commission: 'Subcontractor commission' }[staffEntry.compensation_basis] || staffEntry.compensation_basis || '');
    const engagementLabel = isDanishClinic
      ? (staffEntry.staff_engagement_type === 'subcontractor' ? 'Selvstændig' : 'Medarbejder')
      : (staffEntry.staff_engagement_type === 'subcontractor' ? 'Subcontractor' : 'Employee');
    const currency = billingStats?.grand_totals?.currency || (isDanishClinic ? 'DKK' : 'EUR');
    const clinicName = billingStats?.grand_totals?.clinic_name || 'Your Clinic';
    const periodLabel = month || monthEndMonth || '';
    const fmt = (v) => parseFloat(v || 0).toFixed(2);
    const row = (label, value, bold) => `<tr><td style="padding:8px 12px;border-bottom:1px solid #e8edf2;color:#555;font-size:13px;width:55%;">${label}</td><td style="padding:8px 12px;border-bottom:1px solid #e8edf2;font-size:13px;${bold ? 'font-weight:700;color:#002D55;' : ''}">${value}</td></tr>`;
    const staffProfile = staffList2.find((staff) => String(staff.user_id || staff.id || '') === String(staffEntry.staff_id)) || {};
    const weeklyWorkHours = staffProfile.weekly_work_hours || staffEntry.weekly_work_hours || '37.5';
    const employeeCpr = staffProfile.cpr || staffEntry.cpr || '';
    const employeeAddress = [staffProfile.address || staffEntry.address || '', staffProfile.city || staffEntry.city || '', staffProfile.zip || staffEntry.zip || '']
      .filter(Boolean)
      .join(', ');
    const companyAddress = [clinicAddress, clinicPostalCode, clinicCity].filter(Boolean).join(', ');
    const payPeriodRange = getMonthRangeFromInput(periodLabel);
    const payPeriodDays = payPeriodRange ? payPeriodRange.end.diff(payPeriodRange.start, 'days') + 1 : null;
    const pdfCreatedDate = new Date().toLocaleDateString();
    const parseTimeToMinutes = (timeValue) => {
      const [hours, minutes] = String(timeValue || '').split(':').map((part) => Number(part));
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
        return null;
      }
      return (hours * 60) + minutes;
    };
    const buildMonthDateList = (range) => {
      if (!range) return [];
      const dates = [];
      const cursor = range.start.clone().startOf('day');
      const monthEnd = range.end.clone().startOf('day');
      while (cursor.isSameOrBefore(monthEnd, 'day')) {
        dates.push(cursor.clone());
        cursor.add(1, 'day');
      }
      return dates;
    };
    const absenceEntry = (monthEndAbsenceReport?.staff_summary || []).find((item) => String(item.staff_id) === String(staffEntry.staff_id)) || null;
    const workHoursBreakdown = buildPayslipWorkHoursBreakdown({
      monthValue: periodLabel,
      weeklyWorkHours,
      absenceSummary: absenceEntry,
    });
    const totalMonthlyWorkHours = workHoursBreakdown.reduce((sum, week) => sum + Number(week.subtotalHours || 0), 0);
    const totalMonthlyExpectedHours = workHoursBreakdown.reduce((sum, week) => sum + Number(week.registeredHours || 0), 0);
    const registeredWeeklyHours = Number(weeklyWorkHours || 0) || 0;
    const dailyWorkHours = workHoursBreakdown[0]?.dailyWorkHours != null
      ? Number(workHoursBreakdown[0].dailyWorkHours || 0)
      : (registeredWeeklyHours > 0 ? registeredWeeklyHours / 5 : 0);
    const absenceBreakdown = absenceEntry
      ? [
        absenceEntry.vacation_days ? `Vacation ${absenceEntry.vacation_days}` : null,
        absenceEntry.sick_day_days ? `Sick ${absenceEntry.sick_day_days}` : null,
        absenceEntry.paid_leave_days ? `Paid leave ${absenceEntry.paid_leave_days}` : null,
        absenceEntry.agreed_absence_days ? `Agreed ${absenceEntry.agreed_absence_days}` : null,
      ].filter(Boolean).join(' | ')
      : '-';
    const monthlySalaryAmount = Number(staffEntry.monthly_salary_amount || 0) || 0;
    const commissionAmount = Number(staffEntry.calculated_commission || 0) || 0;
    const staffMonthlyRows = (billingStats?.month_groups || []).flatMap((monthGroup) => monthGroup.rows || []).filter((row) => String(row.staff_id) === String(staffEntry.staff_id));
    const staffMonthlyRevenue = staffMonthlyRows.reduce((sum, row) => sum + Number(row.amount || row.booked_price || row.amount_billed || 0), 0);
    const averageRevenuePerWorkedHour = totalMonthlyWorkHours > 0 ? (staffMonthlyRevenue / totalMonthlyWorkHours) : 0;
    const serviceById = new Map((services || []).map((service) => [String(service.id), service]));
    const bookedMinutes = staffMonthlyRows.reduce((sum, row) => {
      const service = serviceById.get(String(row.service_id || ''));
      const appointmentMinutes = Number(service?.duration_minutes || row.duration_minutes || 0) || 0;
      return sum + appointmentMinutes;
    }, 0);
    const monthDates = buildMonthDateList(payPeriodRange);
    let allocatedMinutes = 0;
    try {
      const allocationResponse = await fetch(`${previewBaseUrl}/api/calendar/availability/?staff_member_id=${encodeURIComponent(String(staffEntry.staff_id || ''))}`, {
        headers: { Authorization: `Token ${token}` },
      });
      if (allocationResponse.ok) {
        const allocationData = await allocationResponse.json().catch(() => ({}));
        const availabilitySlots = Array.isArray(allocationData.availability_slots) ? allocationData.availability_slots : [];
        allocatedMinutes = availabilitySlots.reduce((sum, slot) => {
          if (slot.is_available === false) {
            return sum;
          }
          const startMinutes = parseTimeToMinutes(slot.start_time);
          const endMinutes = parseTimeToMinutes(slot.end_time);
          if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || endMinutes <= startMinutes) {
            return sum;
          }
          const slotMinutes = endMinutes - startMinutes;
          if (slot.specific_date) {
            const specificDate = moment(slot.specific_date, 'YYYY-MM-DD', true);
            if (specificDate.isValid() && specificDate.isSameOrAfter(payPeriodRange.start, 'day') && specificDate.isSameOrBefore(payPeriodRange.end, 'day')) {
              return sum + slotMinutes;
            }
            return sum;
          }
          const weekdayIndex = Number(slot.day_of_week);
          if (!Number.isFinite(weekdayIndex)) {
            return sum;
          }
          const occurrences = monthDates.filter((dateValue) => dateValue.isoWeekday() - 1 === weekdayIndex).length;
          return sum + (occurrences * slotMinutes);
        }, 0);
      }
    } catch (allocationError) {
      allocatedMinutes = 0;
    }
    const bookedTimeslotHours = bookedMinutes / 60;
    const allocatedTimeslotHours = allocatedMinutes / 60;
    const occupancyRate = allocatedMinutes > 0 ? (bookedMinutes / allocatedMinutes) * 100 : null;
    const avgNpsValue = careProviderAvgNps != null && careProviderAvgNps !== '' ? Number(careProviderAvgNps) : null;
    const payrollRequest = {
      staffEngagementType: staffEntry.staff_engagement_type,
      compensationBasis: staffEntry.compensation_basis,
      baseMonthlySalaryAmount: monthlySalaryAmount,
      commissionAmount,
      month: periodLabel,
      staffMemberId: staffEntry.staff_id,
      registeredHours: totalMonthlyWorkHours,
      expectedHours: totalMonthlyExpectedHours,
      pensionPercent: staffProfile.pension_percent || staffEntry.pension_percent || 0,
      employerPensionPercent: staffProfile.employer_pension_percent || staffEntry.employer_pension_percent || 0,
      taxFradrag: staffProfile.tax_fradrag || staffEntry.tax_fradrag || 0,
      taxTrakprocent: staffProfile.tax_trakprocent || staffEntry.tax_trakprocent || 0,
      vacationScheme: staffProfile.vacation_scheme || staffEntry.vacation_scheme || 'paid_vacation_days',
      holidayAllowancePercent: staffProfile.holiday_allowance_percent || staffEntry.holiday_allowance_percent || 12.5,
      vacationAccrualPerMonth: staffProfile.vacation_accrual_per_month || staffEntry.vacation_accrual_per_month || 2.08,
      paidVacationDays: staffProfile.paid_vacation_days || staffEntry.paid_vacation_days || 0,
    };
    const normalizeBackendPayroll = (backendPayroll) => ({
      staffEngagementType: backendPayroll?.staff_engagement_type || payrollRequest.staffEngagementType,
      compensationBasis: backendPayroll?.compensation_basis || payrollRequest.compensationBasis,
      baseSalaryGross: Number(backendPayroll?.base_salary_gross || 0) || 0,
      grossPay: Number(backendPayroll?.gross_pay || 0) || 0,
      commissionAmount: Number(backendPayroll?.commission_amount || 0) || 0,
      amIncome: Number(backendPayroll?.am_income || 0) || 0,
      amBidrag: Number(backendPayroll?.am_bidrag || 0) || 0,
      aIncome: Number(backendPayroll?.a_income || 0) || 0,
      personFradrag: Number(backendPayroll?.person_fradrag || 0) || 0,
      skattegrundlag: Number(backendPayroll?.skattegrundlag || 0) || 0,
      aSkat: Number(backendPayroll?.a_skat || 0) || 0,
      atpEmployee: Number(backendPayroll?.atp_employee || 0) || 0,
      atpEmployer: Number(backendPayroll?.atp_employer || 0) || 0,
      pensionEmployee: Number(backendPayroll?.pension_employee || 0) || 0,
      pensionEmployer: Number(backendPayroll?.pension_employer || 0) || 0,
      netSalary: Number(backendPayroll?.net_salary || 0) || 0,
      vacation: {
        scheme: backendPayroll?.vacation?.scheme || payrollRequest.vacationScheme,
        daysAccrued: Number(backendPayroll?.vacation?.days_accrued || 0) || 0,
        grossAllowance: Number(backendPayroll?.vacation?.gross_allowance || 0) || 0,
        amBidrag: Number(backendPayroll?.vacation?.am_bidrag || 0) || 0,
        aSkat: Number(backendPayroll?.vacation?.a_skat || 0) || 0,
        netAllowance: Number(backendPayroll?.vacation?.net_allowance || 0) || 0,
      },
      eIndkomst: backendPayroll?.e_indkomst || {},
    });
    let previewResponse = null;
    let payroll = null;
    try {
      previewResponse = await axios.post(
        `${previewBaseUrl}/api/calendar/payslip-preview/`,
        payrollRequest,
        { headers: { Authorization: `Token ${token}` } },
      );
      payroll = normalizeBackendPayroll(previewResponse?.data?.payroll);
    } catch (previewError) {
      payroll = null;
    }
    if (!payroll) {
      payroll = calculatePayslipPayroll(payrollRequest);
    }
    const staffMemberToken = String(staffEntry.staff_id || '');
    let saldiOverviewRows = Array.isArray(previewResponse?.data?.saldi_overview)
      ? previewResponse.data.saldi_overview
      : Array.isArray(previewResponse?.data?.payroll?.saldi_overview)
        ? previewResponse.data.payroll.saldi_overview
        : [];
    let archivedPayslips = [];
    let vacationRecords = [];
    if (!saldiOverviewRows.length) {
      archivedPayslips = Array.isArray(payslipArchives) ? payslipArchives : [];
      if (!archivedPayslips.some((archive) => String(archive.staff_member_id || archive.staff_memberId || '') === staffMemberToken)) {
        try {
          const archiveResponse = await axios.get(
            `${previewBaseUrl}/api/calendar/payslip-archives/?staff_member_id=${encodeURIComponent(staffMemberToken)}`,
            { headers: { Authorization: `Token ${token}` } },
          );
          archivedPayslips = Array.isArray(archiveResponse?.data?.results) ? archiveResponse.data.results : archivedPayslips;
        } catch (archiveError) {
          archivedPayslips = Array.isArray(payslipArchives) ? payslipArchives : [];
        }
      }
      try {
        const vacationResponse = await axios.get(
          `${previewBaseUrl}/api/calendar/vacations/`,
          { headers: { Authorization: `Token ${token}` } },
        );
        vacationRecords = Array.isArray(vacationResponse?.data?.vacations) ? vacationResponse.data.vacations : [];
      } catch (vacationError) {
        vacationRecords = [];
      }
    }
    const derivedHourlyRate = monthlySalaryAmount > 0 && totalMonthlyExpectedHours > 0
      ? monthlySalaryAmount / totalMonthlyExpectedHours
      : 0;
    const hourlyPayRate = Number(staffEntry.hourly_pay || staffEntry.hourly_rate || staffEntry.hourlyRate || derivedHourlyRate) || derivedHourlyRate;
    const grossPay = payroll.baseSalaryGross;
    const provisionalPay = payroll.grossPay;
    const amBidrag = payroll.amBidrag;
    const atpEmployee = payroll.atpEmployee;
    const atpEmployer = payroll.atpEmployer;
    const pensionEmployee = payroll.pensionEmployee;
    const pensionEmployer = payroll.pensionEmployer;
    const aSkat = payroll.aSkat;
    const netPay = payroll.netSalary;
    const payslipData = {
      employee: {
        name: staffEntry.staff_name || '',
        cpr: employeeCpr || '',
        address: staffProfile.address || staffEntry.address || '',
        city: staffProfile.city || staffEntry.city || '',
      },
      company: {
        name: clinicName,
        cvr: clinicRegNumber || '',
        address: clinicAddress || '',
        city: clinicCity || '',
      },
      period: {
        start: payPeriodRange ? payPeriodRange.start.format('DD.MM.YYYY') : '',
        end: payPeriodRange ? payPeriodRange.end.format('DD.MM.YYYY') : '',
      },
      salary: {
        gross: payroll.grossPay,
        base_gross: payroll.baseSalaryGross,
        ferieberettiget_indkomst: payroll.grossPay,
        am_bidrag: amBidrag,
        a_skat: aSkat,
        atp_medarbejder: atpEmployee,
        atp_arbejdsgiver: atpEmployer,
        pension_medarbejder: pensionEmployee,
        pension_arbejdsgiver: pensionEmployer,
        nettoløn: netPay,
        eIndkomst: payroll.eIndkomst,
      },
      balances: {
        arbejdstimer: totalMonthlyWorkHours,
        optjent_ferie: payroll.vacation.scheme === 'holiday_allowance' ? payroll.vacation.grossAllowance : payroll.vacation.daysAccrued,
        optjent_ferie_unit: payroll.vacation.scheme === 'holiday_allowance' ? 'currency' : 'days',
      },
      payroll,
      generated_date: pdfCreatedDate,
      payment_date: periodLabel || '',
    };
    if (!saldiOverviewRows.length) {
      saldiOverviewRows = buildPayslipSaldoOverviewRows({
        monthValue: periodLabel,
        weeklyWorkHours,
        staffId: staffEntry.staff_id,
        currentPayroll: payroll,
        archivedPayrolls: archivedPayslips,
        vacationRecords,
        isDanishClinic,
      });
    }
    const formatMoney = (value) => `${currency} ${fmt(value)}`;
    const optjentFerieDisplay = payslipData.balances.optjent_ferie_unit === 'days'
      ? `${fmt(payslipData.balances.optjent_ferie)} ${isDanishClinic ? 'dage' : 'days'}`
      : formatMoney(payslipData.balances.optjent_ferie);
    const formatKpiHours = (value) => `${fmt(value)} ${isDanishClinic ? 'timer' : 'hours'}`;
    const formatKpiPercent = (value) => (value == null ? '-' : `${fmt(value)}%`);
    const kpiCard = (label, value, subtext = '') => `
      <div style="flex:1;min-width:0;background:#ffffff;border:1px solid #dbe5ef;border-radius:14px;padding:12px 14px;box-shadow:0 4px 12px rgba(0,45,85,0.04);box-sizing:border-box;">
        <div style="font-size:10.5px;color:#6c7b8a;text-transform:uppercase;letter-spacing:0.5px;font-weight:700;margin-bottom:6px;">${label}</div>
        <div style="font-size:16px;font-weight:800;color:#002D55;line-height:1.2;">${value}</div>
        ${subtext ? `<div style="margin-top:4px;font-size:10.5px;color:#5f6c7b;line-height:1.35;">${subtext}</div>` : ''}
      </div>`;
    const infoPill = (label, value) => `
      <div style="display:flex;justify-content:space-between;gap:12px;padding:6px 0;border-bottom:1px solid rgba(0,45,85,0.08);font-size:12px;line-height:1.4;">
        <span style="color:#6c7b8a;font-weight:600;">${label}</span>
        <span style="color:#16324f;font-weight:700;text-align:right;">${value || '-'}</span>
      </div>`;
    const numCell = (value, emphasis = false) => `<td style="padding:7px 10px;text-align:right;font-size:11px;font-variant-numeric:tabular-nums;${emphasis ? 'font-weight:800;color:#002D55;' : 'color:#18324a;'}">${value || ''}</td>`;
    const labelCell = (value, emphasis = false) => `<td style="padding:7px 10px;text-align:left;font-size:11px;${emphasis ? 'font-weight:700;color:#16324f;' : 'color:#334e68;'}">${value || ''}</td>`;
    const employeeDisplayAddress = [payslipData.employee.address, payslipData.employee.city, staffProfile.zip || staffEntry.zip || '']
      .filter(Boolean)
      .join(', ') || '-';
    const companyDisplayAddress = [payslipData.company.address, payslipData.company.city, clinicPostalCode || '']
      .filter(Boolean)
      .join(', ') || '-';
    const salaryLabelMap = isDanishClinic
      ? {
        gross: 'Månedsløn brutto',
        monthlySalary: 'Månedsløn',
        provisionalPay: 'Provisionsløn / bonus',
        provisionalPaySub: 'Midlertidig udbetaling / leje af konsultation (momsfrit)',
        amBidrag: 'AM-bidrag (8%)',
        aSkat: 'A-skat',
        atpEmployee: 'ATP medarbejder',
        atpEmployer: 'ATP arbejdsgiver',
        pensionEmployee: 'Pension medarbejder',
        pensionEmployer: 'Pension arbejdsgiver',
        net: 'Nettoløn',
        appointments: 'Behandlinger',
        weeklyHours: 'Registrerede timer pr. uge',
        absenceDays: 'Fraværsdage',
        absenceBreakdown: 'Fravær',
        monthlyWorkHours: 'Månedlige arbejdstimer',
        weeklyJustification: 'Ugentligt timeregnskab',
        appointmentDetail: 'Bilag',
      }
      : {
        gross: 'Gross pay',
        monthlySalary: 'Monthly salary base',
        provisionalPay: 'Provisional pay / bonus',
        provisionalPaySub: 'Temporary payout / office rent (VAT exempt)',
        amBidrag: 'AM contribution',
        aSkat: 'Tax',
        atpEmployee: 'ATP employee',
        atpEmployer: 'ATP employer',
        pensionEmployee: 'Pension employee',
        pensionEmployer: 'Pension employer',
        net: 'Estimated payout',
        appointments: 'Appointments treated',
        weeklyHours: 'Weekly work hours',
        absenceDays: 'Absence days',
        absenceBreakdown: 'Absence breakdown',
        monthlyWorkHours: 'Monthly work hours',
        weeklyJustification: 'Weekly work-hour justification',
        appointmentDetail: 'Appointment detail',
      };

    // Collect appointment rows for this staff member
    const aptRows = [];
    (billingStats?.month_groups || []).forEach((mg) => {
      (mg.rows || []).forEach((r) => {
        if (String(r.staff_id) === String(staffEntry.staff_id)) {
          const svc = services.find((s) => String(s.id) === String(r.service_id));
          const commRate = getServiceCommissionRateForStaff(svc, r.staff_id);
          const commAmt = Number(r.amount || 0) * (commRate / 100);
          aptRows.push({ ...r, commission_rate_pct: commRate, commission_amount: commAmt });
        }
      });
    });

    const aptRowsHtml = aptRows.length === 0 ? '' : `
      <div style="box-sizing:border-box;page-break-inside:auto;break-inside:auto;">
      <h3 style="color:#002D55;margin-top:0;margin-bottom:8px;font-size:14px;">${salaryLabelMap.appointmentDetail}</h3>
      <table style="width:100%;border-collapse:separate;border-spacing:0;font-size:11px;border:1px solid #dbe5ef;border-radius:14px;overflow:hidden;box-shadow:0 2px 10px rgba(0,45,85,0.04);page-break-inside:auto;break-inside:auto;">
        <thead>
          <tr>
            <th colspan="8" style="padding:0;height:32px;line-height:32px;border:none;background:transparent;"></th>
          </tr>
          <tr style="background:linear-gradient(180deg,#f7faff 0%,#eef4fb 100%);">
            <th style="padding:9px 10px;text-align:left;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Dato' : 'Date'}</th>
            <th style="padding:9px 10px;text-align:left;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Patient' : 'Patient'}</th>
            <th style="padding:9px 10px;text-align:left;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Behandling' : 'Service'}</th>
            <th style="padding:9px 10px;text-align:left;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Betaler' : 'Payer'}</th>
            <th style="padding:9px 10px;text-align:right;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Beløb' : 'Amount'}</th>
            <th style="padding:9px 10px;text-align:right;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Procent' : 'Comm%'}</th>
            <th style="padding:9px 10px;text-align:right;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Provision' : 'Commission'}</th>
            <th style="padding:9px 10px;text-align:left;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Status' : 'Status'}</th>
          </tr>
        </thead>
        <tbody>
          ${aptRows.map((r, index) => `<tr style="border-bottom:1px solid #eef;${index % 2 === 0 ? 'background:#fff;' : 'background:#fbfdff;'}">
            <td style="padding:8px 10px;">${r.appointment_date ? new Date(r.appointment_date).toLocaleDateString() : '-'}</td>
            <td style="padding:8px 10px;font-weight:600;color:#16324f;">${r.patient_name || '-'}</td>
            <td style="padding:8px 10px;">${r.service || '-'}</td>
            <td style="padding:8px 10px;">${r.payer === 'insurance' ? (isDanishClinic ? 'Forsikring' : 'Insurance') : (isDanishClinic ? 'Patient' : 'Patient')}</td>
            <td style="padding:8px 10px;text-align:right;font-variant-numeric:tabular-nums;">${formatMoney(r.amount)}</td>
            <td style="padding:8px 10px;text-align:right;font-variant-numeric:tabular-nums;">${fmt(r.commission_rate_pct)}%</td>
            <td style="padding:8px 10px;text-align:right;font-variant-numeric:tabular-nums;font-weight:700;color:#002D55;">${formatMoney(r.commission_amount)}</td>
            <td style="padding:8px 10px;">${(r.invoice_status || 'unbilled').replace(/_/g, ' ')}</td>
          </tr>`).join('')}
          <tr style="background:#f7fafc;border-top:2px solid #b9cce3;">
            <td style="padding:8px 10px;font-weight:700;color:#16324f;">${isDanishClinic ? 'Subtotal / omsætning' : 'Subtotal / revenue'}</td>
            <td style="padding:8px 10px;"></td>
            <td style="padding:8px 10px;"></td>
            <td style="padding:8px 10px;"></td>
            <td style="padding:8px 10px;text-align:right;font-variant-numeric:tabular-nums;font-weight:700;color:#002D55;">${formatMoney(aptRows.reduce((sum, r) => sum + Number(r.amount || 0), 0))}</td>
            <td style="padding:8px 10px;"></td>
            <td style="padding:8px 10px;text-align:right;font-variant-numeric:tabular-nums;font-weight:700;color:#002D55;">${formatMoney(commissionAmount)}</td>
            <td style="padding:8px 10px;"></td>
          </tr>
        </tbody>
        </table>
        </div>`;

    const el = document.createElement('div');
    el.style.cssText = 'width:100%;box-sizing:border-box;padding:0 16px;font-family:Arial,Helvetica,sans-serif;color:#18324a;background:linear-gradient(180deg,#ffffff 0%,#fbfcfe 100%);';
    el.innerHTML = `
      <div style="width:100%;border:1px solid #dbe5ef;border-radius:18px;box-sizing:border-box;padding:32px 18px 16px;margin-bottom:18px;background:linear-gradient(180deg,#ffffff 0%,#f7faff 100%);box-shadow:0 8px 24px rgba(0,45,85,0.05);">
        <div style="display:flex;justify-content:space-between;gap:16px;align-items:flex-start;">
          <div style="min-width:0;flex:1;">
            <div style="font-size:24px;font-weight:800;letter-spacing:0.2px;color:#002D55;line-height:1.1;">${clinicName}</div>
            <div style="font-size:12px;color:#6c7b8a;margin-top:6px;">${isDanishClinic ? 'Lønseddel / Payslip' : 'Payslip'}</div>
          </div>
          <div style="text-align:right;display:flex;flex-direction:column;align-items:flex-end;gap:4px;">
            <div style="font-size:16px;font-weight:800;color:#002D55;line-height:1.15;">${periodLabel}</div>
            <div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:4px 10px;text-align:left;font-size:11px;color:#5f6c7b;line-height:1.35;max-width:320px;">
              <div style="font-weight:700;color:#002D55;">${isDanishClinic ? 'Periode fra:' : 'Period from:'}</div><div>${payslipData.period.start || '-'}</div>
              <div style="font-weight:700;color:#002D55;">${isDanishClinic ? 'Periode til:' : 'Period to:'}</div><div>${payslipData.period.end || '-'}</div>
              <div style="font-weight:700;color:#002D55;">${isDanishClinic ? 'Periodens længde:' : 'Period length:'}</div><div>${payPeriodDays ? `${payPeriodDays} ${isDanishClinic ? 'dage' : 'days'}` : '-'}</div>
              <div style="font-weight:700;color:#002D55;">${isDanishClinic ? 'Lønseddel lavet:' : 'Payslip created:'}</div><div>${payslipData.generated_date || pdfCreatedDate}</div>
            </div>
          </div>
        </div>
      </div>
      <div style="width:100%;display:grid;grid-template-columns:1fr 1fr;gap:14px;align-items:stretch;margin-bottom:18px;box-sizing:border-box;">
        <div style="width:100%;border:1px solid #dbe5ef;border-radius:16px;box-sizing:border-box;padding:14px 16px;background:#fff;box-shadow:0 6px 18px rgba(0,45,85,0.04);">
          <div style="font-size:11px;color:#6c7b8a;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:8px;font-weight:700;">${isDanishClinic ? 'Engagement' : 'Engagement'}: ${engagementLabel}</div>
          <div style="font-size:13px;font-weight:700;color:#16324f;line-height:1.2;margin-bottom:10px;">${payslipData.employee.name || ''}</div>
          <div style="font-size:12px;color:#44566c;line-height:1.55;display:grid;gap:4px;">
            <div><strong style="color:#002D55;">${isDanishClinic ? 'Adresse:' : 'Address:'}</strong> ${employeeDisplayAddress}</div>
            <div><strong style="color:#002D55;">${isDanishClinic ? 'Basis:' : 'Basis:'}</strong> ${basisLabel || '-'}</div>
            <div><strong style="color:#002D55;">${isDanishClinic ? 'CPR-nummer:' : 'CPR number:'}</strong> ${payslipData.employee.cpr || '-'}</div>
          </div>
        </div>
        <div style="width:100%;border:1px solid #dbe5ef;border-radius:16px;box-sizing:border-box;padding:14px 16px;background:#f4f7fb;box-shadow:0 6px 18px rgba(0,45,85,0.04);">
          <div style="font-size:11px;color:#6c7b8a;text-transform:uppercase;letter-spacing:0.6px;margin-bottom:8px;font-weight:700;">${isDanishClinic ? 'Nøgleoplysninger' : 'Key details'}</div>
          ${infoPill(isDanishClinic ? 'Konto' : 'Account', 'NemKonto')}
          ${infoPill(isDanishClinic ? 'Virksomhed' : 'Employer', payslipData.company.name || '-')}
          ${infoPill(isDanishClinic ? 'CVR-nummer' : 'CVR number', payslipData.company.cvr || '-')}
          ${infoPill(isDanishClinic ? 'Virksomhedsadresse' : 'Employer address', companyDisplayAddress)}
        </div>
      </div>
      <div style="width:100%;margin-top:2px;border:1px solid #dbe5ef;border-radius:18px;overflow:hidden;background:#fff;box-shadow:0 8px 24px rgba(0,45,85,0.05);box-sizing:border-box;">
        <div style="padding:13px 16px;background:linear-gradient(180deg,#f7faff 0%,#eef4fb 100%);border-bottom:1px solid #d7e2ef;font-size:11px;font-weight:800;color:#002D55;letter-spacing:0.2px;">${isDanishClinic ? 'Løn- og fradragsoversigt' : 'Earnings and deductions'}</div>
      <table style="width:100%;border-collapse:collapse;">
        <thead>
          <tr style="background:#f9fbfd;">
            <th style="padding:10px 12px;text-align:left;font-size:12px;font-weight:700;color:#002D55;border-bottom:1px solid #d7e2ef;">${isDanishClinic ? 'Beskrivelse' : 'Description'}</th>
            <th style="padding:10px 12px;text-align:right;font-size:12px;font-weight:700;color:#002D55;border-bottom:1px solid #d7e2ef;">${isDanishClinic ? 'Grundlag' : 'Basis'}</th>
            <th style="padding:10px 12px;text-align:right;font-size:12px;font-weight:700;color:#002D55;border-bottom:1px solid #d7e2ef;">${isDanishClinic ? 'Sats' : 'Rate'}</th>
            <th style="padding:10px 12px;text-align:right;font-size:12px;font-weight:700;color:#002D55;border-bottom:1px solid #d7e2ef;">${isDanishClinic ? 'Udbetalt' : 'Paid'}</th>
            <th style="padding:10px 12px;text-align:right;font-size:12px;font-weight:700;color:#002D55;border-bottom:1px solid #d7e2ef;">${isDanishClinic ? 'Trukket' : 'Deducted'}</th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom:1px solid #eef;">
            ${labelCell(isDanishClinic ? 'Månedsløn brutto' : 'Gross pay', true)}
            ${numCell('')}
            ${numCell('')}
            ${numCell(formatMoney(payroll.bruttoløn || grossPay), true)}
            ${numCell('')}
          </tr>
          ${Number(pensionEmployee || 0) > 0 ? `
          <tr style="border-bottom:1px solid #eef;">
            ${labelCell(isDanishClinic ? 'Medarbejder pension' : 'Employee pension')}
            ${numCell(formatMoney(payroll.bruttoløn || grossPay))}
            ${numCell(`${fmt(Number(staffProfile.pension_percent || staffEntry.pension_percent || 0) || 0)}%`)}
            ${numCell('')}
            ${numCell(formatMoney(pensionEmployee), true)}
          </tr>
          ` : ''}
          ${Number(pensionEmployer || 0) > 0 ? `
          <tr style="border-bottom:1px solid #eef;">
            ${labelCell(isDanishClinic ? 'Pension arbejdsgiver' : 'Employer pension')}
            ${numCell(formatMoney(payroll.bruttoløn || grossPay))}
            ${numCell(`${fmt(Number(staffProfile.employer_pension_percent || staffEntry.employer_pension_percent || 0) || 0)}%`)}
            ${numCell(formatMoney(pensionEmployer), true)}
            ${numCell('')}
          </tr>
          ` : ''}
          <tr style="border-bottom:1px solid #eef;">
            ${labelCell(isDanishClinic ? 'AM-indkomst' : 'AM income')}
            ${numCell(formatMoney(payroll.bruttoløn || grossPay))}
            ${numCell('')}
            ${numCell(formatMoney(payroll.AM_indkomst || payroll.amIncome || grossPay), true)}
            ${numCell('')}
          </tr>
          <tr style="border-bottom:1px solid #eef;">
            ${labelCell(isDanishClinic ? 'AM-bidrag' : 'AM contribution')}
            ${numCell(formatMoney(payroll.AM_indkomst || payroll.amIncome || grossPay))}
            ${numCell('8%')}
            ${numCell('')}
            ${numCell(formatMoney(amBidrag), true)}
          </tr>
          <tr style="border-bottom:1px solid #eef;">
            ${labelCell(isDanishClinic ? 'A-indkomst' : 'A income')}
            ${numCell(formatMoney(payroll.AM_indkomst || payroll.amIncome || grossPay))}
            ${numCell('')}
            ${numCell(formatMoney(payroll.A_indkomst || payroll.aIncome || grossPay), true)}
            ${numCell('')}
          </tr>
          <tr style="border-bottom:1px solid #eef;">
            ${labelCell(isDanishClinic ? 'Skattegrundlag' : 'Tax base')}
            ${numCell(formatMoney(payroll.A_indkomst || payroll.aIncome || grossPay))}
            ${numCell('')}
            ${numCell('')}
            ${numCell('')}
          </tr>
          <tr style="border-bottom:1px solid #eef;">
            ${labelCell(isDanishClinic ? 'Personfradrag' : 'Personal allowance')}
            ${numCell(formatMoney(payroll.person_fradrag || payroll.personFradrag || 0))}
            ${numCell('')}
            ${numCell(formatMoney(payroll.skattegrundlag || 0), true)}
            ${numCell('')}
          </tr>
          <tr style="border-bottom:1px solid #eef;">
            ${labelCell(isDanishClinic ? 'A-skat' : 'Tax')}
            ${numCell(formatMoney(payroll.skattegrundlag || 0))}
            ${numCell(`${fmt(Number(staffProfile.tax_trakprocent || staffEntry.tax_trakprocent || 0) || 0)}%`)}
            ${numCell('')}
            ${numCell(formatMoney(aSkat), true)}
          </tr>
          <tr style="border-bottom:1px solid #eef;">
            ${labelCell(isDanishClinic ? 'ATP medarbejder' : 'ATP employee')}
            ${numCell('')}
            ${numCell('')}
            ${numCell('')}
            ${numCell(formatMoney(atpEmployee), true)}
          </tr>
          <tr style="background:#e9f1fb;border-top:2px solid #b9cce3;">
            ${labelCell(isDanishClinic ? 'Nettoløn' : 'Net salary', true)}
            ${numCell('')}
            ${numCell('')}
            ${numCell(formatMoney(netPay), true)}
            ${numCell('')}
          </tr>
          ${String(payroll.vacation?.scheme || '').toLowerCase() === 'holiday_allowance' ? `
          <tr style="background:#f7fafc;">
            ${labelCell(isDanishClinic ? 'Optjent ferie' : 'Accrued vacation', true)}
            ${numCell('')}
            ${numCell('')}
            ${numCell(optjentFerieDisplay, true)}
            ${numCell('')}
          </tr>
          <tr style="border-bottom:1px solid #eef;">
            ${labelCell(isDanishClinic ? 'Brutto feriepenge' : 'Gross vacation pay')}
            ${numCell(formatMoney(payroll.ferieberettiget_indkomst || payroll.grossPay))}
            ${numCell('12.5%')}
            ${numCell(formatMoney(payroll.brutto_feriepenge || payroll.vacation?.grossAllowance || 0), true)}
            ${numCell('')}
          </tr>
          <tr style="border-bottom:1px solid #eef;">
            ${labelCell(isDanishClinic ? 'AM-bidrag feriepenge' : 'Vacation AM contribution')}
            ${numCell(formatMoney(payroll.brutto_feriepenge || payroll.vacation?.grossAllowance || 0))}
            ${numCell('8%')}
            ${numCell('')}
            ${numCell(formatMoney(payroll.AM_bidrag_feriepenge || payroll.vacation?.amBidrag || 0), true)}
          </tr>
          <tr style="border-bottom:1px solid #eef;">
            ${labelCell(isDanishClinic ? 'A-skat feriepenge' : 'Vacation tax')}
            ${numCell(formatMoney((payroll.brutto_feriepenge || payroll.vacation?.grossAllowance || 0) - (payroll.AM_bidrag_feriepenge || payroll.vacation?.amBidrag || 0)))}
            ${numCell(`${fmt(Number(staffProfile.tax_trakprocent || staffEntry.tax_trakprocent || 0) || 0)}%`)}
            ${numCell('')}
            ${numCell(formatMoney(payroll.A_skat_feriepenge || payroll.vacation?.aSkat || 0), true)}
          </tr>
          <tr style="border-bottom:1px solid #eef;">
            ${labelCell(isDanishClinic ? 'Netto feriepenge' : 'Net vacation pay')}
            ${numCell('')}
            ${numCell('')}
            ${numCell(formatMoney(payroll.netto_feriepenge || payroll.vacation?.netAllowance || 0), true)}
            ${numCell('')}
          </tr>
          ` : ''}
        </tbody>
      </table>
      </div>
      ${workHoursBreakdown.length > 0 ? `
        <div style="width:100%;margin-top:0;page-break-before:always;page-break-inside:avoid;break-inside:avoid;box-sizing:border-box;">
        ${saldiOverviewRows.length > 0 ? `
          <div style="width:100%;border:1px solid #dbe5ef;border-radius:18px;overflow:hidden;background:#fff;box-shadow:0 8px 24px rgba(0,45,85,0.05);box-sizing:border-box;page-break-inside:avoid;break-inside:avoid;margin-bottom:18px;">
            <div style="padding:13px 16px;background:linear-gradient(180deg,#f7faff 0%,#eef4fb 100%);border-bottom:1px solid #d7e2ef;font-size:11px;font-weight:800;color:#002D55;letter-spacing:0.2px;">${isDanishClinic ? 'Saldi oversigt' : 'Balance overview'}</div>
            <table style="width:100%;border-collapse:collapse;font-size:11px;page-break-inside:avoid;break-inside:avoid;">
              <thead>
                <tr style="background:#f9fbfd;">
                  <th style="padding:8px 10px;text-align:left;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Type' : 'Type'}</th>
                  <th style="padding:8px 10px;text-align:right;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Perioden' : 'Period'}</th>
                  <th style="padding:8px 10px;text-align:right;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'År til dato' : 'Year to date'}</th>
                </tr>
              </thead>
              <tbody>
                ${saldiOverviewRows.map((row, index) => {
                  const formatSaldoValue = (value, unit) => {
                    if (unit === 'days') {
                      return `${fmt(value)} ${isDanishClinic ? 'dage' : 'days'}`;
                    }
                    if (unit === 'hours') {
                      return `${fmt(value)} ${isDanishClinic ? 'timer' : 'hours'}`;
                    }
                    return formatMoney(value);
                  };
                  return `<tr style="border-bottom:1px solid #eef;${index % 2 === 0 ? 'background:#fff;' : 'background:#fbfdff;'}">
                    <td style="padding:7px 10px;font-weight:700;color:#16324f;">${row.label}</td>
                    <td style="padding:7px 10px;text-align:right;font-variant-numeric:tabular-nums;${row.unit === 'money' ? 'color:#18324a;' : 'color:#18324a;'}">${formatSaldoValue(row.periodValue, row.unit)}</td>
                    <td style="padding:7px 10px;text-align:right;font-variant-numeric:tabular-nums;font-weight:800;color:#002D55;">${formatSaldoValue(row.annualValue, row.unit)}</td>
                  </tr>`;
                }).join('')}
              </tbody>
            </table>
          </div>
        ` : ''}
        <div style="width:100%;border:1px solid #dbe5ef;border-radius:18px;overflow:hidden;background:#fff;box-shadow:0 8px 24px rgba(0,45,85,0.05);box-sizing:border-box;page-break-before:always;break-before:page;page-break-inside:avoid;break-inside:avoid;">
        <div style="padding:13px 16px 12px;background:linear-gradient(180deg,#f7faff 0%,#eef4fb 100%);border:1px solid #d7e2ef;display:flex;justify-content:space-between;align-items:center;gap:12px;page-break-inside:avoid;break-inside:avoid;">
          <div style="font-size:13px;font-weight:800;color:#002D55;letter-spacing:0.2px;">${salaryLabelMap.weeklyJustification}</div>
          <div style="font-size:11px;color:#5f6c7b;">${isDanishClinic ? 'Registrerede timer pr. uge' : 'Registered weekly hours'}: <strong style="color:#16324f;">${registeredWeeklyHours.toFixed(2)}</strong></div>
        </div>
        <div style="padding:12px 16px 0;font-size:11px;color:#5f6c7b;line-height:1.5;">${isDanishClinic ? 'Hver uge prorrateres efter antallet af arbejdsdage i måneden, og fravær trækkes kun på de dage, der ligger i perioden.' : 'Each weekly subtotal is prorated by the number of workdays that actually fall inside the selected month, and absence days are deducted only when they overlap those workdays.'}</div>
        <table style="width:100%;border-collapse:collapse;font-size:11px;page-break-inside:avoid;break-inside:avoid;">
          <thead>
            <tr>
              <th colspan="6" style="padding:0;height:0;line-height:0;border:none;background:transparent;"></th>
            </tr>
            <tr style="background:#f9fbfd;">
              <th style="padding:8px 10px;text-align:left;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Uge' : 'Week'}</th>
              <th style="padding:8px 10px;text-align:left;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Datointerval' : 'Date range'}</th>
              <th style="padding:8px 10px;text-align:right;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Arbejdsdage' : 'Workdays'}</th>
              <th style="padding:8px 10px;text-align:right;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Registrerede timer' : 'Registered hours'}</th>
              <th style="padding:8px 10px;text-align:left;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Fraværsdage' : 'Absence dates'}</th>
              <th style="padding:8px 10px;text-align:right;border-bottom:1px solid #d7e2ef;color:#002D55;">${isDanishClinic ? 'Ugentligt delsum' : 'Subtotal hours'}</th>
            </tr>
          </thead>
          <tbody>
            ${workHoursBreakdown.map((week) => `
              <tr style="border-bottom:1px solid #eef;${week.weekNumber % 2 === 0 ? 'background:#fbfdff;' : 'background:#fff;'}">
                <td style="padding:7px 10px;font-weight:700;color:#16324f;">W${String(week.weekNumber).padStart(2, '0')}</td>
                <td style="padding:7px 10px;">${week.dateRangeLabel}</td>
                <td style="padding:7px 10px;text-align:right;">${week.workdayCount}</td>
                <td style="padding:7px 10px;text-align:right;font-variant-numeric:tabular-nums;">${week.registeredHours.toFixed(2)}</td>
                <td style="padding:7px 10px;">${week.absenceDateLabels || '-'}</td>
                <td style="padding:7px 10px;text-align:right;font-weight:800;color:#002D55;font-variant-numeric:tabular-nums;">${week.subtotalHours.toFixed(2)}</td>
              </tr>
            `).join('')}
            <tr style="background:#f2f7fc;">
              <td colspan="4" style="padding:9px 10px;font-weight:800;color:#002D55;text-align:right;">${isDanishClinic ? 'Samlede månedlige arbejdstimer' : 'Total monthly work hours'}</td>
              <td style="padding:9px 10px;text-align:right;font-weight:800;color:#002D55;">${totalMonthlyWorkHours.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        </div>
        </div>
        <div style="width:100%;border:1px solid #dbe5ef;border-radius:18px;overflow:hidden;background:#fff;box-shadow:0 8px 24px rgba(0,45,85,0.05);box-sizing:border-box;margin-top:18px;margin-bottom:18px;">
          <div style="padding:13px 16px;background:linear-gradient(180deg,#f7faff 0%,#eef4fb 100%);border-bottom:1px solid #d7e2ef;font-size:11px;font-weight:800;color:#002D55;letter-spacing:0.2px;">${isDanishClinic ? 'Performance KPIer' : 'Performance KPIs'}</div>
          <div style="display:flex;gap:12px;flex-wrap:wrap;padding:14px 16px;box-sizing:border-box;">
            ${kpiCard(isDanishClinic ? 'Timeløn' : 'Hourly pay', formatMoney(hourlyPayRate), isDanishClinic ? `Timeløn ${formatMoney(hourlyPayRate)} | ${formatKpiHours(totalMonthlyWorkHours)}` : `Hourly pay ${formatMoney(hourlyPayRate)} | ${formatKpiHours(totalMonthlyWorkHours)}`)}
            ${kpiCard(isDanishClinic ? 'Omsætning per time' : 'Revenue per worked hour', averageRevenuePerWorkedHour > 0 ? formatMoney(averageRevenuePerWorkedHour) : '-', isDanishClinic ? `Omsætning ${formatMoney(staffMonthlyRevenue)} / ${formatKpiHours(totalMonthlyWorkHours)}` : `Revenue ${formatMoney(staffMonthlyRevenue)} / ${formatKpiHours(totalMonthlyWorkHours)}`)}
            ${kpiCard(isDanishClinic ? 'Belægningsgrad' : 'Occupancy', formatKpiPercent(occupancyRate), `${isDanishClinic ? 'Bookede tider' : 'Booked timeslots'} ${formatKpiHours(bookedTimeslotHours)} / ${isDanishClinic ? 'Allokerede tider' : 'Allocated timeslots'} ${formatKpiHours(allocatedTimeslotHours)}`)}
            ${kpiCard(isDanishClinic ? 'Overordnet NPS' : 'Overall NPS', avgNpsValue == null ? '-' : fmt(avgNpsValue), avgNpsValue == null ? (isDanishClinic ? 'Ingen NPS-data tilgængelig' : 'No NPS data available') : '')}
          </div>
        </div>
      ` : ''}
      ${aptRowsHtml ? `<div style="margin-top:0;padding-top:32px;page-break-before:always;page-break-inside:auto;break-inside:auto;box-sizing:border-box;">${aptRowsHtml}</div>` : ''}
      <div style="margin-top:28px;padding-top:14px;border-top:1px solid #e8edf2;font-size:10.5px;color:#718096;text-align:center;">${isDanishClinic ? 'Dette er en automatisk genereret lønseddel. Kontakt klinikadministrationen ved spørgsmål.' : 'This is an automated payslip generated by the clinic. For questions, contact the clinic administrator.'}</div>
    `;
    document.body.appendChild(el);
    html2pdf().set({
      margin: [8.5, 0, 8.5, 0],
      filename: `payslip_${(staffEntry.staff_name || 'staff').replace(/\s+/g, '_')}_${periodLabel}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(el).save().then(() => document.body.removeChild(el));
  };

  const handleDownloadSettlementPDF = (data) => {
    if (!data) return;
    const currency = billingStats?.grand_totals?.currency || 'EUR';
    const rowsHtml = (data.appointment_rows || []).map((r) => `<tr><td>${r.date || ''}</td><td>${r.patient || ''}</td><td>${r.service || ''}</td><td style="text-align:right">${currency} ${parseFloat(r.amount || 0).toFixed(2)}</td><td style="text-align:right">${parseFloat(r.commission_rate || 0).toFixed(1)}%</td><td style="text-align:right">${currency} ${parseFloat(r.commission_amount || 0).toFixed(2)}</td><td>${r.invoice_status || ''}</td></tr>`).join('');
    const el = document.createElement('div');
    el.style.padding = '32px';
    el.style.fontFamily = 'Arial, sans-serif';
    el.innerHTML = `
      <h2 style="color:#002D55;">Subcontractor Settlement | ${data.staff_name}</h2>
      <p style="color:#666;">Period: ${data.month} &nbsp;|&nbsp; ${data.clinic_name}</p>
      <table style="width:100%;border-collapse:collapse;margin-bottom:24px;">
        <tr><td style="padding:6px 8px;border-bottom:1px solid #eee;color:#666;">Appointments treated</td><td style="padding:6px 8px;">${data.appointment_count}</td></tr>
        <tr><td style="padding:6px 8px;border-bottom:1px solid #eee;color:#666;">Gross revenue from patients</td><td style="padding:6px 8px;">${currency} ${parseFloat(data.gross_revenue || 0).toFixed(2)}</td></tr>
        <tr><td style="padding:6px 8px;border-bottom:1px solid #eee;color:#666;">Commission earned (your share)</td><td style="padding:6px 8px;">${currency} ${parseFloat(data.commission_earned || 0).toFixed(2)}</td></tr>
        <tr><td style="padding:6px 8px;border-bottom:1px solid #eee;color:#666;">Clinic cut (room rental)</td><td style="padding:6px 8px;">${currency} ${parseFloat(data.clinic_cut || 0).toFixed(2)}</td></tr>
        <tr><td style="padding:6px 8px;border-bottom:1px solid #eee;color:#002D55;font-weight:bold;">Net payout to you</td><td style="padding:6px 8px;color:#002D55;font-weight:bold;">${currency} ${parseFloat(data.net_payout || 0).toFixed(2)}</td></tr>
      </table>
      <h3 style="color:#002D55;">Appointment details</h3>
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead><tr style="background:#f0f4f8;"><th style="padding:4px 6px;text-align:left;">Date</th><th style="padding:4px 6px;text-align:left;">Patient</th><th style="padding:4px 6px;text-align:left;">Service</th><th style="padding:4px 6px;text-align:right;">Amount</th><th style="padding:4px 6px;text-align:right;">Comm%</th><th style="padding:4px 6px;text-align:right;">Commission</th><th style="padding:4px 6px;text-align:left;">Status</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
    `;
    document.body.appendChild(el);
    html2pdf().set({ margin: 12, filename: `settlement_${(data.staff_name || 'sub').replace(/\s+/g, '_')}_${data.month}.pdf`, jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' } }).from(el).save().then(() => document.body.removeChild(el));
  };

  const getMonthEndSubcontractorOptions = () => {
    return staffList2.filter((staff) => (
      String(staff.staff_engagement_type || '').toLowerCase() === 'subcontractor'
      || String(staff.compensation_basis || '').toLowerCase() === 'subcontractor_commission'
    ));
  };

  // Fetch staff members for filter dropdown
  const fetchStaffMembers = async () => {
    try {
      const configuredBaseUrl = (process.env.REACT_APP_BACKEND_URL || "https://api.virtus-one.com").replace(/\/$/, '');
      const staffBaseUrls = Array.from(new Set([
        configuredBaseUrl,
        configuredBaseUrl.replace(/\/api$/, ''),
        'https://api.virtus-one.com',
      ])).filter(Boolean);

      for (const baseUrl of staffBaseUrls) {
        const response = await fetch(`${baseUrl}/user/list_staff/`, {
          headers: { "Authorization": `Token ${token}` },
          method: "GET",
        });

        if (!response.ok) {
          continue;
        }

        const data = await response.json();
        const staffArr = Array.isArray(data?.staff)
          ? data.staff
          : (Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []));
        const normalizedStaff = staffArr.map((staff) => ({
          ...staff,
          user_id: staff.user_id || staff.userId || staff.id || '',
          id: staff.id || staff.user_id || staff.userId || '',
          first_name: staff.first_name || staff.firstName || '',
          last_name: staff.last_name || staff.lastName || '',
          cpr: staff.cpr || staff.pii_number || '',
          weekly_work_hours: staff.weekly_work_hours || staff.weeklyWorkHours || '37.5',
          role_type: staff.role_type || staff.role || '',
          staff_engagement_type: staff.staff_engagement_type || staff.staffEngagementType || 'employee',
          compensation_basis: normalizeStaffCompensationBasis(
            staff.staff_engagement_type || staff.staffEngagementType || 'employee',
            staff.compensation_basis || staff.compensationBasis || ''
          ),
          monthly_salary_amount: staff.monthly_salary_amount || staff.monthlySalaryAmount || '',
          pension_percent: staff.pension_percent || staff.pensionPercent || '',
          employer_pension_percent: staff.employer_pension_percent || staff.employerPensionPercent || '',
          vacation_scheme: staff.vacation_scheme || staff.vacationScheme || 'paid_vacation_days',
          paid_vacation_days: staff.paid_vacation_days || staff.paidVacationDays || '',
          tax_fradrag: staff.tax_fradrag || staff.taxFradrag || '',
          tax_trakprocent: staff.tax_trakprocent || staff.taxTrakprocent || '',
          holiday_allowance_percent: staff.holiday_allowance_percent || staff.holidayAllowancePercent || '12.5',
          vacation_accrual_per_month: staff.vacation_accrual_per_month || staff.vacationAccrualPerMonth || '2.08',
        }));

        setStaffList2(normalizedStaff);
        return normalizedStaff;
      }

      setStaffList2([]);
      return [];
    } catch (err) {
      console.error("Failed to fetch staff members:", err);
      setStaffList2([]);
      return [];
    }
  };

  const fetchEditAppointmentPayerOptions = async (patientId) => {
    const normalizedPatientId = String(patientId || '').trim();
    if (!normalizedPatientId) {
      setEditingPayerInsuranceOptions([]);
      setEditingPayerOtherOptions([]);
      return;
    }

    setEditingPayerOptionsLoading(true);
    setEditingPayerOptionsError('');
    try {
      const [insuranceResponse, otherPayersResponse] = await Promise.all([
        fetch(`${backendBaseUrl}/profile/patient/${normalizedPatientId}/insurance/`, {
          headers: { Authorization: `Token ${token}` },
        }),
        fetch(`${backendBaseUrl}/api/profile/clinic/other-payers/`, {
          headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
        }),
      ]);
      const insuranceOptions = insuranceResponse.ok
        ? (await insuranceResponse.json()).map((insurance) => ({
            id: String(insurance.id || ''),
            label: insurance.insurance_provider || insurance.insurance_provider_name || insurance.name || 'Insurance',
            billingEmail: insurance.billing_email || '',
          })).filter((option) => option.id)
        : [];

      const otherPayerOptions = otherPayersResponse.ok
        ? (await otherPayersResponse.json()).map((payer) => ({
            id: String(payer.id || ''),
            label: payer.name || 'Other payer',
            billingEmail: payer.billing_email || '',
          })).filter((option) => option.id)
        : [];

      setEditingPayerInsuranceOptions(insuranceOptions);
      setEditingPayerOtherOptions(otherPayerOptions);
      if (insuranceOptions.length === 0 && otherPayerOptions.length === 0) {
        setEditingPayerOptionsError('No insurance or other payer records found for this appointment.');
      }
    } catch (error) {
      console.error('Failed to load payer options for appointment edit', error);
      setEditingPayerInsuranceOptions([]);
      setEditingPayerOtherOptions([]);
      setEditingPayerOptionsError('Failed to load payer options.');
    } finally {
      setEditingPayerOptionsLoading(false);
    }
  };

  const getStaffFullName = (staff = {}) => {
    return [
      staff.name,
      `${staff.firstName || ''} ${staff.lastName || ''}`.trim(),
      `${staff.first_name || ''} ${staff.last_name || ''}`.trim()
    ].find((value) => String(value || '').trim()) || '';
  };

  const splitNameParts = (name = '') => {
    return String(name)
      .trim()
      .split(/\s+/)
      .map((part) => part.trim())
      .filter(Boolean);
  };

  const getDisplayLabelParts = (name = '') => {
    const parts = splitNameParts(name);
    const firstName = parts[0] || '';
    const lastName = parts.length > 1 ? parts[parts.length - 1] : '';

    return { firstName, lastName };
  };

  const buildAutoStaffInitials = (firstName = '', lastName = '') => {
    const fullName = `${String(firstName || '').trim()} ${String(lastName || '').trim()}`.trim();
    if (!fullName) return '';

    const { firstName: normalizedFirstName, lastName: normalizedLastName } = getDisplayLabelParts(fullName);
    const duplicateKey = `${normalizedFirstName.toLowerCase()}|${(normalizedLastName[0] || '').toLowerCase()}`;
    const duplicateStaff = staffList.filter((candidate) => {
      const candidateName = getStaffFullName(candidate);
      if (!candidateName) return false;

      const candidateParts = getDisplayLabelParts(candidateName);
      return `${candidateParts.firstName.toLowerCase()}|${(candidateParts.lastName[0] || '').toLowerCase()}` === duplicateKey;
    });

    return getUniqueStaffDisplayLabel(fullName, duplicateStaff.length);
  };

  const toTitleCase = (value = '') => {
    const text = String(value || '');
    if (!text) return '';

    return text[0].toUpperCase() + text.slice(1).toLowerCase();
  };

  useEffect(() => {
    if (staffInitialsCustomized) {
      return;
    }

    setStaffInitials(buildAutoStaffInitials(staffFirstName, staffLastName));
  }, [staffFirstName, staffLastName, staffInitialsCustomized]);

  const clearStaffFields = () => {
    setStaffFirstName('');
    setStaffLastName('');
    setStaffInitials('');
    setStaffInitialsCustomized(false);
    setStaffEmail('');
    setStaffEmailError('');
    setStaffAuthIDError('');
    setStaffCpr('');
    setStaffAddress('');
    setStaffCity('');
    setStaffZip('');
    setStaffEmploymentStartedAt('');
    setStaffEmploymentActive(true);
    setStaffEmploymentTerminatedAt('');
    setStaffRole('Physiotherapist');
    setStaffEngagementType('employee');
    setStaffCompensationBasis('fixed_salary');
    setStaffMonthlySalaryAmount('');
    setStaffPensionPercent('');
    setStaffEmployerPensionPercent('');
    setStaffVacationScheme('paid_vacation_days');
    setStaffPaidVacationDays('');
    setStaffTaxFradrag('');
    setStaffTaxTrakprocent('');
    setStaffHolidayAllowancePercent('12.5');
    setStaffVacationAccrualPerMonth('2.08');
    setStaffWeeklyWorkHours('37.5');
    setStaffAuthorization_ID('');
    setStaffSeatTier('');
    setStaffAuthorizationValid(null);
    setStaffAuthorizationError('');
  };

  const clearServiceFields = () => {
    setServiceCode('');
    setDanmarkCode('');
    setServiceName('');
    setServiceDuration('');
    setServicePrice('');
    setServiceDescription('');
    setServiceRequiresVat(false);
    setServiceVatAmountPercent('');
    setServiceCommissionRate('');
    setServiceCommissionOverrides({});
    setServiceCommissionOverridesOpen(false);
    setServiceColor(DEFAULT_SERVICE_COLOR);
    setServiceIsClass(false);
    setServiceClassParticipantLimit('');
    setServiceClassWeekdays([]);
    setServiceClassStartTime('');
    setServiceClassDefaultRoomId('');
    setServiceClassPrimaryStaff('');
    setServiceClassWeekdayPrimaryStaffs({});
  };

  const canAddStaff = Boolean(
    String(staffFirstName || '').trim()
    && String(staffEmail || '').trim()
    && (!isClinicalStaffRole(staffRole) || String(staffAuthorization_ID || '').trim())
    && !staffEmailError
    && !staffAuthIDError
  );

  const canAddService = Boolean(
    String(serviceCode || '').trim()
    && String(serviceName || '').trim()
    && String(serviceDuration || '').trim()
    && String(servicePrice || '').trim()
    && String(serviceCommissionRate || '').trim()
    && (!serviceRequiresVat || String(serviceVatAmountPercent || '').trim())
    && (!serviceIsClass || (String(serviceClassParticipantLimit || '').trim() && normalizeClassWeekdays(serviceClassWeekdays).length > 0 && String(serviceClassStartTime || '').trim() && String(serviceClassDefaultRoomId || '').trim()))
  );

  const regularServices = services.filter((service) => !service.is_class);
  const classServices = services.filter((service) => service.is_class);

  const getClassRoomDetails = (roomId) => {
    const normalizedRoomId = String(roomId || '');
    if (!normalizedRoomId) {
      return { locationName: '', roomName: '', roomColor: '' };
    }

    for (const location of clinicLocations) {
      const room = (location.rooms || []).find((candidate) => String(candidate.id) === normalizedRoomId);
      if (room) {
        return { locationName: location.name || '', roomName: room.name || '', roomColor: room.color || '' };
      }
    }

    return { locationName: '', roomName: normalizedRoomId, roomColor: '' };
  };

  const getClassRoomOptions = () => clinicLocations.flatMap((location) => (location.rooms || [])
    .filter((room) => room.is_active !== false && room.supports_classes === true)
    .map((room) => ({ location, room })));

  const getFirstClassRoomId = () => {
    const firstRoom = getClassRoomOptions()[0];
    return firstRoom ? String(firstRoom.room.id) : '';
  };

  const getClinicTreatmentDisplayName = (treatment) => {
    if (!treatment) {
      return '';
    }

    const diagnosis = treatment.diagnosis || {};
    return treatment.display_name || treatment.name || treatment.diagnosis_title || diagnosis.title || diagnosis.diagnosis || 'Untitled course';
  };

  const getPatientDisplayById = (userId) => {
    const normalizedUserId = String(userId || '');
    const patient = clinicPatientDirectory[normalizedUserId]
      || clinicPatients.find((entry) => String(entry.user_id || entry.userId || entry.user_id_str || entry.id) === normalizedUserId);
    if (!patient) {
      return {
        label: normalizedUserId,
        fullName: '',
        cprNumber: '',
        treatmentCourseLabel: clinicPatientTreatmentInfo[normalizedUserId]?.label || '',
      };
    }

    const firstName = String(patient.first_name || patient.firstName || '').trim();
    const lastName = String(patient.last_name || patient.lastName || '').trim();
    const fullName = `${firstName} ${lastName}`.trim();
    const cprNumber = String(patient.cpr || patient.pii_number || '').trim();
    const label = cprNumber ? `${fullName || String(patient.email || normalizedUserId)} - CPR: ${cprNumber}` : (fullName || String(patient.email || normalizedUserId));
    return {
      label,
      fullName: fullName || String(patient.email || normalizedUserId),
      cprNumber,
      treatmentCourseLabel: clinicPatientTreatmentInfo[normalizedUserId]?.label || '',
    };
  };

  const getUniqueStaffDisplayLabel = (name = '', duplicateIndex = 0) => {
    const { firstName, lastName } = getDisplayLabelParts(name);
    if (!firstName && !lastName) return '';

    const firstNameDisplay = toTitleCase(firstName);

    if (!lastName) {
      if (duplicateIndex <= 0) return firstNameDisplay;
      const extensionLength = Math.min(firstNameDisplay.length, duplicateIndex + 2);
      return firstNameDisplay.slice(0, extensionLength);
    }

    const lastNameLength = Math.min(lastName.length, Math.max(1, duplicateIndex + 1));
    const lastNameFragment = toTitleCase(lastName.slice(0, lastNameLength));
    return `${firstNameDisplay} ${lastNameFragment}.`.trim();
  };

  const classScheduleGridHeight = ((CLASS_SCHEDULE_END_MINUTES - CLASS_SCHEDULE_START_MINUTES) / CLASS_SCHEDULE_SLOT_MINUTES) * CLASS_SCHEDULE_ROW_HEIGHT;

  const getStaffDisplayById = (userId) => {
    const normalizedUserId = String(userId);
    const staff = staffList.find((s) => String(s.userId || s.user_id || s.id) === normalizedUserId);
    if (!staff) return { label: '', fullName: '' };

    const fullName = getStaffFullName(staff);

    // Prefer DB-stored initials when available
    const dbInitials = staff.initials ? String(staff.initials).trim().toUpperCase() : '';
    if (dbInitials) {
      return { label: dbInitials, fullName: fullName || dbInitials };
    }

    // No DB initials; fall back to computed label from name
    if (!fullName) return { label: '', fullName: '' };

    const { firstName, lastName } = getDisplayLabelParts(fullName);
    const duplicateKey = `${firstName.toLowerCase()}|${(lastName[0] || '').toLowerCase()}`;
    const duplicateStaff = staffList.filter((candidate) => {
      const candidateName = getStaffFullName(candidate);
      if (!candidateName) return false;
      const candidateParts = getDisplayLabelParts(candidateName);
      return `${candidateParts.firstName.toLowerCase()}|${(candidateParts.lastName[0] || '').toLowerCase()}` === duplicateKey;
    });

    const duplicateIndex = duplicateStaff.findIndex(
      (candidate) => String(candidate.userId || candidate.user_id || candidate.id) === normalizedUserId
    );

    return {
      label: getUniqueStaffDisplayLabel(fullName, duplicateIndex),
      fullName
    };
  };

  // Fetch and display invoice details
  const handleViewInvoice = async (invoiceId) => {
    if (!invoiceId) return;
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "https://api.virtus-one.com";
      const response = await fetch(`${baseUrl}/api/calendar/invoices/${invoiceId}/`, {
        headers: { "Authorization": `Token ${token}` },
        method: "GET",
      });
      if (!response.ok) {
        throw new Error("Failed to fetch invoice");
      }
      const invoiceData = await response.json();
      setSelectedInvoiceForPreview(invoiceData);
      setMarkPaidPaymentMethod(normalizeMarkPaidPaymentMethodValue(invoiceData.payment_method || invoiceData.payment_method_label || ''));
      setInvoicePreviewOpen(true);
    } catch (err) {
      console.error("Failed to load invoice:", err);
    }
  };

  const handleBillingActionResponse = async (responseData, options = {}) => {
    const invoiceId = responseData?.invoice?.id || responseData?.invoice_id || options.invoiceId || null;
    const shouldOpenInvoice = options.openInvoice ?? Boolean(invoiceId && ['send_invoice_per_mail', 'stripe_payment'].includes(responseData?.action));

    setBillPreviewOpen(false);
    setBillDialogOpen(false);
    setBillPreviewInvoices([]);
    setSelectedBillingRows([]);
    setSelectedBillingInvoices([]);
    setActionMenuAnchor(null);
    setBulkActionMenuAnchor(null);
    setBillPreviewBillingType('clinic');
    setBillPreviewStaffMemberId('');
    setBillPreviewReturnTarget('dialog');

  setBillPreviewReturnTarget('dialog');
    await fetchBillingStats();
    await fetchMonthEndSnapshots({ silent: true });

    if (shouldOpenInvoice && invoiceId) {
      await handleViewInvoice(invoiceId);
    }
  };

  const buildBillingPreviewGroups = (rows = [], groupingMode = billingGroupMode) => {
    const groups = {};

    rows.forEach((row) => {
      const key = groupingMode === 'single'
        ? row.appointment_id
        : `${row.payer || 'patient'}|${row.patient_name}`;
      if (!groups[key]) {
        groups[key] = {
          rows: [],
          total: 0,
          patientPortion: 0,
          insurancePortion: 0,
          patient: row.patient_name,
          payer: row.payer,
        };
      }

      const rowAmount = parseFloat(row.amount || 0);
      const rowPatientPortion = parseFloat(row.patient_portion || 0);
      const rowInsurancePortion = parseFloat(row.insurance_portion || 0);
      let normalizedPatientPortion = rowPatientPortion;
      let normalizedInsurancePortion = rowInsurancePortion;

      if (rowPatientPortion === 0 && rowInsurancePortion === 0 && rowAmount > 0) {
        if (row.payer === 'insurance') {
          normalizedPatientPortion = 0;
          normalizedInsurancePortion = rowAmount;
        } else {
          normalizedPatientPortion = rowAmount;
          normalizedInsurancePortion = 0;
        }
      }

      groups[key].rows.push({
        ...row,
        patient_portion: String(normalizedPatientPortion),
        insurance_portion: String(normalizedInsurancePortion),
      });
      groups[key].patientPortion += normalizedPatientPortion;
      groups[key].insurancePortion += normalizedInsurancePortion;
      groups[key].total += rowAmount;
    });

    return Object.values(groups);
  };

  // Build invoice preview from selected rows before billing
  const handlePreviewBeforeBill = () => {
    if (selectedBillingRows.length === 0) {
      setBillDialogError("Please select at least one appointment");
      return;
    }
    // Gather selected row data for preview
    const allRows = billingStats?.month_groups?.flatMap(mg => mg.rows) || [];
    const selectedRows = allRows.filter(r => selectedBillingRows.includes(r.appointment_id));
    if (selectedRows.length === 0) {
      setBillDialogError("No matching rows found");
      return;
    }
    setBillPreviewBillingType('clinic');
    setBillPreviewStaffMemberId('');
    setBillPreviewInvoices(buildBillingPreviewGroups(selectedRows));
    setBillDialogOpen(false);
    setBillPreviewOpen(true);
  };

  const handlePreviewMonthEndCandidates = () => {
    const allCandidateRows = monthEndReport?.month_groups?.flatMap((monthGroup) => monthGroup.rows || []).filter((row) => Boolean(row.can_bill)) || [];
    const candidateRows = monthEndFinalizeMode === 'staff_subcontractor'
      ? allCandidateRows.filter((row) => String(row.staff_id || '') === String(monthEndFinalizeStaffId || ''))
      : allCandidateRows;

    if (monthEndFinalizeMode === 'staff_subcontractor' && !monthEndFinalizeStaffId) {
      setMonthEndReportError('Select a subcontractor before reviewing a subcontractor batch.');
      return;
    }
    if (candidateRows.length === 0) {
      setMonthEndReportError('No unbilled appointments are available for the selected month-end report.');
      return;
    }

    setMonthEndReportError('');
    setBillDialogError('');
    setBillPreviewBillingType(monthEndFinalizeMode);
    setBillPreviewStaffMemberId(monthEndFinalizeMode === 'staff_subcontractor' ? monthEndFinalizeStaffId : '');
    setBillPreviewReturnTarget('close');
    setSelectedBillingRows(candidateRows.map((row) => row.appointment_id));
    setBillPreviewInvoices(buildBillingPreviewGroups(candidateRows));
    setBillDialogOpen(false);
    setBillPreviewOpen(true);
  };

  // Confirm billing after preview
  const handleConfirmBillAfterPreview = async () => {
    setBillPreviewLoading(true);
    setBillDialogError("");
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "https://api.virtus-one.com";
      const payload = {
        appointment_ids: selectedBillingRows,
        mode: billingGroupMode,
        billing_type: billPreviewBillingType || 'clinic',
      };
      if ((billPreviewBillingType || 'clinic') === 'staff_subcontractor' && billPreviewStaffMemberId) {
        payload.staff_member_id = billPreviewStaffMemberId;
      }

      const response = await fetch(`${baseUrl}/api/calendar/billing-actions/bill-selected/`, {
        method: "POST",
        headers: {
          "Authorization": `Token ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        const errorMsg = errData.errors ? JSON.stringify(errData.errors) : (errData.error || errData.detail || "Failed to bill appointments");
        throw new Error(errorMsg);
      }

      const billResponse = await response.json();
      await handleBillingActionResponse(billResponse, {
        invoiceId: billResponse?.invoices?.[0]?.id,
        openInvoice: Boolean(billResponse?.invoices?.length),
      });
      await fetchMonthEndReport({ silent: true });
    } catch (err) {
      setBillDialogError(err.message || "Failed to bill appointments");
    }
    setBillPreviewLoading(false);
  };

  // Handle single-row action menu
  const handleOpenRowAction = (event, row) => {
    setSelectedBillingRow(row);
    setActionMenuAnchor(event.currentTarget);
  };
  const handleCloseRowAction = () => {
    setActionMenuAnchor(null);
    setSelectedBillingRow(null);
  };
  const handleRowCancelBilling = () => {
    if (selectedBillingRow?.invoice_id) {
      setSelectedBillingInvoices([selectedBillingRow.invoice_id]);
    }
    handleCloseRowAction();
    setCancelBillingDialogOpen(true);
  };
  const handleRowSendReminder = () => {
    if (selectedBillingRow?.invoice_id) {
      setSelectedBillingInvoices([selectedBillingRow.invoice_id]);
    }
    handleCloseRowAction();
    setReminderType("1st");
    setReminderDialogOpen(true);
  };
  const handleRowAccredit = () => {
    if (selectedBillingRow?.invoice_id) {
      setSelectedBillingInvoices([selectedBillingRow.invoice_id]);
    }
    handleCloseRowAction();
    setCreditPatientHasPaidBalance(false);
    setCreditTransferToPatientBalance(false);
    setCreditDialogOpen(true);
  };
  const handleRowViewInvoice = () => {
    if (selectedBillingRow?.invoice_id) {
      handleViewInvoice(selectedBillingRow.invoice_id);
    }
    handleCloseRowAction();
  };
  const handleRowForgiveBalance = async () => {
    const invoiceId = selectedBillingRow?.invoice_id;
    handleCloseRowAction();
    if (invoiceId) {
      await handleForgiveBalance(invoiceId);
    }
  };

  // Change payer for unbilled appointment
  const handleRowChangePayer = () => {
    if (selectedBillingRow) {
      setChangePayerTarget(selectedBillingRow);
      setChangePayerValue(selectedBillingRow.payer || 'patient');
      handleCloseRowAction();
      setChangePayerDialogOpen(true);
    }
  };
  const handleConfirmChangePayer = async () => {
    if (!changePayerTarget) return;
    setChangePayerLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "https://api.virtus-one.com";
      const payload = {
        payer_type: changePayerValue,
        insurance_paid: changePayerValue === 'insurance',
      };
      const response = await fetch(`${baseUrl}/api/calendar/appointments/${changePayerTarget.appointment_id}/`, {
        method: "PATCH",
        headers: {
          "Authorization": `Token ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to update payer");
      setChangePayerDialogOpen(false);
      setChangePayerTarget(null);
      await fetchBillingStats();
    } catch (err) {
      console.error("Failed to change payer:", err);
    }
    setChangePayerLoading(false);
  };

  // Edit appointment from row click
  const handleOpenEditAppointment = async (row) => {
    if (!staffList2.length) {
      await fetchStaffMembers();
    }
    const appointmentId = row?.appointment_id || row?.id;
    let appointmentData = {
      ...row,
      appointment_id: appointmentId || row?.appointment_id || row?.id || '',
    };

    if (appointmentId) {
      try {
        const response = await fetch(`${backendBaseUrl}/api/calendar/appointments/${appointmentId}/`, {
          headers: { Authorization: `Token ${token}`, Accept: 'application/json' },
        });
        if (response.ok) {
          const fetchedAppointment = await response.json();
          appointmentData = {
            ...row,
            ...fetchedAppointment,
            appointment_id: appointmentId || fetchedAppointment.id || row?.appointment_id || row?.id || '',
            staff_id: fetchedAppointment.staff_member || fetchedAppointment.staff_member_id || row?.staff_id || '',
            amount: fetchedAppointment.booked_price ?? row?.amount ?? '',
            currency: fetchedAppointment.booked_currency || row?.currency || 'EUR',
            patient_notes: fetchedAppointment.patient_notes || row?.patient_notes || '',
            appointment_type: fetchedAppointment.appointment_type || row?.appointment_type || 'follow_up',
            payer_type: fetchedAppointment.payer_type || row?.payer || 'patient',
            payer_insurance: fetchedAppointment.payer_insurance || fetchedAppointment.payer_insurance_id || row?.payer_insurance || row?.payer_insurance_id || '',
            payer_other: fetchedAppointment.payer_other || fetchedAppointment.payer_other_id || row?.payer_other || row?.payer_other_id || '',
            payer_referral_letter: fetchedAppointment.payer_referral_letter || fetchedAppointment.payer_referral_letter_id || row?.payer_referral_letter || row?.payer_referral_letter_id || '',
          };
        }
      } catch (error) {
        console.warn('Failed to load full appointment details for editing', error);
      }
    }

    setEditingAppointment(appointmentData);
    setEditingPayer(appointmentData?.payer_type || row?.payer || 'patient');
    setEditingPayerInsuranceId(String(appointmentData?.payer_insurance || appointmentData?.payer_insurance_id || ''));
    setEditingPayerOtherId(String(appointmentData?.payer_other || appointmentData?.payer_other_id || ''));
    await fetchEditAppointmentPayerOptions(appointmentData?.patient_id || row?.patient_id);
    setEditAppointmentDialogOpen(true);
  };

  useEffect(() => {
    let cancelled = false;

    const loadVideoSession = async () => {
      const appointmentId = editingAppointment?.appointment_id || editingAppointment?.id;
      if (!editAppointmentDialogOpen || !appointmentId || !token) {
        setEditingVideoSession(null);
        return;
      }

      try {
        const response = await fetch(`${backendBaseUrl}/api/calendar/appointments/${appointmentId}/video-session/`, {
          headers: { Authorization: `Token ${token}`, Accept: 'application/json' },
        });
        const payload = await response.json();
        if (!cancelled) {
          setEditingVideoSession(response.ok ? payload : null);
        }
      } catch (error) {
        if (!cancelled) {
          setEditingVideoSession(null);
        }
      }
    };

    loadVideoSession();

    return () => {
      cancelled = true;
    };
  }, [backendBaseUrl, editAppointmentDialogOpen, editingAppointment?.appointment_id, editingAppointment?.id, token]);

  const handleCloseEditAppointment = () => {
    setEditAppointmentDialogOpen(false);
    setEditingAppointment(null);
    setEditingVideoSession(null);
    setVideoCallOpen(false);
    setVideoCallAppointment(null);
    setEditingPayer('patient');
    setEditingPayerInsuranceId('');
    setEditingPayerOtherId('');
    setEditingPayerInsuranceOptions([]);
    setEditingPayerOtherOptions([]);
    setEditingPayerOptionsLoading(false);
    setEditingPayerOptionsError('');
  };

  const handleOpenEditAppointmentVideoCall = () => {
    if (warnIfVideoCallAlreadyActive()) {
      return;
    }

    const appointmentId = editingAppointment?.appointment_id || editingAppointment?.id;
    if (!appointmentId) {
      return;
    }

    setVideoCallAppointment({
      ...editingAppointment,
      id: appointmentId,
      appointment_id: appointmentId,
    });
    setVideoCallOpen(true);
  };

  const handleCloseVideoCall = () => {
    setVideoCallOpen(false);
    setVideoCallAppointment(null);
  };

  const handleSaveEditAppointment = async () => {
    const appointmentId = editingAppointment?.appointment_id || editingAppointment?.id;
    if (!appointmentId) return;
    setEditSaving(true);
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "https://api.virtus-one.com";
      const resolvedInsuranceId = editingPayer === 'insurance'
        ? (editingPayerInsuranceId || editingAppointment.payer_insurance || editingAppointment.payer_insurance_id || '')
        : '';
      const resolvedOtherPayerId = editingPayer === 'other'
        ? (editingPayerOtherId || editingAppointment.payer_other || editingAppointment.payer_other_id || '')
        : '';
      const payload = {
        appointment_type: editingAppointment.appointment_type_raw || editingAppointment.appointment_type,
        payer_type: editingPayer,
        insurance_paid: editingPayer === 'insurance',
        status: editingAppointment.status,
        patient_notes: editingAppointment.patient_notes || '',
        booked_price: parseFloat(editingAppointment.amount || 0),
      };
      if (editingPayer === 'insurance') {
        payload.payer_insurance = resolvedInsuranceId || null;
        payload.payer_other = null;
        payload.payer_referral_letter = editingAppointment.payer_referral_letter || editingAppointment.payer_referral_letter_id || null;
      } else if (editingPayer === 'other') {
        payload.payer_other = resolvedOtherPayerId || null;
        payload.payer_insurance = null;
        payload.payer_referral_letter = null;
      } else {
        payload.payer_insurance = null;
        payload.payer_other = null;
        payload.payer_referral_letter = null;
      }
      if (editingAppointment.staff_id) {
        payload.staff_member = editingAppointment.staff_id;
      }
      const response = await fetch(`${baseUrl}/api/calendar/appointments/${appointmentId}/`, {
        method: "PATCH",
        headers: {
          "Authorization": `Token ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to update appointment");
      handleCloseEditAppointment();
      await fetchBillingStats();
    } catch (err) {
      console.error("Failed to save appointment:", err);
    }
    setEditSaving(false);
  };

  // Bulk invoice actions from the billed rows action bar
  const handleOpenBulkInvoiceActions = (event) => {
    setBulkActionMenuAnchor(event.currentTarget);
  };
  const handleCloseBulkInvoiceActions = () => {
    setBulkActionMenuAnchor(null);
  };
  const collectSelectedInvoiceIds = () => {
    const allRows = billingStats?.month_groups?.flatMap(mg => mg.rows) || [];
    const ids = [...new Set(
      allRows
        .filter(r => selectedBillingRows.includes(r.appointment_id) && r.invoice_id)
        .map(r => r.invoice_id)
    )];
    setSelectedBillingInvoices(ids);
    return ids;
  };

  //Handle invoice bulk actions
  const handleBulkInvoiceAction = async (action, invoiceIds = selectedBillingInvoices, extraPayload = {}) => {
    if (invoiceIds.length === 0) {
      return false;
    }

    const isReminder = action === "send_reminder";
    if (isReminder && !reminderType) {
      setReminderError("Please select a reminder type");
      return;
    }

    const targetDialog = 
      action === "accredit" ? setCreditLoading :
      action === "mark_paid" ? setMarkPaidLoading :
      action === "cancel" ? setCancelBillingLoading :
      setReminderLoading;

    targetDialog(true);
    const setError = 
      action === "accredit" ? setCreditError :
      action === "mark_paid" ? setMarkPaidError :
      action === "cancel" ? setCancelBillingError :
      setReminderError;
    setError("");

    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "https://api.virtus-one.com";
      const payload = {
        invoice_ids: invoiceIds,
        action: action,
        ...extraPayload,
      };

      if (action === 'accredit') {
        payload.patient_has_paid_balance = creditPatientHasPaidBalance;
        payload.transfer_to_patient_balance = creditTransferToPatientBalance;
      }

      if (isReminder) {
        payload.reminder_type = reminderType;
      }

      const response = await fetch(`${baseUrl}/api/calendar/billing-actions/invoice-bulk/`, {
        method: "POST",
        headers: {
          "Authorization": `Token ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || `Failed to ${action} invoices`);
      }

      const responseData = await response.json();

      if (action === "accredit") {
        setCreditDialogOpen(false);
        setCreditPatientHasPaidBalance(false);
        setCreditTransferToPatientBalance(false);
      } else if (action === "mark_paid") {
        setMarkPaidDialogOpen(false);
        setMarkPaidInvoiceIds([]);
      } else if (action === "cancel") {
        setCancelBillingDialogOpen(false);
      } else if (isReminder) {
        setReminderDialogOpen(false);
      }

      await handleBillingActionResponse(responseData, { openInvoice: false });
      return true;
    } catch (err) {
      setError(err.message || `Failed to ${action} invoices`);
      return false;
    } finally {
      targetDialog(false);
    }
  };

  const handleCloseCreditDialog = () => {
    setCreditDialogOpen(false);
    setCreditError("");
    setCreditPatientHasPaidBalance(false);
    setCreditTransferToPatientBalance(false);
  };

  const openMarkPaidDialog = (invoiceIds, initialPaymentMethod = MARK_PAID_PAYMENT_METHOD_OPTIONS[0]?.value || '') => {
    const normalizedInvoiceIds = Array.isArray(invoiceIds) ? invoiceIds.filter(Boolean) : [];
    if (normalizedInvoiceIds.length === 0) {
      return;
    }

    setMarkPaidInvoiceIds(normalizedInvoiceIds);
    setMarkPaidPaymentMethod(initialPaymentMethod || MARK_PAID_PAYMENT_METHOD_OPTIONS[0]?.value || '');
    setMarkPaidError('');
    setMarkPaidDialogOpen(true);
  };

  const handleCloseMarkPaidDialog = () => {
    setMarkPaidDialogOpen(false);
    setMarkPaidError('');
    setMarkPaidInvoiceIds([]);
    setMarkPaidPaymentMethod(MARK_PAID_PAYMENT_METHOD_OPTIONS[0]?.value || '');
  };

  const handleConfirmMarkPaid = async () => {
    const success = await handleBulkInvoiceAction('mark_paid', markPaidInvoiceIds, {
      payment_method: markPaidPaymentMethod,
    });

    if (success) {
      handleCloseMarkPaidDialog();
    }
  };

  // Format currency display
  const formatCurrency = (amount, currency = "EUR") => {
    const num = parseFloat(amount) || 0;
    return `${currency} ${num.toFixed(2)}`;
  };

  const getInvoiceBilledAmount = (row) => parseFloat(row?.invoice_total_amount || row?.total_amount || row?.amount || row?.amount_billed || 0);

  const getInvoicePaidAmount = (row) => {
    const paidAmount = parseFloat(row?.amount_paid || 0);
    const billedAmount = getInvoiceBilledAmount(row);
    const invoiceStatus = String(row?.invoice_status || row?.status || '').toLowerCase();

    if (paidAmount > 0) {
      return paidAmount;
    }

    if (invoiceStatus === 'paid') {
      return billedAmount;
    }

    return 0;
  };

  const getServiceCommissionRateForStaff = (service, staffId) => {
    if (!service) return 0;
    const normalizedStaffId = String(staffId || '');
    const overrideValue = service.commission_overrides?.[normalizedStaffId];
    const resolvedRate = overrideValue != null && String(overrideValue).trim() !== ''
      ? Number(overrideValue)
      : Number(service.commission_rate || 0);
    return Number.isFinite(resolvedRate) ? resolvedRate : 0;
  };

  const buildCommissionSummary = (billingData, serviceCatalog) => {
    const monthGroups = billingData?.month_groups || [];
    const allRows = monthGroups.flatMap((monthGroup) => monthGroup.rows || []);
    const unbilledRows = allRows.filter((row) => String(row.invoice_status || 'unbilled').toLowerCase() === 'unbilled');
    const serviceById = new Map((serviceCatalog || []).map((service) => [String(service.id), service]));

    const summaryByStaff = new Map();
    let totalRevenue = 0;
    let totalCommission = 0;

    unbilledRows.forEach((row) => {
      const amount = Number(row.amount || 0);
      const staffId = String(row.staff_id || '');
      const service = serviceById.get(String(row.service_id || ''));
      const commissionRate = getServiceCommissionRateForStaff(service, staffId);
      const commissionAmount = amount * (commissionRate / 100);

      totalRevenue += amount;
      totalCommission += commissionAmount;

      const existing = summaryByStaff.get(staffId) || {
        staff_id: staffId,
        staff_name: row.staff_name || 'Unknown staff',
        revenue: 0,
        commission: 0,
        count: 0,
      };

      existing.revenue += amount;
      existing.commission += commissionAmount;
      existing.count += 1;
      summaryByStaff.set(staffId, existing);
    });

    return {
      totalRevenue,
      totalCommission,
      unbilledCount: unbilledRows.length,
      byStaff: Array.from(summaryByStaff.values()).sort((a, b) => b.revenue - a.revenue),
    };
  };

  const getMonthCommissionTotal = (rows = []) => {
    return rows.reduce((total, row) => {
      const service = services.find((svc) => String(svc.id) === String(row.service_id));
      const commissionRate = getServiceCommissionRateForStaff(service, row.staff_id);
      const commissionAmount = Number(row.amount || 0) * (commissionRate / 100);
      return total + commissionAmount;
    }, 0);
  };
  const getBillableServiceLabel = (row) => {
    if (!row?.service_id) {
      return '-';
    }

    const service = services.find((svc) => String(svc.id) === String(row.service_id));
    return service?.name || row.service || '-';
  };

  const getMonthOverdueBillsCount = (rows = []) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const closedStatuses = new Set(['paid', 'accredited', 'refunded', 'cancelled']);

    return rows.filter((row) => {
      const invoiceStatus = String(row.invoice_status || '').toLowerCase();
      if (!row.invoice_id || !row.payment_due_date) return false;
      if (invoiceStatus === 'overdue') return true;
      if (closedStatuses.has(invoiceStatus)) return false;

      const dueDate = new Date(row.payment_due_date);
      if (Number.isNaN(dueDate.getTime())) return false;
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today;
    }).length;
  };

  const isBillingRowOverdue = (row) => {
    const invoiceStatus = String(row?.invoice_status || '').toLowerCase();
    if (!row?.invoice_id || !row?.payment_due_date) return false;
    if (invoiceStatus === 'overdue') return true;
    if (['paid', 'accredited', 'refunded', 'cancelled'].includes(invoiceStatus)) return false;

    const dueDate = new Date(row.payment_due_date);
    if (Number.isNaN(dueDate.getTime())) return false;
    dueDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  const getBillingRowsForMonthGroup = (monthGroup) => {
    let rows = [...(monthGroup?.rows || [])];
    rows = rows.filter((row) => Boolean(row.can_bill) || Boolean(row.invoice_id));
    if (billingSortByStaff) {
      rows = rows.filter((row) => row.staff_id === billingSortByStaff);
    }
    return rows;
  };

  const getBillingSelectionPresetRows = (rows = [], preset = 'all') => {
    if (preset === 'overdue') {
      return rows.filter(isBillingRowOverdue);
    }
    if (preset === 'unbilled') {
      return rows.filter((row) => String(row.invoice_status || '').toLowerCase() === 'unbilled');
    }
    if (preset === 'draft') {
      return rows.filter((row) => String(row.invoice_status || '').toLowerCase() === 'draft');
    }
    return rows;
  };

  const handleOpenBillingSelectMenu = (event, rows) => {
    setBillingSelectMenuAnchor(event.currentTarget);
    setBillingSelectMenuRows(rows || []);
  };

  const handleCloseBillingSelectMenu = () => {
    setBillingSelectMenuAnchor(null);
    setBillingSelectMenuRows([]);
  };

  const handleBillingSelectPreset = (preset) => {
    const targetIds = getBillingSelectionPresetRows(billingSelectMenuRows, preset).map((row) => row.appointment_id);
    const targetIdSet = new Set(billingSelectMenuRows.map((row) => row.appointment_id));

    setSelectedBillingRows((prev) => {
      const preserved = prev.filter((id) => !targetIdSet.has(id));
      return Array.from(new Set([...preserved, ...targetIds]));
    });

    handleCloseBillingSelectMenu();
  };

  const getServiceVatBreakdown = (service, amount) => {
    const serviceAmount = Number(amount || 0);
    const vatRate = service && service.requires_vat ? Number(service.vat_amount_percent || 0) : 0;
    const normalizedVatRate = Number.isFinite(vatRate) ? vatRate : 0;
    const vatAmount = service && service.requires_vat ? serviceAmount * (normalizedVatRate / 100) : 0;

    return {
      serviceAmount,
      vatRate: normalizedVatRate,
      vatAmount,
      totalAmount: serviceAmount + vatAmount,
      requiresVat: Boolean(service && service.requires_vat),
    };
  };

  const getFilteredBillingRows = (billingData, staffId = '') => {
    const rows = billingData?.month_groups?.flatMap((monthGroup) => monthGroup.rows || []) || [];
    if (!staffId) {
      return rows;
    }
    return rows.filter((row) => String(row.staff_id || '') === String(staffId));
  };

  const getVatPeriodMeta = (dateValue, periodType) => {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    const year = date.getFullYear();
    const monthIndex = date.getMonth();

    if (periodType === 'quarter') {
      const quarter = Math.floor(monthIndex / 3) + 1;
      return {
        key: `${year}-Q${quarter}`,
        label: `Q${quarter} ${year}`,
        sortKey: `${year}-${quarter}`,
      };
    }

    if (periodType === 'half_year') {
      const half = monthIndex < 6 ? 1 : 2;
      return {
        key: `${year}-H${half}`,
        label: `H${half} ${year}`,
        sortKey: `${year}-${half}`,
      };
    }

    if (periodType === 'year') {
      return {
        key: `${year}`,
        label: `${year}`,
        sortKey: `${year}`,
      };
    }

    return {
      key: `${year}-${String(monthIndex + 1).padStart(2, '0')}`,
      label: date.toLocaleString(undefined, { month: 'long', year: 'numeric' }),
      sortKey: `${year}-${String(monthIndex + 1).padStart(2, '0')}`,
    };
  };

  const getVatAccountingDate = (row) => row?.billing_date || row?.appointment_date || null;

  const getVatStatusLabel = (row) => {
    const status = String(row?.invoice_status || 'unbilled').toLowerCase();
    if (!row?.invoice_id) {
      return 'Unbilled';
    }
    return toTitleCase(status.replace(/_/g, ' ')) || 'Unbilled';
  };

  const getVatPayerLabel = (row) => {
    const payer = String(row?.payer || '').toLowerCase();
    if (payer === 'insurance') return 'Insurance';
    if (payer === 'split') return 'Split';
    return 'Patient';
  };

  const buildVatPeriodSummary = (billingData, serviceCatalog, periodType, staffId = '') => {
    const rows = getFilteredBillingRows(billingData, staffId);

    const currentYear = new Date().getFullYear();
    const allDates = rows.map((r) => new Date(r.appointment_date || r.billing_date)).filter((d) => !Number.isNaN(d.getTime()));
    const filterStartDate = billingData?.filters?.start_date ? new Date(billingData.filters.start_date) : null;
    const filterEndDate = billingData?.filters?.end_date ? new Date(billingData.filters.end_date) : null;
    const rangeStart = filterStartDate && !Number.isNaN(filterStartDate.getTime())
      ? filterStartDate
      : (allDates.length > 0 ? new Date(Math.min(...allDates)) : new Date(currentYear, 0, 1));
    const rangeEnd = filterEndDate && !Number.isNaN(filterEndDate.getTime())
      ? filterEndDate
      : (allDates.length > 0 ? new Date(Math.max(...allDates)) : new Date(currentYear, 11, 31));
    const periodMap = new Map();

    const addPeriodPlaceholder = (dateValue, type) => {
      const meta = getVatPeriodMeta(dateValue, type);
      if (meta && !periodMap.has(meta.key)) {
        periodMap.set(meta.key, { key: meta.key, label: meta.label, sortKey: meta.sortKey, vatAppointmentCount: 0, invoicedVatCount: 0, unbilledVatCount: 0, serviceAmount: 0, vatAmount: 0, grossAmount: 0, items: [] });
      }
    };

    const addMonthRange = (startDate, endDate) => {
      const cur = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const last = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      while (cur <= last) {
        addPeriodPlaceholder(new Date(cur), 'month');
        cur.setMonth(cur.getMonth() + 1);
      }
    };

    const addQuarterRange = (startDate, endDate) => {
      let curYear = startDate.getFullYear();
      let curQuarter = Math.floor(startDate.getMonth() / 3);
      const endYear = endDate.getFullYear();
      const endQuarter = Math.floor(endDate.getMonth() / 3);

      while (curYear < endYear || (curYear === endYear && curQuarter <= endQuarter)) {
        addPeriodPlaceholder(new Date(curYear, curQuarter * 3, 1), 'quarter');
        curQuarter += 1;
        if (curQuarter > 3) {
          curQuarter = 0;
          curYear += 1;
        }
      }
    };

    const addHalfYearRange = (startDate, endDate) => {
      let curYear = startDate.getFullYear();
      let curHalf = startDate.getMonth() < 6 ? 0 : 1;
      const endYear = endDate.getFullYear();
      const endHalf = endDate.getMonth() < 6 ? 0 : 1;

      while (curYear < endYear || (curYear === endYear && curHalf <= endHalf)) {
        addPeriodPlaceholder(new Date(curYear, curHalf * 6, 1), 'half_year');
        curHalf += 1;
        if (curHalf > 1) {
          curHalf = 0;
          curYear += 1;
        }
      }
    };

    const addYearRange = (startDate, endDate) => {
      for (let year = startDate.getFullYear(); year <= endDate.getFullYear(); year += 1) {
        addPeriodPlaceholder(new Date(year, 0, 1), 'year');
      }
    };

    if (periodType === 'month') {
      addMonthRange(rangeStart, rangeEnd);
    } else if (periodType === 'quarter') {
      addQuarterRange(rangeStart, rangeEnd);
    } else if (periodType === 'half_year') {
      addHalfYearRange(rangeStart, rangeEnd);
    } else if (periodType === 'year') {
      addYearRange(rangeStart, rangeEnd);
    }
    const serviceById = new Map((serviceCatalog || []).map((service) => [String(service.id), service]));

    let totalServiceAmount = 0;
    let totalVatAmount = 0;
    let totalGrossAmount = 0;
    let totalVatAppointments = 0;
    let invoicedVatCount = 0;
    let unbilledVatCount = 0;

    rows.forEach((row) => {
      const accountingDate = getVatAccountingDate(row);
      const periodMeta = getVatPeriodMeta(accountingDate, periodType);
      if (!periodMeta) {
        return;
      }

      const service = serviceById.get(String(row.service_id || ''));
      const vatBreakdown = getServiceVatBreakdown(service, row.amount);

      if (!vatBreakdown.requiresVat) {
        return;
      }

      totalServiceAmount += vatBreakdown.serviceAmount;
      totalVatAmount += vatBreakdown.vatAmount;
      totalGrossAmount += vatBreakdown.totalAmount;
      totalVatAppointments += 1;
      if (row.invoice_id) {
        invoicedVatCount += 1;
      } else {
        unbilledVatCount += 1;
      }

      const existing = periodMap.get(periodMeta.key) || {
        key: periodMeta.key,
        label: periodMeta.label,
        sortKey: periodMeta.sortKey,
        vatAppointmentCount: 0,
        invoicedVatCount: 0,
        unbilledVatCount: 0,
        serviceAmount: 0,
        vatAmount: 0,
        grossAmount: 0,
        items: [],
      };

      existing.vatAppointmentCount += 1;
      if (row.invoice_id) {
        existing.invoicedVatCount += 1;
      } else {
        existing.unbilledVatCount += 1;
      }
      existing.serviceAmount += vatBreakdown.serviceAmount;
      existing.vatAmount += vatBreakdown.vatAmount;
      existing.grossAmount += vatBreakdown.totalAmount;
      existing.items.push({
        key: row.appointment_id || `${periodMeta.key}-${existing.vatAppointmentCount}`,
        appointmentDate: row.appointment_date,
        accountingDate,
        accountingDateLabel: accountingDate ? new Date(accountingDate).toLocaleDateString() : '-',
        dateLabel: row.appointment_date ? new Date(row.appointment_date).toLocaleDateString() : '-',
        billingDateLabel: row.billing_date ? new Date(row.billing_date).toLocaleDateString() : '-',
        dueDateLabel: row.payment_due_date ? new Date(row.payment_due_date).toLocaleDateString() : '-',
        patientName: row.patient_name || '-',
        payerLabel: getVatPayerLabel(row),
        invoiceNumber: row.invoice_number || '-',
        invoiceStatusLabel: getVatStatusLabel(row),
        staffLabel: getStaffDisplayById(row.staff_id).label || row.staff_name || '-',
        serviceName: row.service || '-',
          serviceName: getBillableServiceLabel(row),
        vatRate: vatBreakdown.vatRate,
        serviceAmount: vatBreakdown.serviceAmount,
        vatAmount: vatBreakdown.vatAmount,
        grossAmount: vatBreakdown.totalAmount,
      });
      periodMap.set(periodMeta.key, existing);
    });

    const startDate = billingData?.filters?.start_date;
    const endDate = billingData?.filters?.end_date;
    const rangeLabel = startDate && endDate
      ? `${startDate} to ${endDate}`
      : startDate
        ? `From ${startDate}`
        : endDate
          ? `Until ${endDate}`
          : 'Current billing results';

    return {
      periodType,
      currency: rows[0]?.currency || 'EUR',
      rangeLabel,
      appointmentCount: rows.length,
      vatAppointmentCount: totalVatAppointments,
      invoicedVatCount,
      unbilledVatCount,
      totalServiceAmount,
      totalVatAmount,
      totalGrossAmount,
      periods: Array.from(periodMap.values())
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
        .map((period) => ({
          ...period,
          items: [...period.items].sort((a, b) => new Date(a.accountingDate || a.appointmentDate) - new Date(b.accountingDate || b.appointmentDate)),
        })),
    };
  };

  const getInvoiceStatusChip = (status) => {
    const normalizedStatus = String(status || 'unbilled').toLowerCase();
    const chip = {
      label: toTitleCase(normalizedStatus.replace(/_/g, ' ')) || 'Unbilled',
      backgroundColor: '#E0E0E0',
      color: '#666666',
    };

    if (normalizedStatus === 'paid') {
      chip.label = 'Paid';
      chip.backgroundColor = '#E8F5E9';
      chip.color = '#2E7D32';
      return chip;
    }

    if (normalizedStatus === 'accredited') {
      chip.label = 'Accredited';
      chip.backgroundColor = '#E3F2FD';
      chip.color = '#1565C0';
      return chip;
    }

    if (normalizedStatus === 'refunded' || normalizedStatus === 'cancelled') {
      chip.label = toTitleCase(normalizedStatus);
      chip.backgroundColor = '#FFEBEE';
      chip.color = '#C62828';
      return chip;
    }

    if (normalizedStatus === 'inkasso') {
      chip.label = 'Inkasso';
      chip.backgroundColor = '#FFEBEE';
      chip.color = '#C62828';
      return chip;
    }

    if (normalizedStatus === 'draft' || normalizedStatus === 'sent' || normalizedStatus === 'reopened' || normalizedStatus === 'viewed' || normalizedStatus === 'accepted' || normalizedStatus === 'challenged' || normalizedStatus === 'partially_paid' || normalizedStatus === 'overdue' || normalizedStatus === 'reminder_1' || normalizedStatus === 'reminder_2' || normalizedStatus === 'reminder_3' || normalizedStatus === 'unbilled') {
      chip.label = normalizedStatus === 'unbilled' ? 'Unbilled' : toTitleCase(normalizedStatus.replace(/_/g, ' '));
      chip.backgroundColor = '#FFF3E0';
      chip.color = '#F57C00';
      return chip;
    }

    return chip;
  };

  // Handle row selection toggle
  const handleToggleBillingRowSelected = (appointmentId) => {
    setSelectedBillingRows((prev) =>
      prev.includes(appointmentId)
        ? prev.filter((id) => id !== appointmentId)
        : [...prev, appointmentId]
    );
  };

  // Handle invoice selection toggle
  const handleToggleBillingInvoiceSelected = (invoiceId) => {
    setSelectedBillingInvoices((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  // Get staff display name
  const getStaffDisplayName = (staffId) => {
    if (!staffId) return "-";
    const staff = staffList2.find((s) => s.user_id === staffId || s.id === staffId);
    if (!staff) return "-";
    const name = `${staff.first_name || ""} ${staff.last_name || ""}`.trim();
    return name || staff.email || "-";
  };

  const getInvoiceIssuerName = (invoiceLike) => {
    const clinicBusinessName = (clinicName || invoiceLike?.care_provider_name || '').trim() || 'Clinic';
    const staffName = (invoiceLike?.staff_member_name || '').trim();
    const isStaffSubcontractor = String(invoiceLike?.billing_type || '').toLowerCase() === 'staff_subcontractor';

    if (!isStaffSubcontractor) {
      return clinicBusinessName;
    }

    if (invoiceIssuerNameMode === 'custom' && String(invoiceCustomIssuerName || '').trim()) {
      return String(invoiceCustomIssuerName).trim();
    }
    if (invoiceIssuerNameMode === 'clinic') {
      return clinicBusinessName;
    }
    return staffName || clinicBusinessName;
  };

  // Generate and download invoice as PDF
  const handleDownloadInvoicePDF = () => {
    if (!selectedInvoiceForPreview) return;

    const element = document.getElementById('invoice-printable-content');
    if (!element) return;

    const opt = {
      margin: 10,
      filename: `Invoice_${selectedInvoiceForPreview.invoice_number}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
    };

    html2pdf().set(opt).from(element).save();
  };

  const handleDownloadVatSummaryPDF = () => {
    const element = document.getElementById('billing-vat-summary-printable');
    if (!element) return;

    const datePart = [billingDateRangeStart || 'all', billingDateRangeEnd || 'all'].join('_');
    const opt = {
      margin: 10,
      filename: `VAT_Summary_${billingVatPeriod}_${datePart}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { orientation: 'landscape', unit: 'mm', format: 'a4' }
    };

    html2pdf().set(opt).from(element).save();
  };

  const handleDownloadMonthEndReportPDF = () => {
    const element = document.getElementById('month-end-report-printable');
    if (!element) return;

    const opt = {
      margin: 10,
      filename: `Month_End_Report_${toYearMonthToken(monthEndMonth) || 'selected'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { orientation: 'landscape', unit: 'mm', format: 'a4' }
    };

    html2pdf().set(opt).from(element).save();
  };

  const handleDownloadBillPreviewPDF = () => {
    const element = document.getElementById('bill-preview-printable');
    if (!element) return;

    const opt = {
      margin: 8,
      filename: `Invoice_Preview_${toYearMonthToken(monthEndMonth) || 'selected'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' },
      pagebreak: { mode: ['css', 'legacy'] }
    };

    html2pdf().set(opt).from(element).save();
  };

  // Load data on mount
  useEffect(() => {
    if (tabIndex === 3) {
      fetchBillingStats();
      fetchStaffMembers();
      fetchMonthEndReport();
      fetchMonthEndSnapshots({ silent: true });
    }
    if (tabIndex === 2) {
      fetchServices();
      fetchStaffList();
      fetchClinicLocations();
    }
  }, [tabIndex]);

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Paper elevation={0} className={classes.tabsWrapper}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          classes={{ root: classes.tabsControlRoot, indicator: classes.tabsIndicator }}
        >
          <Tab label="Details" className={classes.tabRoot} />
          <Tab label="Staff" className={classes.tabRoot} />
          <Tab label="Services" className={classes.tabRoot} />
          <Tab label="Statistics" className={classes.tabRoot} />
          <Tab label="Analytics" className={classes.tabRoot} />
        </Tabs>
      </Paper>
      <Card className={classes.card} elevation={0}>
        {tabIndex === 0 && (
          <form autoComplete="off" noValidate>
            <Typography
              className={classes.bodyText}
              style={{marginTop: 12, marginBottom: 4, marginLeft: 8}}
              gutterBottom
            >
              Here you can add / change your personal information and set up notifications.
            </Typography>
            <Divider style={{marginTop: 16, marginBottom: 4, marginLeft: 8}}/>
            <CardContent className={classes.cardContent} style={{padding: 16}}>
              <Typography variant="subtitle1" style={{fontWeight:600, marginBottom: 24, color: "#002D55"}}>Edit clinic details</Typography>
              <Grid container spacing={2}>
                <Grid item md={6} xs={12}>
                  <TextField
                    margin="none"
                    size="small"
                    fullWidth
                    label="First name of owner"
                    name="firstName"
                    className={classes.orangeFocused}
                    onChange={event => setFirstName(event.target.value)}
                    required
                    value={firstName}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    margin="none"
                    size="small"
                    fullWidth
                    label="Last name of owner"
                    name="lastName"
                    className={classes.orangeFocused}
                    onChange={event => setLastName(event.target.value)}
                    required
                    value={lastName}
                    variant="outlined"
                  />
                </Grid>
                {/* SaaS B2B Clinic Fields for Denmark, UK, EU */}
                <Grid item md={6} xs={12}>
                  <TextField
                    margin="none"
                    size="small"
                    fullWidth
                    label="Name of Clinic or Company"
                    name="clinicName"
                    className={classes.orangeFocused}
                    variant="outlined"
                    value={clinicName}
                    onChange={e => setClinicName(e.target.value)}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    margin="none"
                    size="small"
                    fullWidth
                    label="Business Registration Number"
                    name="clinicRegNumber"
                    className={classes.orangeFocused}
                    variant="outlined"
                    value={clinicRegNumber}
                    onChange={e => setClinicRegNumber(e.target.value)}
                    helperText="CVR for Denmark, Company No. for UK, VAT for EU"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    margin="none"
                    size="small"
                    fullWidth
                    label="Clinic Address"
                    name="clinicAddress"
                    className={classes.orangeFocused}
                    variant="outlined"
                    value={clinicAddress}
                    onChange={e => setClinicAddress(e.target.value)}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    margin="none"
                    size="small"
                    fullWidth
                    label="Clinic Email"
                    name="clinicEmail"
                    className={classes.orangeFocused}
                    variant="outlined"
                    value={clinicEmail}
                    onChange={e => setClinicEmail(e.target.value)}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    margin="none"
                    size="small"
                    fullWidth
                    label="Clinic City"
                    name="clinicCity"
                    className={classes.orangeFocused}
                    variant="outlined"
                    value={clinicCity}
                    onChange={e => setClinicCity(e.target.value)}
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    margin="none"
                    size="small"
                    fullWidth
                    label="Clinic Postal Code"
                    name="clinicPostalCode"
                    className={classes.orangeFocused}
                    variant="outlined"
                    value={clinicPostalCode}
                    onChange={e => setClinicPostalCode(e.target.value)}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Autocomplete
                    options={countryOptions}
                    value={countryOptions.includes(country) ? country : ''}
                    onChange={(event, newValue) => setContry(newValue || '')}
                    PaperComponent={({ children, ...paperProps }) => (
                      <Paper {...paperProps} style={{ borderRadius: 14, marginTop: 8 }}>
                        {children}
                      </Paper>
                    )}
                    renderOption={(option) => (
                      <span style={{ fontSize: 'clamp(0.72rem, 0.9vw, 0.875rem)', color: '#002D55' }}>
                        {option}
                      </span>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        margin="none"
                        size="small"
                        label="Clinic Country"
                        name="clinicCountry"
                        className={classes.orangeFocused}
                        variant="outlined"
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    margin="none"
                    size="small"
                    fullWidth
                    label="Clinic Phone Number"
                    name="clinicPhone"
                    className={classes.orangeFocused}
                    variant="outlined"
                    value={clinicPhone}
                    onChange={e => setClinicPhone(e.target.value)}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    margin="none"
                    size="small"
                    fullWidth
                    label="Main Contact Email"
                    name="email"
                    className={classes.orangeFocused}
                    onChange={event => setEmail(event.target.value)}
                    required
                    value={email}
                    variant="outlined"
                    disabled
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    margin="none"
                    size="small"
                    fullWidth
                    label="Number of Practitioners"
                    name="numPractitioners"
                    type="number"
                    className={classes.orangeFocused}
                    variant="outlined"
                  />
                </Grid>
                
                
                <Grid item md={6} xs={12}>
                  <TextField
                    margin="none"
                    size="small"
                    fullWidth
                    label="Main Contact Phone Number"
                    name="mainContactPhone"
                    className={classes.orangeFocused}
                    variant="outlined"
                    value={mainContactPhone}
                    onChange={e => setMainContactPhone(e.target.value)}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    margin="none"
                    size="small"
                    fullWidth
                    label="Main Contact Person"
                    name="mainContact"
                    className={classes.orangeFocused}
                    variant="outlined"
                    value={mainContactPerson}
                    onChange={e => setMainContactPerson(e.target.value)}
                  />
                </Grid>
                
              </Grid>
            </CardContent>
            <Divider className={classes.footerDivider} />
            <Box className={classes.footerActions} display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
              <Button
                className={clsx(classes.Button, classes.detailsSaveButton, classes.footerActionButton)}
                variant="contained"
                fullWidth
                style={{ flex: 1 }}
                onClick={saveHandler}
              >
                Save details
              </Button>
              <Button
                className={clsx(classes.Button3, classes.detailsCancelButton, classes.footerActionButton)}
                variant="outlined"
                fullWidth
                style={{ flex: 1 }}
                onClick={handleDetailsCancel}
              >
                Cancel
              </Button>
            </Box>
          </form>
        )}
        {tabIndex === 3 && (
            <Box style={{padding: '12px 0', marginLeft: 8 }}>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" style={{ gap: 12 }}>
              <Typography
                className={classes.bodyText}
                style={{marginBottom: 12, flex: '1 1 320px'}}
              >
                Revenue, billing and accounting management for all care provider appointments.
              </Typography>
            </Box>
            <Divider style={{marginTop: 16, marginBottom: 24, marginLeft: 0}}/>
            {/* Filters Section */}
            <Box style={{marginBottom: 16, padding: '12px 16px', backgroundColor: '#e6eaf0', borderRadius: 12}}>
              <Typography style={{fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#002D55'}}>Filters</Typography>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Status"
                    select
                    value={billingStatusScope}
                    onChange={(e) => setBillingStatusScope(e.target.value)}
                    variant="outlined"
                    size="small"
                    margin="none"
                    fullWidth
                    className={classes.orangeFocused}
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                  >
                    <MenuItem value="all">All (Billed & Unbilled)</MenuItem>
                    <MenuItem value="unbilled">Unbilled Only</MenuItem>
                    <MenuItem value="billed">Billed Only</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={billingDateRangeStart}
                    onChange={(e) => setBillingDateRangeStart(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                    margin="none"
                    fullWidth
                    className={classes.orangeFocused}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="End Date"
                    type="date"
                    value={billingDateRangeEnd}
                    onChange={(e) => setBillingDateRangeEnd(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                    margin="none"
                    fullWidth
                    className={classes.orangeFocused}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Payer Type"
                    select
                    value={billingPayerType}
                    onChange={(e) => setBillingPayerType(e.target.value)}
                    variant="outlined"
                    size="small"
                    margin="none"
                    fullWidth
                    className={classes.orangeFocused}
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="patient">Patient</MenuItem>
                    <MenuItem value="insurance">Insurance</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    label="Sort by Staff"
                    select
                    value={billingSortByStaff}
                    onChange={(e) => setBillingSortByStaff(e.target.value)}
                    variant="outlined"
                    size="small"
                    margin="none"
                    fullWidth
                    className={classes.orangeFocused}
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                  >
                    <MenuItem value="">All Staff (no filter)</MenuItem>
                    {staffList2.map((staff) => (
                      <MenuItem key={staff.user_id || staff.id} value={staff.user_id || staff.id}>
                        {`${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.email || 'Staff'}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Button
                    variant="contained"
                    className={classes.Button}
                    onClick={fetchBillingStats}
                    disabled={billingLoading}
                    fullWidth
                    style={{height: 40}}
                  >
                    {billingLoading ? "Loading..." : "Search"}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* Error Message */}
            {billingError && (
              <Typography color="error" style={{marginBottom: 12, padding: '8px 12px', backgroundColor: '#FFEBEE', borderRadius: 12, fontSize: 13}}>
                {billingError}
              </Typography>
            )}

            {/* Loading State */}
            {billingLoading && (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            )}

            {/* Empty State */}
            {!billingLoading && billingStats && billingStats.month_groups.length === 0 && (
              <Typography style={{textAlign: 'center', color: '#999', padding: '20px 0'}}>
                No appointments found for the selected filters.
              </Typography>
            )}

            {/* Bills Table - Month Grouped */}
            {!billingLoading && billingStats && billingStats.month_groups.length > 0 && (
              <Box>
                {/* Clinical Admin Cockpit */}
                {billingStats.grand_totals && (() => {
                  const commissionSummary = buildCommissionSummary(billingStats, services);
                  const vatSummary = buildVatPeriodSummary(billingStats, services, billingVatPeriod, billingSortByStaff);
                  const hasVat = vatSummary.appointmentCount > 0;

                  return (
                    <Box style={{marginBottom: 16}}>
                      {/* --- Cockpit Bar (always visible) --- */}
                      <Box
                        onClick={() => setCockpitOpen((prev) => !prev)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setCockpitOpen((prev) => !prev);
                          }
                        }}
                        style={{
                          padding: '12px 16px',
                          backgroundColor: '#002D55',
                          borderRadius: cockpitOpen ? '12px 12px 0 0' : 12,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 12,
                          cursor: 'pointer',
                        }}
                      >
                        <Box>
                          <Typography style={{fontSize: 13, fontWeight: 700, color: '#fff'}}>
                            Clinical Admin Cockpit
                          </Typography>
                          <Typography style={{fontSize: 11, color: 'rgba(255,255,255,0.65)'}}>
                            Month-end billing | VAT accounting | Commission
                          </Typography>
                        </Box>
                        <Box style={{display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap'}}>
                          <Box style={{textAlign: 'center'}}>
                            <Typography style={{fontSize: 11, color: 'rgba(255,255,255,0.65)'}}>Appointments</Typography>
                            <Typography style={{fontSize: 15, fontWeight: 700, color: '#fff'}}>{billingStats.grand_totals.count}</Typography>
                          </Box>
                          <Box style={{textAlign: 'center'}}>
                            <Typography style={{fontSize: 11, color: 'rgba(255,255,255,0.65)'}}>Billed</Typography>
                            <Typography style={{fontSize: 15, fontWeight: 700, color: '#81C784'}}>{billingStats.grand_totals.billed_count}</Typography>
                          </Box>
                          <Box style={{textAlign: 'center'}}>
                            <Typography style={{fontSize: 11, color: 'rgba(255,255,255,0.65)'}}>Unbilled</Typography>
                            <Typography style={{fontSize: 15, fontWeight: 700, color: '#FFB74D'}}>{billingStats.grand_totals.unbilled_count}</Typography>
                          </Box>
                          <Box style={{textAlign: 'center'}}>
                            <Typography style={{fontSize: 11, color: 'rgba(255,255,255,0.65)'}}>Total Revenue</Typography>
                            <Typography style={{fontSize: 15, fontWeight: 700, color: '#fff'}}>{formatCurrency(billingStats.grand_totals.appointment_total)}</Typography>
                          </Box>
                          <Typography style={{fontSize: 22, fontWeight: 700, lineHeight: 1, color: '#fff'}}>
                            {cockpitOpen ? '?' : '?'}
                          </Typography>
                        </Box>
                      </Box>

                      {/* --- Cockpit Drawer --- */}
                      <Collapse in={cockpitOpen} timeout="auto" unmountOnExit>
                        <Box style={{backgroundColor: '#fff', borderRadius: '0 0 12px 12px', border: '1px solid #001f3d', borderTop: 'none'}}>
                          <Box style={{borderBottom: '1px solid #dce6f0', backgroundColor: '#f5f9ff'}}>
                            <Tabs
                              value={cockpitTab}
                              onChange={(_, v) => setCockpitTab(v)}
                              indicatorColor="primary"
                              textColor="primary"
                              variant="scrollable"
                              scrollButtons="auto"
                              style={{minHeight: 40}}
                            >
                              <Tab label="Overview" value={0} style={{minHeight: 40, fontSize: 12, fontWeight: 600}} />
                              <Tab label="Month-End" value={1} style={{minHeight: 40, fontSize: 12, fontWeight: 600}} />
                              <Tab label="VAT Report" value={2} style={{minHeight: 40, fontSize: 12, fontWeight: 600}} />
                              <Tab label="Payroll" value={3} style={{minHeight: 40, fontSize: 12, fontWeight: 600}} />
                              <Tab label="Receivables" value={4} style={{minHeight: 40, fontSize: 12, fontWeight: 600}} />
                              <Tab label="Annual" value={5} style={{minHeight: 40, fontSize: 12, fontWeight: 600}} />
                            </Tabs>
                          </Box>

                          {/* ---- TAB 0: Overview ---- */}
                          {cockpitTab === 0 && (
                            <Box style={{padding: 16}}>
                              <Grid container spacing={2} style={{marginBottom: 8}}>
                                <Grid item xs={6} sm={3}>
                                  <Typography style={{fontSize: 13, color: '#666'}}>Total Appointments</Typography>
                                  <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#002D55'}}>{billingStats.grand_totals.count}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Typography style={{fontSize: 13, color: '#666'}}>Billed</Typography>
                                  <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#2E7D32'}}>{billingStats.grand_totals.billed_count}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Typography style={{fontSize: 13, color: '#666'}}>Unbilled</Typography>
                                  <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#F57C00'}}>{billingStats.grand_totals.unbilled_count}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                  <Typography style={{fontSize: 13, color: '#666'}}>Total Amount</Typography>
                                  <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#002D55'}}>{formatCurrency(billingStats.grand_totals.appointment_total)}</Typography>
                                </Grid>
                              </Grid>

                              {commissionSummary.unbilledCount > 0 && (
                                <Box style={{marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(0, 45, 85, 0.12)'}}>
                                  <Box style={{display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap'}}>
                                    <Box>
                                      <Typography style={{fontSize: 13, fontWeight: 700, color: '#002D55'}}>Unbilled Summary</Typography>
                                      <Typography style={{fontSize: 12, color: '#5f6c7b'}}>Revenue and commission for items not yet billed.</Typography>
                                    </Box>
                                    <Box style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                                      <Typography
                                        onClick={() => setBillingCommissionSummaryOpen((prev) => !prev)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(event) => {
                                          if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            setBillingCommissionSummaryOpen((prev) => !prev);
                                          }
                                        }}
                                        style={{fontSize: 12, fontWeight: 700, color: '#5f6c7b', cursor: 'pointer'}}
                                      >
                                        {billingCommissionSummaryOpen ? 'Hide staff breakdown' : 'Show staff breakdown'}
                                      </Typography>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        className={classes.Button3}
                                        onClick={() => setCockpitTab(1)}
                                        style={{fontSize: 11}}
                                      >
                                        Go to Month-End ?
                                      </Button>
                                    </Box>
                                  </Box>
                                  <Grid container spacing={2} style={{marginBottom: 8}}>
                                    <Grid item xs={6} sm={3}>
                                      <Typography style={{fontSize: 13, color: '#666'}}>Unbilled Appointments</Typography>
                                      <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#002D55'}}>{commissionSummary.unbilledCount}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography style={{fontSize: 13, color: '#666'}}>Unbilled Revenue</Typography>
                                      <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#002D55'}}>
                                        {formatCurrency(commissionSummary.totalRevenue, billingStats?.grand_totals?.currency || 'EUR')}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography style={{fontSize: 13, color: '#666'}}>Calculated Commission</Typography>
                                      <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#2E7D32'}}>
                                        {formatCurrency(commissionSummary.totalCommission, billingStats?.grand_totals?.currency || 'EUR')}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography style={{fontSize: 13, color: '#666'}}>Staffs with Activity</Typography>
                                      <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#002D55'}}>
                                        {commissionSummary.byStaff.length}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                  <Collapse in={billingCommissionSummaryOpen} timeout="auto" unmountOnExit>
                                    <Box>
                                      <Grid container spacing={2} style={{padding: '0 0 6px', borderBottom: '1px solid #dbe7f4'}}>
                                        <Grid item xs={3}>
                                          <Typography style={{fontSize: 11, fontWeight: 700, color: '#666'}}>Staff</Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                          <Typography style={{fontSize: 11, fontWeight: 700, color: '#666'}}>Count</Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                          <Typography style={{fontSize: 11, fontWeight: 700, color: '#666'}}>Unbilled Revenue</Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                          <Typography style={{fontSize: 11, fontWeight: 700, color: '#666'}}>Calculated Commission</Typography>
                                        </Grid>
                                      </Grid>
                                      {commissionSummary.byStaff.map((item) => (
                                        <Grid container key={item.staff_id || item.staff_name} spacing={2} style={{padding: '8px 0', borderTop: '1px solid #dbe7f4'}}>
                                          <Grid item xs={3}>
                                            <Typography style={{fontSize: 13, color: '#002D55', fontWeight: 600}}>{item.staff_name}</Typography>
                                          </Grid>
                                          <Grid item xs={3}>
                                            <Typography style={{fontSize: 13, color: '#002D55'}}>{item.count} unbilled</Typography>
                                          </Grid>
                                          <Grid item xs={3}>
                                            <Typography style={{fontSize: 13, color: '#002D55'}}>{formatCurrency(item.revenue, billingStats?.grand_totals?.currency || 'EUR')} revenue</Typography>
                                          </Grid>
                                          <Grid item xs={3}>
                                            <Typography style={{fontSize: 13, color: '#002D55'}}>{formatCurrency(item.commission, billingStats?.grand_totals?.currency || 'EUR')} commission</Typography>
                                          </Grid>
                                        </Grid>
                                      ))}
                                    </Box>
                                  </Collapse>
                                </Box>
                              )}
                            </Box>
                          )}

                          {/* ---- TAB 1: Month-End ---- */}
                          {cockpitTab === 1 && (
                            <Box style={{padding: 16}}>
                              <Grid container spacing={2} alignItems="flex-end" style={{marginBottom: 12}}>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    label="Month"
                                    type="month"
                                    value={monthEndMonth}
                                    onChange={(e) => setMonthEndMonth(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    variant="outlined"
                                    size="small"
                                    margin="none"
                                    fullWidth
                                    className={classes.orangeFocused}
                                  />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                  <Autocomplete
                                    multiple
                                    options={staffList2}
                                    value={staffList2.filter((staff) => monthEndStaffIds.includes(String(staff.user_id || staff.id || '')))}
                                    onChange={(_, value) => setMonthEndStaffIds(value.map((staff) => String(staff.user_id || staff.id || '')))}
                                    getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.email || 'Staff'}
                                    renderTags={(value, getTagProps) => value.map((option, index) => (
                                      <Chip
                                        {...getTagProps({ index })}
                                        key={String(option.user_id || option.id || index)}
                                        label={`${option.first_name || ''} ${option.last_name || ''}`.trim() || option.email || 'Staff'}
                                        size="small"
                                        style={{ backgroundColor: '#eef1fb', color: '#002D55' }}
                                      />
                                    ))}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Selected staff"
                                        placeholder={monthEndStaffIds.length === 0 ? 'All staff' : ''}
                                        variant="outlined"
                                        size="small"
                                        className={classes.orangeFocused}
                                      />
                                    )}
                                  />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField
                                    label="Finalize as"
                                    select
                                    value={monthEndFinalizeMode}
                                    onChange={(e) => {
                                      const nextMode = e.target.value;
                                      setMonthEndFinalizeMode(nextMode);
                                      if (nextMode !== 'staff_subcontractor') {
                                        setMonthEndFinalizeStaffId('');
                                      }
                                    }}
                                    variant="outlined"
                                    size="small"
                                    margin="none"
                                    fullWidth
                                    className={classes.orangeFocused}
                                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                                  >
                                    <MenuItem value="clinic">Clinic billing batch</MenuItem>
                                    <MenuItem value="staff_subcontractor">Subcontractor batch</MenuItem>
                                  </TextField>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField
                                    label="Subcontractor"
                                    select
                                    disabled={monthEndFinalizeMode !== 'staff_subcontractor'}
                                    value={monthEndFinalizeStaffId}
                                    onChange={(e) => setMonthEndFinalizeStaffId(e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    margin="none"
                                    fullWidth
                                    className={classes.orangeFocused}
                                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                                  >
                                    <MenuItem value="">Select subcontractor</MenuItem>
                                    {getMonthEndSubcontractorOptions().map((staff) => (
                                      <MenuItem key={staff.user_id || staff.id} value={staff.user_id || staff.id}>
                                        {`${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.email || 'Staff'}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Grid>
                              </Grid>

                              <Box style={{display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12}}>
                                <Button
                                  variant="contained"
                                  className={classes.Button}
                                  onClick={() => {
                                    fetchMonthEndReport();
                                    fetchMonthEndSnapshots({ silent: true });
                                  }}
                                  disabled={monthEndReportLoading}
                                >
                                  {monthEndReportLoading ? 'Loading...' : 'Load Report'}
                                </Button>
                                <Button
                                  variant="outlined"
                                  className={classes.Button3}
                                  onClick={handlePreviewMonthEndCandidates}
                                  disabled={monthEndReportLoading || !monthEndReport?.candidate_summary?.count}
                                >
                                  Review & Finish Month
                                </Button>
                                <Button
                                  variant="outlined"
                                  className={classes.Button3}
                                  onClick={handleDownloadMonthEndReportPDF}
                                  disabled={!monthEndReport}
                                  startIcon={<GetAppIcon />}
                                >
                                  Report PDF
                                </Button>
                              </Box>

                              {monthEndReportError && (
                                <Typography color="error" style={{marginBottom: 12, fontSize: 12}}>
                                  {monthEndReportError}
                                </Typography>
                              )}

                              {monthEndReport && (
                                <Box id="month-end-report-printable">
                                  <Typography style={{fontSize: 18, fontWeight: 700, color: '#002D55', marginBottom: 4}}>
                                    Month-End Revenue Report
                                  </Typography>
                                  <Typography style={{fontSize: 12, color: '#666', marginBottom: 12}}>
                                    Reporting month {monthEndMonth || 'selected month'}{billingPayerType ? ` | Payer filtered to ${billingPayerType}` : ''}{monthEndStaffIds.length ? ` | ${monthEndStaffIds.length} staff selected` : ' | All staff'} | Finalization mode {monthEndFinalizeMode === 'staff_subcontractor' ? 'Subcontractor batch' : 'Clinic billing batch'}
                                  </Typography>

                                  <Grid container spacing={2} style={{marginBottom: 12}}>
                                    <Grid item xs={6} sm={3}>
                                      <Typography style={{fontSize: 13, color: '#666'}}>Appointments</Typography>
                                      <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#002D55'}}>{monthEndReport.report_summary?.appointment_count || 0}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography style={{fontSize: 13, color: '#666'}}>Revenue</Typography>
                                      <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#002D55'}}>{formatCurrency(monthEndReport.report_summary?.total_revenue)}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography style={{fontSize: 13, color: '#666'}}>Unbilled</Typography>
                                      <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#F57C00'}}>{formatCurrency(monthEndReport.report_summary?.unbilled_revenue)}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography style={{fontSize: 13, color: '#666'}}>Billed</Typography>
                                      <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#2E7D32'}}>{formatCurrency(monthEndReport.report_summary?.billed_revenue)}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography style={{fontSize: 13, color: '#666'}}>Commission</Typography>
                                      <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#2E7D32'}}>{formatCurrency(monthEndReport.report_summary?.total_commission)}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography style={{fontSize: 13, color: '#666'}}>VAT</Typography>
                                      <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#C62828'}}>{formatCurrency(monthEndReport.report_summary?.total_vat)}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography style={{fontSize: 13, color: '#666'}}>Employee Salary Base</Typography>
                                      <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#002D55'}}>{formatCurrency(monthEndReport.report_summary?.employee_salary_total)}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography style={{fontSize: 13, color: '#666'}}>Estimated People Cost</Typography>
                                      <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#002D55'}}>{formatCurrency(monthEndReport.report_summary?.total_estimated_people_cost)}</Typography>
                                    </Grid>
                                  </Grid>

                                  <Box style={{marginBottom: 12, padding: '10px 12px', backgroundColor: '#fff', borderRadius: 10, border: '1px solid #ddd5f2'}}>
                                    <Typography style={{fontSize: 12, fontWeight: 700, color: '#002D55', marginBottom: 6}}>
                                      Invoice Review Queue
                                    </Typography>
                                    <Typography style={{fontSize: 12, color: '#5f6c7b', marginBottom: 8}}>
                                      {monthEndReport.candidate_summary?.count || 0} unbilled appointments worth {formatCurrency(monthEndReport.candidate_summary?.total_revenue)} are ready to review before invoice creation.
                                    </Typography>
                                    {(monthEndReport.candidate_summary?.invoice_groups || []).length === 0 ? (
                                      <Typography style={{fontSize: 12, color: '#666'}}>No invoice groups are waiting for month-end billing.</Typography>
                                    ) : (
                                      <TableContainer component={Card} elevation={0} style={{borderRadius: 12, overflow: 'hidden', border: '1px solid #ddd5f2'}}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow style={{backgroundColor: '#ede7fb'}}>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Patient</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Payer</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}} align="right">Appointments</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}} align="right">Revenue</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {(monthEndReport.candidate_summary?.invoice_groups || []).map((group, index) => (
                                              <TableRow key={`${group.patient_name}-${group.payer}-${index}`}>
                                                <TableCell style={{fontSize: 12}}>{group.patient_name || 'Patient'}</TableCell>
                                                <TableCell style={{fontSize: 12}}>{group.payer === 'insurance' ? 'Insurance' : 'Patient'}</TableCell>
                                                <TableCell style={{fontSize: 12}} align="right">{group.count}</TableCell>
                                                <TableCell style={{fontSize: 12, fontWeight: 600}} align="right">{formatCurrency(group.total_amount)}</TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    )}
                                  </Box>

                                  <TableContainer component={Card} elevation={0} style={{borderRadius: 12, overflow: 'hidden', border: '1px solid #ddd5f2', marginBottom: 12}}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow style={{backgroundColor: '#ede7fb'}}>
                                          <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Staff</TableCell>
                                          <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Engagement</TableCell>
                                          <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Compensation</TableCell>
                                          <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}} align="right">Weekly hours</TableCell>
                                          <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}} align="right">Absence days</TableCell>
                                          <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}} align="right">Revenue</TableCell>
                                          <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}} align="right">Unbilled</TableCell>
                                          <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}} align="right">Commission</TableCell>
                                          <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}} align="right">Estimated Payout</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {(monthEndReport.staff_summary || []).map((staff) => (
                                          <TableRow key={staff.staff_id || staff.staff_name}>
                                            <TableCell style={{fontSize: 12, color: '#002D55', fontWeight: 600}}>
                                              <Box>
                                                <Typography style={{fontSize: 12, color: '#002D55', fontWeight: 600}}>{staff.staff_name}</Typography>
                                                <Typography style={{fontSize: 11, color: '#6f7b87'}}>
                                                  {(() => {
                                                    const absenceRow = (monthEndAbsenceReport?.staff_summary || []).find((item) => String(item.staff_id) === String(staff.staff_id));
                                                    if (!absenceRow) {
                                                      return 'No absence records loaded';
                                                    }
                                                    const parts = [];
                                                    if (absenceRow.vacation_days) parts.push(`Vacation ${absenceRow.vacation_days}`);
                                                    if (absenceRow.sick_day_days) parts.push(`Sick ${absenceRow.sick_day_days}`);
                                                    if (absenceRow.paid_leave_days) parts.push(`Paid ${absenceRow.paid_leave_days}`);
                                                    if (absenceRow.agreed_absence_days) parts.push(`Agreed ${absenceRow.agreed_absence_days}`);
                                                    return parts.length ? parts.join(' | ') : 'No absence days';
                                                  })()}
                                                </Typography>
                                              </Box>
                                            </TableCell>
                                            <TableCell style={{fontSize: 12}}>{formatStaffEngagementTypeLabel(staff.staff_engagement_type)}</TableCell>
                                            <TableCell style={{fontSize: 12}}>{formatStaffCompensationBasisLabel(staff.compensation_basis)}</TableCell>
                                            <TableCell style={{fontSize: 12}} align="right">{(() => {
                                              const staffProfile = staffList2.find((item) => String(item.user_id || item.id || '') === String(staff.staff_id));
                                              return staffProfile?.weekly_work_hours || staff.weekly_work_hours || '37.5';
                                            })()}</TableCell>
                                            <TableCell style={{fontSize: 12}} align="right">{(monthEndAbsenceReport?.staff_summary || []).find((item) => String(item.staff_id) === String(staff.staff_id))?.absence_days || 0}</TableCell>
                                            <TableCell style={{fontSize: 12}} align="right">{formatCurrency(staff.total_revenue)}</TableCell>
                                            <TableCell style={{fontSize: 12}} align="right">{formatCurrency(staff.unbilled_revenue)}</TableCell>
                                            <TableCell style={{fontSize: 12, color: '#2E7D32', fontWeight: 600}} align="right">{formatCurrency(staff.calculated_commission)}</TableCell>
                                            <TableCell style={{fontSize: 12, color: '#002D55', fontWeight: 700}} align="right">{formatCurrency(staff.estimated_payout)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>

                                  <Box style={{padding: '10px 12px', backgroundColor: '#fff', borderRadius: 10, border: '1px solid #ddd5f2'}}>
                                    <Typography style={{fontSize: 12, fontWeight: 700, color: '#002D55', marginBottom: 6}}>
                                      Bank Statement Reconciliation
                                    </Typography>
                                    <Typography style={{fontSize: 12, color: '#5f6c7b', marginBottom: 10}}>
                                      Upload a bank CSV to auto-match invoices by invoice number, reference, or unique amount and mark them paid or partly paid.
                                    </Typography>
                                    <Box style={{display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8}}>
                                      <Button
                                        variant="contained"
                                        className={classes.Button}
                                        onClick={handleBankStatementUploadClick}
                                        disabled={bankStatementUploadLoading}
                                      >
                                        {bankStatementUploadLoading ? 'Processing CSV...' : 'Choose CSV file'}
                                      </Button>
                                    </Box>
                                    {bankStatementUploadError && (
                                      <Typography style={{fontSize: 12, color: '#C62828', marginBottom: 8}}>
                                        {bankStatementUploadError}
                                      </Typography>
                                    )}
                                    {bankStatementUploadResult ? (
                                      <Box>
                                        <Typography style={{fontSize: 12, color: '#002D55', marginBottom: 4}}>
                                          {bankStatementUploadResult.fileName} processed: {bankStatementUploadResult.rows_processed || 0} rows, {bankStatementUploadResult.matched_count || 0} matched, {bankStatementUploadResult.paid_count || 0} paid, {bankStatementUploadResult.partial_count || 0} partially paid, {bankStatementUploadResult.unmatched_count || 0} unmatched.
                                        </Typography>
                                        {Array.isArray(bankStatementUploadResult.matches) && bankStatementUploadResult.matches.length > 0 && (
                                          <TableContainer component={Card} elevation={0} style={{borderRadius: 12, overflow: 'hidden', border: '1px solid #ddd5f2', marginTop: 8}}>
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow style={{backgroundColor: '#f6f3fd'}}>
                                                  <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Invoice</TableCell>
                                                  <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Status</TableCell>
                                                  <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}} align="right">Amount</TableCell>
                                                  <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Reference</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {bankStatementUploadResult.matches.map((matchRow) => (
                                                  <TableRow key={`${matchRow.row}-${matchRow.invoice_id}`}>
                                                    <TableCell style={{fontSize: 12}}>{matchRow.invoice_number}</TableCell>
                                                    <TableCell style={{fontSize: 12, fontWeight: 600, color: matchRow.status === 'paid' ? '#2E7D32' : '#F57C00'}}>
                                                      {matchRow.status || matchRow.payment_status}
                                                    </TableCell>
                                                    <TableCell style={{fontSize: 12}} align="right">{matchRow.amount}</TableCell>
                                                    <TableCell style={{fontSize: 12}}>{matchRow.text}</TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </TableContainer>
                                        )}
                                        {Array.isArray(bankStatementUploadResult.unmatched_rows) && bankStatementUploadResult.unmatched_rows.length > 0 && (
                                          <Typography style={{fontSize: 12, color: '#666', marginTop: 8}}>
                                            Unmatched rows: {bankStatementUploadResult.unmatched_rows.length}
                                          </Typography>
                                        )}
                                      </Box>
                                    ) : (
                                      <Typography style={{fontSize: 12, color: '#666'}}>
                                        No bank CSV has been uploaded yet.
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          )}

                          {/* ---- TAB 2: VAT Report ---- */}
                          {cockpitTab === 2 && (
                            <Box style={{padding: 16}}>
                              {!billingStats ? (
                                <Typography style={{fontSize: 13, color: '#666'}}>Run a billing search first to see VAT data.</Typography>
                              ) : (
                                <Box>
                                  <Box style={{display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8}}>
                                    <Box style={{display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center'}}>
                                      <TextField
                                        label="Period"
                                        select
                                        value={billingVatPeriod}
                                        onChange={(e) => setBillingVatPeriod(e.target.value)}
                                        variant="outlined"
                                        size="small"
                                        className={classes.orangeFocused}
                                        style={{minWidth: 160}}
                                        SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                                      >
                                        <MenuItem value="month">Monthly</MenuItem>
                                        <MenuItem value="quarter">Quarterly</MenuItem>
                                        <MenuItem value="half_year">Bi-yearly</MenuItem>
                                        <MenuItem value="year">Yearly</MenuItem>
                                      </TextField>
                                      <Button
                                        variant="contained"
                                        className={classes.Button}
                                        onClick={handleDownloadVatSummaryPDF}
                                        startIcon={<GetAppIcon />}
                                      >
                                        VAT PDF
                                      </Button>
                                    </Box>
                                  </Box>
                                  <Box id="billing-vat-summary-printable">
                                    <Typography style={{fontSize: 18, fontWeight: 700, color: '#002D55', marginBottom: 6}}>
                                      VAT Accounting Report
                                    </Typography>
                                    <Typography style={{fontSize: 12, color: '#666', marginBottom: 12}}>
                                      {vatSummary.rangeLabel} | {billingVatPeriod === 'month' ? 'Monthly' : billingVatPeriod === 'quarter' ? 'Quarterly' : billingVatPeriod === 'half_year' ? 'Bi-yearly' : 'Yearly'} grouping | Accounting date uses invoice date when available
                                      {billingSortByStaff ? ` | Staff filtered` : ''}
                                    </Typography>
                                    <Grid container spacing={2} style={{marginBottom: 12}}>
                                      <Grid item xs={6} sm={3}>
                                        <Typography style={{fontSize: 13, color: '#666'}}>Filtered Appointments</Typography>
                                        <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#002D55'}}>{vatSummary.appointmentCount}</Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={3}>
                                        <Typography style={{fontSize: 13, color: '#666'}}>VAT Services</Typography>
                                        <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#002D55'}}>{vatSummary.vatAppointmentCount}</Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={2}>
                                        <Typography style={{fontSize: 13, color: '#666'}}>Invoiced VAT</Typography>
                                        <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#2E7D32'}}>{vatSummary.invoicedVatCount}</Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={2}>
                                        <Typography style={{fontSize: 13, color: '#666'}}>Unbilled VAT</Typography>
                                        <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#F57C00'}}>{vatSummary.unbilledVatCount}</Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={2}>
                                        <Typography style={{fontSize: 13, color: '#666'}}>Service Total</Typography>
                                        <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#002D55'}}>
                                          {formatCurrency(vatSummary.totalServiceAmount, vatSummary.currency)}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={2}>
                                        <Typography style={{fontSize: 13, color: '#666'}}>VAT Total</Typography>
                                        <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#C62828'}}>
                                          {formatCurrency(vatSummary.totalVatAmount, vatSummary.currency)}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={2}>
                                        <Typography style={{fontSize: 13, color: '#666'}}>Gross Total</Typography>
                                        <Typography style={{fontSize: 16, fontWeight: 'bold', color: '#2E7D32'}}>
                                          {formatCurrency(vatSummary.totalGrossAmount, vatSummary.currency)}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                    {vatSummary.periods.length === 0 ? (
                                      <Typography style={{fontSize: 13, color: '#666', padding: '8px 0'}}>
                                        No VAT-enabled services were found for the current filters.
                                      </Typography>
                                    ) : (
                                      <TableContainer component={Card} elevation={0} style={{borderRadius: 12, overflow: 'hidden', border: '1px solid #ead9b7'}}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow style={{backgroundColor: '#f8edd2'}}>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Period</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Accounting Date</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Invoice #</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Status</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Patient</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Payer</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Staff</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Service</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}} align="right">VAT %</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}} align="right">Net</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}} align="right">VAT</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}} align="right">Gross</TableCell>
                                              <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 12}}>Due Date</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {vatSummary.periods.map((period) => (
                                              <React.Fragment key={period.key}>
                                                <TableRow style={{backgroundColor: '#fcf4df'}}>
                                                  <TableCell colSpan={12} style={{fontSize: 12, color: '#002D55', fontWeight: 700}}>
                                                    {period.label} | {period.vatAppointmentCount} VAT services | {period.invoicedVatCount} invoiced | {period.unbilledVatCount} unbilled | {formatCurrency(period.serviceAmount, vatSummary.currency)} net | {formatCurrency(period.vatAmount, vatSummary.currency)} VAT | {formatCurrency(period.grossAmount, vatSummary.currency)} gross
                                                  </TableCell>
                                                </TableRow>
                                                {period.items.length === 0 ? (
                                                  <TableRow>
                                                    <TableCell colSpan={13} style={{fontSize: 11, color: '#999', fontStyle: 'italic', paddingLeft: 24}}>No VAT-applicable services this period</TableCell>
                                                  </TableRow>
                                                ) : period.items.map((item) => (
                                                  <TableRow key={item.key}>
                                                    <TableCell style={{fontSize: 12, color: '#002D55'}}>{period.label}</TableCell>
                                                    <TableCell style={{fontSize: 12}}>{item.accountingDateLabel}</TableCell>
                                                    <TableCell style={{fontSize: 12}}>{item.invoiceNumber}</TableCell>
                                                    <TableCell style={{fontSize: 12}}>{item.invoiceStatusLabel}</TableCell>
                                                    <TableCell style={{fontSize: 12}}>{item.patientName}</TableCell>
                                                    <TableCell style={{fontSize: 12}}>{item.payerLabel}</TableCell>
                                                    <TableCell style={{fontSize: 12}}>{item.staffLabel}</TableCell>
                                                    <TableCell style={{fontSize: 12}}>{item.serviceName}</TableCell>
                                                    <TableCell style={{fontSize: 12}} align="right">{item.vatRate.toFixed(2)}%</TableCell>
                                                    <TableCell style={{fontSize: 12}} align="right">{formatCurrency(item.serviceAmount, vatSummary.currency)}</TableCell>
                                                    <TableCell style={{fontSize: 12, color: '#C62828', fontWeight: 600}} align="right">{formatCurrency(item.vatAmount, vatSummary.currency)}</TableCell>
                                                    <TableCell style={{fontSize: 12, color: '#2E7D32', fontWeight: 600}} align="right">{formatCurrency(item.grossAmount, vatSummary.currency)}</TableCell>
                                                    <TableCell style={{fontSize: 12}}>{item.dueDateLabel}</TableCell>
                                                  </TableRow>
                                                ))}
                                              </React.Fragment>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    )}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          )}

                          {/* ---- TAB 3: Payroll & Payslips ---- */}
                          {cockpitTab === 3 && (
                            <Box style={{padding: 16}}>
                              <Typography style={{fontSize: 14, fontWeight: 700, color: '#002D55', marginBottom: 12}}>Payroll &amp; Payslips</Typography>

                              {/* Subcontractor Settlement */}
                              <Box style={{background: '#f8fbff', border: '1px solid #d7e2ef', borderRadius: 8, padding: 16, marginBottom: 16}}>
                                <Typography style={{fontSize: 13, fontWeight: 700, color: '#002D55', marginBottom: 8}}>Subcontractor Settlement</Typography>
                                <Box style={{display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap'}}>
                                  <select
                                    value={settlementStaffId}
                                    onChange={(e) => setSettlementStaffId(e.target.value)}
                                    style={{fontSize: 12, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', minWidth: 180}}
                                  >
                                    <option value="">Select subcontractor...</option>
                                    {staffList2.filter((s) => s.staff_engagement_type === 'subcontractor' || (s.compensation_basis || '').includes('subcontractor')).map((s) => (
                                      <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                                    ))}
                                  </select>
                                  <Button size="small" variant="contained" style={{background: '#002D55', color: '#fff', fontSize: 11}} onClick={fetchSettlementData} disabled={settlementLoading || !settlementStaffId}>
                                    {settlementLoading ? 'Loading...' : 'Load Settlement'}
                                  </Button>
                                  {settlementData && (
                                    <Button size="small" variant="outlined" style={{fontSize: 11}} onClick={() => handleDownloadSettlementPDF(settlementData)}>
                                      PDF
                                    </Button>
                                  )}
                                </Box>
                                {settlementError && <Typography style={{fontSize: 12, color: '#c62828'}}>{settlementError}</Typography>}
                                {settlementData && (
                                  <Box>
                                    <Grid container spacing={2} style={{marginBottom: 8}}>
                                      {[
                                        ['Staff', settlementData.staff_name],
                                        ['Period', settlementData.month],
                                        ['Engagement', settlementData.engagement_type],
                                        ['Appointments', settlementData.appointment_count],
                                        ['Gross revenue', settlementData.gross_revenue],
                                        ['Commission earned', settlementData.commission_earned],
                                        ['Clinic cut', settlementData.clinic_cut],
                                        ['Net payout', settlementData.net_payout],
                                      ].map(([label, val]) => (
                                        <Grid item xs={6} sm={3} key={label}>
                                          <Typography style={{fontSize: 11, color: '#666'}}>{label}</Typography>
                                          <Typography style={{fontSize: 13, fontWeight: 600, color: '#002D55'}}>{val}</Typography>
                                        </Grid>
                                      ))}
                                    </Grid>
                                    {(settlementData.appointment_rows || []).length > 0 && (
                                      <TableContainer component={Card} elevation={0} style={{borderRadius: 8, border: '1px solid #d7e2ef'}}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow style={{background: '#f0f4f8'}}>
                                              <TableCell style={{fontSize: 11, fontWeight: 700}}>Date</TableCell>
                                              <TableCell style={{fontSize: 11, fontWeight: 700}}>Patient</TableCell>
                                              <TableCell style={{fontSize: 11, fontWeight: 700}}>Service</TableCell>
                                              <TableCell style={{fontSize: 11, fontWeight: 700}} align="right">Amount</TableCell>
                                              <TableCell style={{fontSize: 11, fontWeight: 700}} align="right">Comm%</TableCell>
                                              <TableCell style={{fontSize: 11, fontWeight: 700}} align="right">Commission</TableCell>
                                              <TableCell style={{fontSize: 11, fontWeight: 700}}>Status</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {settlementData.appointment_rows.map((r, idx) => (
                                              <TableRow key={idx}>
                                                <TableCell style={{fontSize: 11}}>{r.date}</TableCell>
                                                <TableCell style={{fontSize: 11}}>{r.patient}</TableCell>
                                                <TableCell style={{fontSize: 11}}>{r.service}</TableCell>
                                                <TableCell style={{fontSize: 11}} align="right">{r.amount}</TableCell>
                                                <TableCell style={{fontSize: 11}} align="right">{r.commission_rate}%</TableCell>
                                                <TableCell style={{fontSize: 11}} align="right">{r.commission_amount}</TableCell>
                                                <TableCell style={{fontSize: 11}}>{r.invoice_status}</TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    )}
                                  </Box>
                                )}
                              </Box>

                              {/* Staff Payslip */}
                              <Box style={{background: '#f8fbff', border: '1px solid #d7e2ef', borderRadius: 8, padding: 16}}>
                                <Typography style={{fontSize: 13, fontWeight: 700, color: '#002D55', marginBottom: 8}}>Staff Payslips</Typography>
                                <Box style={{display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12}}>
                                  <select
                                    value={payslipSelectedStaffId}
                                    onChange={(e) => setPayslipSelectedStaffId(e.target.value)}
                                    style={{fontSize: 12, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', minWidth: 180}}
                                  >
                                    <option value="">Select staff member...</option>
                                    {staffList2.map((s) => (
                                      <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                                    ))}
                                  </select>
                                  <select
                                    value={payslipLocale}
                                    onChange={(e) => setPayslipLocale(e.target.value)}
                                    style={{fontSize: 12, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', minWidth: 120}}
                                  >
                                    <option value="da">Dansk</option>
                                    <option value="en">English</option>
                                  </select>
                                  <Button
                                    size="small" variant="contained"
                                    style={{background: '#2E7D32', color: '#fff', fontSize: 11}}
                                    disabled={payslipSending || !payslipSelectedStaffId || !monthEndMonth}
                                    onClick={() => handleSendPayslips(monthEndMonth, [payslipSelectedStaffId])}
                                  >
                                    {payslipSending ? 'Sending...' : 'Send payslip email'}
                                  </Button>
                                  {monthEndReport && monthEndReport.staff_summary && (
                                    <Button
                                      size="small" variant="outlined" style={{fontSize: 11}}
                                      onClick={() => {
                                        const entry = (monthEndReport.staff_summary || []).find((s) => String(s.staff_id) === String(payslipSelectedStaffId));
                                        handleDownloadPayslipPDF(entry, monthEndMonth, payslipLocale);
                                      }}
                                      disabled={!payslipSelectedStaffId}
                                    >
                                      {payslipLocale === 'da' ? 'Download PDF (DA)' : 'Download PDF (EN)'}
                                    </Button>
                                  )}
                                </Box>
                                {payslipSendResult && (
                                  <Box style={{background: payslipSendResult.error ? '#fdecea' : '#e8f5e9', borderRadius: 6, padding: '8px 12px', fontSize: 12}}>
                                    {payslipSendResult.error ? payslipSendResult.error : `Sent ${payslipSendResult.sent_count || 0} of ${payslipSendResult.total || 0} payslips`}
                                  </Box>
                                )}
                                {monthEndAbsenceReport && (
                                  <Box style={{marginTop: 12, marginBottom: 12, background: '#fff', border: '1px solid #d7e2ef', borderRadius: 8, padding: 12}}>
                                    <Grid container spacing={2}>
                                      <Grid item xs={6} sm={3}>
                                        <Typography style={{fontSize: 11, color: '#666'}}>Absence records</Typography>
                                        <Typography style={{fontSize: 15, fontWeight: 700, color: '#002D55'}}>{monthEndAbsenceReport.total_records || 0}</Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={3}>
                                        <Typography style={{fontSize: 11, color: '#666'}}>Absence days</Typography>
                                        <Typography style={{fontSize: 15, fontWeight: 700, color: '#002D55'}}>{monthEndAbsenceReport.total_days || 0}</Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={3}>
                                        <Typography style={{fontSize: 11, color: '#666'}}>Vacation days</Typography>
                                        <Typography style={{fontSize: 15, fontWeight: 700, color: '#002D55'}}>{monthEndAbsenceReport.by_type?.vacation || 0}</Typography>
                                      </Grid>
                                      <Grid item xs={6} sm={3}>
                                        <Typography style={{fontSize: 11, color: '#666'}}>Sick days</Typography>
                                        <Typography style={{fontSize: 15, fontWeight: 700, color: '#002D55'}}>{monthEndAbsenceReport.by_type?.sick_day || 0}</Typography>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                )}

                                <Box style={{marginTop: 12, marginBottom: 12, background: '#fff', border: '1px solid #d7e2ef', borderRadius: 8, padding: 16}}>
                                  <Box style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8}}>
                                    <Typography style={{fontSize: 13, fontWeight: 700, color: '#002D55'}}>Archived Payslips</Typography>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      style={{fontSize: 11}}
                                      onClick={fetchPayslipArchives}
                                      disabled={payslipArchivesLoading || !monthEndMonth}
                                    >
                                      {payslipArchivesLoading ? 'Loading...' : 'Load archives'}
                                    </Button>
                                  </Box>
                                  <Typography style={{fontSize: 12, color: '#666', marginBottom: 12}}>
                                    Loads finalized payslips stored after email send for the selected month. Filtered by the selected staff member when one is chosen.
                                  </Typography>
                                  {payslipArchivesError && (
                                    <Box style={{background: '#fdecea', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#c62828', marginBottom: 12}}>
                                      {payslipArchivesError}
                                    </Box>
                                  )}
                                  {(payslipArchives || []).length === 0 ? (
                                    <Typography style={{fontSize: 12, color: '#999', fontStyle: 'italic'}}>
                                      No archived payslips loaded yet.
                                    </Typography>
                                  ) : (
                                    <TableContainer component={Card} elevation={0} style={{borderRadius: 8, border: '1px solid #d7e2ef'}}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow style={{background: '#f0f4f8'}}>
                                            <TableCell style={{fontSize: 11, fontWeight: 700}}>Month</TableCell>
                                            <TableCell style={{fontSize: 11, fontWeight: 700}}>Staff</TableCell>
                                            <TableCell style={{fontSize: 11, fontWeight: 700}} align="right">Adjustment</TableCell>
                                            <TableCell style={{fontSize: 11, fontWeight: 700}}>Created</TableCell>
                                            <TableCell style={{fontSize: 11, fontWeight: 700}} align="right">Actions</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {payslipArchives.map((archive) => (
                                            <TableRow key={archive.id} hover>
                                              <TableCell style={{fontSize: 11}}>{archive.report_month}</TableCell>
                                              <TableCell style={{fontSize: 11}}>{archive.staff_member_email || archive.staff_member_id}</TableCell>
                                              <TableCell style={{fontSize: 11}} align="right">{formatCurrency(archive.adjustment_total)}</TableCell>
                                              <TableCell style={{fontSize: 11}}>{archive.created_at ? new Date(archive.created_at).toLocaleString() : ''}</TableCell>
                                              <TableCell style={{fontSize: 11}} align="right">
                                                <Box style={{display: 'flex', justifyContent: 'flex-end', gap: 8}}>
                                                  <Button size="small" variant="outlined" style={{fontSize: 10}} onClick={() => openPayslipArchivePdf(archive)}>
                                                    PDF
                                                  </Button>
                                                  <Button size="small" variant="outlined" style={{fontSize: 10}} onClick={() => downloadPayslipArchiveHtml(archive)}>
                                                    HTML
                                                  </Button>
                                                </Box>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  )}
                                </Box>

                                <TableContainer component={Card} elevation={0} style={{borderRadius: 8, border: '1px solid #d7e2ef'}}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow style={{background: '#f0f4f8'}}>
                                        <TableCell style={{fontSize: 11, fontWeight: 700}}>Month</TableCell>
                                        <TableCell style={{fontSize: 11, fontWeight: 700}} align="right">Appointments</TableCell>
                                        <TableCell style={{fontSize: 11, fontWeight: 700}} align="right">Billed</TableCell>
                                        <TableCell style={{fontSize: 11, fontWeight: 700}} align="right">Collected (paid)</TableCell>
                                        <TableCell style={{fontSize: 11, fontWeight: 700}} align="right">Partial</TableCell>
                                        <TableCell style={{fontSize: 11, fontWeight: 700}} align="right">Outstanding</TableCell>
                                        <TableCell style={{fontSize: 11, fontWeight: 700}} align="right">Collection %</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {billingStats.month_groups.map((mg) => {
                                        const rows = mg.rows || [];
                                        const billedRows = rows.filter((r) => r.invoice_id);
                                        const billedAmt = billedRows.reduce((acc, r) => acc + getInvoiceBilledAmount(r), 0);
                                        const collectedAmt = billedRows.reduce((acc, r) => acc + getInvoicePaidAmount(r), 0);
                                        const partialRows = billedRows.filter((r) => {
                                          const billed = getInvoiceBilledAmount(r);
                                          const paid = getInvoicePaidAmount(r);
                                          return paid > 0 && paid < billed;
                                        });
                                        const partialAmt = partialRows.reduce((acc, r) => acc + getInvoicePaidAmount(r), 0);
                                        const outstandingAmt = billedRows.reduce((acc, r) => acc + Math.max((getInvoiceBilledAmount(r) - getInvoicePaidAmount(r)), 0), 0);
                                        const collPct = billedAmt > 0 ? ((collectedAmt / billedAmt) * 100).toFixed(0) : '-';
                                        const collPctColor = collPct !== '-' && Number(collPct) >= 80 ? '#2E7D32' : '#c62828';
                                        return (
                                          <TableRow key={mg.month}>
                                            <TableCell style={{fontSize: 11}}>{mg.month_display}</TableCell>
                                            <TableCell style={{fontSize: 11}} align="right">{rows.length}</TableCell>
                                            <TableCell style={{fontSize: 11}} align="right">{billedAmt.toFixed(2)}</TableCell>
                                            <TableCell style={{fontSize: 11, color: '#2E7D32', fontWeight: 600}} align="right">{collectedAmt.toFixed(2)}</TableCell>
                                            <TableCell style={{fontSize: 11, color: '#e65100'}} align="right">{partialAmt.toFixed(2)}</TableCell>
                                            <TableCell style={{fontSize: 11, color: '#c62828'}} align="right">{outstandingAmt.toFixed(2)}</TableCell>
                                            <TableCell style={{fontSize: 11, color: collPctColor, fontWeight: 600}} align="right">{collPct}{collPct !== '-' ? '%' : ''}</TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>

                              {/* Accounts Receivable Aging */}
                              <Box style={{background: '#f8fbff', border: '1px solid #d7e2ef', borderRadius: 8, padding: 16, marginBottom: 16}}>
                                <Box style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8}}>
                                  <Typography style={{fontSize: 13, fontWeight: 700, color: '#002D55', flex: 1}}>Accounts Receivable Aging</Typography>
                                  <Button size="small" variant="contained" style={{background: '#002D55', color: '#fff', fontSize: 11}} onClick={fetchAgingReport} disabled={agingLoading}>
                                    {agingLoading ? 'Loading...' : 'Load Aging Report'}
                                  </Button>
                                </Box>
                                {agingError && <Typography style={{fontSize: 12, color: '#c62828'}}>{agingError}</Typography>}
                                {agingReport && (
                                  <Box>
                                    <Grid container spacing={2} style={{marginBottom: 12}}>
                                      {Object.entries(agingReport.buckets || {}).map(([key, bucket]) => {
                                        const colors = {current: '#2E7D32', '1_30': '#f9a825', '31_60': '#e65100', '61_90': '#c62828', '90_plus': '#7b0000', no_due_date: '#757575'};
                                        return (
                                          <Grid item xs={6} sm={2} key={key}>
                                            <Box style={{background: '#fff', border: `2px solid ${colors[key] || '#ccc'}`, borderRadius: 8, padding: '8px 12px', textAlign: 'center'}}>
                                              <Typography style={{fontSize: 11, color: colors[key] || '#666', fontWeight: 700}}>{bucket.label}</Typography>
                                              <Typography style={{fontSize: 14, fontWeight: 700, color: colors[key] || '#333'}}>{bucket.count}</Typography>
                                              <Typography style={{fontSize: 11, color: '#555'}}>{bucket.amount}</Typography>
                                            </Box>
                                          </Grid>
                                        );
                                      })}
                                    </Grid>
                                    <Box style={{display: 'flex', gap: 16, marginBottom: 8}}>
                                      <Typography style={{fontSize: 12, color: '#666'}}>Total outstanding: <strong>{agingReport.total_outstanding}</strong></Typography>
                                      <Typography style={{fontSize: 12, color: '#c62828'}}>Total overdue: <strong>{agingReport.total_overdue}</strong> ({agingReport.total_overdue_count} invoices)</Typography>
                                    </Box>
                                    {Object.entries(agingReport.buckets || {}).filter(([, b]) => (b.invoices || []).length > 0).map(([key, bucket]) => (
                                      <Box key={key} style={{marginBottom: 12}}>
                                        <Typography style={{fontSize: 12, fontWeight: 700, color: '#002D55', marginBottom: 4}}>{bucket.label}</Typography>
                                        <TableContainer component={Card} elevation={0} style={{borderRadius: 8, border: '1px solid #d7e2ef'}}>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow style={{background: '#f0f4f8'}}>
                                                <TableCell style={{fontSize: 10, fontWeight: 700}}>Invoice #</TableCell>
                                                <TableCell style={{fontSize: 10, fontWeight: 700}}>Patient</TableCell>
                                                <TableCell style={{fontSize: 10, fontWeight: 700}}>Payer</TableCell>
                                                <TableCell style={{fontSize: 10, fontWeight: 700}}>Status</TableCell>
                                                <TableCell style={{fontSize: 10, fontWeight: 700}} align="right">Amount</TableCell>
                                                <TableCell style={{fontSize: 10, fontWeight: 700}} align="right">Paid</TableCell>
                                                <TableCell style={{fontSize: 10, fontWeight: 700}}>Due Date</TableCell>
                                                <TableCell style={{fontSize: 10, fontWeight: 700}} align="right">Days overdue</TableCell>
                                                <TableCell style={{fontSize: 10, fontWeight: 700}}>Actions</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {bucket.invoices.map((inv) => (
                                                <TableRow key={inv.invoice_id}>
                                                  <TableCell style={{fontSize: 10}}>
                                                    <Box style={{display: 'flex', alignItems: 'center', gap: 3}}>
                                                      <span>{inv.invoice_number || '-'}</span>
                                                      {inv.ean_submission_status && ['delivered', 'accepted'].includes(inv.ean_submission_status) && (
                                                        <Tooltip title="Sent via EAN successfully">
                                                          <CheckCircleIcon style={{fontSize: 11, color: '#2E7D32'}} />
                                                        </Tooltip>
                                                      )}
                                                      {inv.sent_date && (
                                                        <Tooltip title={inv.email_opened ? 'Invoice viewed by patient' : 'Invoice sent - not yet viewed'}>
                                                          <MarkunreadMailboxIcon style={{fontSize: 11, color: inv.email_opened ? '#1565C0' : '#9E9E9E'}} />
                                                        </Tooltip>
                                                      )}
                                                    </Box>
                                                  </TableCell>
                                                  <TableCell style={{fontSize: 10}}>{inv.patient_name}</TableCell>
                                                  <TableCell style={{fontSize: 10}}>{inv.payer}</TableCell>
                                                  <TableCell style={{fontSize: 10}}>{inv.status}</TableCell>
                                                  <TableCell style={{fontSize: 10}} align="right">{inv.total_amount}</TableCell>
                                                  <TableCell style={{fontSize: 10}} align="right">
                                                    {editingPaidInvoiceId === inv.invoice_id ? (
                                                      <Box style={{display: 'flex', gap: 4, alignItems: 'center'}}>
                                                        <input
                                                          type="number"
                                                          value={editingPaidAmount}
                                                          onChange={(e) => setEditingPaidAmount(e.target.value)}
                                                          style={{width: 70, fontSize: 10, padding: '2px 4px', border: '1px solid #002D55', borderRadius: 4}}
                                                        />
                                                        <Button size="small" variant="contained" style={{fontSize: 9, padding: '1px 6px', background: '#002D55', color: '#fff', minWidth: 0}} onClick={() => handleSetPaidAmount(inv.invoice_id, editingPaidAmount)}>?</Button>
                                                        <Button size="small" variant="text" style={{fontSize: 9, padding: '1px 4px', minWidth: 0}} onClick={() => setEditingPaidInvoiceId(null)}>?</Button>
                                                      </Box>
                                                    ) : (
                                                      <Box style={{display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end'}}>
                                                        <span>{getInvoicePaidAmount(inv).toFixed(2)}</span>
                                                        <Button size="small" variant="text" style={{fontSize: 9, padding: '0 4px', minWidth: 0, color: '#002D55'}} onClick={() => { setEditingPaidInvoiceId(inv.invoice_id); setEditingPaidAmount(String(getInvoicePaidAmount(inv))); }}>?</Button>
                                                      </Box>
                                                    )}
                                                  </TableCell>
                                                  <TableCell style={{fontSize: 10}}>{inv.due_date || '-'}</TableCell>
                                                  <TableCell style={{fontSize: 10, color: inv.days_overdue > 0 ? '#c62828' : '#2E7D32', fontWeight: 600}} align="right">{inv.days_overdue}</TableCell>
                                                  <TableCell style={{fontSize: 10}}>
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenRowAction(e, { invoice_id: inv.invoice_id, invoice_number: inv.invoice_number, invoice_status: inv.status, patient_name: inv.patient_name, sent_date: inv.sent_date, email_opened: inv.email_opened, ean_submission_status: inv.ean_submission_status, total_amount: inv.total_amount, amount_paid: inv.amount_paid }); }}>
                                                      <MoreVertIcon style={{fontSize: 14}} />
                                                    </IconButton>
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Box>

                              {/* Insurance Claims */}
                              <Box style={{background: '#f8fbff', border: '1px solid #d7e2ef', borderRadius: 8, padding: 16}}>
                                <Typography style={{fontSize: 13, fontWeight: 700, color: '#002D55', marginBottom: 8}}>Insurance Claims</Typography>
                                {billingStats.month_groups.some((mg) => (mg.rows || []).some((r) => (r.payer_type || r.payer || '').toLowerCase().includes('insurance') || (r.billing_type || '').toLowerCase().includes('insurance'))) ? (
                                  <TableContainer component={Card} elevation={0} style={{borderRadius: 8, border: '1px solid #d7e2ef'}}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow style={{background: '#f0f4f8'}}>
                                          <TableCell style={{fontSize: 11, fontWeight: 700}}>Invoice #</TableCell>
                                          <TableCell style={{fontSize: 11, fontWeight: 700}}>Patient</TableCell>
                                          <TableCell style={{fontSize: 11, fontWeight: 700}}>Month</TableCell>
                                          <TableCell style={{fontSize: 11, fontWeight: 700}} align="right">Amount</TableCell>
                                          <TableCell style={{fontSize: 11, fontWeight: 700}}>Status</TableCell>
                                          <TableCell style={{fontSize: 11, fontWeight: 700}}>Due Date</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {billingStats.month_groups.flatMap((mg) =>
                                          (mg.rows || []).filter((r) => (r.payer_type || r.payer || '').toLowerCase().includes('insurance') || (r.billing_type || '').toLowerCase().includes('insurance')).map((r, idx) => (
                                            <TableRow key={`${mg.month}-${idx}`}>
                                              <TableCell style={{fontSize: 11}}>{r.invoice_number || '-'}</TableCell>
                                              <TableCell style={{fontSize: 11}}>{r.patient_name || r.patient}</TableCell>
                                              <TableCell style={{fontSize: 11}}>{mg.month_display}</TableCell>
                                              <TableCell style={{fontSize: 11}} align="right">{r.total_amount || r.amount_billed || '-'}</TableCell>
                                              <TableCell style={{fontSize: 11}}><Chip label={r.invoice_status || 'unbilled'} size="small" style={{fontSize: 10, height: 18, background: r.invoice_status === 'paid' ? '#e8f5e9' : r.invoice_status === 'unpaid' ? '#fdecea' : '#fff8e1'}} /></TableCell>
                                              <TableCell style={{fontSize: 11}}>{r.due_date || '-'}</TableCell>
                                            </TableRow>
                                          ))
                                        )}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                ) : (
                                  <Typography style={{fontSize: 12, color: '#666'}}>No insurance-billed appointments found in the current date range.</Typography>
                                )}
                              </Box>
                            </Box>
                          )}

                          {/* ---- TAB 5: Annual Summary ---- */}
                          {cockpitTab === 5 && (
                            <Box style={{padding: 16}}>
                              <Typography style={{fontSize: 14, fontWeight: 700, color: '#002D55', marginBottom: 12}}>Annual Revenue Summary</Typography>
                              <Box style={{display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap'}}>
                                <input
                                  type="number"
                                  value={annualYear}
                                  min={2020}
                                  max={new Date().getFullYear() + 1}
                                  onChange={(e) => setAnnualYear(Number(e.target.value))}
                                  style={{fontSize: 13, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', width: 90}}
                                />
                                <Button size="small" variant="contained" style={{background: '#002D55', color: '#fff', fontSize: 11}} onClick={() => fetchAnnualStats(annualYear)} disabled={annualStatsLoading}>
                                  {annualStatsLoading ? 'Loading...' : 'Load Year'}
                                </Button>
                                {annualStats && (
                                  <Button size="small" variant="outlined" style={{fontSize: 11}} onClick={() => {
                                    const currency = billingStats?.grand_totals?.currency || 'EUR';
                                    const rowsHtml = (annualStats.months || []).map((m) => `<tr><td>${m.month_label}</td><td align="right">${m.appointment_count}</td><td align="right">${currency} ${parseFloat(m.total_revenue||0).toFixed(2)}</td><td align="right">${currency} ${parseFloat(m.billed_revenue||0).toFixed(2)}</td><td align="right">${currency} ${parseFloat(m.collected_revenue||0).toFixed(2)}</td><td align="right">${currency} ${parseFloat(m.unbilled_revenue||0).toFixed(2)}</td><td align="right">${currency} ${parseFloat(m.total_commission||0).toFixed(2)}</td><td align="right">${currency} ${parseFloat(m.total_vat||0).toFixed(2)}</td></tr>`).join('');
                                    const yt = annualStats.year_totals || {};
                                    const totRow = `<tr style="font-weight:bold;background:#f0f4f8;"><td>TOTAL</td><td align="right">${yt.appointment_count||0}</td><td align="right">${currency} ${parseFloat(yt.total_revenue||0).toFixed(2)}</td><td align="right">${currency} ${parseFloat(yt.billed_revenue||0).toFixed(2)}</td><td align="right">${currency} ${parseFloat(yt.collected_revenue||0).toFixed(2)}</td><td align="right">${currency} ${parseFloat(yt.unbilled_revenue||0).toFixed(2)}</td><td align="right">${currency} ${parseFloat(yt.total_commission||0).toFixed(2)}</td><td align="right">${currency} ${parseFloat(yt.total_vat||0).toFixed(2)}</td></tr>`;
                                    const el = document.createElement('div');
                                    el.style.padding = '32px';
                                    el.style.fontFamily = 'Arial, sans-serif';
                                    el.innerHTML = `<h2 style="color:#002D55;">Annual Revenue Summary ${annualStats.year}</h2><table style="width:100%;border-collapse:collapse;font-size:12px;"><thead><tr style="background:#002D55;color:#fff;"><th style="padding:6px;">Month</th><th style="padding:6px;">Appts</th><th style="padding:6px;">Revenue</th><th style="padding:6px;">Billed</th><th style="padding:6px;">Collected</th><th style="padding:6px;">Unbilled</th><th style="padding:6px;">Commission</th><th style="padding:6px;">VAT</th></tr></thead><tbody>${rowsHtml}${totRow}</tbody></table>`;
                                    document.body.appendChild(el);
                                    html2pdf().set({margin:12, filename:`annual_summary_${annualStats.year}.pdf`, jsPDF:{unit:'mm',format:'a4',orientation:'landscape'}}).from(el).save().then(()=>document.body.removeChild(el));
                                  }}>
                                  Annual PDF
                                </Button>
                                )}
                              </Box>
                              {annualStatsError && <Typography style={{fontSize: 12, color: '#c62828'}}>{annualStatsError}</Typography>}
                              {annualStats && (
                                <Box>
                                  <TableContainer component={Card} elevation={0} style={{borderRadius: 8, border: '1px solid #d7e2ef'}}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow style={{background: '#002D55'}}>
                                          <TableCell style={{fontSize: 11, fontWeight: 700, color: '#fff'}}>Month</TableCell>
                                          <TableCell style={{fontSize: 11, fontWeight: 700, color: '#fff'}} align="right">Appts</TableCell>
                                          <TableCell style={{fontSize: 11, fontWeight: 700, color: '#fff'}} align="right">Total Revenue</TableCell>
                                          <TableCell style={{fontSize: 11, fontWeight: 700, color: '#fff'}} align="right">Billed</TableCell>
                                          <TableCell style={{fontSize: 11, fontWeight: 700, color: '#fff'}} align="right">Collected</TableCell>
                                          <TableCell style={{fontSize: 11, fontWeight: 700, color: '#fff'}} align="right">Unbilled</TableCell>
                                          <TableCell style={{fontSize: 11, fontWeight: 700, color: '#fff'}} align="right">Commission</TableCell>
                                          <TableCell style={{fontSize: 11, fontWeight: 700, color: '#fff'}} align="right">VAT</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                            {(annualStats.months || []).map((m) => (
                                          <TableRow key={m.month} style={{background: m.appointment_count === 0 ? '#fafafa' : '#fff'}}>
                                            <TableCell style={{fontSize: 11}}>{m.month_label}</TableCell>
                                            <TableCell style={{fontSize: 11}} align="right">{m.appointment_count}</TableCell>
                                            <TableCell style={{fontSize: 11, fontWeight: 600}} align="right">{parseFloat(m.total_revenue || 0).toFixed(2)}</TableCell>
                                            <TableCell style={{fontSize: 11}} align="right">{parseFloat(m.billed_revenue || 0).toFixed(2)}</TableCell>
                                            <TableCell style={{fontSize: 11, color: '#2E7D32'}} align="right">{parseFloat(m.collected_revenue || 0).toFixed(2)}</TableCell>
                                            <TableCell style={{fontSize: 11, color: '#c62828'}} align="right">{parseFloat(m.unbilled_revenue || 0).toFixed(2)}</TableCell>
                                            <TableCell style={{fontSize: 11}} align="right">{parseFloat(m.total_commission || 0).toFixed(2)}</TableCell>
                                            <TableCell style={{fontSize: 11}} align="right">{parseFloat(m.total_vat || 0).toFixed(2)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                      {annualStats.year_totals && (
                                        <TableFooter>
                                          <TableRow style={{background: '#f0f4f8'}}>
                                            <TableCell style={{fontSize: 12, fontWeight: 700, color: '#002D55'}}>TOTAL {annualStats.year}</TableCell>
                                            <TableCell style={{fontSize: 12, fontWeight: 700, color: '#002D55'}} align="right">{annualStats.year_totals.appointment_count}</TableCell>
                                            <TableCell style={{fontSize: 12, fontWeight: 700, color: '#002D55'}} align="right">{parseFloat(annualStats.year_totals.total_revenue || 0).toFixed(2)}</TableCell>
                                            <TableCell style={{fontSize: 12, fontWeight: 700, color: '#002D55'}} align="right">{parseFloat(annualStats.year_totals.billed_revenue || 0).toFixed(2)}</TableCell>
                                            <TableCell style={{fontSize: 12, fontWeight: 700, color: '#2E7D32'}} align="right">{parseFloat(annualStats.year_totals.collected_revenue || 0).toFixed(2)}</TableCell>
                                            <TableCell style={{fontSize: 12, fontWeight: 700, color: '#c62828'}} align="right">{parseFloat(annualStats.year_totals.unbilled_revenue || 0).toFixed(2)}</TableCell>
                                            <TableCell style={{fontSize: 12, fontWeight: 700, color: '#002D55'}} align="right">{parseFloat(annualStats.year_totals.total_commission || 0).toFixed(2)}</TableCell>
                                            <TableCell style={{fontSize: 12, fontWeight: 700, color: '#002D55'}} align="right">{parseFloat(annualStats.year_totals.total_vat || 0).toFixed(2)}</TableCell>
                                          </TableRow>
                                        </TableFooter>
                                      )}
                                    </Table>
                                  </TableContainer>
                                </Box>
                              )}
                            </Box>
                          )}

                        </Box>
                      </Collapse>
                    </Box>
                  );
                })()}

                {billingStats.month_groups.map((monthGroup) => (
                  <Box key={monthGroup.month} style={{marginBottom: 16}}>
                    <Typography style={{fontSize: 14, fontWeight: 700, color: '#002D55', marginBottom: 8}}>
                      {monthGroup.month_display}
                    </Typography>

                    {(() => {
                      const visibleRows = getBillingRowsForMonthGroup(monthGroup);
                      const selectedRowsInGroup = visibleRows.filter((row) => selectedBillingRows.includes(row.appointment_id));
                      const unbilledSelectedInGroup = selectedRowsInGroup.filter((row) => row.invoice_status === 'unbilled');
                      const billedSelectedInGroup = selectedRowsInGroup.filter((row) => row.invoice_id);

                      return (
                        <>
                        <TableContainer component={Card} elevation={0} style={{borderRadius: '18px 18px 0px 0px', overflowX: isMobile ? 'auto' : 'hidden', overflowY: 'hidden', border: '1px solid #d7e2ef'}}>
                          <Table size="small" style={{width: '100%', minWidth: isMobile ? 1080 : '100%'}}>
                            <TableHead>
                              <TableRow style={{backgroundColor: '#F0F8FF'}}>
                                <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 13}}>Date</TableCell>
                                <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 13}}>Staff</TableCell>
                                <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 13}} align="right">Amount</TableCell>
                                <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 13}} align="right">Paid</TableCell>
                                <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 13}} align="right">Commission</TableCell>
                                <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 13}}>Payer</TableCell>
                                <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 13}}>Invoice Status</TableCell>
                                <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 13}}>Invoice #</TableCell>
                                <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 13}}>Due Date</TableCell>
                                <TableCell style={{fontWeight: 600, color: '#002D55', fontSize: 13}} align="center">
                                  {selectedRowsInGroup.length > 0 ? (
                                    <Box style={{display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap'}}>
                                      {unbilledSelectedInGroup.length > 0 && (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          className={classes.Button}
                                          onClick={() => setBillDialogOpen(true)}
                                        >
                                          Bill ({unbilledSelectedInGroup.length})
                                        </Button>
                                      )}
                                      {billedSelectedInGroup.length > 0 && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          className={classes.Button3}
                                          onClick={(e) => {
                                            collectSelectedInvoiceIds();
                                            handleOpenBulkInvoiceActions(e);
                                          }}
                                        >
                                          Invoice Actions ({billedSelectedInGroup.length})
                                        </Button>
                                      )}
                                    </Box>
                                  ) : (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      className={classes.Button3}
                                      onClick={(e) => handleOpenBillingSelectMenu(e, visibleRows)}
                                    >
                                      Select
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {visibleRows.map((row) => (
                                <TableRow key={row.appointment_id} hover onClick={() => handleOpenEditAppointment(row)} style={{cursor: 'pointer'}}>
                                  <TableCell style={{fontSize: 13}}>
                                    {row.appointment_date ? new Date(row.appointment_date).toLocaleDateString() : '-'}
                                  </TableCell>
                                  <TableCell style={{fontSize: 13}}>{getStaffDisplayById(row.staff_id).label || '-'}</TableCell>
                                  <TableCell style={{fontSize: 13, textAlign: 'right', fontWeight: 600}}>{row.currency} {parseFloat(row.amount).toFixed(2)}</TableCell>
                                  <TableCell style={{fontSize: 13, textAlign: 'right'}}>
                                    {row.invoice_id ? (
                                      <span style={{color: getInvoicePaidAmount(row) >= parseFloat(row.amount || 0) ? '#2E7D32' : getInvoicePaidAmount(row) > 0 ? '#E65100' : '#9E9E9E', fontWeight: getInvoicePaidAmount(row) > 0 ? 600 : 400}}>
                                        {row.currency} {getInvoicePaidAmount(row).toFixed(2)}
                                      </span>
                                    ) : '-'}
                                  </TableCell>
                                  <TableCell style={{fontSize: 13, textAlign: 'right', fontWeight: 600}}>
                                    {(() => {
                                      const service = services.find((svc) => String(svc.id) === String(row.service_id));
                                      const commissionRate = getServiceCommissionRateForStaff(service, row.staff_id);
                                      const commissionAmount = Number(row.amount || 0) * (commissionRate / 100);
                                      return `${row.currency} ${commissionAmount.toFixed(2)}`;
                                    })()}
                                  </TableCell>
                                  <TableCell style={{fontSize: 13}}>
                                    <Chip
                                      label={row.payer === 'insurance' ? 'Insurance' : 'Patient'}
                                      size="small"
                                      style={{
                                        backgroundColor: row.payer === 'insurance' ? '#E8F5E9' : '#EEEEEE',
                                        color: row.payer === 'insurance' ? '#2E7D32' : '#424242',
                                        fontSize: 11
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell style={{fontSize: 13}}>
                                    {(() => {
                                      const chip = getInvoiceStatusChip(row.invoice_status);
                                      return (
                                        <Chip
                                          label={chip.label}
                                          size="small"
                                          style={{
                                            backgroundColor: chip.backgroundColor,
                                            color: chip.color,
                                            fontSize: 11,
                                          }}
                                        />
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell style={{fontSize: 13}}>
                                    {row.invoice_number ? (
                                      <Box style={{display: 'flex', alignItems: 'center', gap: 4}}>
                                        <Typography
                                          onClick={(e) => { e.stopPropagation(); handleViewInvoice(row.invoice_id); }}
                                          style={{color: '#002D55', textDecoration: 'underline', cursor: 'pointer', fontSize: 11}}
                                        >
                                          {row.invoice_number}
                                        </Typography>
                                        {row.ean_submission_status && ['delivered', 'accepted'].includes(row.ean_submission_status) && (
                                          <Tooltip title="Sent via EAN successfully">
                                            <CheckCircleIcon style={{fontSize: 13, color: '#2E7D32'}} />
                                          </Tooltip>
                                        )}
                                        {row.sent_date && (
                                          <Tooltip title={row.email_opened ? 'Invoice viewed by patient' : 'Invoice sent - not yet viewed (may be in spam)'}>
                                            <MarkunreadMailboxIcon style={{fontSize: 13, color: row.email_opened ? '#1565C0' : '#9E9E9E'}} />
                                          </Tooltip>
                                        )}
                                      </Box>
                                    ) : (
                                      '-'
                                    )}
                                  </TableCell>
                                  <TableCell style={{fontSize: 13}}>
                                    {row.payment_due_date ? new Date(row.payment_due_date).toLocaleDateString() : '-'}
                                  </TableCell>
                                  <TableCell align="center" style={{fontSize: 13}} onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                      checked={selectedBillingRows.includes(row.appointment_id)}
                                      onChange={() => handleToggleBillingRowSelected(row.appointment_id)}
                                      color="primary"
                                      size="small"
                                    />
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenRowAction(e, row); }}>
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                            {monthGroup.totals && (
                              <TableFooter>
                                <TableRow style={{backgroundColor: '#e6eaf0'}}>
                                  <TableCell style={{fontSize: 12, fontWeight: 700, color: '#002D55'}}>
                                    Appointments: {monthGroup.totals.count}
                                  </TableCell>
                                  <TableCell style={{fontSize: 12, fontWeight: 700, color: '#002D55'}}>
                                    Billed / Unbilled: {monthGroup.totals.billed_count} / {monthGroup.totals.unbilled_count}
                                  </TableCell>
                                  <TableCell style={{fontSize: 12, fontWeight: 700, color: '#002D55'}} />
                                  <TableCell style={{fontSize: 12, fontWeight: 700, color: '#002D55'}} align="right">
                                    {formatCurrency(monthGroup.totals.appointment_total)}
                                  </TableCell>
                                  <TableCell style={{fontSize: 12, fontWeight: 700, color: '#2E7D32'}} align="right">
                                    {(() => {
                                      const total = (monthGroup.rows || []).reduce((sum, r) => sum + getInvoicePaidAmount(r), 0);
                                      return `${billingStats?.grand_totals?.currency || 'EUR'} ${total.toFixed(2)}`;
                                    })()}
                                  </TableCell>
                                  <TableCell style={{fontSize: 12, fontWeight: 700, color: '#002D55'}} align="right">
                                    {formatCurrency(getMonthCommissionTotal(monthGroup.rows || []), billingStats?.grand_totals?.currency || 'EUR')}
                                  </TableCell>
                                  <TableCell style={{fontSize: 12, fontWeight: 700, color: '#002D55'}}>
                                    Patient / Insurance: {formatCurrency(monthGroup.totals.patient_portion_total)} / {formatCurrency(monthGroup.totals.insurance_portion_total)}
                                  </TableCell>
                                  <TableCell style={{fontSize: 12, fontWeight: 700, color: '#002D55'}}>
                                    Overdue Bills: {getMonthOverdueBillsCount(monthGroup.rows || [])}
                                  </TableCell>
                                  <TableCell />
                                  <TableCell />
                                </TableRow>
                              </TableFooter>
                            )}
                          </Table>
                        </TableContainer>
                        {selectedRowsInGroup.length > 0 && (
                          <Box style={{marginTop: 4, padding: '10px 14px', backgroundColor: '#E8F5E9', borderRadius: '0 0 12px 12px', border: '1px solid #d7e2ef', borderTop: 'none', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center'}}>
                            <Typography style={{fontSize: 13, fontWeight: 600, color: '#002D55', marginRight: 8}}>
                              {selectedRowsInGroup.length} selected:
                            </Typography>
                            {unbilledSelectedInGroup.length > 0 && (
                              <Button
                                variant="contained"
                                className={classes.Button}
                                onClick={() => setBillDialogOpen(true)}
                                size="small"
                                startIcon={<ReceiptIcon />}
                              >
                                Bill Unbilled ({unbilledSelectedInGroup.length})
                              </Button>
                            )}
                            {billedSelectedInGroup.length > 0 && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => { collectSelectedInvoiceIds(); handleOpenBulkInvoiceActions(e); }}
                                style={{color: '#002D55', borderColor: '#002D55'}}
                              >
                                Invoice Actions ({billedSelectedInGroup.length})
                              </Button>
                            )}
                            <Button
                              size="small"
                              onClick={() => { setSelectedBillingRows([]); setSelectedBillingInvoices([]); }}
                              style={{color: '#666', marginLeft: 'auto'}}
                            >
                              Clear Selection
                            </Button>
                          </Box>
                        )}
                        </>
                      );
                    })()}

                  </Box>
                ))}
              </Box>
            )}

            <Menu
              anchorEl={billingSelectMenuAnchor}
              open={Boolean(billingSelectMenuAnchor)}
              onClose={handleCloseBillingSelectMenu}
            >
              <MenuItem onClick={() => handleBillingSelectPreset('all')}>Select All</MenuItem>
              <MenuItem onClick={() => handleBillingSelectPreset('overdue')}>Select Overdue</MenuItem>
              <MenuItem onClick={() => handleBillingSelectPreset('unbilled')}>Select Unbilled</MenuItem>
              <MenuItem onClick={() => handleBillingSelectPreset('draft')}>Select Drafts</MenuItem>
            </Menu>



            {/* Bill Dialog */}
            <Dialog open={billDialogOpen} onClose={() => setBillDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{ className: classes.dialogPaper }}>
              <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.dialogTitleRow}>
                  <Typography className={classes.dialogTitleText}>Bill Selected Appointments</Typography>
                  <IconButton onClick={() => setBillDialogOpen(false)} className={classes.closeButton} size="small"><CloseIcon fontSize="small" /></IconButton>
                </Box>
              </DialogTitle>
              <DialogContent className={classes.dialogContent}>
                <Paper elevation={0} className={classes.dialogPanel}>
                  <Typography className={classes.dialogSectionLabel}>Invoice Setup</Typography>
                  <Typography style={{marginBottom: 12, fontSize: 14}}>
                    Selected: {selectedBillingRows.length} appointments
                  </Typography>
                  <TextField
                    label="Grouping Mode"
                    select
                    value={billingGroupMode}
                    onChange={(e) => setBillingGroupMode(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={classes.orangeFocused}
                    style={{marginBottom: 12}}
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                  >
                    <MenuItem value="group_by_payer">Group by Payer (1 invoice per payer)</MenuItem>
                    <MenuItem value="single">1 Invoice Per Appointment</MenuItem>
                  </TextField>
                  {billDialogError && (
                    <Typography color="error" style={{marginBottom: 12, fontSize: 12}}>
                      {billDialogError}
                    </Typography>
                  )}
                </Paper>
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button onClick={() => setBillDialogOpen(false)} className={clsx(classes.Button3, classes.dialogActionButton)}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  className={classes.Button}
                  onClick={handlePreviewBeforeBill}
                  disabled={billDialogLoading}
                  className={clsx(classes.Button, classes.dialogActionButton)}
                >
                  Preview Invoices
                </Button>
              </DialogActions>
            </Dialog>

            {/* Invoice Preview Before Billing Dialog */}
            <Dialog open={billPreviewOpen} onClose={() => setBillPreviewOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile} PaperProps={{ className: classes.dialogPaper }}>
              <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.dialogTitleRow}>
                  <Typography className={classes.dialogTitleText}>
                    Invoice Preview (Preview)
                  </Typography>
                  <IconButton
                    aria-label="close"
                    onClick={() => setBillPreviewOpen(false)}
                    size="small"
                    className={classes.closeButton}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent dividers className={clsx(classes.dialogContent, classes.dialogContentFlush)}>
                <Box id="bill-preview-printable" style={{backgroundColor: 'transparent', padding: 0}}>
                {billPreviewInvoices.length === 0 ? (
                  <Typography style={{color: '#999', textAlign: 'center', padding: 20}}>No invoices to preview.</Typography>
                ) : (
                  billPreviewInvoices.map((group, idx) => (
                    <Box
                      key={idx}
                      style={{
                        padding: '0px',
                        marginBottom: idx < billPreviewInvoices.length - 1 ? 16 : 0,
                        margin: 16,
                        backgroundColor: '#fff',
                        borderRadius: 18,
                        border: '1px solid #dfe6ee',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                        pageBreakAfter: idx < billPreviewInvoices.length - 1 ? 'always' : 'auto',
                        breakAfter: idx < billPreviewInvoices.length - 1 ? 'page' : 'auto',
                      }}
                    >
                    <Box style={{padding: '40px', fontFamily: 'Arial, sans-serif', backgroundColor: '#FFF', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.08)'}}>
                      {/* Header */}
                      <Box style={{marginBottom: '30px', paddingBottom: '20px', borderBottom: '3px solid #002D55'}}>
                        <Typography style={{fontSize: '24px', fontWeight: 'bold', color: '#002D55', marginBottom: '5px'}}>
                          {clinicName || 'Clinic'}
                        </Typography>
                        <Typography style={{fontSize: '12px', color: '#666', marginBottom: '15px'}}>
                          {clinicEmail || ''}{clinicPhone ? ` | ${clinicPhone}` : ''}
                        </Typography>
                      </Box>

                      {/* Invoice Title */}
                      <Box style={{display: 'flex', justifyContent: 'space-between', marginBottom: '30px'}}>
                        <Box>
                          <Typography style={{fontSize: '28px', fontWeight: 'bold', color: '#002D55'}}>
                            INVOICE (Preview)
                          </Typography>
                        </Box>
                        <Box style={{textAlign: 'right'}}>
                          <Typography style={{fontSize: '12px', color: '#666', marginBottom: '5px'}}>
                            <b>Invoice No.:</b> {idx + 1} of {billPreviewInvoices.length}
                          </Typography>
                          <Typography style={{fontSize: '12px', color: '#666', marginBottom: '5px'}}>
                            <b>Invoice Date:</b> {new Date().toLocaleDateString()}
                          </Typography>
                          <Typography style={{fontSize: '12px', color: '#666'}}>
                            <b>Due Date:</b> {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Bill To + Summary */}
                      <Box style={{marginBottom: '30px', display: 'flex', justifyContent: 'space-between'}}>
                        <Box style={{width: '48%'}}>
                          <Typography style={{fontSize: '11px', fontWeight: 'bold', color: '#002D55', marginBottom: '8px', textTransform: 'uppercase'}}>
                            Bill To
                          </Typography>
                          <Typography style={{fontSize: '12px', marginBottom: '3px'}}>
                            <b>{group.patient || 'Patient'}</b>
                          </Typography>
                          <Typography style={{fontSize: '11px', color: '#666'}}>
                            Payer: {group.payer === 'insurance' ? 'Insurance' : 'Patient'}
                          </Typography>
                        </Box>
                        <Box style={{width: '48%', backgroundColor: '#F5F5F5', padding: '15px', borderRadius: '4px'}}>
                          <Typography style={{fontSize: '11px', fontWeight: 'bold', color: '#002D55', marginBottom: '10px', textTransform: 'uppercase'}}>
                            Summary
                          </Typography>
                          <Box style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '11px'}}>
                            <span>Appointments:</span>
                            <span>{group.rows.length}</span>
                          </Box>
                          <Box style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '11px'}}>
                            <span style={{color: '#2E7D32', fontWeight: 'bold'}}>Patient Portion:</span>
                            <span style={{color: '#2E7D32', fontWeight: 'bold'}}>{group.rows[0]?.currency || 'EUR'} {group.patientPortion.toFixed(2)}</span>
                          </Box>
                          <Box style={{display: 'flex', justifyContent: 'space-between', fontSize: '11px'}}>
                            <span style={{color: '#1565C0', fontWeight: 'bold'}}>Insurance Portion:</span>
                            <span style={{color: '#1565C0', fontWeight: 'bold'}}>{group.rows[0]?.currency || 'EUR'} {group.insurancePortion.toFixed(2)}</span>
                          </Box>
                        </Box>
                      </Box>

                      {/* Line Items Table */}
                      <Box style={{marginBottom: '20px'}}>
                        <Table style={{width: '100%', borderCollapse: 'collapse'}}>
                          <TableHead>
                            <TableRow style={{backgroundColor: '#002D55'}}>
                              <TableCell style={{fontSize: '11px', fontWeight: 'bold', color: '#FFF', padding: '12px', textAlign: 'left'}}>Service</TableCell>
                              <TableCell style={{fontSize: '11px', fontWeight: 'bold', color: '#FFF', padding: '12px', textAlign: 'left'}}>Date</TableCell>
                              <TableCell style={{fontSize: '11px', fontWeight: 'bold', color: '#FFF', padding: '12px', textAlign: 'right'}}>Amount</TableCell>
                              <TableCell style={{fontSize: '11px', fontWeight: 'bold', color: '#FFF', padding: '12px', textAlign: 'right'}}>Patient</TableCell>
                              <TableCell style={{fontSize: '11px', fontWeight: 'bold', color: '#FFF', padding: '12px', textAlign: 'right'}}>Insurance</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.rows.map((row, rIdx) => (
                              <TableRow key={rIdx} style={{backgroundColor: rIdx % 2 === 0 ? '#FAFAFA' : '#FFF', borderBottom: '1px solid #E0E0E0'}}>
                                <TableCell style={{fontSize: '11px', padding: '10px', textAlign: 'left'}}>{row.service || '-'}</TableCell>
                                <TableCell style={{fontSize: '11px', padding: '10px', textAlign: 'left', color: '#666'}}>{row.appointment_date ? new Date(row.appointment_date).toLocaleDateString() : '-'}</TableCell>
                                <TableCell style={{fontSize: '11px', padding: '10px', textAlign: 'right', fontWeight: '600'}}>{row.currency || 'EUR'} {parseFloat(row.amount).toFixed(2)}</TableCell>
                                <TableCell style={{fontSize: '11px', padding: '10px', textAlign: 'right', color: '#2E7D32', fontWeight: '600'}}>{row.currency || 'EUR'} {parseFloat(row.patient_portion || 0).toFixed(2)}</TableCell>
                                <TableCell style={{fontSize: '11px', padding: '10px', textAlign: 'right', color: '#1565C0', fontWeight: '600'}}>{row.currency || 'EUR'} {parseFloat(row.insurance_portion || 0).toFixed(2)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>

                      {/* Totals */}
                      <Box style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '30px'}}>
                        <Box style={{width: '280px'}}>
                          <Box style={{display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #E0E0E0', fontSize: '12px'}}>
                            <span>Subtotal:</span>
                            <span>{group.rows[0]?.currency || 'EUR'} {group.total.toFixed(2)}</span>
                          </Box>
                          <Box style={{display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 'bold', color: '#002D55', backgroundColor: '#F5F5F5', padding: '12px'}}>
                            <span>TOTAL DUE:</span>
                            <span>{group.rows[0]?.currency || 'EUR'} {group.total.toFixed(2)}</span>
                          </Box>
                        </Box>
                      </Box>

                      {/* Footer */}
                      <Box style={{marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #E0E0E0', fontSize: '10px', color: '#666'}}>
                        <Typography style={{fontSize: '10px', color: '#666'}}>
                          <b>Terms and Conditions:</b> Payment is due within 30 days of invoice date.
                        </Typography>
                      </Box>
                    </Box>
                    </Box>
                  ))
                )}
                </Box>
                {billDialogError && (
                  <Typography color="error" style={{marginTop: 12, fontSize: 12, padding: '0 16px 12px'}}>
                    {billDialogError}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button onClick={() => {
                  setBillPreviewOpen(false);
                  if (billPreviewReturnTarget === 'dialog') {
                    setBillDialogOpen(true);
                  }
                }} className={clsx(classes.Button3, classes.dialogActionButton)}>
                  Back
                </Button>
                <Button
                  variant="outlined"
                  className={classes.Button3}
                  onClick={handleDownloadBillPreviewPDF}
                  disabled={billPreviewInvoices.length === 0}
                >
                  Preview PDF
                </Button>
                <Button
                  variant="contained"
                  className={classes.Button}
                  onClick={handleConfirmBillAfterPreview}
                  disabled={billPreviewLoading || billPreviewInvoices.length === 0}
                  className={clsx(classes.Button, classes.dialogActionButton)}
                >
                  {billPreviewLoading ? "Creating Invoices..." : `Confirm & Create ${billPreviewInvoices.length} Invoice(s)`}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Row Action Menu (per-row, context-aware) */}
            <Menu
              anchorEl={actionMenuAnchor}
              open={Boolean(actionMenuAnchor)}
              onClose={handleCloseRowAction}
            >
              <MenuItem onClick={() => { handleCloseRowAction(); handleOpenEditAppointment(selectedBillingRow); }}>
                <EditIcon fontSize="small" style={{ marginRight: 8, color: '#002D55' }} />
                Edit Appointment
              </MenuItem>
              {selectedBillingRow?.invoice_id ? ([
                <MenuItem key="view" onClick={handleRowViewInvoice}>
                  <VisibilityIcon fontSize="small" style={{ marginRight: 8, color: '#002D55' }} />
                  View Invoice
                </MenuItem>,
                <Divider key="d1" />,
                <MenuItem
                  key="mark-paid"
                  onClick={async () => {
                    const invoiceId = selectedBillingRow?.invoice_id;
                    handleCloseRowAction();
                    if (invoiceId) {
                      await handleViewInvoice(invoiceId);
                    }
                  }}
                >
                  <CheckCircleIcon fontSize="small" style={{ marginRight: 8, color: '#2E7D32' }} />
                  Mark as Paid
                </MenuItem>,
                <MenuItem key="reminder" onClick={handleRowSendReminder}>
                  <NotificationsActiveIcon fontSize="small" style={{ marginRight: 8, color: '#FF9800' }} />
                  Send Reminder
                </MenuItem>,
                <MenuItem key="accredit" onClick={handleRowAccredit}>
                  <ReceiptIcon fontSize="small" style={{ marginRight: 8, color: '#4CAF50' }} />
                  Accredit
                </MenuItem>,
                (selectedBillingRow?.amount_paid !== undefined && parseFloat(selectedBillingRow?.total_amount || selectedBillingRow?.invoice_total_amount || 0) > parseFloat(selectedBillingRow?.amount_paid || 0)) ? (
                  <MenuItem key="forgive" onClick={handleRowForgiveBalance}>
                    <CancelIcon fontSize="small" style={{ marginRight: 8, color: '#c62828' }} />
                    Forgive Remaining Balance
                  </MenuItem>
                ) : null,
                <Divider key="d2" />,
                <MenuItem key="cancel" onClick={handleRowCancelBilling}>
                  <CancelIcon fontSize="small" style={{ marginRight: 8, color: '#F44336' }} />
                  Cancel Invoice
                </MenuItem>
              ]) : ([
                <Divider key="d3" />,
                <MenuItem key="payer" onClick={handleRowChangePayer}>
                  <SwapHorizIcon fontSize="small" style={{ marginRight: 8, color: '#1565C0' }} />
                  Change Payer
                </MenuItem>
              ])}
            </Menu>

            {/* Bulk Invoice Actions Menu */}
            <Menu
              anchorEl={bulkActionMenuAnchor}
              open={Boolean(bulkActionMenuAnchor)}
              onClose={handleCloseBulkInvoiceActions}
            >
              <MenuItem onClick={() => {
                handleCloseBulkInvoiceActions();
                collectSelectedInvoiceIds();
                setReminderType("1st");
                setReminderDialogOpen(true);
              }}>
                <NotificationsActiveIcon fontSize="small" style={{ marginRight: 8, color: '#FF9800' }} />
                Send Reminder(s)
              </MenuItem>
              <MenuItem onClick={() => {
                handleCloseBulkInvoiceActions();
                collectSelectedInvoiceIds();
                setCreditError("");
                setCreditPatientHasPaidBalance(false);
                setCreditTransferToPatientBalance(false);
                setCreditDialogOpen(true);
              }}>
                <ReceiptIcon fontSize="small" style={{ marginRight: 8, color: '#4CAF50' }} />
                Accredit Invoice(s)
              </MenuItem>
              <MenuItem onClick={() => {
                handleCloseBulkInvoiceActions();
                openMarkPaidDialog(collectSelectedInvoiceIds());
              }}>
                <CheckCircleIcon fontSize="small" style={{ marginRight: 8, color: '#2E7D32' }} />
                Mark as Paid
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => {
                handleCloseBulkInvoiceActions();
                collectSelectedInvoiceIds();
                setCancelBillingDialogOpen(true);
              }}>
                <CancelIcon fontSize="small" style={{ marginRight: 8, color: '#F44336' }} />
                Cancel Invoice(s)
              </MenuItem>
              <MenuItem onClick={() => {
                handleCloseBulkInvoiceActions();
                setSelectedBillingRows([]);
                setSelectedBillingInvoices([]);
              }}>
                <CancelIcon fontSize="small" style={{ marginRight: 8, color: '#666' }} />
                Cancel Selection
              </MenuItem>
            </Menu>

            {/* Credit Dialog */}
            <Dialog open={creditDialogOpen} onClose={handleCloseCreditDialog} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{ className: classes.dialogPaper }}>
              <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.dialogTitleRow}>
                  <Typography className={classes.dialogTitleText}>Accredit Invoices</Typography>
                  <IconButton onClick={handleCloseCreditDialog} className={classes.closeButton} size="small"><CloseIcon fontSize="small" /></IconButton>
                </Box>
              </DialogTitle>
              <DialogContent className={classes.dialogContent}>
                <Paper elevation={0} className={classes.dialogPanel}>
                  <Typography className={classes.dialogSectionLabel}>Confirmation</Typography>
                  <Typography style={{marginTop: 8, marginBottom: 12}}>
                    Accredit {selectedBillingInvoices.length} invoice(s)?
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={creditPatientHasPaidBalance}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setCreditPatientHasPaidBalance(checked);
                          if (!checked) {
                            setCreditTransferToPatientBalance(false);
                          }
                        }}
                        color="primary"
                      />
                    }
                    label="Patient has already paid this balance"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={creditTransferToPatientBalance}
                        onChange={(event) => setCreditTransferToPatientBalance(event.target.checked)}
                        color="primary"
                        disabled={!creditPatientHasPaidBalance}
                      />
                    }
                    label="Keep the amount as patient balance instead of reopening the invoice"
                  />
                  <Typography variant="caption" style={{ display: 'block', marginTop: 4, color: '#666' }}>
                    Leave both unchecked to reopen the invoice as unpaid so it can be edited or resent.
                  </Typography>
                  {creditError && (
                    <Typography color="error" style={{marginBottom: 12}}>
                      {creditError}
                    </Typography>
                  )}
                </Paper>
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button onClick={handleCloseCreditDialog} className={clsx(classes.Button3, classes.dialogActionButton)}>Cancel</Button>
                <Button
                  variant="contained"
                  className={classes.Button2}
                  onClick={() => handleBulkInvoiceAction("accredit")}
                  disabled={creditLoading}
                  classes={{ root: classes.dialogActionButton }}
                >
                  {creditLoading ? "Processing..." : "Accredit"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Cancel Billing Dialog */}
            <Dialog open={cancelBillingDialogOpen} onClose={() => setCancelBillingDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{ className: classes.dialogPaper }}>
              <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.dialogTitleRow}>
                  <Typography className={classes.dialogTitleText}>Cancel Invoices</Typography>
                  <IconButton onClick={() => setCancelBillingDialogOpen(false)} className={classes.closeButton} size="small"><CloseIcon fontSize="small" /></IconButton>
                </Box>
              </DialogTitle>
              <DialogContent className={classes.dialogContent}>
                <Paper elevation={0} className={classes.dialogPanel}>
                  <Typography className={classes.dialogSectionLabel}>Confirmation</Typography>
                  <Typography style={{marginTop: 8, marginBottom: 12}}>
                    Cancel {selectedBillingInvoices.length} invoice(s)?
                  </Typography>
                  {cancelBillingError && (
                    <Typography color="error" style={{marginBottom: 12}}>
                      {cancelBillingError}
                    </Typography>
                  )}
                </Paper>
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button onClick={() => setCancelBillingDialogOpen(false)} className={clsx(classes.Button3, classes.dialogActionButton)}>Close</Button>
                <Button
                  variant="contained"
                  className={classes.Button2}
                  onClick={() => handleBulkInvoiceAction("cancel")}
                  disabled={cancelBillingLoading}
                  classes={{ root: classes.dialogActionButton }}
                >
                  {cancelBillingLoading ? "Processing..." : "Cancel Invoices"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Reminder Dialog */}
            <Dialog open={reminderDialogOpen} onClose={() => setReminderDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{ className: classes.dialogPaper }}>
              <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.dialogTitleRow}>
                  <Typography className={classes.dialogTitleText}>Send Reminder</Typography>
                  <IconButton onClick={() => setReminderDialogOpen(false)} className={classes.closeButton} size="small"><CloseIcon fontSize="small" /></IconButton>
                </Box>
              </DialogTitle>
              <DialogContent className={classes.dialogContent}>
                <Paper elevation={0} className={classes.dialogPanel}>
                  <Typography className={classes.dialogSectionLabel}>Reminder Settings</Typography>
                  <Typography style={{marginTop: 8, marginBottom: 12}}>
                    Send reminder for {selectedBillingInvoices.length} invoice(s)
                  </Typography>
                  <TextField
                    label="Reminder Type"
                    select
                    value={reminderType}
                    onChange={(e) => setReminderType(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={classes.orangeFocused}
                    style={{marginBottom: 12}}
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                  >
                    <MenuItem value="1st">1st Reminder</MenuItem>
                    <MenuItem value="2nd">2nd Reminder</MenuItem>
                    <MenuItem value="3rd">3rd Reminder</MenuItem>
                    <MenuItem value="inkasso">Inkasso</MenuItem>
                  </TextField>
                  {reminderError && (
                    <Typography color="error" style={{marginBottom: 12}}>
                      {reminderError}
                    </Typography>
                  )}
                </Paper>
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button onClick={() => setReminderDialogOpen(false)} className={clsx(classes.Button3, classes.dialogActionButton)}>Cancel</Button>
                <Button
                  variant="contained"
                  className={classes.Button2}
                  onClick={() => handleBulkInvoiceAction("send_reminder")}
                  disabled={reminderLoading}
                  classes={{ root: classes.dialogActionButton }}
                >
                  {reminderLoading ? "Sending..." : "Send"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Mark Paid Dialog */}
            <Dialog open={markPaidDialogOpen} onClose={handleCloseMarkPaidDialog} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{ className: classes.dialogPaper }}>
              <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.dialogTitleRow}>
                  <Typography className={classes.dialogTitleText}>Mark Invoice Paid</Typography>
                  <IconButton onClick={handleCloseMarkPaidDialog} className={classes.closeButton} size="small"><CloseIcon fontSize="small" /></IconButton>
                </Box>
              </DialogTitle>
              <DialogContent className={classes.dialogContent}>
                <Paper elevation={0} className={classes.dialogPanel}>
                  <Typography className={classes.dialogSectionLabel}>Payment Details</Typography>
                  <Typography style={{ marginTop: 8, marginBottom: 12 }}>
                    Mark {markPaidInvoiceIds.length} invoice(s) as paid.
                  </Typography>
                  <TextField
                    label="Payment Method"
                    select
                    value={markPaidPaymentMethod}
                    onChange={(e) => setMarkPaidPaymentMethod(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={classes.orangeFocused}
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                  >
                    {MARK_PAID_PAYMENT_METHOD_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography variant="caption" style={{ display: 'block', marginTop: 8, color: '#666' }}>
                    This will update the invoice and appointment payment status using the selected method.
                  </Typography>
                  {markPaidError && (
                    <Typography color="error" style={{ marginTop: 12 }}>
                      {markPaidError}
                    </Typography>
                  )}
                </Paper>
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button onClick={handleCloseMarkPaidDialog} className={clsx(classes.Button3, classes.dialogActionButton)}>Cancel</Button>
                <Button
                  variant="contained"
                  className={classes.Button2}
                  onClick={handleConfirmMarkPaid}
                  disabled={markPaidLoading}
                  classes={{ root: classes.dialogActionButton }}
                >
                  {markPaidLoading ? 'Processing...' : 'Mark as Paid'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Invoice Preview Dialog */}
            <Dialog open={invoicePreviewOpen} onClose={() => setInvoicePreviewOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile} PaperProps={{ className: classes.dialogPaper }}>
              <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.dialogTitleRow}>
                  <Typography className={classes.dialogTitleText}>
                    Invoice Details
                  </Typography>
                  <IconButton
                    aria-label="close"
                    onClick={() => setInvoicePreviewOpen(false)}
                    size="small"
                    className={classes.closeButton}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent dividers className={clsx(classes.dialogContent, classes.dialogContentFlush)}>
                {selectedInvoiceForPreview && (
                  <InvoicePrintableContent
                    invoice={selectedInvoiceForPreview}
                    issuerName={getInvoiceIssuerName(selectedInvoiceForPreview)}
                    services={services}
                    issuerSelector={
                      String(selectedInvoiceForPreview.billing_type || '').toLowerCase() === 'staff_subcontractor' ? (
                        <Box style={{marginBottom: '16px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center'}}>
                          <TextField
                            select
                            label="Subcontractor Invoice Name"
                            variant="outlined"
                            size="small"
                            value={invoiceIssuerNameMode}
                            onChange={(e) => setInvoiceIssuerNameMode(e.target.value)}
                            style={{minWidth: 240}}
                            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                          >
                            <MenuItem value="staff">Use Staff Name</MenuItem>
                            <MenuItem value="clinic">Use Clinic Name</MenuItem>
                            <MenuItem value="custom">Use Custom Business Name</MenuItem>
                          </TextField>
                          {invoiceIssuerNameMode === 'custom' && (
                            <TextField
                              label="Custom Business Name"
                              variant="outlined"
                              size="small"
                              value={invoiceCustomIssuerName}
                              onChange={(e) => setInvoiceCustomIssuerName(e.target.value)}
                              style={{minWidth: 280}}
                            />
                          )}
                        </Box>
                      ) : null
                    }
                    clinic={{
                      address: clinicAddress,
                      postalCode: clinicPostalCode,
                      city: clinicCity,
                      cvr: clinicRegNumber,
                      bankAccount: '',
                      phone: clinicPhone,
                      email: clinicEmail,
                    }}
                    patient={{
                      name: selectedInvoiceForPreview?.patient?.name
                        || [selectedInvoiceForPreview?.patient?.user?.first_name, selectedInvoiceForPreview?.patient?.user?.last_name].filter(Boolean).join(' ').trim()
                        || selectedInvoiceForPreview?.patient?.user?.full_name,
                      address: selectedInvoiceForPreview?.patient?.address || '',
                      postalCode: selectedInvoiceForPreview?.patient?.zip || selectedInvoiceForPreview?.patient?.postal_code || '',
                      city: selectedInvoiceForPreview?.patient?.city || '',
                      email: selectedInvoiceForPreview?.patient?.email || '',
                    }}
                  />
                )}
              </DialogContent>
              <DialogActions className={classes.dialogActions} style={{ width: '100%' }}>
                <Box display="flex" alignItems="flex-end" justifyContent="space-between" width="100%" style={{ gap: 12 }}>
                  <Box display="flex" alignItems="center" style={{ gap: 12, minHeight: 36 }}>
                    {selectedInvoiceForPreview && !['paid', 'accredited', 'refunded', 'cancelled'].includes(String(selectedInvoiceForPreview.status || selectedInvoiceForPreview.invoice_status || '').toLowerCase()) && (
                      <Button
                        variant="contained"
                        className={classes.Button2}
                        onClick={() => {
                          const invoiceId = selectedInvoiceForPreview.id;
                          if (!invoiceId) return;
                          setMarkPaidPaymentMethod(normalizeMarkPaidPaymentMethodValue(selectedInvoiceForPreview.payment_method || selectedInvoiceForPreview.payment_method_label || ''));
                          openMarkPaidDialog([invoiceId], normalizeMarkPaidPaymentMethodValue(selectedInvoiceForPreview.payment_method || selectedInvoiceForPreview.payment_method_label || ''));
                        }}
                        disabled={markPaidLoading}
                        classes={{ root: classes.dialogActionButton }}
                      >
                        Mark as Paid
                      </Button>
                    )}
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="flex-end" style={{ gap: 8, marginLeft: 'auto' }}>
                    <Button onClick={() => setInvoicePreviewOpen(false)} className={clsx(classes.Button3, classes.dialogActionButton)}>
                      Close
                    </Button>
                    <Button
                      variant="contained"
                      className={classes.Button}
                      onClick={handleDownloadInvoicePDF}
                      classes={{ root: classes.dialogActionButton }}
                      startIcon={<GetAppIcon />}
                    >
                      Download PDF
                    </Button>
                  </Box>
                </Box>
              </DialogActions>
            </Dialog>

            {/* Change Payer Dialog */}
            <Dialog open={changePayerDialogOpen} onClose={() => setChangePayerDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{ className: classes.dialogPaper }}>
              <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.dialogTitleRow}>
                  <Typography className={classes.dialogTitleText}>Change Payer</Typography>
                  <IconButton onClick={() => setChangePayerDialogOpen(false)} className={classes.closeButton} size="small"><CloseIcon fontSize="small" /></IconButton>
                </Box>
              </DialogTitle>
              <DialogContent className={classes.dialogContent}>
                {changePayerTarget && (
                  <Paper elevation={0} className={classes.dialogPanel}>
                    <Typography className={classes.dialogSectionLabel}>Payer Selection</Typography>
                    <Typography style={{marginBottom: 12, fontSize: 13, color: '#666'}}>
                      Appointment: {changePayerTarget.patient_name} | {changePayerTarget.service} ({changePayerTarget.appointment_date ? new Date(changePayerTarget.appointment_date).toLocaleDateString() : '-'})
                    </Typography>
                    <TextField
                      label="Payer"
                      select
                      value={changePayerValue}
                      onChange={(e) => setChangePayerValue(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.orangeFocused}
                      SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                    >
                      <MenuItem value="patient">Patient</MenuItem>
                      <MenuItem value="insurance">Insurance</MenuItem>
                    </TextField>
                  </Paper>
                )}
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button onClick={() => setChangePayerDialogOpen(false)} className={clsx(classes.Button3, classes.dialogActionButton)}>Cancel</Button>
                <Button
                  variant="contained"
                  className={classes.Button}
                  onClick={handleConfirmChangePayer}
                  disabled={changePayerLoading}
                  classes={{ root: classes.dialogActionButton }}
                >
                  {changePayerLoading ? "Updating..." : "Update Payer"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Edit Appointment Dialog */}
            <Dialog open={editAppointmentDialogOpen} onClose={handleCloseEditAppointment} maxWidth="md" fullWidth fullScreen={isMobile} PaperProps={{ className: classes.dialogPaper }}>
              <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.dialogTitleRow}>
                  <Typography className={classes.dialogTitleText}>Edit Appointment</Typography>
                  {(editingAppointment?.appointment_type === 'video_consultation' || editingVideoSession?.room_name) && (
                    <Tooltip title={editingVideoSession && Number(editingVideoSession.participant_count || 0) > 0 && editingVideoSession.status !== 'ended' ? 'Patient is waiting in the videocall' : 'Open videocall'}>
                      <IconButton onClick={handleOpenEditAppointmentVideoCall} className={classes.closeButton} size="small" aria-label="open videocall">
                        <Badge color="secondary" variant="dot" overlap="circular" invisible={!(editingVideoSession && Number(editingVideoSession.participant_count || 0) > 0 && editingVideoSession.status !== 'ended')}>
                          <VideoCallIcon fontSize="small" />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton
                    aria-label="close"
                    onClick={handleCloseEditAppointment}
                    size="small"
                    className={classes.closeButton}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent className={classes.dialogContent}>
                {editingAppointment && (
                  <Box className={classes.dialogPanelStack}>
                    <Paper elevation={0} className={classes.dialogPanel}>
                      <Typography className={classes.dialogSectionLabel}>Appointment Details</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Patient"
                            value={editingAppointment.patient_name || '-'}
                            fullWidth variant="outlined" size="small" disabled className={classes.orangeFocused}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Appointment Date"
                            value={editingAppointment.appointment_date ? new Date(editingAppointment.appointment_date).toLocaleDateString() : '-'}
                            fullWidth variant="outlined" size="small" disabled className={classes.orangeFocused}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            select
                            label="Service Type"
                            value={editingAppointment.appointment_type || 'follow_up'}
                            onChange={(e) => setEditingAppointment({...editingAppointment, appointment_type: e.target.value})}
                            fullWidth variant="outlined" size="small" className={classes.orangeFocused}
                            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                          >
                            <MenuItem value="initial_consultation">Initial Consultation</MenuItem>
                            <MenuItem value="follow_up">Follow-up</MenuItem>
                            <MenuItem value="treatment_session">Treatment Session</MenuItem>
                            <MenuItem value="assessment">Assessment</MenuItem>
                            <MenuItem value="video_consultation">Video Consultation</MenuItem>
                            <MenuItem value="in_person">In-Person</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Amount"
                            type="number"
                            value={editingAppointment.amount || ''}
                            onChange={(e) => setEditingAppointment({...editingAppointment, amount: e.target.value})}
                            fullWidth variant="outlined" size="small" className={classes.orangeFocused}
                            InputProps={{
                              startAdornment: <span style={{marginRight: 4, color: '#666'}}>{editingAppointment.currency || 'EUR'}</span>
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                    <Paper elevation={0} className={classes.dialogPanel}>
                      <Typography className={classes.dialogSectionLabel}>Assignment And Billing</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            select
                            label="Staff Member"
                            value={editingAppointment.staff_id || ''}
                            onChange={(e) => setEditingAppointment({...editingAppointment, staff_id: e.target.value})}
                            fullWidth variant="outlined" size="small" className={classes.orangeFocused}
                            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                          >
                            {staffList2.map((staff) => (
                              <MenuItem key={staff.user_id || staff.id} value={staff.user_id || staff.id}>
                                {`${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.email || 'Staff'}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            select
                            label="Payer Type"
                            value={editingPayer}
                            onChange={(e) => setEditingPayer(e.target.value)}
                            fullWidth variant="outlined" size="small" className={classes.orangeFocused}
                            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                          >
                            <MenuItem value="patient">Patient</MenuItem>
                            <MenuItem value="insurance">Insurance</MenuItem>
                            <MenuItem value="other">Other payer</MenuItem>
                          </TextField>
                        </Grid>
                        {editingPayer === 'insurance' && (
                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              label="Insurance"
                              value={editingPayerInsuranceId}
                              onChange={(e) => setEditingPayerInsuranceId(e.target.value)}
                              fullWidth variant="outlined" size="small" className={classes.orangeFocused}
                              SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                              helperText={editingPayerOptionsLoading ? 'Loading patient insurances...' : (editingPayerInsuranceOptions.length === 0 ? 'No insurance records found for this patient' : '')}
                            >
                              <MenuItem value="">Select insurance</MenuItem>
                              {editingPayerInsuranceOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                  {option.label}
                                  {option.billingEmail ? ` | ${option.billingEmail}` : ''}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        )}
                        {editingPayer === 'other' && (
                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              label="Other Payer"
                              value={editingPayerOtherId}
                              onChange={(e) => setEditingPayerOtherId(e.target.value)}
                              fullWidth variant="outlined" size="small" className={classes.orangeFocused}
                              SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                              helperText={editingPayerOptionsLoading ? 'Loading other payers...' : (editingPayerOtherOptions.length === 0 ? 'No other payers found in clinic settings' : '')}
                            >
                              <MenuItem value="">Select other payer</MenuItem>
                              {editingPayerOtherOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                  {option.label}
                                  {option.billingEmail ? ` | ${option.billingEmail}` : ''}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        )}
                        {editingPayerOptionsError && (
                          <Grid item xs={12}>
                            <Typography variant="caption" style={{ color: '#6f7c8d' }}>
                              {editingPayerOptionsError}
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                          <TextField
                            select
                            label="Status"
                            value={editingAppointment.status || 'scheduled'}
                            onChange={(e) => setEditingAppointment({...editingAppointment, status: e.target.value})}
                            fullWidth variant="outlined" size="small" className={classes.orangeFocused}
                            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                          >
                            <MenuItem value="scheduled">Scheduled</MenuItem>
                            <MenuItem value="confirmed">Confirmed</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                            <MenuItem value="no_show">No Show</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Billing Status"
                            value={editingAppointment.billing_status || editingAppointment.invoice_status || '-'}
                            fullWidth variant="outlined" size="small" disabled className={classes.orangeFocused}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                    <Paper elevation={0} className={classes.dialogPanel}>
                      <Typography className={classes.dialogSectionLabel}>Notes</Typography>
                      <TextField
                        label="Notes"
                        value={editingAppointment.patient_notes || ''}
                        onChange={(e) => setEditingAppointment({...editingAppointment, patient_notes: e.target.value})}
                        fullWidth variant="outlined" size="small" multiline rows={3} className={classes.orangeFocused}
                      />
                    </Paper>
                  </Box>
                )}
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Box display="flex" width="100%" alignItems="center" justifyContent="space-between" style={{ gap: 12 }}>
                  <Button
                    variant="contained"
                    className={classes.Button}
                    style={{ padding: '6px 16px', fontWeight: 600, whiteSpace: 'nowrap' }}
                    onClick={() => navigateToSelectedPatientPaymentOverview(editingAppointment)}
                    disabled={!getAppointmentPatientId(editingAppointment)}
                  >
                    Open patient payment overview
                  </Button>
                  <Box display="flex" alignItems="center" style={{ gap: 8, marginLeft: 'auto' }}>
                    <Button onClick={handleCloseEditAppointment} className={clsx(classes.Button3, classes.dialogActionButton)} style={{padding: '6px 16px', fontWeight: 600}}>Cancel</Button>
                    <Button
                      variant="contained"
                      className={classes.Button}
                      onClick={handleSaveEditAppointment}
                      disabled={editSaving}
                      classes={{ root: classes.dialogActionButton }}
                    >
                      {editSaving ? "Saving..." : "Save Changes"}
                    </Button>
                  </Box>
                </Box>
              </DialogActions>
            </Dialog>
            {videoCallOpen && videoCallAppointment && (
              <VideoCallModal
                open={videoCallOpen}
                appointment={videoCallAppointment}
                authToken={token}
                onClose={handleCloseVideoCall}
              />
            )}
          </Box>
        )}
        {tabIndex === 2 && (
          <Box style={{padding: '12px 0'}}>
            {/* Services Offered */}
            <Typography className={classes.bodyText} style={{marginBottom: 8, marginLeft: 8}}>
              List and manage the services your clinic offers. All staff are assigned by default; deselect to exclude.
            </Typography>
            <Divider style={{marginTop: 16, marginBottom: 4, marginLeft: 8}}/>
            <Box className={classes.staffForm}>
              <Typography variant="subtitle1" style={{fontWeight: 600, marginBottom: 12, color: '#002D55'}}>Add New Service</Typography>
              {/* Staff assignment checkboxes */}
                {staffList.length > 0 && (
                  <Grid item xs={12} md={12} style={{marginBottom: 12}}>
                    <Typography variant="caption" style={{color: '#002D55', fontWeight: 600, display: 'block', marginBottom: 4}}>
                      Offered by (deselect to exclude):
                    </Typography>
                    <Box display="flex" flexWrap="wrap" style={{gap: 4}}>
                      {staffList.map(staff => {
                        const id = String(staff.userId || staff.user_id || staff.id);
                        const checked = serviceStaffMembers.includes(id);
                        const name = staff.name || `${staff.firstName || staff.first_name || ''} ${staff.lastName || staff.last_name || ''}`.trim() || staff.email || 'Staff';
                        return (
                          <Box
                            key={id}
                            onClick={() => {
                              setServiceStaffMembers(prev =>
                                prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                              );
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', cursor: 'pointer',
                              background: checked ? '#002D55' : '#e6eaf0',
                              color: checked ? '#fff' : '#002D55',
                              borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600,
                              userSelect: 'none',
                              textDecoration: checked ? 'none' : 'line-through',
                            }}
                          >
                            {name}
                          </Box>
                        );
                      })}
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12} md={4} style={{marginBottom: 12}}>
                  <Typography variant="caption" style={{color: '#002D55', fontWeight: 600, display: 'block', marginBottom: 6}}>
                    Calendar color
                  </Typography>
                  <Box display="flex" alignItems="center" flexWrap="wrap" style={{gap: 8}}>
                    {SERVICE_COLOR_PRESETS.map((swatch) => (
                      <Box
                        key={swatch}
                        onClick={() => setServiceColor(swatch)}
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          backgroundColor: swatch,
                          cursor: 'pointer',
                          border: serviceColor === swatch ? '2.5px solid #002D55' : '2px solid transparent',
                          boxShadow: serviceColor === swatch ? '0 0 0 1.5px #fff inset' : 'none',
                        }}
                        title={swatch}
                      />
                    ))}
                    <input
                      type="color"
                      value={serviceColor || DEFAULT_SERVICE_COLOR}
                      onChange={(e) => setServiceColor(e.target.value)}
                      style={{ width: 28, height: 28, padding: 0, border: '1px solid #dce8f5', borderRadius: 6, cursor: 'pointer', backgroundColor: 'transparent' }}
                      title="Custom service color"
                    />
                  </Box>
                </Grid>
              <Grid container spacing={2} alignItems="flex-start" justifyContent="flex-start">
                <Grid item xs={12} md={2}>
                  <TextField
                    margin="none"
                    size="small"
                    label="Service code"
                    value={serviceCode}
                    onChange={e => setServiceCode(e.target.value)}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                  />
                </Grid>
                {clinicOperatesInDenmark && (
                  <Grid item xs={12} md={2}>
                    <TextField
                      margin="none"
                      size="small"
                      label="Danmark code"
                      value={danmarkCode}
                      onChange={e => setDanmarkCode(e.target.value)}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={2}>
                  <TextField
                    margin="none"
                    size="small"
                    label="Service Name"
                    value={serviceName}
                    onChange={e => setServiceName(e.target.value)}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    margin="none"
                    size="small"
                    label="Duration (min)"
                    value={serviceDuration}
                    onChange={e => setServiceDuration(e.target.value.replace(/\D/, ''))}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    margin="none"
                    size="small"
                    label={`Price (${currency})`}
                    value={servicePrice}
                    onChange={e => setServicePrice(e.target.value.replace(/[^\d.]/g, ''))}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    select
                    margin="none"
                    size="small"
                    label="Currency"
                    value={currency}
                    onChange={e => setCurrency(e.target.value)}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                  >
                    {currencyOptions.map(opt => (
                      <MenuItem key={opt} value={opt} className={classes.menuItem}>{opt}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <TextField
                    select
                    margin="none"
                    size="small"
                    label="Require VAT (Yes/No)"
                    value={serviceRequiresVat ? 'yes' : 'no'}
                    onChange={(e) => {
                      const checked = e.target.value === 'yes';
                      setServiceRequiresVat(checked);
                      if (!checked) {
                        setServiceVatAmountPercent('');
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                  >
                    <MenuItem value="no" className={classes.menuItem}>No</MenuItem>
                    <MenuItem value="yes" className={classes.menuItem}>Yes</MenuItem>
                  </TextField>
                </Grid>
                {serviceRequiresVat && (
                  <Grid item xs={12} md={2}>
                    <TextField
                      margin="none"
                      size="small"
                      label="VAT amount (%)"
                      value={serviceVatAmountPercent}
                      onChange={e => setServiceVatAmountPercent(e.target.value.replace(/[^\d.]/g, ''))}
                      variant="outlined"
                      className={classes.orangeFocused}
                      type="number"
                      inputProps={{ min: 0, max: 100, step: '0.01' }}
                      fullWidth
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={2}>
                  <TextField
                    margin="none"
                    size="small"
                    label="Commission rate (%)"
                    value={serviceCommissionRate}
                    onChange={e => setServiceCommissionRate(e.target.value.replace(/[^\d.]/g, ''))}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                    type="number"
                    inputProps={{ min: 0, max: 100, step: '0.01' }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={serviceIsClass}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setServiceIsClass(checked);
                          if (!checked) {
                            setServiceClassParticipantLimit('');
                            setServiceClassDefaultRoomId('');
                            setServiceClassPrimaryStaff('');
                              setServiceClassWeekdayPrimaryStaffs({});
                          }
                        }}
                        style={{ color: serviceIsClass ? '#FF7F27' : '#002D55' }}
                      />
                    )}
                    label={<span style={{ color: '#002D55', fontWeight: 600 }}>Class / holdtræning</span>}
                  />
                </Grid>
                {serviceIsClass && (
                  <>
                    <Grid item xs={12} md={2}>
                      <TextField
                        margin="none"
                        size="small"
                        label="Participant limit"
                        value={serviceClassParticipantLimit}
                        onChange={e => setServiceClassParticipantLimit(e.target.value.replace(/\D/, ''))}
                        fullWidth
                        variant="outlined"
                        className={classes.orangeFocused}
                        type="number"
                        inputProps={{ min: 1, step: 1 }}
                      />
                    </Grid>
                        <Grid item xs={12} md={3}>
                      <TextField
                        select
                        margin="none"
                        size="small"
                            label="Weekdays"
                            value={serviceClassWeekdays}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setServiceClassWeekdays(typeof nextValue === 'string' ? nextValue.split(',') : nextValue);
                            }}
                        fullWidth
                        variant="outlined"
                        className={classes.orangeFocused}
                            SelectProps={{
                              multiple: true,
                              MenuProps: SELECT_MENU_PROPS,
                              renderValue: (selected) => formatClassWeekdayLabel(selected),
                            }}
                      >
                        {CLASS_WEEKDAY_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={String(option.value)} className={classes.menuItem}>
                                <Checkbox checked={normalizeClassWeekdays(serviceClassWeekdays).includes(option.value)} style={{ color: '#FF7F27', marginRight: 8 }} />
                                {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        margin="none"
                        size="small"
                        label="Start time"
                        type="time"
                        value={serviceClassStartTime}
                        onChange={e => setServiceClassStartTime(e.target.value)}
                        fullWidth
                        variant="outlined"
                        className={classes.orangeFocused}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        margin="none"
                        size="small"
                        label="Default room"
                        value={serviceClassDefaultRoomId}
                        onChange={e => setServiceClassDefaultRoomId(e.target.value)}
                        fullWidth
                        variant="outlined"
                        className={classes.orangeFocused}
                        SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                      >
                        {getClassRoomOptions()
                          .map(({ location, room }) => (
                            <MenuItem key={room.id} value={room.id} className={classes.menuItem}>
                              {`${location.name} | ${room.name}`}
                            </MenuItem>
                          ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        margin="none"
                        size="small"
                        label="Primary class staff"
                        value={serviceClassPrimaryStaff}
                        onChange={(e) => setServiceClassPrimaryStaff(e.target.value)}
                        fullWidth
                        variant="outlined"
                        className={classes.orangeFocused}
                        SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                      >
                        {staffList
                          .filter((staff) => {
                            const id = String(staff.userId || staff.user_id || staff.id);
                            return serviceStaffMembers.includes(id) || String(serviceClassPrimaryStaff || '') === id;
                          })
                          .map((staff) => {
                            const id = String(staff.userId || staff.user_id || staff.id);
                            const name = staff.name || `${staff.firstName || staff.first_name || ''} ${staff.lastName || staff.last_name || ''}`.trim() || staff.email || 'Staff';
                            return (
                              <MenuItem key={id} value={id} className={classes.menuItem}>
                                {name}
                              </MenuItem>
                            );
                          })}
                      </TextField>
                    </Grid>
                    {normalizeClassWeekdays(serviceClassWeekdays).length > 0 && (
                      <Grid item xs={12}>
                        <Box style={{ border: '1px solid #dce8f5', borderRadius: 12, background: '#f7faff', padding: 12 }}>
                          <Typography variant="caption" style={{ color: '#002D55', fontWeight: 600, display: 'block', marginBottom: 10 }}>
                            Primary staff by weekday
                          </Typography>
                          <Grid container spacing={2}>
                            {normalizeClassWeekdays(serviceClassWeekdays).map((weekday) => {
                              const fallbackStaffId = String(serviceClassPrimaryStaff || serviceStaffMembers[0] || '').trim();
                              const selectedStaffId = getClassWeekdayPrimaryStaffValue(serviceClassWeekdayPrimaryStaffs, weekday, fallbackStaffId);
                              return (
                                <Grid item xs={12} sm={6} md={4} key={`service-weekday-primary-${weekday}`}>
                                  <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    label={`${CLASS_WEEKDAY_OPTIONS.find((option) => option.value === weekday)?.label || 'Day'} primary staff`}
                                    value={selectedStaffId}
                                    onChange={(event) => setServiceClassWeekdayPrimaryStaffs((current) => ({
                                      ...current,
                                      [String(weekday)]: String(event.target.value || ''),
                                    }))}
                                    className={classes.orangeFocused}
                                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                                  >
                                    {staffList
                                      .filter((staff) => {
                                        const id = String(staff.userId || staff.user_id || staff.id);
                                        return serviceStaffMembers.includes(id) || selectedStaffId === id || fallbackStaffId === id;
                                      })
                                      .map((staff) => {
                                        const id = String(staff.userId || staff.user_id || staff.id);
                                        const name = staff.name || `${staff.firstName || staff.first_name || ''} ${staff.lastName || staff.last_name || ''}`.trim() || staff.email || 'Staff';
                                        return (
                                          <MenuItem key={id} value={id} className={classes.menuItem}>
                                            {name}
                                          </MenuItem>
                                        );
                                      })}
                                  </TextField>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Box>
                      </Grid>
                    )}
                  </>
                )}
                {staffList.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Box style={{ border: '1px solid #dce8f5', borderRadius: 12, background: '#ffffff', overflow: 'hidden' }}>
                      <Box
                        onClick={() => setServiceCommissionOverridesOpen(prev => !prev)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 14px',
                          cursor: 'pointer',
                          background: '#f7faff',
                        }}
                      >
                        <Typography
                          variant="caption"
                          style={{ color: '#002D55', fontWeight: 600 }}
                        >
                          Exclusive Staff Commission Overrides
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{ color: '#5f6c7b', fontWeight: 600 }}
                        >
                          {serviceCommissionOverridesOpen ? 'Hide' : 'Show'}
                        </Typography>
                      </Box>
                      <Collapse in={serviceCommissionOverridesOpen} timeout="auto" unmountOnExit>
                        <Box style={{ padding: 12 }}>
                          <Typography variant="body2" style={{ color: '#5f6c7b', marginBottom: 10, fontSize: 12 }}>
                            Leave blank to use the default commission rate for that staff member.
                          </Typography>
                          <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
                            {staffList.map(staff => {
                              const id = String(staff.userId || staff.user_id || staff.id);
                              if (!serviceStaffMembers.includes(id)) {
                                return null;
                              }
                              const name = staff.name || `${staff.firstName || staff.first_name || ''} ${staff.lastName || staff.last_name || ''}`.trim() || staff.email || 'Staff';
                              return (
                                <Box key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 260 }}>
                                  <Typography variant="body2" style={{ color: '#002D55', fontWeight: 600, minWidth: 120 }}>
                                    {name}
                                  </Typography>
                                  <TextField
                                    size="small"
                                    label="Override (%)"
                                    value={serviceCommissionOverrides[id] || ''}
                                    onChange={e => setServiceCommissionOverrides(prev => ({ ...prev, [id]: e.target.value.replace(/[^\d.]/g, '') }))}
                                    variant="outlined"
                                    className={classes.orangeFocused}
                                    type="number"
                                    inputProps={{ min: 0, max: 100, step: '0.01' }}
                                    style={{ width: 140 }}
                                  />
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      </Collapse>
                    </Box>
                  </Grid>
                )}
                
                
                {canAddService && (
                  <Grid item xs={12}>
                    <Box display="flex" style={{ gap: 8, marginTop: 8 }}>
                      <Button
                        variant="contained"
                        className={classes.Button}
                        fullWidth
                        style={{ minHeight: 38, marginRight: 0, flex: 1 }}
                        onClick={addService}
                      >
                        Add Service
                      </Button>
                      <Button
                        className={classes.Button3}
                        variant="outlined"
                        fullWidth
                        style={{ flex: 1 }}
                        onClick={clearServiceFields}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Services list */}
            <Box className={classes.staffList}>
              <Typography variant="subtitle1" style={{fontWeight: 600, marginBottom: 8, color: '#002D55'}}>
                Current Services
                {servicesLoading && <span style={{marginLeft: 8, fontSize: 12, color: '#757575'}}>Loading...</span>}
              </Typography>
              {regularServices.length === 0 && !servicesLoading ? (
                <Typography color="textSecondary">No services added yet.</Typography>
              ) : (
                <Box>
                  <Grid container style={{fontWeight: 600, color: '#002D55', marginBottom: 4, fontSize: 13}}>
                    <Grid item xs={clinicOperatesInDenmark ? 2 : 3}>Name</Grid>
                    <Grid item xs={1}>Service code</Grid>
                    {clinicOperatesInDenmark && <Grid item xs={1}>Danmark code</Grid>}
                    <Grid item xs={1}>Min</Grid>
                    <Grid item xs={1}>Price</Grid>
                    <Grid item xs={1}>Comm.</Grid>
                    <Grid item xs={1}>VAT</Grid>
                    <Grid item xs={1}>Curr.</Grid>
                    <Grid item xs={2}>Staff</Grid>
                    <Grid item xs={1}></Grid>
                  </Grid>
                  {regularServices.map(service => (
                    <Grid container key={service.id} className={classes.staffItem} alignItems="center">
                      <Grid item xs={clinicOperatesInDenmark ? 2 : 3} style={{fontSize: 13, display: 'flex', alignItems: 'center', gap: 8}}>
                        <Box
                          component="span"
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: service.color || DEFAULT_SERVICE_COLOR,
                            border: '1px solid rgba(0, 45, 85, 0.12)',
                            flexShrink: 0,
                          }}
                          title={service.color || DEFAULT_SERVICE_COLOR}
                        />
                        <span>{service.name}</span>
                      </Grid>
                      <Grid item xs={1} style={{fontSize: 13, wordBreak: 'break-word'}}>{service.service_code || '-'}</Grid>
                      {clinicOperatesInDenmark && (
                        <Grid item xs={1} style={{fontSize: 13, wordBreak: 'break-word'}}>{service.danmark_code || '-'}</Grid>
                      )}
                      <Grid item xs={1} style={{fontSize: 13}}>{service.duration_minutes}</Grid>
                      <Grid item xs={1} style={{fontSize: 13}}>{service.price}</Grid>
                      <Grid item xs={1} style={{fontSize: 13}}>{service.commission_rate != null ? `${service.commission_rate}%` : '0%'}</Grid>
                      <Grid item xs={1} style={{fontSize: 13}}>{service.requires_vat ? `${service.vat_amount_percent || ''}%` : 'No'}</Grid>
                      <Grid item xs={1} style={{fontSize: 13}}>{service.currency}</Grid>
                      <Grid item xs={2} style={{fontSize: 12, color: '#5f6c7b'}}>
                        {(service.staff_members || []).length === 0
                          ? 'All staff'
                          : (
                            <Box display="flex" flexWrap="wrap" gridGap={6}>
                              {(service.staff_members || []).map((userId) => {
                                const staffDisplay = getStaffDisplayById(userId);
                                return (
                                  <Tooltip key={userId} title={staffDisplay.fullName || 'Unknown staff'} arrow>
                                    <Box
                                      component="span"
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        padding: '2px 8px',
                                        borderRadius: 999,
                                        backgroundColor: '#eef4fb',
                                        color: '#002D55',
                                        fontSize: 11,
                                        fontWeight: 600,
                                        lineHeight: 1.6,
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {staffDisplay.label || '--'}
                                    </Box>
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          )
                        }
                      </Grid>
                      <Grid item xs={1} style={{display: 'flex', gap: 8}}>
                        <button
                          className={classes.removeBtn}
                          style={{color: '#002D55'}}
                          onClick={() => openEditService(service)}
                          type="button"
                        >
                          Edit
                        </button>
                        <button
                          className={classes.removeBtn}
                          onClick={() => deleteService(service.id)}
                          type="button"
                        >
                          Remove
                        </button>
                      </Grid>
                    </Grid>
                  ))}
                </Box>
              )}
            </Box>

            <Box className={classes.staffList} style={{ marginTop: 16 }}>
              <Typography variant="subtitle1" style={{fontWeight: 600, marginBottom: 8, color: '#002D55'}}>
                Current Classes
                {classServices.length > 0 && <span style={{marginLeft: 8, fontSize: 12, color: '#757575'}}>{classServices.length} class{classServices.length === 1 ? '' : 'es'}</span>}
              </Typography>
              {classServices.length === 0 && !servicesLoading ? (
                <Typography color="textSecondary">No classes added yet.</Typography>
              ) : (
                <Box>
                  <Grid container style={{fontWeight: 600, color: '#002D55', marginBottom: 4, fontSize: 13}}>
                    <Grid item xs={12} md={2}>Name</Grid>
                    <Grid item xs={6} md={1}>Days</Grid>
                    <Grid item xs={6} md={1}>Time</Grid>
                    <Grid item xs={6} md={1}>Duration</Grid>
                    <Grid item xs={6} md={2}>Location</Grid>
                    <Grid item xs={6} md={2}>Room</Grid>
                    <Grid item xs={6} md={1}>Limit</Grid>
                    <Grid item xs={12} md={2}>Staff</Grid>
                  </Grid>
                  {classServices.map((service) => {
                    const roomDetails = getClassRoomDetails(service.class_default_room);
                    const classColor = service.color || roomDetails.roomColor || DEFAULT_SERVICE_COLOR;
                    return (
                      <Grid container key={service.id} className={classes.staffItem} alignItems="center">
                        <Grid item xs={12} md={2} style={{fontSize: 13, display: 'flex', alignItems: 'center', gap: 8}}>
                          <Box
                            component="span"
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: classColor,
                              border: '1px solid rgba(0, 45, 85, 0.12)',
                              flexShrink: 0,
                            }}
                            title={classColor}
                          />
                          <span>{service.name}</span>
                        </Grid>
                        <Grid item xs={6} md={1} style={{fontSize: 13}}>{formatClassWeekdayLabel(service.class_weekdays?.length ? service.class_weekdays : service.class_weekday)}</Grid>
                        <Grid item xs={6} md={1} style={{fontSize: 13}}>{service.class_start_time || '-'}</Grid>
                        <Grid item xs={6} md={1} style={{fontSize: 13}}>{service.duration_minutes}</Grid>
                        <Grid item xs={6} md={2} style={{fontSize: 13}}>{roomDetails.locationName || '-'}</Grid>
                        <Grid item xs={6} md={2} style={{fontSize: 13}}>{roomDetails.roomName || '-'}</Grid>
                        <Grid item xs={6} md={1} style={{fontSize: 13}}>{service.class_participant_limit || '-'}</Grid>
                        <Grid item xs={12} md={2} style={{fontSize: 12, color: '#5f6c7b'}}>
                          {(service.staff_members || []).length === 0
                            ? 'All staff'
                            : (
                              <Box display="flex" flexWrap="wrap" gridGap={6}>
                                {(service.staff_members || []).map((userId) => {
                                  const staffDisplay = getStaffDisplayById(userId);
                                  return (
                                    <Tooltip key={userId} title={staffDisplay.fullName || 'Unknown staff'} arrow>
                                      <Box
                                        component="span"
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          padding: '2px 8px',
                                          borderRadius: 999,
                                          backgroundColor: '#eef4fb',
                                          color: '#002D55',
                                          fontSize: 11,
                                          fontWeight: 600,
                                          lineHeight: 1.6,
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {staffDisplay.label || '--'}
                                      </Box>
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            )
                          }
                        </Grid>
                      </Grid>
                    );
                  })}
                </Box>
              )}
            </Box>

            <Box className={classes.staffList} style={{ marginTop: 16 }}>
              <Typography variant="subtitle1" style={{fontWeight: 600, marginBottom: 8, color: '#002D55'}}>
                Weekly Class Overview
              </Typography>
              <Typography variant="body2" style={{ color: '#5f6c7b', marginBottom: 12 }}>
                Weekly recurring classes are shown in the same time-and-column structure as the room allocation schedule.
              </Typography>
              <Box style={{ overflowX: 'auto' }}>
                <Box style={{ minWidth: 980, border: '1px solid #dbe7f2', borderRadius: 14, overflow: 'hidden', background: '#ffffff' }}>
                  <Box
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '64px repeat(7, minmax(120px, 1fr))',
                    }}
                  >
                    <Box style={{ borderRight: '1px solid #dbe7f2', background: '#f7faff' }} />
                    {CLASS_WEEKDAY_OPTIONS.map((weekday) => (
                      <Box key={weekday.value} style={{ padding: '10px 8px', borderRight: '1px solid #dbe7f2', borderBottom: '1px solid #dbe7f2', background: '#f7faff', minHeight: 58 }}>
                        <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 16, letterSpacing: 0.3, textTransform: 'uppercase' }}>{weekday.label}</Typography>
                        <Typography variant="caption" style={{ color: '#6f7b87', fontSize: 16, marginTop: 3, display: 'block' }}>Recurring template</Typography>
                      </Box>
                    ))}

                    <Box style={{ position: 'relative', height: classScheduleGridHeight, borderRight: '1px solid #dbe7f2', background: 'linear-gradient(180deg, #ffffff 0%, #fafcff 100%)' }}>
                      {CLASS_SCHEDULE_HOUR_MARKERS.map((marker) => {
                        const top = ((marker - CLASS_SCHEDULE_START_MINUTES) / CLASS_SCHEDULE_SLOT_MINUTES) * CLASS_SCHEDULE_ROW_HEIGHT;
                        return (
                          <Typography
                            key={`class-time-${marker}`}
                            variant="caption"
                            style={{
                              position: 'absolute',
                              top: top,
                              left: 8,
                              color: '#718193',
                              fontSize: 15,
                              transform: 'translateY(-50%)',
                              whiteSpace: 'nowrap',
                              fontWeight: 400,
                            }}
                          >
                            {formatClassScheduleMinutesLabel(marker)}
                          </Typography>
                        );
                      })}
                    </Box>

                    {CLASS_WEEKDAY_OPTIONS.map((weekday) => {
                      const classesForDay = classServices
                        .filter((service) => normalizeClassWeekdays(service.class_weekdays?.length ? service.class_weekdays : service.class_weekday).includes(Number(weekday.value)))
                        .slice()
                        .sort((left, right) => String(left.class_start_time || '').localeCompare(String(right.class_start_time || '')));

                      return (
                        <Box
                          key={`class-day-${weekday.value}`}
                          style={{
                            position: 'relative',
                            height: classScheduleGridHeight,
                            borderLeft: '1px solid #dbe7f2',
                            backgroundImage: 'repeating-linear-gradient(180deg, #ffffff 0px, #ffffff 23px, #eef4fb 24px)',
                            cursor: 'crosshair',
                          }}
                          onMouseDown={(event) => startClassScheduleSelection(weekday.value, event)}
                        >
                          {classesForDay.length === 0 ? (
                            <Typography variant="body2" style={{ position: 'absolute', top: 8, left: 10, color: '#a0afbf', fontSize: 14 }}>
                              No classes
                            </Typography>
                          ) : null}
                          {classScheduleSelectionPreview && String(classScheduleSelectionPreview.weekdayValue) === String(weekday.value) ? (
                            <Box
                              style={{
                                position: 'absolute',
                                left: 6,
                                right: 6,
                                top: ((classScheduleSelectionPreview.startMinutes - CLASS_SCHEDULE_START_MINUTES) / CLASS_SCHEDULE_SLOT_MINUTES) * CLASS_SCHEDULE_ROW_HEIGHT,
                                height: Math.max(((classScheduleSelectionPreview.endMinutes - classScheduleSelectionPreview.startMinutes) / CLASS_SCHEDULE_SLOT_MINUTES) * CLASS_SCHEDULE_ROW_HEIGHT, CLASS_SCHEDULE_ROW_HEIGHT),
                                borderRadius: 10,
                                border: '1px dashed rgba(255, 127, 39, 0.55)',
                                background: 'rgba(255, 127, 39, 0.16)',
                                pointerEvents: 'none',
                                zIndex: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0 8px',
                              }}
                            >
                              <Typography style={{ color: '#002D55', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {formatClassScheduleMinutesLabel(classScheduleSelectionPreview.startMinutes)} - {formatClassScheduleMinutesLabel(classScheduleSelectionPreview.endMinutes)}
                              </Typography>
                            </Box>
                          ) : null}
                          {classesForDay.map((service) => {
                            const layout = getClassScheduleBlockLayout(service);
                            if (!layout) {
                              return null;
                            }

                            const roomDetails = getClassRoomDetails(service.class_default_room);
                            const classColor = service.color || roomDetails.roomColor || DEFAULT_SERVICE_COLOR;
                            const attendeeCount = Array.isArray(service.class_patients) ? service.class_patients.length : 0;
                            const participantLimit = service.class_participant_limit != null ? Number(service.class_participant_limit) : null;

                            return (
                              <Box
                                key={`overview-${service.id}`}
                                data-class-block="true"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openEditService(service);
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    openEditService(service);
                                  }
                                }}
                                style={{
                                  position: 'absolute',
                                  left: 6,
                                  right: 6,
                                  top: layout.top,
                                  height: layout.height,
                                  padding: '6px 8px 7px',
                                  borderRadius: 10,
                                  background: `${classColor}18`,
                                  border: `1px solid ${classColor}33`,
                                  borderTop: '1px solid rgba(0, 45, 85, 0.05)',
                                  borderRight: '1px solid rgba(0, 45, 85, 0.05)',
                                  borderBottom: '1px solid rgba(0, 45, 85, 0.05)',
                                  boxShadow: '0 6px 18px rgba(0, 45, 85, 0.08)',
                                  cursor: 'pointer',
                                  overflow: 'hidden',
                                }}
                              >
                                <Typography style={{ fontWeight: 700, color: '#002D55', fontSize: 12, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {service.class_start_time || '-'} | {service.name}
                                </Typography>
                                <Typography variant="body2" style={{ color: '#46607a', fontSize: 11, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>
                                  {roomDetails.locationName || 'No location'} | {roomDetails.roomName || 'No room'}
                                </Typography>
                                <Typography variant="body2" style={{ color: '#46607a', fontSize: 11, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {attendeeCount}{participantLimit ? ` / ${participantLimit}` : ''} attendees
                                </Typography>
                                <Typography variant="caption" style={{ color: '#46607a', fontSize: 11, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  Click to edit roster and schedule
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Edit service dialog */}
            <Dialog
              open={!!editingService}
              onClose={() => setEditingService(null)}
              maxWidth={editingService?.is_class ? false : 'sm'}
              fullWidth
              fullScreen={isMobile}
              PaperProps={{
                className: classes.dialogPaper,
                style: editingService?.is_class ? { width: 'min(96vw, 1200px)', maxWidth: 'min(96vw, 1200px)' } : undefined,
              }}
            >
              <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.dialogTitleRow}>
                  <Typography className={classes.dialogTitleText}>{editingService?.isDraft ? 'Add Class' : 'Edit Service'}</Typography>
                  <IconButton onClick={() => setEditingService(null)} className={classes.closeButton} size="small">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent className={classes.dialogContent}>
                <Box className={classes.dialogPanelStack}>
                  <Paper elevation={0} className={classes.dialogPanel}>
                    <Typography className={classes.dialogSectionLabel}>Service Details</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          size="small"
                          label="Service code"
                          fullWidth
                          variant="outlined"
                          value={editServiceForm.service_code}
                          onChange={e => setEditServiceForm(f => ({...f, service_code: e.target.value}))}
                          className={classes.orangeFocused}
                        />
                      </Grid>
                      {clinicOperatesInDenmark && (
                        <Grid item xs={12} md={4}>
                          <TextField
                            size="small"
                            label="Danmark code"
                            fullWidth
                            variant="outlined"
                            value={editServiceForm.danmark_code}
                            onChange={e => setEditServiceForm(f => ({...f, danmark_code: e.target.value}))}
                            className={classes.orangeFocused}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          label="Service Name"
                          fullWidth
                          variant="outlined"
                          value={editServiceForm.name}
                          onChange={e => {
                            setEditServiceForm(f => ({ ...f, name: e.target.value }));
                          }}
                          className={classes.orangeFocused}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          size="small" label="Duration (min)" fullWidth variant="outlined" type="number"
                          value={editServiceForm.duration_minutes}
                          onChange={e => setEditServiceForm(f => ({...f, duration_minutes: e.target.value.replace(/\D/, '')}))}
                          className={classes.orangeFocused}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          size="small" label="Price" fullWidth variant="outlined" type="number"
                          value={editServiceForm.price}
                          onChange={e => setEditServiceForm(f => ({...f, price: e.target.value.replace(/[^\d.]/g, '')}))}
                          className={classes.orangeFocused}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          size="small"
                          label="Commission rate (%)"
                          fullWidth
                          variant="outlined"
                          type="number"
                          value={editServiceForm.commission_rate}
                          onChange={e => setEditServiceForm(f => ({...f, commission_rate: e.target.value.replace(/[^\d.]/g, '')}))}
                          className={classes.orangeFocused}
                          inputProps={{ min: 0, max: 100, step: '0.01' }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          size="small"
                          label="Require VAT (Yes/No)"
                          fullWidth
                          variant="outlined"
                          value={editServiceForm.requires_vat ? 'yes' : 'no'}
                          onChange={(e) => {
                            const checked = e.target.value === 'yes';
                            setEditServiceForm(f => ({
                              ...f,
                              requires_vat: checked,
                              vat_amount_percent: checked ? f.vat_amount_percent : '',
                            }));
                          }}
                          className={classes.orangeFocused}
                          SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                        >
                          <MenuItem value="no" className={classes.menuItem}>No</MenuItem>
                          <MenuItem value="yes" className={classes.menuItem}>Yes</MenuItem>
                        </TextField>
                      </Grid>
                      {editServiceForm.requires_vat && (
                        <Grid item xs={12} md={4}>
                          <TextField
                            size="small"
                            label="VAT amount (%)"
                            fullWidth
                            variant="outlined"
                            type="number"
                            value={editServiceForm.vat_amount_percent}
                            onChange={e => setEditServiceForm(f => ({...f, vat_amount_percent: e.target.value.replace(/[^\d.]/g, '')}))}
                            className={classes.orangeFocused}
                            inputProps={{ min: 0, max: 100, step: '0.01' }}
                          />
                        </Grid>
                      )}
                      <Grid item xs={4}>
                        <TextField
                          select size="small" label="Currency" fullWidth variant="outlined"
                          value={editServiceForm.currency}
                          onChange={e => setEditServiceForm(f => ({...f, currency: e.target.value}))}
                          className={classes.orangeFocused}
                          SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                        >
                          {currencyOptions.map(opt => <MenuItem key={opt} value={opt} className={classes.menuItem}>{opt}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          label="Description (optional)"
                          fullWidth
                          variant="outlined"
                          multiline
                          rows={2}
                          value={editServiceForm.description}
                          onChange={e => setEditServiceForm(f => ({...f, description: e.target.value}))}
                          className={classes.orangeFocused}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={editServiceForm.is_class}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setEditServiceForm((f) => ({
                                  ...f,
                                  is_class: checked,
                                  class_participant_limit: checked ? f.class_participant_limit : '',
                                  class_default_room: checked ? f.class_default_room : '',
                                  class_primary_staff: checked ? f.class_primary_staff : '',
                                  class_weekday_primary_staffs: checked ? f.class_weekday_primary_staffs : {},
                                }));
                              }}
                              style={{ color: editServiceForm.is_class ? '#FF7F27' : '#002D55' }}
                            />
                          )}
                          label={<span style={{ color: '#002D55', fontWeight: 600 }}>Class / holdtræning</span>}
                        />
                      </Grid>
                      {editServiceForm.is_class && (
                        <>
                          <Grid item xs={12} md={4}>
                            <TextField
                              size="small"
                              label="Participant limit"
                              fullWidth
                              variant="outlined"
                              type="number"
                              value={editServiceForm.class_participant_limit}
                              onChange={e => setEditServiceForm(f => ({ ...f, class_participant_limit: e.target.value.replace(/\D/, '') }))}
                              className={classes.orangeFocused}
                              inputProps={{ min: 1, step: 1 }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              select
                              size="small"
                              label="Weekdays"
                              fullWidth
                              variant="outlined"
                              value={editServiceForm.class_weekdays}
                              onChange={(e) => {
                                const nextValue = e.target.value;
                                setEditServiceForm((f) => ({
                                  ...f,
                                  class_weekdays: typeof nextValue === 'string' ? nextValue.split(',') : nextValue,
                                }));
                              }}
                              className={classes.orangeFocused}
                              SelectProps={{
                                multiple: true,
                                MenuProps: SELECT_MENU_PROPS,
                                renderValue: (selected) => formatClassWeekdayLabel(selected),
                              }}
                            >
                              {CLASS_WEEKDAY_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={String(option.value)} className={classes.menuItem}>
                                  <Checkbox checked={normalizeClassWeekdays(editServiceForm.class_weekdays).includes(option.value)} style={{ color: '#FF7F27', marginRight: 8 }} />
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              size="small"
                              label="Start time"
                              fullWidth
                              variant="outlined"
                              type="time"
                              value={editServiceForm.class_start_time}
                              onChange={e => setEditServiceForm(f => ({ ...f, class_start_time: e.target.value }))}
                              className={classes.orangeFocused}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              select
                              size="small"
                              label="Default room"
                              fullWidth
                              variant="outlined"
                              value={editServiceForm.class_default_room}
                              onChange={e => setEditServiceForm(f => ({ ...f, class_default_room: e.target.value }))}
                              className={classes.orangeFocused}
                              SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                            >
                              {getClassRoomOptions()
                                .map(({ location, room }) => (
                                  <MenuItem key={room.id} value={room.id} className={classes.menuItem}>
                                    {`${location.name} | ${room.name}`}
                                  </MenuItem>
                                ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              select
                              size="small"
                              label="Primary class staff"
                              fullWidth
                              variant="outlined"
                              value={editServiceForm.class_primary_staff}
                              onChange={(e) => setEditServiceForm((f) => ({
                                ...f,
                                class_primary_staff: e.target.value,
                              }))}
                              className={classes.orangeFocused}
                              SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                            >
                              {staffList
                                .filter((staff) => {
                                  const id = String(staff.userId || staff.user_id || staff.id);
                                  return editServiceForm.staff_members.includes(id) || String(editServiceForm.class_primary_staff || '') === id;
                                })
                                .map((staff) => {
                                  const id = String(staff.userId || staff.user_id || staff.id);
                                  const name = staff.name || `${staff.firstName || staff.first_name || ''} ${staff.lastName || staff.last_name || ''}`.trim() || staff.email || 'Staff';
                                  return (
                                    <MenuItem key={id} value={id} className={classes.menuItem}>
                                      {name}
                                    </MenuItem>
                                  );
                                })}
                            </TextField>
                          </Grid>
                          {normalizeClassWeekdays(editServiceForm.class_weekdays).length > 0 && (
                            <Grid item xs={12}>
                              <Box style={{ border: '1px solid #dce8f5', borderRadius: 12, background: '#f7faff', padding: 12 }}>
                                <Typography variant="caption" style={{ color: '#002D55', fontWeight: 600, display: 'block', marginBottom: 10 }}>
                                  Primary staff by weekday
                                </Typography>
                                <Grid container spacing={2}>
                                  {normalizeClassWeekdays(editServiceForm.class_weekdays).map((weekday) => {
                                    const fallbackStaffId = String(editServiceForm.class_primary_staff || (editServiceForm.staff_members[0] || '') || '').trim();
                                    const selectedStaffId = getClassWeekdayPrimaryStaffValue(editServiceForm.class_weekday_primary_staffs, weekday, fallbackStaffId);
                                    return (
                                      <Grid item xs={12} sm={6} md={4} key={`edit-weekday-primary-${weekday}`}>
                                        <TextField
                                          select
                                          size="small"
                                          fullWidth
                                          variant="outlined"
                                          label={`${CLASS_WEEKDAY_OPTIONS.find((option) => option.value === weekday)?.label || 'Day'} primary staff`}
                                          value={selectedStaffId}
                                          onChange={(event) => setEditServiceForm((form) => ({
                                            ...form,
                                            class_weekday_primary_staffs: {
                                              ...(form.class_weekday_primary_staffs || {}),
                                              [String(weekday)]: String(event.target.value || ''),
                                            },
                                          }))}
                                          className={classes.orangeFocused}
                                          SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                                        >
                                          {staffList
                                            .filter((staff) => {
                                              const id = String(staff.userId || staff.user_id || staff.id);
                                              return editServiceForm.staff_members.includes(id) || selectedStaffId === id || fallbackStaffId === id;
                                            })
                                            .map((staff) => {
                                              const id = String(staff.userId || staff.user_id || staff.id);
                                              const name = staff.name || `${staff.firstName || staff.first_name || ''} ${staff.lastName || staff.last_name || ''}`.trim() || staff.email || 'Staff';
                                              return (
                                                <MenuItem key={id} value={id} className={classes.menuItem}>
                                                  {name}
                                                </MenuItem>
                                              );
                                            })}
                                        </TextField>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              </Box>
                            </Grid>
                          )}
                            {editServiceForm.is_class && (
                              <Grid item xs={12}>
                                <Box style={{ border: '1px solid #dce8f5', borderRadius: 12, background: '#ffffff', overflow: 'hidden' }}>
                                  <Box
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      padding: '12px 14px',
                                      background: '#f7faff',
                                    }}
                                  >
                                    <Typography className={classes.dialogSectionLabel} style={{ marginBottom: 0 }}>Weekly attendees</Typography>
                                    <Typography variant="caption" style={{ color: '#5f6c7b', fontWeight: 600 }}>
                                      {editServiceForm.class_patients.length} selected
                                    </Typography>
                                  </Box>
                                  <Box style={{ padding: 12 }}>
                                    <Autocomplete
                                      multiple
                                      options={clinicPatientSearchInput.trim().length >= 2 ? clinicPatients.filter((patient) => !editServiceForm.class_patients.includes(String(patient.user_id || patient.userId || patient.id))) : []}
                                      loading={clinicPatientsLoading}
                                      inputValue={clinicPatientSearchInput}
                                      onInputChange={(event, value, reason) => {
                                        if (reason === 'input' || reason === 'clear') {
                                          setClinicPatientSearchInput(value);
                                        }
                                      }}
                                      value={[]}
                                      onChange={(event, value) => {
                                        const nextPatientIds = value.map((patient) => String(patient.user_id || patient.userId || patient.id));
                                        const nextPatientIdSet = new Set(nextPatientIds);
                                        setEditServiceForm((form) => ({
                                          ...form,
                                          class_patients: Array.from(new Set([...(form.class_patients || []), ...nextPatientIds])),
                                        }));
                                        setClinicPatientSearchInput('');
                                        setClinicPatientTreatmentInfo((current) => {
                                          const next = {};
                                          Object.entries(current).forEach(([patientId, treatmentInfo]) => {
                                            if (nextPatientIdSet.has(String(patientId))) {
                                              next[patientId] = treatmentInfo;
                                            }
                                          });
                                          return next;
                                        });
                                        setClinicPatientTreatmentOptions((current) => {
                                          const next = {};
                                          Object.entries(current).forEach(([patientId, treatmentOptions]) => {
                                            if (nextPatientIdSet.has(String(patientId))) {
                                              next[patientId] = treatmentOptions;
                                            }
                                          });
                                          return next;
                                        });
                                      }}
                                      getOptionLabel={(patient) => {
                                        const firstName = String(patient.first_name || patient.firstName || '').trim();
                                        const lastName = String(patient.last_name || patient.lastName || '').trim();
                                        return `${firstName} ${lastName}`.trim() || String(patient.email || patient.user_id || patient.id || 'Patient');
                                      }}
                                      getOptionSelected={(option, value) => String(option.user_id || option.userId || option.id) === String(value.user_id || value.userId || value.id)}
                                      renderOption={(patient) => {
                                        const patientId = String(patient.user_id || patient.userId || patient.id);
                                        const patientDisplay = getPatientDisplayById(patientId);
                                        return (
                                          <Box display="flex" alignItems="center" style={{ gap: 10, width: '100%' }}>
                                            <Box>
                                              <Typography variant="body2" style={{ color: '#002D55', fontWeight: 600 }}>
                                                {patientDisplay.fullName || patientDisplay.label || patientId}
                                              </Typography>
                                              <Typography variant="caption" style={{ color: '#6f7b87', display: 'block' }}>
                                                {patientDisplay.cprNumber ? `CPR: ${patientDisplay.cprNumber}` : 'CPR: -'}
                                              </Typography>
                                              <Typography variant="caption" style={{ color: '#6f7b87', display: 'block' }}>
                                                Treatment course: {patientDisplay.treatmentCourseLabel || '-'}
                                              </Typography>
                                            </Box>
                                          </Box>
                                        );
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          size="small"
                                          label="Add weekly attendees"
                                          placeholder="Type at least 2 characters to search patients"
                                          variant="outlined"
                                          className={classes.orangeFocused}
                                          InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                              <>
                                                {clinicPatientsLoading ? (
                                                  <InputAdornment position="end">
                                                    <CircularProgress size={18} style={{ color: '#FF7F27' }} />
                                                  </InputAdornment>
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                              </>
                                            ),
                                          }}
                                        />
                                      )}
                                      renderTags={() => null}
                                      filterSelectedOptions
                                      noOptionsText={clinicPatientSearchInput.trim().length >= 2 ? 'No clinic patients match your search' : 'Type at least 2 characters to search clinic patients'}
                                      open={clinicPatientSearchInput.trim().length >= 2}
                                      disableCloseOnSelect
                                    />
                                    {editServiceForm.class_patients.length > 0 && (
                                      <Box display="flex" flexDirection="column" style={{ gap: 10, marginTop: 12 }}>
                                        {editServiceForm.class_patients.map((patientId) => {
                                          const patientDisplay = getPatientDisplayById(patientId);
                                          const treatmentOptions = clinicPatientTreatmentOptions[String(patientId)] || [];
                                          const selectedTreatmentId = String(editServiceForm.class_patient_treatment_links?.[patientId] || clinicPatientTreatmentInfo[String(patientId)]?.treatmentId || '');
                                          return (
                                            <Box
                                              key={patientId}
                                              style={{ backgroundColor: '#eef4fb', color: '#002D55', fontWeight: 600, borderRadius: 10, padding: '10px 12px', border: '1px solid #dce8f5' }}
                                            >
                                              <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 12, flexWrap: 'wrap' }}>
                                                <Box style={{ flex: '1 1 260px', minWidth: 0 }}>
                                                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
                                                    {patientDisplay.fullName || patientDisplay.label || patientId}
                                                  </Typography>
                                                  <Typography style={{ color: '#46607a', fontSize: 12, lineHeight: 1.25, marginTop: 3 }}>
                                                    {patientDisplay.cprNumber ? `CPR: ${patientDisplay.cprNumber}` : 'CPR: -'}
                                                  </Typography>
                                                </Box>
                                                <TextField
                                                  select
                                                  size="small"
                                                  variant="outlined"
                                                  label="Course"
                                                  value={selectedTreatmentId}
                                                  className={classes.orangeFocused}
                                                  SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                                                  onChange={(event) => {
                                                    const nextTreatmentId = String(event.target.value || '');
                                                    const selectedTreatment = treatmentOptions.find((treatment) => String(treatment.id) === nextTreatmentId) || null;
                                                    setEditServiceForm((form) => ({
                                                      ...form,
                                                      class_patient_treatment_links: {
                                                        ...(form.class_patient_treatment_links || {}),
                                                        [String(patientId)]: nextTreatmentId,
                                                      },
                                                    }));
                                                    setClinicPatientTreatmentInfo((current) => ({
                                                      ...current,
                                                      [String(patientId)]: {
                                                        treatmentId: nextTreatmentId,
                                                        label: getClinicTreatmentDisplayName(selectedTreatment),
                                                      },
                                                    }));
                                                  }}
                                                  style={{ marginTop: 0, backgroundColor: '#fff', width: '100%', maxWidth: 300, flex: '0 0 300px' }}
                                                >
                                                  {treatmentOptions.length > 0 ? treatmentOptions.map((treatment) => (
                                                    <MenuItem key={treatment.id} value={String(treatment.id)}>
                                                      {getClinicTreatmentDisplayName(treatment)}
                                                    </MenuItem>
                                                  )) : (
                                                    <MenuItem value="">No treatments available</MenuItem>
                                                  )}
                                                </TextField>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => setEditServiceForm((form) => ({
                                                    ...form,
                                                    class_patients: form.class_patients.filter((value) => String(value) !== String(patientId)),
                                                    class_patient_treatment_links: Object.fromEntries(
                                                      Object.entries(form.class_patient_treatment_links || {}).filter(([value]) => String(value) !== String(patientId))
                                                    ),
                                                  }))}
                                                  onMouseDown={() => {
                                                    setClinicPatientTreatmentInfo((current) => {
                                                      const next = { ...current };
                                                      delete next[String(patientId)];
                                                      return next;
                                                    });
                                                    setClinicPatientTreatmentOptions((current) => {
                                                      const next = { ...current };
                                                      delete next[String(patientId)];
                                                      return next;
                                                    });
                                                    loadedClinicPatientIdsRef.current.delete(String(patientId));
                                                  }}
                                                  style={{ color: '#F44336', marginTop: -4, marginRight: -4 }}
                                                >
                                                  <CloseIcon fontSize="small" />
                                                </IconButton>
                                              </Box>
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </Grid>
                            )}
                        </>
                      )}
                      {staffList.length > 0 && (
                        <Grid item xs={12}>
                          <Box style={{ border: '1px solid #dce8f5', borderRadius: 12, background: '#ffffff', overflow: 'hidden' }}>
                            <Box
                              onClick={() => setEditServiceCommissionOverridesOpen(prev => !prev)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 14px',
                                cursor: 'pointer',
                                background: '#f7faff',
                              }}
                            >
                              <Typography className={classes.dialogSectionLabel} style={{ marginBottom: 0 }}>Exclusive Staff Commission Overrides</Typography>
                              <Typography variant="caption" style={{color: '#5f6c7b', fontWeight: 600}}>
                                {editServiceCommissionOverridesOpen ? 'Hide' : 'Show'}
                              </Typography>
                            </Box>
                            <Collapse in={editServiceCommissionOverridesOpen} timeout="auto" unmountOnExit>
                              <Box style={{ padding: 12 }}>
                                <Typography variant="body2" style={{ color: '#5f6c7b', marginBottom: 10, fontSize: 12 }}>
                                  Leave blank to use the default commission rate for that staff member.
                                </Typography>
                                <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
                                  {staffList.map(staff => {
                                    const id = String(staff.userId || staff.user_id || staff.id);
                                    if (!editServiceForm.staff_members.includes(id)) {
                                      return null;
                                    }
                                    const name = staff.name || `${staff.firstName || staff.first_name || ''} ${staff.lastName || staff.last_name || ''}`.trim() || staff.email || 'Staff';
                                    return (
                                      <Box key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 260 }}>
                                        <Typography variant="body2" style={{ color: '#002D55', fontWeight: 600, minWidth: 120 }}>
                                          {name}
                                        </Typography>
                                        <TextField
                                          size="small"
                                          label="Override (%)"
                                          value={editServiceForm.commission_overrides[id] || ''}
                                          onChange={e => setEditServiceForm(f => ({
                                            ...f,
                                            commission_overrides: {
                                              ...(f.commission_overrides || {}),
                                              [id]: e.target.value.replace(/[^\d.]/g, ''),
                                            },
                                          }))}
                                          variant="outlined"
                                          className={classes.orangeFocused}
                                          type="number"
                                          inputProps={{ min: 0, max: 100, step: '0.01' }}
                                          style={{ width: 140 }}
                                        />
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            </Collapse>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                  <Paper elevation={0} className={classes.dialogPanel}>
                    <Typography className={classes.dialogSectionLabel}>Calendar Appearance</Typography>
                    <Typography variant="caption" style={{color: '#002D55', fontWeight: 600, display: 'block', marginBottom: 6}}>
                      Calendar color
                    </Typography>
                    <Box display="flex" alignItems="center" flexWrap="wrap" style={{gap: 8}}>
                      {SERVICE_COLOR_PRESETS.map((swatch) => (
                        <Box
                          key={swatch}
                          onClick={() => setEditServiceForm(f => ({ ...f, color: swatch }))}
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            backgroundColor: swatch,
                            cursor: 'pointer',
                            border: editServiceForm.color === swatch ? '2.5px solid #002D55' : '2px solid transparent',
                            boxShadow: editServiceForm.color === swatch ? '0 0 0 1.5px #fff inset' : 'none',
                          }}
                          title={swatch}
                        />
                      ))}
                      <input
                        type="color"
                        value={editServiceForm.color || DEFAULT_SERVICE_COLOR}
                        onChange={e => setEditServiceForm(f => ({ ...f, color: e.target.value }))}
                        style={{ width: 28, height: 28, padding: 0, border: '1px solid #dce8f5', borderRadius: 6, cursor: 'pointer', backgroundColor: 'transparent' }}
                        title="Custom service color"
                      />
                    </Box>
                  </Paper>
                  {staffList.length > 0 && (
                    <Paper elevation={0} className={classes.dialogPanel}>
                      <Typography className={classes.dialogSectionLabel}>Staff Availability</Typography>
                      <Typography variant="caption" style={{color: '#002D55', fontWeight: 600, display: 'block', marginBottom: 6}}>
                        Offered by (deselect to exclude):
                      </Typography>
                      <Box display="flex" flexWrap="wrap" style={{gap: 6}}>
                        {staffList.map(staff => {
                          const id = String(staff.userId || staff.user_id || staff.id);
                          const checked = editServiceForm.staff_members.includes(id);
                          const name = staff.name || `${staff.firstName || staff.first_name || ''} ${staff.lastName || staff.last_name || ''}`.trim() || staff.email || 'Staff';
                          return (
                            <Box
                              key={id}
                                    onClick={() => setEditServiceForm(f => {
                                      const nextStaffMembers = checked
                                        ? f.staff_members.filter((x) => x !== id)
                                        : [...f.staff_members, id];
                                      const nextPrimaryStaff = checked && String(f.class_primary_staff || '') === id
                                        ? (nextStaffMembers[0] || '')
                                        : f.class_primary_staff || (nextStaffMembers[0] || '');
                                      return {
                                        ...f,
                                        staff_members: nextStaffMembers,
                                        class_primary_staff: nextPrimaryStaff,
                                      };
                                    })}
                              style={{
                                display: 'flex', alignItems: 'center', cursor: 'pointer',
                                background: checked ? '#002D55' : '#e6eaf0',
                                color: checked ? '#fff' : '#002D55',
                                borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 600,
                                userSelect: 'none',
                                textDecoration: checked ? 'none' : 'line-through',
                              }}
                            >
                              {name}
                            </Box>
                          );
                        })}
                      </Box>
                    </Paper>
                  )}
                </Box>
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button onClick={() => setEditingService(null)} className={clsx(classes.Button3, classes.dialogActionButton)}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  className={classes.Button}
                  onClick={updateService}
                  disabled={editServiceSaving || !editServiceForm.service_code || !editServiceForm.name || !editServiceForm.duration_minutes || !editServiceForm.price || !editServiceForm.commission_rate || (editServiceForm.requires_vat && !editServiceForm.vat_amount_percent) || (editServiceForm.is_class && (!editServiceForm.class_participant_limit || !editServiceForm.class_default_room))}
                  classes={{ root: classes.dialogActionButton }}
                >
                  {editServiceSaving ? 'Saving...' : (editingService?.isDraft ? 'Create Class' : 'Save Changes')}
                </Button>
              </DialogActions>
            </Dialog>

            <Box style={{ border: '1px solid #d7e3ef', borderRadius: 18, padding: 16, backgroundColor: '#f9fbfd', margin: '16px 0 12px' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#002D55', marginBottom: 6 }}>
                Insurance support
              </Typography>
              <Typography variant="body2" style={{ color: '#5f6c7b', marginBottom: 12 }}>
                Select the insurers your clinic works with, then optionally exclude specific insurers for individual staff members.
              </Typography>

              <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 16 }}>
                {insuranceNetworkOptions.map((option) => {
                  const selected = supportedInsuranceNetworks.includes(option.value);
                  return (
                    <Chip
                      key={option.value}
                      label={option.label}
                      clickable
                      onClick={() => toggleClinicInsuranceNetwork(option.value)}
                      style={{
                        backgroundColor: selected ? '#002D55' : '#eef4fb',
                        color: selected ? '#ffffff' : '#002D55',
                        fontWeight: 600,
                      }}
                    />
                  );
                })}
              </Box>

              {staffList.length > 0 && (
                <Box>
                  <Typography variant="body2" style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                    Staff exclusions
                  </Typography>
                  {staffList.map((staff) => {
                    const staffId = String(staff.userId || staff.user_id || staff.id || '');
                    const override = findInsuranceStaffOverride(staffId);
                    const enabled = Boolean(override);
                    return (
                      <Box key={`insurance-staff-${staffId}`} style={{ borderTop: '1px solid #e5e9ef', paddingTop: 12, marginTop: 12 }}>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={enabled}
                              onChange={(e) => toggleInsuranceStaffOverride(staffId, e.target.checked)}
                              style={{ color: enabled ? '#FF7F27' : '#002D55' }}
                            />
                          )}
                          label={(
                            <span className={classes.staffExclusionText}>
                              {`${staff.firstName || ''} ${staff.lastName || ''}`.trim() || staff.email}
                            </span>
                          )}
                        />
                        {enabled && (
                          <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginTop: 6 }}>
                            {supportedInsuranceNetworks.map((networkValue) => {
                              const option = insuranceNetworkOptions.find((item) => item.value === networkValue);
                              const excluded = (override?.excludedNetworks || []).includes(networkValue);
                              return (
                                <Chip
                                  key={`${staffId}-${networkValue}`}
                                  label={option?.label || networkValue}
                                  clickable
                                  onClick={() => toggleInsuranceStaffNetwork(staffId, networkValue)}
                                  className={classes.staffExclusionChip}
                                  style={{
                                    backgroundColor: excluded ? '#002D55' : '#eef4fb',
                                    color: excluded ? '#ffffff' : '#002D55',
                                    fontWeight: 600,
                                  }}
                                />
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Box display="flex" justifyContent="flex-end" style={{ marginTop: 14 }}>
                <Button
                  variant="contained"
                  size="small"
                  className={classes.Button2}
                  onClick={handleSaveInsuranceSupport}
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save insurance support'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
        {tabIndex === 5 && (
          <Grid item xs={12} >
            <ClinicSubscription_Settings className={classes.subscriptionTabPanel}/>
          </Grid>
        )}
        {tabIndex === 1 && (
          <Box style={{padding: '12px 0 0'}}>
            <Typography
              className={classes.bodyText}
              style={{marginBottom: 8, marginLeft: 8}}
            >
              Here you can add staff and manage their roles.
            </Typography>
            <Divider style={{marginTop: 16, marginBottom: 4, marginLeft: 8}}/>
            <Box className={classes.staffForm}>
              <Typography variant="subtitle1" style={{fontWeight:600, marginBottom:24, color: "#002D55"}}>Add Staff Member</Typography>
              <Box style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16}}>
                <Button
                  size="small"
                  variant={staffFormLanguage === 'en' ? 'contained' : 'outlined'}
                  onClick={() => setStaffFormLanguage('en')}
                  style={{minWidth: 44}}
                >
                  ????
                </Button>
                <Button
                  size="small"
                  variant={staffFormLanguage === 'da' ? 'contained' : 'outlined'}
                  onClick={() => setStaffFormLanguage('da')}
                  style={{minWidth: 44}}
                >
                  ????
                </Button>
              </Box>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="none"
                    size="small"
                    label={staffLabel('First name', 'Fornavn')}
                    value={staffFirstName}
                    onChange={e => setStaffFirstName(e.target.value)}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="none"
                    size="small"
                    label={staffLabel('Last name', 'Efternavn')}
                    value={staffLastName}
                    onChange={e => setStaffLastName(e.target.value)}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="none"
                    size="small"
                    label={staffLabel('Initials', 'Initialer')}
                    value={staffInitials}
                    onChange={e => {
                      const nextValue = String(e.target.value || '').toUpperCase();
                      const autoValue = buildAutoStaffInitials(staffFirstName, staffLastName);
                      setStaffInitials(nextValue);
                      setStaffInitialsCustomized(nextValue !== autoValue);
                    }}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="none"
                    size="small"
                    label={staffLabel('Email', 'E-mail')}
                    value={staffEmail}
                    onChange={e => {
                      const val = e.target.value.trim();
                      setStaffEmail(e.target.value);
                      const existsInPending = staffList.some(
                        s => String(s.email || '').toLowerCase() === val.toLowerCase()
                      );
                      setStaffEmailError(existsInPending ? 'A staff member with this email is already in the list.' : '');
                    }}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                    error={!!staffEmailError}
                    InputProps={staffEmailError ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={staffEmailError}>
                            <ErrorOutlineIcon style={{ color: '#f44336', fontSize: 18 }} />
                          </Tooltip>
                        </InputAdornment>
                      )
                    } : undefined}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="none"
                    size="small"
                    label={staffLabel('CPR number', 'CPR-nummer')}
                    value={staffCpr}
                    onChange={e => setStaffCpr(e.target.value)}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="none"
                    size="small"
                    label={staffLabel('Address', 'Adresse')}
                    value={staffAddress}
                    onChange={e => setStaffAddress(e.target.value)}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="none"
                    size="small"
                    label={staffLabel('City', 'By')}
                    value={staffCity}
                    onChange={e => setStaffCity(e.target.value)}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="none"
                    size="small"
                    label={staffLabel('ZIP / postal code', 'Postnummer')}
                    value={staffZip}
                    onChange={e => setStaffZip(e.target.value)}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="none"
                    size="small"
                    label={staffLabel('Engagement type', 'Ansættelsesform')}
                    select
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                    value={staffEngagementType}
                    onChange={e => {
                      const nextEngagementType = e.target.value;
                      setStaffEngagementType(nextEngagementType);
                      setStaffCompensationBasis(normalizeStaffCompensationBasis(nextEngagementType, staffCompensationBasis));
                      if (String(nextEngagementType || '').toLowerCase() === 'subcontractor') {
                        setStaffMonthlySalaryAmount('');
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                  >
                    {staffEngagementTypes.map(option => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="none"
                    size="small"
                    label={staffLabel('Compensation basis', 'Løngrundlag')}
                    select
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                    value={normalizeStaffCompensationBasis(staffEngagementType, staffCompensationBasis)}
                    onChange={e => setStaffCompensationBasis(normalizeStaffCompensationBasis(staffEngagementType, e.target.value))}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                  >
                    {getStaffCompensationBasisOptions(staffEngagementType).map(option => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="none"
                    size="small"
                    label={staffLabel('Weekly work hours', 'Ugentlige arbejdstimer')}
                    value={staffWeeklyWorkHours}
                    onChange={e => setStaffWeeklyWorkHours(e.target.value.replace(/[^\d.]/g, ''))}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                    type="number"
                    inputProps={{ min: 0, step: '0.5' }}
                  />
                </Grid>
                {String(staffEngagementType || '').toLowerCase() !== 'subcontractor' && (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        margin="none"
                        size="small"
                        label={String(staffCompensationBasis || '').toLowerCase() === 'commission_salary' ? staffLabel('Base monthly salary amount', 'Fast grundløn') : staffLabel('Monthly salary amount', 'Månedsløn')}
                        value={staffMonthlySalaryAmount}
                        onChange={e => setStaffMonthlySalaryAmount(e.target.value.replace(/[^\d.]/g, ''))}
                        fullWidth
                        variant="outlined"
                        className={classes.orangeFocused}
                        type="number"
                        inputProps={{ min: 0, step: '0.01' }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        margin="none"
                        size="small"
                        label={staffLabel('Employee pension %', 'Medarbejder pension %')}
                        value={staffPensionPercent}
                        onChange={e => setStaffPensionPercent(e.target.value.replace(/[^\d.]/g, ''))}
                        fullWidth
                        variant="outlined"
                        className={classes.orangeFocused}
                        type="number"
                        inputProps={{ min: 0, max: 100, step: '0.01' }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        margin="none"
                        size="small"
                        label={staffLabel('Employer pension %', 'Arbejdsgiver pension %')}
                        value={staffEmployerPensionPercent}
                        onChange={e => setStaffEmployerPensionPercent(e.target.value.replace(/[^\d.]/g, ''))}
                        fullWidth
                        variant="outlined"
                        className={classes.orangeFocused}
                        type="number"
                        inputProps={{ min: 0, max: 100, step: '0.01' }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <TextField
                        margin="none"
                        size="small"
                        label={staffLabel('Deduction (fradrag)', 'Fradrag')}
                        value={staffTaxFradrag}
                        onChange={e => setStaffTaxFradrag(e.target.value.replace(/[^\d.]/g, ''))}
                        fullWidth
                        variant="outlined"
                        className={classes.orangeFocused}
                        type="number"
                        inputProps={{ min: 0, step: '0.01' }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        margin="none"
                        size="small"
                        label={staffLabel('Tax rate', 'Trækprocent')}
                        value={staffTaxTrakprocent}
                        onChange={e => setStaffTaxTrakprocent(e.target.value.replace(/[^\d.]/g, ''))}
                        fullWidth
                        variant="outlined"
                        className={classes.orangeFocused}
                        type="number"
                        inputProps={{ min: 0, max: 100, step: '0.01' }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        margin="none"
                        size="small"
                        label={staffLabel('Vacation scheme', 'Ferieskema')}
                        select
                        SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                        value={staffVacationScheme}
                        onChange={e => setStaffVacationScheme(e.target.value)}
                        fullWidth
                        variant="outlined"
                        className={classes.orangeFocused}
                      >
                        <MenuItem value="paid_vacation_days">{staffLabel('Paid vacation days', 'Betalte feriedage')}</MenuItem>
                        <MenuItem value="holiday_allowance">{staffLabel('Holiday Allowance %', 'Feriepenge %')}</MenuItem>
                      </TextField>
                    </Grid>
                    {staffVacationScheme === 'paid_vacation_days' && (
                      <Grid item xs={12} md={4}>
                        <TextField
                          margin="none"
                          size="small"
                          label={staffLabel('Paid vacation days', 'Betalte feriedage')}
                          value={staffPaidVacationDays}
                          onChange={e => setStaffPaidVacationDays(e.target.value.replace(/[^\d.]/g, ''))}
                          fullWidth
                          variant="outlined"
                          className={classes.orangeFocused}
                          type="number"
                          inputProps={{ min: 0, step: '0.01' }}
                        />
                      </Grid>
                    )}
                    {staffVacationScheme === 'holiday_allowance' && (
                      <Grid item xs={12} md={4}>
                        <TextField
                          margin="none"
                          size="small"
                          label={staffLabel('Holiday Allowance %', 'Feriepenge %')}
                          value={staffHolidayAllowancePercent}
                          onChange={e => setStaffHolidayAllowancePercent(e.target.value.replace(/[^\d.]/g, ''))}
                          fullWidth
                          variant="outlined"
                          className={classes.orangeFocused}
                          type="number"
                          inputProps={{ min: 0, max: 100, step: '0.01' }}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={4}>
                      <TextField
                        margin="none"
                        size="small"
                        label={staffLabel('Vacation accrual / month', 'Ferieopsparing / måned')}
                        value={staffVacationAccrualPerMonth}
                        onChange={e => setStaffVacationAccrualPerMonth(e.target.value.replace(/[^\d.]/g, ''))}
                        fullWidth
                        variant="outlined"
                        className={classes.orangeFocused}
                        type="number"
                        disabled={clinicOperatesInDenmark}
                        inputProps={{ min: 0, step: '0.01' }}
                      />
                    </Grid>
                  </>
                )}
                
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="none"
                    size="small"
                    label={staffLabel('Role', 'Rolle')}
                    select
                    SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                    value={staffRole}
                    onChange={e => {
                      setStaffRole(e.target.value);
                      if (!isClinicalStaffRole(e.target.value)) {
                        setStaffAuthIDError('');
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                  >
                    {staffRoles.map(role => (
                      <MenuItem key={role} value={role}>{staffLabel(role, ({ Physiotherapist: 'Fysioterapeut', Admin: 'Administrator', Receptionist: 'Receptionist', Assistant: 'Assistent' }[role] || role))}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    margin="none"
                    size="small"
                    label={isClinicalStaffRole(staffRole) ? 'Authorization ID' : 'Authorization ID (optional)'}
                    value={staffAuthorization_ID}
                    onChange={e => {
                      setStaffAuthorization_ID(e.target.value);
                      if (e.target.value.trim()) setStaffAuthIDError('');
                    }}
                    onBlur={() => {
                      if (!staffAuthorization_ID.trim()) {
                        setStaffAuthIDError(isClinicalStaffRole(staffRole) ? 'Authorization ID is required.' : '');
                      } else {
                        setStaffAuthIDError('');
                        validateAuthorizationID(staffAuthorization_ID);
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                    error={!!staffAuthIDError || (staffAuthorizationValid === false && !!staffAuthorizationError)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {staffAuthorizationLoading ? (
                            <CircularProgress size={16} style={{ color: '#FF7F27' }} />
                          ) : staffAuthIDError ? (
                            <Tooltip title={staffAuthIDError}>
                              <ErrorOutlineIcon style={{ color: '#f44336', fontSize: 18 }} />
                            </Tooltip>
                          ) : staffAuthorizationValid === true ? (
                            <Tooltip title={staffAuthorizationError || 'Authorization valid'}>
                              <CheckCircleIcon style={{ color: '#4caf50', fontSize: 18 }} />
                            </Tooltip>
                          ) : staffAuthorizationValid === false ? (
                            <Tooltip title={staffAuthorizationError || 'Invalid Authorization ID'}>
                              <ErrorOutlineIcon style={{ color: '#f44336', fontSize: 18 }} />
                            </Tooltip>
                          ) : null}
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    margin="none"
                    size="small"
                    label="Seat tier"
                    value={staffSeatTier}
                    onChange={e => setStaffSeatTier(e.target.value)}
                    fullWidth
                    variant="outlined"
                    className={classes.orangeFocused}
                    helperText="Leave as Inherit to use the clinic tier."
                  >
                    <MenuItem value="">Inherit clinic tier</MenuItem>
                    <MenuItem value="stage0">Stage 0 | Free Entry</MenuItem>
                    <MenuItem value="stage1">Stage 1 | Clinical Workflow</MenuItem>
                    <MenuItem value="stage2">Stage 2 | Full Platform</MenuItem>
                  </TextField>
                </Grid>
                {canAddStaff && (
                  <Grid item xs={12}>
                    <Box display="flex" style={{ gap: 8, marginTop: 8 }}>
                      <Button
                        className={classes.Button}
                        style={{ minHeight: 38, marginRight: 0, flex: 1 }}
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => {
                          const emailTrimmed = staffEmail.trim();
                          const alreadyExists = staffList.some(
                            s => String(s.email || '').toLowerCase() === emailTrimmed.toLowerCase()
                          );
                          if (alreadyExists) {
                            setStaffEmailError('A staff member with this email is already in the list.');
                            return;
                          }
                          if (isClinicalStaffRole(staffRole) && !staffAuthorization_ID.trim()) {
                            setStaffAuthIDError('Authorization ID is required.');
                            return;
                          }
                          setStaffList([...staffList, { firstName: staffFirstName, lastName: staffLastName, initials: staffInitials, email: staffEmail, role: staffRole, cpr: staffCpr, address: staffAddress, city: staffCity, zip: staffZip, employment_started_at: staffEmploymentStartedAt, employment_active: staffEmploymentActive, employment_terminated_at: staffEmploymentTerminatedAt, weekly_work_hours: staffWeeklyWorkHours || '37.5', staff_engagement_type: staffEngagementType, compensation_basis: normalizeStaffCompensationBasis(staffEngagementType, staffCompensationBasis), monthly_salary_amount: String(staffEngagementType || '').toLowerCase() === 'subcontractor' ? '' : staffMonthlySalaryAmount, pension_percent: String(staffEngagementType || '').toLowerCase() === 'subcontractor' ? '' : staffPensionPercent, employer_pension_percent: String(staffEngagementType || '').toLowerCase() === 'subcontractor' ? '' : staffEmployerPensionPercent, vacation_scheme: String(staffEngagementType || '').toLowerCase() === 'subcontractor' ? 'holiday_allowance' : staffVacationScheme, paid_vacation_days: String(staffEngagementType || '').toLowerCase() === 'subcontractor' ? '' : staffPaidVacationDays, tax_fradrag: String(staffEngagementType || '').toLowerCase() === 'subcontractor' ? '' : staffTaxFradrag, tax_trakprocent: String(staffEngagementType || '').toLowerCase() === 'subcontractor' ? '' : staffTaxTrakprocent, holiday_allowance_percent: String(staffEngagementType || '').toLowerCase() === 'subcontractor' ? '' : staffHolidayAllowancePercent, vacation_accrual_per_month: String(staffEngagementType || '').toLowerCase() === 'subcontractor' ? '' : staffVacationAccrualPerMonth, authorization_ID: isClinicalStaffRole(staffRole) ? staffAuthorization_ID : '', seat_tier: staffSeatTier }]);
                          clearStaffFields();
                        }}
                      >
                        Add
                      </Button>
                      <Button
                        className={classes.Button3}
                        variant="outlined"
                        fullWidth
                        style={{ flex: 1 }}
                        onClick={clearStaffFields}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
            <Box className={classes.staffList}>
              <Typography variant="subtitle1" style={{fontWeight:600, marginBottom:8, marginRight: 10, color: "#002D55"}}>Current Staff</Typography>
              <Typography variant="body2" style={{ color: '#5f6c7b', marginBottom: 12 }}>
                Independent subcontractors can connect their own Stripe account here without changing clinic-level billing.
              </Typography>
              {staffList.length === 0 ? (
                <Typography color="textSecondary">No staff members added yet.</Typography>
              ) : (
                staffList.map((staff, idx) => (
                  <Box key={staff.userId || idx} className={classes.staffItem}>
                    <span>
                      <b>{(staff.firstName || staff.name || '') + (staff.lastName ? (' ' + staff.lastName) : '')}</b> &lt;{staff.email}&gt;
                      <span className={classes.staffRole}>{staff.role} | {formatStaffEngagementTypeLabel(staff.staff_engagement_type)} | {formatStaffCompensationBasisLabel(normalizeStaffCompensationBasis(staff.staff_engagement_type, staff.compensation_basis))}{staff.employment_started_at ? ` | Started ${staff.employment_started_at}` : ''}{staff.employment_active === false ? ` | Inactive${staff.employment_terminated_at ? ` since ${staff.employment_terminated_at}` : ''}` : ' | Active'}</span>
                    </span>
                    <div style={{ display: 'flex', gap: 8, flexWrap: isMobile ? 'wrap' : 'nowrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <button
                        className={classes.removeBtn}
                        onClick={() => setStaffList(staffList.filter((_, i) => i !== idx))}
                        style={{ flex: isMobile ? '1 1 110px' : '0 0 auto', whiteSpace: 'nowrap' }}
                        type="button"
                      >
                        Remove
                      </button>
                      {staff.userId && (
                        <button
                          className={clsx(classes.Button, classes.staffActionBtn)}
                          style={{ flex: isMobile ? '1 1 110px' : '0 0 auto', whiteSpace: 'nowrap' }}
                          onClick={() => openEditStaff(staff)}
                          type="button"
                        >
                          Edit
                        </button>
                      )}
                      {String(staff.staff_engagement_type || 'employee').toLowerCase() === 'subcontractor' && (
                        <button
                          className={clsx(classes.Button, classes.staffActionBtn)}
                          style={{ flex: isMobile ? '1 1 130px' : '0 0 auto', whiteSpace: 'nowrap' }}
                          onClick={() => handleStartStaffStripeOnboarding(staff)}
                          type="button"
                        >
                          {staff.stripe_onboarding_complete ? 'Stripe ready' : 'Set up Stripe'}
                        </button>
                      )}
                      <button
                        className={clsx(classes.Button, classes.staffActionBtn)}
                        style={{ flex: isMobile ? '1 1 130px' : '0 0 auto', whiteSpace: 'nowrap' }}
                        onClick={() => sendStaffResetEmail(staff)}
                        type="button"
                      >
                        Send reset email
                      </button>
                    </div>
                  </Box>
                ))
              )}
            </Box>
        <Divider className={classes.footerDivider} />
          <Box className={classes.footerActions} display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
            <Button
              className={clsx(classes.Button, classes.footerActionButton)}
              variant="contained"
              onClick={() => setOwnerPasswordDialogOpen(true)}
              disabled={!hasPendingStaff}
              fullWidth
              style={{ flex: 1 }}
            >
              Create staff
            </Button>
            <Button
              className={clsx(classes.Button3, classes.footerActionButton)}
              variant="outlined"
              onClick={handleCancel}
              fullWidth
              style={{ flex: 1 }}
            >
              <b>Cancel</b>
            </Button>
          </Box>

          <Dialog open={ownerPasswordDialogOpen} onClose={() => setOwnerPasswordDialogOpen(false)} PaperProps={{ className: classes.dialogPaper }} fullScreen={isMobile}>
            <DialogTitle className={classes.dialogTitle}>
              <Box className={classes.dialogTitleRow}>
                <Typography className={classes.dialogTitleText}>Confirm Creating Staff</Typography>
                <IconButton onClick={() => { setOwnerPassword(''); setOwnerPasswordDialogOpen(false); }} className={classes.closeButton} size="small"><CloseIcon fontSize="small" /></IconButton>
              </Box>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <Paper elevation={0} className={classes.dialogPanel}>
                <Typography className={classes.dialogSectionLabel}>Owner Verification</Typography>
                <Typography style={{marginBottom: 12, fontSize: 13, color: '#666'}}>Please enter your password to confirm creating the staff members.</Typography>
                <TextField
                  margin="none"
                  size="small"
                  label="Password"
                  type="password"
                  value={ownerPassword}
                  onChange={e => setOwnerPassword(e.target.value)}
                  fullWidth
                  variant="outlined"
                  className={classes.orangeFocused}
                />
              </Paper>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button onClick={() => { setOwnerPassword(''); setOwnerPasswordDialogOpen(false); }} className={clsx(classes.Button3, classes.dialogActionButton)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                className={classes.Button2}
                onClick={() => { setOwnerPasswordDialogOpen(false); saveStaffChanges(); }}
                disabled={!ownerPassword}
                classes={{ root: classes.dialogActionButton }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={Boolean(editingStaff)} onClose={() => setEditingStaff(null)} PaperProps={{ className: classes.dialogPaper }} fullWidth maxWidth="sm" fullScreen={isMobile}>
            <DialogTitle className={classes.dialogTitle}>
              <Box className={classes.dialogTitleRow}>
                <Typography className={classes.dialogTitleText}>Edit Staff Member</Typography>
                <IconButton onClick={() => setEditingStaff(null)} className={classes.closeButton} size="small"><CloseIcon fontSize="small" /></IconButton>
              </Box>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <Paper elevation={0} className={classes.dialogPanel}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="none"
                      size="small"
                      label="First name"
                      value={editStaffForm.firstName}
                      onChange={e => setEditStaffForm(prev => ({ ...prev, firstName: e.target.value }))}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="none"
                      size="small"
                      label="Last name"
                      value={editStaffForm.lastName}
                      onChange={e => setEditStaffForm(prev => ({ ...prev, lastName: e.target.value }))}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="none"
                      size="small"
                      label="Initials"
                      value={editStaffForm.initials}
                      onChange={e => setEditStaffForm(prev => ({ ...prev, initials: String(e.target.value || '').toUpperCase() }))}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="none"
                      size="small"
                      label="Email"
                      value={editStaffForm.email}
                      onChange={e => setEditStaffForm(prev => ({ ...prev, email: e.target.value }))}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="none"
                      size="small"
                      label="CPR number"
                      value={editStaffForm.cpr}
                      onChange={e => setEditStaffForm(prev => ({ ...prev, cpr: e.target.value }))}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="none"
                      size="small"
                      label="Address"
                      value={editStaffForm.address}
                      onChange={e => setEditStaffForm(prev => ({ ...prev, address: e.target.value }))}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="none"
                      size="small"
                      label="City"
                      value={editStaffForm.city}
                      onChange={e => setEditStaffForm(prev => ({ ...prev, city: e.target.value }))}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="none"
                      size="small"
                      label="ZIP / postal code"
                      value={editStaffForm.zip}
                      onChange={e => setEditStaffForm(prev => ({ ...prev, zip: e.target.value }))}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="none"
                      size="small"
                      label="Engagement type"
                      select
                      SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                      value={editStaffForm.staff_engagement_type}
                      onChange={e => setEditStaffForm(prev => ({
                        ...prev,
                        staff_engagement_type: e.target.value,
                        compensation_basis: normalizeStaffCompensationBasis(e.target.value, prev.compensation_basis),
                        monthly_salary_amount: String(e.target.value || '').toLowerCase() === 'subcontractor' ? '' : prev.monthly_salary_amount,
                        pension_percent: String(e.target.value || '').toLowerCase() === 'subcontractor' ? '' : prev.pension_percent,
                        vacation_scheme: String(e.target.value || '').toLowerCase() === 'subcontractor' ? 'holiday_allowance' : prev.vacation_scheme,
                        paid_vacation_days: String(e.target.value || '').toLowerCase() === 'subcontractor' ? '' : prev.paid_vacation_days,
                        tax_fradrag: String(e.target.value || '').toLowerCase() === 'subcontractor' ? '' : prev.tax_fradrag,
                        tax_trakprocent: String(e.target.value || '').toLowerCase() === 'subcontractor' ? '' : prev.tax_trakprocent,
                        holiday_allowance_percent: String(e.target.value || '').toLowerCase() === 'subcontractor' ? '' : prev.holiday_allowance_percent,
                        vacation_accrual_per_month: String(e.target.value || '').toLowerCase() === 'subcontractor' ? '' : prev.vacation_accrual_per_month,
                      }))}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                    >
                      {staffEngagementTypes.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="none"
                      size="small"
                      label={String(editStaffForm.staff_engagement_type || '').toLowerCase() === 'subcontractor'
                        ? staffLabel('Status', 'Status')
                        : staffLabel('Employment status', 'Ansættelsesstatus')}
                      select
                      SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                      value={editStaffForm.employment_active ? 'active' : 'inactive'}
                      onChange={e => setEditStaffForm(prev => {
                        const isActive = e.target.value === 'active';
                        return {
                          ...prev,
                          employment_active: isActive,
                          employment_terminated_at: isActive ? '' : prev.employment_terminated_at,
                        };
                      })}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="none"
                      size="small"
                      label={String(editStaffForm.staff_engagement_type || '').toLowerCase() === 'subcontractor'
                        ? staffLabel('Start date', 'Startdato')
                        : staffLabel('Employment start date', 'Ansættelsesstart')}
                      value={editStaffForm.employment_started_at}
                      onChange={e => setEditStaffForm(prev => ({ ...prev, employment_started_at: e.target.value }))}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {!editStaffForm.employment_active && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        margin="none"
                        size="small"
                        label={staffLabel('Termination date', 'Fratrædelsesdato')}
                        value={editStaffForm.employment_terminated_at}
                        fullWidth
                        variant="outlined"
                        className={classes.orangeFocused}
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        onChange={e => setEditStaffForm(prev => {
                          const terminationDate = e.target.value;
                          return {
                            ...prev,
                            employment_terminated_at: terminationDate,
                            employment_active: terminationDate ? false : prev.employment_active,
                          };
                        })}
                      />
                    </Grid>
                  )}
                  
                  {String(editStaffForm.staff_engagement_type || '').toLowerCase() !== 'subcontractor' && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        margin="none"
                        size="small"
                          label={staffLabel('Compensation basis', 'Løngrundlag')}
                        select
                        SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                        value={normalizeStaffCompensationBasis(editStaffForm.staff_engagement_type, editStaffForm.compensation_basis)}
                        onChange={e => setEditStaffForm(prev => ({ ...prev, compensation_basis: normalizeStaffCompensationBasis(prev.staff_engagement_type, e.target.value) }))}
                        fullWidth
                        variant="outlined"
                        className={classes.orangeFocused}
                      >
                        {getStaffCompensationBasisOptions(editStaffForm.staff_engagement_type).map(option => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="none"
                      size="small"
                      label={staffLabel('Weekly work hours', 'Ugentlige arbejdstimer')}
                      value={editStaffForm.weekly_work_hours}
                      onChange={e => setEditStaffForm(prev => ({ ...prev, weekly_work_hours: e.target.value.replace(/[^\d.]/g, '') }))}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                      type="number"
                      inputProps={{ min: 0, step: '0.5' }}
                    />
                  </Grid>
                  {String(editStaffForm.staff_engagement_type || '').toLowerCase() !== 'subcontractor' && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          margin="none"
                          size="small"
                          label={String(editStaffForm.compensation_basis || '').toLowerCase() === 'commission_salary' ? staffLabel('Base monthly salary amount', 'Fast grundløn') : staffLabel('Monthly salary amount', 'Månedsløn')}
                          value={editStaffForm.monthly_salary_amount}
                          onChange={e => setEditStaffForm(prev => ({ ...prev, monthly_salary_amount: e.target.value.replace(/[^\d.]/g, '') }))}
                          fullWidth
                          variant="outlined"
                          className={classes.orangeFocused}
                          type="number"
                          inputProps={{ min: 0, step: '0.01' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          margin="none"
                          size="small"
                          label={staffLabel('Employee pension %', 'Medarbejder pension %')}
                          value={editStaffForm.pension_percent || ''}
                          onChange={e => setEditStaffForm(prev => ({ ...prev, pension_percent: e.target.value.replace(/[^\d.]/g, '') }))}
                          fullWidth
                          variant="outlined"
                          className={classes.orangeFocused}
                          type="number"
                          inputProps={{ min: 0, max: 100, step: '0.01' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          margin="none"
                          size="small"
                          label={staffLabel('Employer pension %', 'Arbejdsgiver pension %')}
                          value={editStaffForm.employer_pension_percent || ''}
                          onChange={e => setEditStaffForm(prev => ({ ...prev, employer_pension_percent: e.target.value.replace(/[^\d.]/g, '') }))}
                          fullWidth
                          variant="outlined"
                          className={classes.orangeFocused}
                          type="number"
                          inputProps={{ min: 0, max: 100, step: '0.01' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          margin="none"
                          size="small"
                          label={staffLabel('Vacation scheme', 'Ferieskema')}
                          select
                          SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                          value={editStaffForm.vacation_scheme || 'paid_vacation_days'}
                          onChange={e => setEditStaffForm(prev => ({ ...prev, vacation_scheme: e.target.value }))}
                          fullWidth
                          variant="outlined"
                          className={classes.orangeFocused}
                        >
                          <MenuItem value="paid_vacation_days">{staffLabel('Paid vacation days', 'Betalte feriedage')}</MenuItem>
                          <MenuItem value="holiday_allowance">{staffLabel('Holiday Allowance %', 'Feriepenge %')}</MenuItem>
                        </TextField>
                      </Grid>
                      {String(editStaffForm.vacation_scheme || '') === 'paid_vacation_days' && (
                        <Grid item xs={12} sm={6}>
                          <TextField
                            margin="none"
                            size="small"
                            label={staffLabel('Paid vacation days', 'Betalte feriedage')}
                            value={editStaffForm.paid_vacation_days || ''}
                            onChange={e => setEditStaffForm(prev => ({ ...prev, paid_vacation_days: e.target.value.replace(/[^\d.]/g, '') }))}
                            fullWidth
                            variant="outlined"
                            className={classes.orangeFocused}
                            type="number"
                            inputProps={{ min: 0, step: '0.01' }}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          margin="none"
                          size="small"
                          label={staffLabel('Deduction (fradrag)', 'Fradrag')}
                          value={editStaffForm.tax_fradrag || ''}
                          onChange={e => setEditStaffForm(prev => ({ ...prev, tax_fradrag: e.target.value.replace(/[^\d.]/g, '') }))}
                          fullWidth
                          variant="outlined"
                          className={classes.orangeFocused}
                          type="number"
                          inputProps={{ min: 0, step: '0.01' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          margin="none"
                          size="small"
                          label={staffLabel('Tax rate', 'Trækprocent')}
                          value={editStaffForm.tax_trakprocent || ''}
                          onChange={e => setEditStaffForm(prev => ({ ...prev, tax_trakprocent: e.target.value.replace(/[^\d.]/g, '') }))}
                          fullWidth
                          variant="outlined"
                          className={classes.orangeFocused}
                          type="number"
                          inputProps={{ min: 0, max: 100, step: '0.01' }}
                        />
                      </Grid>
                      {String(editStaffForm.vacation_scheme || '') === 'holiday_allowance' && (
                        <Grid item xs={12} sm={6}>
                          <TextField
                            margin="none"
                            size="small"
                            label={staffLabel('Holiday Allowance %', 'Feriepenge %')}
                            value={editStaffForm.holiday_allowance_percent || ''}
                            onChange={e => setEditStaffForm(prev => ({ ...prev, holiday_allowance_percent: e.target.value.replace(/[^\d.]/g, '') }))}
                            fullWidth
                            variant="outlined"
                            className={classes.orangeFocused}
                            type="number"
                            inputProps={{ min: 0, max: 100, step: '0.01' }}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          margin="none"
                          size="small"
                          label={staffLabel('Vacation accrual / month', 'Ferieopsparing / måned')}
                          value={editStaffForm.vacation_accrual_per_month || ''}
                          onChange={e => setEditStaffForm(prev => ({ ...prev, vacation_accrual_per_month: e.target.value.replace(/[^\d.]/g, '') }))}
                          fullWidth
                          variant="outlined"
                          className={classes.orangeFocused}
                          type="number"
                          disabled={clinicOperatesInDenmark}
                          inputProps={{ min: 0, step: '0.01' }}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="none"
                      size="small"
                      label={staffLabel('Role', 'Rolle')}
                      select
                      SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                      value={editStaffForm.role}
                      onChange={e => setEditStaffForm(prev => ({ ...prev, role: e.target.value }))}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                    >
                      {staffRoles.map(role => (
                        <MenuItem key={role} value={role}>{staffLabel(role, ({ Physiotherapist: 'Fysioterapeut', Admin: 'Administrator', Receptionist: 'Receptionist', Assistant: 'Assistent' }[role] || role))}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      margin="none"
                      size="small"
                      label={isClinicalStaffRole(editStaffForm.role) ? 'Authorization ID' : 'Authorization ID (optional)'}
                      value={editStaffForm.authorization_ID}
                      onChange={e => setEditStaffForm(prev => ({ ...prev, authorization_ID: e.target.value }))}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                      helperText={isClinicalStaffRole(editStaffForm.role) ? 'Required for clinical staff.' : 'Not required for non-clinical staff.'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="none"
                      size="small"
                      label={staffLabel('Seat tier', 'Licensniveau')}
                      select
                      SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                      value={editStaffForm.seat_tier || ''}
                      onChange={e => setEditStaffForm(prev => ({ ...prev, seat_tier: e.target.value }))}
                      fullWidth
                      variant="outlined"
                      className={classes.orangeFocused}
                      helperText={staffLabel('Leave as Inherit to use the clinic tier.', 'Vælg Nedarv for at bruge klinikkens niveau.')}
                    >
                      <MenuItem value="">{staffLabel('Inherit clinic tier', 'Nedarv klinikniveau')}</MenuItem>
                      <MenuItem value="stage0">Stage 0 | Free Entry</MenuItem>
                      <MenuItem value="stage1">Stage 1 | Clinical Workflow</MenuItem>
                      <MenuItem value="stage2">Stage 2 | Full Platform</MenuItem>
                    </TextField>
                  </Grid>
                  {editStaffError && (
                    <Grid item xs={12}>
                      <Typography style={{ color: '#f44336', fontSize: 13 }}>{editStaffError}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </DialogContent>
            <DialogActions className={classes.dialogActions} style={{ flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center' }}>
              <Button onClick={() => setEditingStaff(null)} className={clsx(classes.Button3, classes.dialogActionButton)} disabled={editStaffSaving} fullWidth={isMobile}>
                Cancel
              </Button>
              <Button
                variant="contained"
                className={classes.Button2}
                onClick={handleSaveEditStaff}
                disabled={editStaffSaving}
                classes={{ root: classes.dialogActionButton }}
                fullWidth={isMobile}
              >
                {editStaffSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogActions>
          </Dialog>
          </Box>
        )}

        {/* Tab 4: Analytics (CRM Dashboard) */}
        {tabIndex === 4 && (
          <Box style={{padding: '12px 0'}}>
            <CRMProviderDashboard analyticsKpis={{
              isDanishClinic: clinicOperatesInDenmark,
              currency: billingStats?.grand_totals?.currency || (clinicOperatesInDenmark ? 'DKK' : 'EUR'),
              reportSummary: monthEndReport?.report_summary || {},
              staffSummary: monthEndReport?.staff_summary || [],
              staffProfiles: staffList2,
              bookedMinutes: (billingStats?.month_groups || []).flatMap((monthGroup) => monthGroup.rows || []).reduce((sum, row) => {
                const service = (services || []).find((item) => String(item.id) === String(row.service_id || ''));
                const durationMinutes = Number(service?.duration_minutes || row.duration_minutes || 0) || 0;
                return sum + durationMinutes;
              }, 0),
              avgNps: careProviderAvgNps,
            }} />
          </Box>
        )}
      </Card>
    </div>
  );
};

ClinicDetails.propTypes = {
  className: PropTypes.string
};

export default ClinicDetails;
